'use client';

import UiIcon from '@/components/ui/UiIcon';
import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import SiteHeader from '@/components/SiteHeader';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import ImageCropper from '@/components/ImageCropper';
import MessageBubble from '@/components/chat/MessageBubble';
// Комментарий на русском: VirtuosoMessageList архивирован, используем CSS-скролл.
import MessageList, { type MessageListHandle } from '@/components/chat/MessageList';
import { AvatarFx } from '@/components/chat/AvatarFx';
import AnimatedSTEM from '@/components/chat/AnimatedSTEM';
import { VoicePanel } from '@/components/chat/voice';
import { API_URL, apiClient, type UserPreview } from '@/lib/api';
import { clearAuth, getAccessToken, getRefreshToken, getStoredUserRaw, setStoredUser } from '@/lib/authStorage';
import { compressImage, ensureCanvasReadableImageFile, optimizeImageForChatUpload } from '@/lib/imageUtils';
import { logUploadError } from '@/utils/errorLogger';
import { MAX_MESSAGE_LENGTH, debounce, sanitizeMessage, validateMessage, throttle, extractFirstUrl } from '@/utils/messageUtils';
import { disableWebPush, enableWebPush, getWebPushStatus } from '@/utils/pushUtils';
import {
  deleteCachedVoice,
  putVoiceBlobToCache,
  detectAudioPlatform,
  getPreferredAudioMime,
  resolveVoiceUrl,
  purgeVoiceCache,
} from '@/utils/voiceFileCache';
import { subscribeUpdates } from '@/utils/updatesWatcher';
import { purgeSiteCache } from '@/utils/siteCache';
import { getCachedChatPhotoBlob, putChatPhotoBlobToCache, purgeChatPhotoCache } from '@/utils/chatMediaCache';
import { clearPhotoCacheByChat, clearAllPhotoCache, getPhotoCacheSize } from '@/utils/chatPhotoCache';
import { chatKvClearUser, chatKvDel, chatKvGet, chatKvSet } from '@/utils/chatOfflineStore';
import { enterIosSingleInputMode } from '@/utils/iosSingleInputMode';
import type {
  AuthSession,
  BlockedUser,
  Conversation,
  DirectMessage,
  EmployeeUser,
  FriendRequest,
  MessageDensity,
  MessengerSettings,
  SearchUser,
  User,
} from './_types';
import { installMessengerViewportFix } from './_lib/iosViewportFix';
import { DateDivider, MonoTrashIcon, MonoWallpaperIcon } from './_components/messageChrome';
import { MessengerProfileSettingsSection } from './_components/settings/MessengerProfileSettingsSection';
import { MessengerSettingsPane } from './_components/settings/MessengerSettingsPane';
import { POLL_CONVERSATIONS_MS, POLL_MESSAGES_MS, dedupeLastById, formatDialogTime, makeClientId } from './_lib/messengerUtils';
import { apiCall as apiCallRaw } from './_lib/apiCall';
import { dayStamp, formatDayDividerRu } from './_lib/messageDay';
import { DraftLinkPreviewCard, useDraftLinkPreview } from './_lib/draftLinkPreview';
import { findDraftProject, parseContextId, readContextProjectId, serializeContextMessage, useDirectMessageDraft, type DraftProject } from './_lib/directMessageDraft';
import {
  DEFAULT_MY_PROFILE_FORM,
  MESSENGER_SETTINGS_SECTIONS,
  getSettingsSectionTitle,
  type MessengerSettingsSectionId,
  type MyProfileForm,
} from './_lib/messengerSettingsConfig';
import {
  chatMessagesStore,
  getChatMessageById,
  getChatMessagesSnapshot,
  getChatMessagesTail,
  getChatOldestServerId,
} from './_lib/chatMessagesStore';
import {
  detectStorageKind,
  formatBytesShort,
  offlineChatHashKey,
  offlineChatListHashKey,
  offlineChatMemKey,
  offlineConversationsKey,
  offlineDmKey,
  offlineDmWallpaperKey,
  offlinePendingKey,
  offlineRoomKey,
  offlineRoomWallpaperKey,
  offlineRoomsKey,
  offlineSettingsKey,
  offlineSyncCursorKey,
  type StorageCategoryId,
  type StorageStat,
} from './_lib/messengerStorage';

// Комментарий на русском: типы комнат (группы/каналы/помощники) для нового режима чатов.
type RoomType = 'group' | 'channel' | 'assistant';
type ChatRoom = {
  id: number;
  type: RoomType;
  title: string;
  avatar_url?: string | null;
  voice_call?: {
    active: boolean;
    call_id: number | null;
    participants_count: number;
  };
  created_at?: string;
  created_by?: number;
};
type RoomConversation = {
  room: ChatRoom;
  last_message: null | { id: number; from_user: User; message: string; created_at: string };
  unread_count: number;
};

// Комментарий на русском: офлайн-кэш/очередь мессенджера (IndexedDB).
type PendingOutboxItem = {
  kind: 'dm' | 'room';
  targetId: number; // userId (DM) или roomId (Room)
  message: string;
  replyToId?: number;
  clientId: string;
  createdAt: string;
  // Комментарий на русском: если true — отправляем текст без предпросмотра (пользователь закрыл превью перед отправкой).
  linkPreviewDisabled?: boolean;
};

const CHAT_OFFLINE_MAX_MESSAGES = 1000;
const CHAT_OFFLINE_SAVE_DEBOUNCE_MS = 350;
const CHAT_LOAD_TIMEOUT_MS = 2600;
const CHAT_SEND_TIMEOUT_MS = 4500;
// Комментарий на русском: грузим по 100 сообщений за раз (меньше кликов/подгрузок при скролле назад).
const CHAT_PAGE_LIMIT = 100;
// Комментарий на русском: фоновый префетч — 30 самых свежих чатов, по 40 сообщений (только JSON, без скачивания медиа).
const CHAT_PREFETCH_TOP_CHATS = 30;
const CHAT_PREFETCH_DELAY_MS = 140;
// Комментарий на русском: лимит чатов в ChatMap (постоянная память). LRU — старые удаляются.
const CHAT_MAP_MAX_SIZE = 100;
// Комментарий на русском: подгружаем историю, когда пользователь дошёл до 70% контента (30% от верха).
const CHAT_OLDER_TRIGGER_RATIO = 0.30;

// Комментарий на русском: единая проверка "мобилка" по брейкпоинту Tailwind sm (640px).
const isMobileViewport = () => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 639px)').matches;

// Комментарий на русском: “мобильно” для поля ввода — телефон/планшет/тач (важно из‑за экранной клавиатуры).
const isMobileLikeViewport = () =>
  isMobileViewport() || (typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches);

function formatGamePresenceText(presence?: User['game_presence'] | null): string | null {
  if (!presence) return null;
  const title = (presence.game_title || 'Игра').trim();
  const detail = (presence.detail || presence.map_title || presence.state_label || '').trim();
  return detail ? `${title}: ${detail}` : `Играет в ${title}`;
}

// Комментарий на русском: парс px из computed-style (нужно для расчёта высоты по строкам).
const cssPx = (v: string) => {
  const n = Number.parseFloat(v || '');
  return Number.isFinite(n) ? n : 0;
};

const insertNewline = (el: HTMLTextAreaElement, value: string, setValue: (v: string) => void) => {
  const start = typeof el.selectionStart === 'number' ? el.selectionStart : value.length;
  const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : start;
  const next = `${value.slice(0, start)}\n${value.slice(end)}`;
  setValue(next);
  requestAnimationFrame(() => {
    try {
      el.selectionStart = el.selectionEnd = start + 1;
    } catch {}
  });
};

function calcDesktopTextareaMaxHeightPx(el: HTMLTextAreaElement, rows: number): number {
  const cs = getComputedStyle(el);
  const lh = cssPx(cs.lineHeight) || cssPx(cs.fontSize) * 1.4 || 20;
  return Math.round(lh * rows + cssPx(cs.paddingTop) + cssPx(cs.paddingBottom));
}

function calcComposeTextareaMaxHeightPx(el: HTMLTextAreaElement, chatHeightPx: number): number {
  // Комментарий на русском: мобилка/планшет — ограничиваем по “видимой” высоте чата (над клавиатурой).
  if (isMobileLikeViewport()) return Math.max(120, Math.round(chatHeightPx * 0.6));
  // Комментарий на русском: десктоп — считаем по строкам, чтобы было ровно “до 25 строк”.
  return calcDesktopTextareaMaxHeightPx(el, 25);
}

function autosizeTextarea(el: HTMLTextAreaElement, maxHeightPx: number) {
  // Комментарий на русском: сбрасываем высоту → меряем scrollHeight → ставим clamp,
  // а при переполнении включаем вертикальный скролл (без ползунка — через scrollbar-hide).
  el.style.height = '0px';
  const sh = el.scrollHeight || 0;
  const h = Math.min(sh, Math.max(0, maxHeightPx));
  el.style.height = `${h}px`;
  el.style.overflowY = sh > maxHeightPx + 1 ? 'auto' : 'hidden';
}

function MessagesPageInner() {
  const searchParams = useSearchParams();
  const router = useRouter();

  // iOS: фикс для клавиатуры/вьюпорта. Вынесли в отдельный модуль, чтобы не раздувать страницу.
  useEffect(
    () =>
      installMessengerViewportFix({
        messagesScrollRef,
        composeInChatRef,
        scrollToBottom: () => virtuosoRef.current?.scrollToBottom('auto'),
      }),
    []
  );
  useEffect(() => {
    // Комментарий на русском: чистим таймер "подтверждения" очистки кэша.
    return () => {
      if (cachePurgeArmTRef.current != null) window.clearTimeout(cachePurgeArmTRef.current);
      cachePurgeArmTRef.current = null;
      if (chatMemSaveTRef.current != null) window.clearTimeout(chatMemSaveTRef.current);
      chatMemSaveTRef.current = null;
    };
  }, []);

  const [isAuthed, setIsAuthed] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const meRef = useRef<User | null>(null);
  // Комментарий на русском: текущий uid для сброса RAM-кэшей при смене пользователя (чтобы не “текли” сообщения между аккаунтами).
  const activeUidRef = useRef<number>(0);
  const myRoleRef = useRef<string>('');
  const myIsSuperRef = useRef(false);

  const getMyUserId = useCallback((): number => {
    const id = Number((meRef.current as any)?.id || 0) || 0;
    if (id > 0) return id;
    try {
      const raw = getStoredUserRaw();
      const u = raw ? JSON.parse(raw) : null;
      const uid = Number(u?.id || 0) || 0;
      return uid > 0 ? uid : 0;
    } catch {
      return 0;
    }
  }, []);
  const [pageVisible, setPageVisible] = useState(true);
  // Комментарий на русском: онлайн/офлайн статус сети для индикации и очереди сообщений.
  const [isOnline, setIsOnline] = useState(true);
  // Комментарий на русском: статус подключения (UI) — "Загрузка" во время попыток, "Нет сети" только после подтверждения.
  const [netBanner, setNetBanner] = useState<'hidden' | 'loading' | 'offline'>('hidden');
  const netAttemptRef = useRef(0);
  const netLoopRunningRef = useRef(false);
  const netTimerRef = useRef<number | null>(null);
  // Комментарий на русском: очередь сообщений для офлайн-отправки.
  const pendingMessagesRef = useRef<Map<string, PendingOutboxItem>>(new Map());
  const [pendingCount, setPendingCount] = useState(0);
  // Комментарий на русском: защита от параллельной отправки очереди (иначе можно запустить несколько sendAll подряд).
  const pendingSendingRef = useRef(false);

  // Комментарий на русском: отслеживаем онлайн/офлайн статус браузера.
  useEffect(() => {
    const updateOnline = () => {
      const online = typeof navigator !== 'undefined' ? navigator.onLine !== false : true;
      setIsOnline(online);
    };
    updateOnline();
    window.addEventListener('online', updateOnline);
    window.addEventListener('offline', updateOnline);
    return () => {
      window.removeEventListener('online', updateOnline);
      window.removeEventListener('offline', updateOnline);
    };
  }, []);

  // Комментарий на русском: сохраняем очередь исходящих в IndexedDB, чтобы она переживала перезагрузку/закрытие.
  useEffect(() => {
    const uid = getMyUserId();
    if (!uid) return;
    const items = Array.from(pendingMessagesRef.current.values());
    if (!items.length) {
      void chatKvDel(offlinePendingKey(uid));
      return;
    }
    void chatKvSet(offlinePendingKey(uid), items);
  }, [pendingCount]);

  // Комментарий на русском: оптимизация polling — в фоне (hidden) не дёргаем тяжёлые запросы.
  useEffect(() => {
    const onVis = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVis);
    onVis();
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const [tab, setTab] = useState<'chats' | 'friends' | 'employees' | 'settings'>('chats');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  // Комментарий на русском: персональный фон (wallpaper) для текущего DM + входящий запрос от собеседника.
  const [dmWallpaper, setDmWallpaper] = useState<null | { id: number; url: string; blur: number; brightness: number }>(null);
  const [dmWallpaperSrc, setDmWallpaperSrc] = useState<string>('');
  const dmWallpaperObjUrlRef = useRef<string>('');
  const dmWallpaperKeyRef = useRef<string>('');
  const [dmWallpaperRequest, setDmWallpaperRequest] = useState<any>(null);
  // Комментарий на русском: общий фон комнаты (виден всем участникам, устанавливает admin/owner).
  const [roomWallpaper, setRoomWallpaper] = useState<null | { id: number; url: string; blur: number; brightness: number }>(null);
  const [roomWallpaperSrc, setRoomWallpaperSrc] = useState<string>('');
  const roomWallpaperObjUrlRef = useRef<string>('');
  const roomWallpaperKeyRef = useRef<string>('');
  // UI настроек фона
  const [dmWallpaperOpen, setDmWallpaperOpen] = useState(false);
  // Комментарий на русском: профиль пользователя внутри чата (раскрывается из капельки).
  const [peerProfileOpen, setPeerProfileOpen] = useState(false);
  const [peerProfileTab, setPeerProfileTab] = useState<'voice' | 'media' | 'files' | 'design'>('voice');
  // Комментарий на русском: настройки уведомлений (расширенные).
  const [peerMuted, setPeerMuted] = useState(false);
  const [peerMuteContent, setPeerMuteContent] = useState(false);
  const [peerMuteSound, setPeerMuteSound] = useState(false);
  // Комментарий на русском: активное меню иконки (null = закрыто).
  const [peerProfileMenu, setPeerProfileMenu] = useState<'notifications' | 'trash' | 'secret' | 'block' | 'share' | null>(null);
  // Комментарий на русском: фильтры медиа во вкладке профиля.
  const [mediaOwnerFilter, setMediaOwnerFilter] = useState<'all' | 'me' | 'peer'>('all');
  const [mediaTypeFilter, setMediaTypeFilter] = useState<'all' | 'photo' | 'video'>('all');
  // Комментарий на русском: кэш индекса медиа (считаем 1 раз при открытии вкладки, чтобы плитки не “обновлялись”).
  type PeerMediaItem = { id: number; url: string; kind: 'photo' | 'video'; isMe: boolean; createdAt: string; mime?: string };
  const [peerMediaIndex, setPeerMediaIndex] = useState<PeerMediaItem[]>([]);
  const peerMediaIndexKeyRef = useRef<string>('');
  // Комментарий на русском: подгружаем фото пачками по 40 при скролле.
  const [mediaVisibleCount, setMediaVisibleCount] = useState(100);
  // Комментарий на русском: локальный кэш src (blob url) для уже загруженных фото в сетке.
  const [mediaSrcByUrl, setMediaSrcByUrl] = useState<Record<string, string>>({});
  const mediaObjUrlMapRef = useRef<Map<string, string>>(new Map());
  const mediaLoadingRef = useRef<Set<string>>(new Set());
  // Комментарий на русском: фильтр файлов (чьи файлы показываем).
  const [filesOwnerFilter, setFilesOwnerFilter] = useState<'all' | 'me' | 'peer'>('all');
  // Комментарий на русском: photo viewer для вкладки "Медиа" (как в чате).
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [photoViewerUrl, setPhotoViewerUrl] = useState<string>(''); // Комментарий на русском: текущий URL (сырой, может быть защищённым).
  const [photoViewerList, setPhotoViewerList] = useState<string[]>([]);
  const [photoViewerIndex, setPhotoViewerIndex] = useState(0);
  const [photoViewerSrc, setPhotoViewerSrc] = useState<string>(''); // Комментарий на русском: готовый src (blob/data) для <img>.
  const photoViewerObjUrlRef = useRef<string>('');
  const photoViewerKeyRef = useRef<string>('');
  const photoViewerSwipeRef = useRef<{ startX: number; startY: number; dx: number; dy: number } | null>(null);
  const [photoViewerDragY, setPhotoViewerDragY] = useState(0);
  const [photoViewerDragging, setPhotoViewerDragging] = useState(false);
  const [photoViewerScale, setPhotoViewerScale] = useState(1);
  const [photoViewerTranslate, setPhotoViewerTranslate] = useState({ x: 0, y: 0 });
  const photoViewerImgRef = useRef<HTMLImageElement | null>(null);
  const photoViewerPointerIdRef = useRef<number | null>(null);
  const photoViewerStartRef = useRef<{ x: number; y: number } | null>(null);
  const photoViewerDragYRef = useRef<number>(0);
  const photoViewerPinchRef = useRef<{ distance: number; center: { x: number; y: number }; initialScale: number; initialTranslate: { x: number; y: number } } | null>(null);
  // Комментарий на русском: ref для свайпа вверх (закрытие профиля).
  const peerProfileSwipeRef = useRef<{ startY: number; startTime: number } | null>(null);
  const [peerProfileDragY, setPeerProfileDragY] = useState(0);
  // Комментарий на русском: pull-down для увеличения аватара профиля.
  const [peerProfilePullY, setPeerProfilePullY] = useState(0);
  const peerProfilePullRef = useRef<{ startY: number; startPullY: number } | null>(null);
  const dmWallpaperFileInputRef = useRef<HTMLInputElement>(null);
  const [wallpaperCropFile, setWallpaperCropFile] = useState<File | null>(null);
  const [dmWallpaperFile, setDmWallpaperFile] = useState<File | null>(null);
  const [dmWallpaperFilePreview, setDmWallpaperFilePreview] = useState<string>('');
  const [dmWallpaperBlurDraft, setDmWallpaperBlurDraft] = useState<number>(0);
  const [dmWallpaperBrightnessDraft, setDmWallpaperBrightnessDraft] = useState<number>(100);
  const [dmWallpaperBusy, setDmWallpaperBusy] = useState(false);
  const [dmWallpaperToast, setDmWallpaperToast] = useState<string | null>(null);
  // Комментарий на русском: на iOS иногда плохо работает нативное перетаскивание input[type=range] внутри скролла.
  // Делаем "страховку" — пересчитываем значение по позиции пальца/курсора.
  const touchRangeDragRef = useRef(false);
  const setRangeValueFromClientX = useCallback(
    (el: HTMLInputElement, clientX: number, min: number, max: number, setter: (v: number) => void) => {
      try {
        const r = el.getBoundingClientRect();
        const w = Math.max(1, r.width || 1);
        const x = Math.max(r.left, Math.min(r.right, clientX));
        const t = (x - r.left) / w;
        const raw = min + t * (max - min);
        const next = Math.round(raw);
        setter(Math.max(min, Math.min(max, next)));
      } catch {
        // ignore
      }
    },
    []
  );

  useEffect(() => {
    // Комментарий на русском: preview выбранного файла фона.
    if (!dmWallpaperFile) {
      setDmWallpaperFilePreview('');
      return;
    }
    let u = '';
    try {
      u = URL.createObjectURL(dmWallpaperFile);
      setDmWallpaperFilePreview(u);
    } catch {
      setDmWallpaperFilePreview('');
      return;
    }
    return () => {
      try {
        if (u) URL.revokeObjectURL(u);
      } catch {}
    };
  }, [dmWallpaperFile]);

  useEffect(() => {
    // Комментарий на русском: фон чата загружаем через fetch с Authorization (как фото-сообщения),
    // потому что <img/background-image> не умеют добавлять Bearer header.
    const src = String(dmWallpaper?.url || '').trim();
    if (!src) {
      dmWallpaperKeyRef.current = '';
      try {
        const prev = dmWallpaperObjUrlRef.current;
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      } catch {}
      dmWallpaperObjUrlRef.current = '';
      setDmWallpaperSrc('');
      return;
    }
    if (src.startsWith('blob:') || src.startsWith('data:')) {
      dmWallpaperKeyRef.current = src;
      setDmWallpaperSrc(src);
      return;
    }
    // Если уже есть objectURL для этого src — не качаем повторно.
    if (dmWallpaperKeyRef.current === src && dmWallpaperObjUrlRef.current && dmWallpaperObjUrlRef.current.startsWith('blob:')) {
      setDmWallpaperSrc(dmWallpaperObjUrlRef.current);
      return;
    }

    let cancelled = false;
    const ac = new AbortController();

    void (async () => {
      try {
        // Сначала пробуем CacheStorage (работает даже офлайн).
        const cached = await getCachedChatPhotoBlob(src);
        if (cached && !cancelled) {
          const u0 = URL.createObjectURL(cached);
          try {
            const prev = dmWallpaperObjUrlRef.current;
            if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
          } catch {}
          dmWallpaperObjUrlRef.current = u0;
          dmWallpaperKeyRef.current = src;
          setDmWallpaperSrc(u0);
          return;
        }

        // Если офлайн и кэша нет — ждём сеть (эффект перезапустится по isOnline).
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

        const token = getAccessToken() || '';
        const headers: Record<string, string> = { Accept: 'image/*,*/*;q=0.8' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(src, { method: 'GET', credentials: 'include', headers, signal: ac.signal });
        if (!res.ok) return;
        const ct = String(res.headers.get('content-type') || '').toLowerCase();
        if (!ct.startsWith('image/')) return;
        const blob = await res.blob();
        if (cancelled) return;
        void putChatPhotoBlobToCache(src, blob, ct);
        const u = URL.createObjectURL(blob);
        try {
          const prev = dmWallpaperObjUrlRef.current;
          if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        } catch {}
        dmWallpaperObjUrlRef.current = u;
        dmWallpaperKeyRef.current = src;
        setDmWallpaperSrc(u);
      } catch {
        // ignore
      }
    })();

    return () => {
      cancelled = true;
      try {
        ac.abort();
      } catch {}
    };
  }, [dmWallpaper?.url, isOnline]);

  useEffect(() => {
    return () => {
      try {
        const prev = dmWallpaperObjUrlRef.current;
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      } catch {}
      dmWallpaperObjUrlRef.current = '';
      dmWallpaperKeyRef.current = '';
    };
  }, []);

  useEffect(() => {
    // Комментарий на русском: фон комнаты загружаем через fetch с Authorization (как фото-сообщения),
    // потому что <img/background-image> не умеют добавлять Bearer header.
    const src = String(roomWallpaper?.url || '').trim();
    if (!src) {
      roomWallpaperKeyRef.current = '';
      try {
        const prev = roomWallpaperObjUrlRef.current;
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      } catch {}
      roomWallpaperObjUrlRef.current = '';
      setRoomWallpaperSrc('');
      return;
    }
    if (src.startsWith('blob:') || src.startsWith('data:')) {
      roomWallpaperKeyRef.current = src;
      setRoomWallpaperSrc(src);
      return;
    }
    // Если уже есть objectURL для этого src — не качаем повторно.
    if (roomWallpaperKeyRef.current === src && roomWallpaperObjUrlRef.current && roomWallpaperObjUrlRef.current.startsWith('blob:')) {
      setRoomWallpaperSrc(roomWallpaperObjUrlRef.current);
      return;
    }

    let cancelled = false;
    const ac = new AbortController();

    void (async () => {
      try {
        // Сначала пробуем CacheStorage (работает даже офлайн).
        const cached = await getCachedChatPhotoBlob(src);
        if (cached && !cancelled) {
          const u0 = URL.createObjectURL(cached);
          try {
            const prev = roomWallpaperObjUrlRef.current;
            if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
          } catch {}
          roomWallpaperObjUrlRef.current = u0;
          roomWallpaperKeyRef.current = src;
          setRoomWallpaperSrc(u0);
          return;
        }

        // Если офлайн и кэша нет — ждём сеть (эффект перезапустится по isOnline).
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

        const token = getAccessToken() || '';
        const headers: Record<string, string> = { Accept: 'image/*,*/*;q=0.8' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(src, { method: 'GET', credentials: 'include', headers, signal: ac.signal });
        if (!res.ok) return;
        const ct = String(res.headers.get('content-type') || '').toLowerCase();
        if (!ct.startsWith('image/')) return;
        const blob = await res.blob();
        if (cancelled) return;
        void putChatPhotoBlobToCache(src, blob, ct);
        const u = URL.createObjectURL(blob);
        try {
          const prev = roomWallpaperObjUrlRef.current;
          if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        } catch {}
        roomWallpaperObjUrlRef.current = u;
        roomWallpaperKeyRef.current = src;
        setRoomWallpaperSrc(u);
      } catch {
        // ignore
      }
    })();

    return () => {
      cancelled = true;
      try {
        ac.abort();
      } catch {}
    };
  }, [roomWallpaper?.url, isOnline]);

  useEffect(() => {
    return () => {
      try {
        const prev = roomWallpaperObjUrlRef.current;
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      } catch {}
      roomWallpaperObjUrlRef.current = '';
      roomWallpaperKeyRef.current = '';
    };
  }, []);

  const [rooms, setRooms] = useState<RoomConversation[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [roomDraft, setRoomDraft] = useState('');

  useEffect(() => {
    // Комментарий на русском: синхронизируем черновики настроек (ползунки) с текущим фоном активного чата,
    // чтобы в "Настройки → Интерфейс" показывались актуальные значения.
    const active = selectedRoom ? roomWallpaper : dmWallpaper;
    if (!selectedUser && !selectedRoom) return;
    const blur0 = Number((active as any)?.blur || 0) || 0;
    const bright0 = Number((active as any)?.brightness || 100) || 100;
    setDmWallpaperBlurDraft(blur0);
    setDmWallpaperBrightnessDraft(bright0);
  }, [
    selectedUser?.id,
    selectedRoom?.id,
    dmWallpaper?.blur,
    dmWallpaper?.brightness,
    roomWallpaper?.blur,
    roomWallpaper?.brightness,
  ]);

  // Комментарий на русском: настройки комнаты (внутри чата по клику на название).
  const [roomSettingsOpen, setRoomSettingsOpen] = useState(false);
  // Комментарий на русском: панель голосового чата комнаты.
  const [voicePanelOpen, setVoicePanelOpen] = useState(false);
  const [roomSettingsTab, setRoomSettingsTab] = useState<'members' | 'media' | 'projects' | 'files' | 'design'>('members');
  const [roomMeta, setRoomMeta] = useState<null | {
    id: number;
    type: RoomType;
    title: string;
    description: string;
    avatar_url: string | null;
    members_count: number;
    online_count: number;
    my_role: 'owner' | 'admin' | 'member' | '';
  }>(null);
  const [roomMembers, setRoomMembers] = useState<Array<{ user: User; role: string; joined_at?: string }>>([]);
  const [roomProjects, setRoomProjects] = useState<Array<{ id: number; name: string; status?: string; progress?: number }>>([]);
  const [roomPhotos, setRoomPhotos] = useState<Array<{ id: number; url: string; created_at?: string }>>([]);
  const [roomFiles, setRoomFiles] = useState<Array<{ id: number; url: string; name?: string; created_at?: string }>>([]);
  const [roomTitleDraft, setRoomTitleDraft] = useState('');
  const [roomDescDraft, setRoomDescDraft] = useState('');
  const [roomMetaBusy, setRoomMetaBusy] = useState(false);
  const roomAvatarInputRef = useRef<HTMLInputElement>(null);
  const [roomAvatarCropFile, setRoomAvatarCropFile] = useState<File | null>(null);
  const [roomAvatarUploading, setRoomAvatarUploading] = useState(false);
  const [roomSettingsToast, setRoomSettingsToast] = useState<string | null>(null);
  // Комментарий на русском: выпадающие меню "добавить участника/проект" (поиск + скролл).
  const memberAddBtnRef = useRef<HTMLButtonElement>(null);
  const memberAddMenuRef = useRef<HTMLDivElement>(null);
  const memberAddInputRef = useRef<HTMLInputElement>(null);
  const [memberAddOpen, setMemberAddOpen] = useState(false);
  const [memberAddPos, setMemberAddPos] = useState<{ left: number; top: number } | null>(null);
  const [memberAddQuery, setMemberAddQuery] = useState('');
  const [memberAddLoading, setMemberAddLoading] = useState(false);
  const [memberAddResults, setMemberAddResults] = useState<SearchUser[]>([]);

  const projectAddBtnRef = useRef<HTMLButtonElement>(null);
  const projectAddMenuRef = useRef<HTMLDivElement>(null);
  const projectAddInputRef = useRef<HTMLInputElement>(null);
  const [projectAddOpen, setProjectAddOpen] = useState(false);
  const [projectAddPos, setProjectAddPos] = useState<{ left: number; top: number } | null>(null);
  const [projectAddQuery, setProjectAddQuery] = useState('');
  const [projectAddLoading, setProjectAddLoading] = useState(false);
  const [myProjects, setMyProjects] = useState<Array<{ id: number; name: string; status?: string; progress?: number }>>([]);
  // Комментарий на русском: ChatMap в памяти — чтобы при возврате в чат не пересобирать/не скачивать заново.
  // chatHash хранится для сравнения при открытии чата — если хэш из conversations отличается, кэш игнорируется.
  const chatMemRef = useRef<Map<string, { messages: DirectMessage[]; lastId: number; hasMore: boolean; chatHash?: string }>>(new Map());
  // Комментарий на русском: дебаунс сохранения ChatMap в IndexedDB (чтобы не писать слишком часто на poll).
  const chatMemSaveTRef = useRef<number | null>(null);
  const schedulePersistChatMem = (uid: number) => {
    const userId = Number(uid || 0) || 0;
    if (!userId) return;
    if (chatMemSaveTRef.current != null) window.clearTimeout(chatMemSaveTRef.current);
    chatMemSaveTRef.current = window.setTimeout(() => {
      chatMemSaveTRef.current = null;
      try {
        const items = Array.from(chatMemRef.current.entries()).map(([k, v]) => ({
          k,
          lastId: Number((v as any)?.lastId || 0) || 0,
          hasMore: !!(v as any)?.hasMore,
          // Комментарий на русском: сохраняем только “быструю пачку” (40) — историю подгружаем по скроллу.
          messages: Array.isArray((v as any)?.messages) ? (v as any).messages.slice(-CHAT_PAGE_LIMIT) : [],
        }));
        void chatKvSet(offlineChatMemKey(userId), { v: 1, items: items.slice(-32) });
      } catch {
        // ignore
      }
    }, 650);
  };
  // Комментарий на русском: дебаунс записи сообщений в IndexedDB (иначе будет слишком много операций).
  const offlineMessagesSaveTRef = useRef<number | null>(null);
  // Комментарий на русском: iOS — отключаем Prev/Next над клавиатурой (делая остальные поля disabled).
  const iosSingleInputCleanupRef = useRef<null | (() => void)>(null);
  // Комментарий на русском: выбранные сообщения (по стабильному ключу client_id || id) — нужно для пересылки.
  const [selectedMsgKeys, setSelectedMsgKeys] = useState<string[]>([]);
  const selectionMode = selectedMsgKeys.length > 0;
  const selectedMsgKeySet = useMemo(() => new Set(selectedMsgKeys), [selectedMsgKeys]);

  // Комментарий на русском: модалка пересылки — список получателей берём из существующих диалогов.
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardItems, setForwardItems] = useState<DirectMessage[]>([]);
  const [forwardQuery, setForwardQuery] = useState('');
  const [forwardSending, setForwardSending] = useState(false);
  const dmDraft = useDirectMessageDraft(selectedRoom ? 0 : selectedUser?.id || 0);
  const { text: draft, setText: setDraft } = dmDraft;
  const [pendingProjectContext, setPendingProjectContext] = useState<{ key: string; revision: number; project: DraftProject } | null>(null);
  const [verifiedDraftProject, setVerifiedDraftProject] = useState<{ key: string; revision: number; project: DraftProject } | null>(null);
  const [contextNotice, setContextNotice] = useState<{ key: string; text: string } | null>(null);
  const [replyTo, setReplyTo] = useState<DirectMessage | null>(null);
  // Комментарий на русском: режим редактирования отправленного текста (Telegram-логика).
  const [editTarget, setEditTarget] = useState<DirectMessage | null>(null);
  const editPrevDraftRef = useRef('');
  const editPrevRoomDraftRef = useRef('');
  // Комментарий на русском: закреплённое сообщение (локально на устройстве, по чату).
  const [pinnedMessageId, setPinnedMessageId] = useState(0);
  const [composeInChat, setComposeInChat] = useState(false);

  useEffect(() => {
    // Комментарий на русском: при смене чата сбрасываем выбор сообщений, чтобы не переслать “не тот” набор.
    setSelectedMsgKeys([]);
    // Комментарий на русском: поиск по чату сбрасываем при смене диалога/комнаты.
    setInChatSearchOpen(false);
    setInChatSearchQuery('');
    setInChatSearchActiveIdx(0);
    // Комментарий на русском: при смене чата выходим из режима редактирования и сбрасываем закрепление (перезагрузим из localStorage ниже).
    setEditTarget(null);
    editPrevDraftRef.current = '';
    editPrevRoomDraftRef.current = '';
    try {
      const key = selectedRoom?.id
        ? `lisad_pin_room:${selectedRoom.id}`
        : selectedUser?.id
          ? `lisad_pin_dm:${selectedUser.id}`
          : '';
      const raw = key ? localStorage.getItem(key) : null;
      const id = Number(raw || 0) || 0;
      setPinnedMessageId(id > 0 ? id : 0);
    } catch {
      setPinnedMessageId(0);
    }
  }, [selectedRoom?.id, selectedUser?.id]);

  // Комментарий на русском: если компонент размонтируется, возвращаем поля ввода в нормальное состояние.
  useEffect(() => {
    return () => {
      try {
        iosSingleInputCleanupRef.current?.();
      } catch {}
      iosSingleInputCleanupRef.current = null;
    };
  }, []);

  // Комментарий на русском: подписка на стор сообщений для офлайн-снимка и ChatMap без перерендера.
  useEffect(() => {
    const uid = getMyUserId();
    if (!uid) return;
    const dmId = Number(selectedUser?.id || 0) || 0;
    const rid = Number(selectedRoom?.id || 0) || 0;
    const chatKey = dmId ? `dm:${dmId}` : rid ? `room:${rid}` : '';
    if (!chatKey) return;

    const unsubscribe = chatMessagesStore.subscribe(
      (state) => state.chats[chatKey],
      (chat) => {
        if (!chat || !chat.orderedIds.length) return;
        const snap = getChatMessagesTail(chatKey, CHAT_OFFLINE_MAX_MESSAGES);
        if (!snap.length) return;

        const key = dmId ? offlineDmKey(uid, dmId) : offlineRoomKey(uid, rid);
        if (offlineMessagesSaveTRef.current != null) window.clearTimeout(offlineMessagesSaveTRef.current);
        offlineMessagesSaveTRef.current = window.setTimeout(() => {
          void chatKvSet(key, snap);
        }, CHAT_OFFLINE_SAVE_DEBOUNCE_MS);

        // Комментарий на русском: LRU — переносим ключ “в конец”, чтобы ограничение по размеру выкидывало реально старые.
        try {
          if (chatMemRef.current.has(chatKey)) chatMemRef.current.delete(chatKey);
        } catch {}
        chatMemRef.current.set(chatKey, {
          messages: snap,
          lastId: Number(chat.lastServerId || 0) || 0,
          hasMore: !!chat.hasMore,
          chatHash: currentChatHashRef.current || undefined,
        });
        if (chatMemRef.current.size > CHAT_MAP_MAX_SIZE) {
          const first = chatMemRef.current.keys().next().value as string | undefined;
          if (first) chatMemRef.current.delete(first);
        }
        schedulePersistChatMem(uid);
      }
    );

    return () => {
      if (offlineMessagesSaveTRef.current != null) window.clearTimeout(offlineMessagesSaveTRef.current);
      offlineMessagesSaveTRef.current = null;
      unsubscribe();
    };
  }, [getMyUserId, selectedRoom?.id, selectedUser?.id]);
  const [recording, setRecording] = useState(false);
  const [voiceSending, setVoiceSending] = useState(false);
  const [voiceElapsedMs, setVoiceElapsedMs] = useState(0);
  const [showMessagePreview, setShowMessagePreview] = useState(true);
  // Комментарий на русском: приватность уведомлений — показывать/скрывать содержимое сообщений в уведомлениях (push/системные/in-app).
  const [showNotificationContent, setShowNotificationContent] = useState(true);
  // Комментарий на русском: настройки мессенджера (берём с бэка, чтобы было одинаково на всех устройствах).
  const [chatSettingsLoaded, setChatSettingsLoaded] = useState(false);
  // Комментарий на русском: версия настроек (Unix timestamp) — для инвалидации кэша между устройствами.
  const settingsVersionRef = useRef<number>(0);
  const [readReceiptsEnabled, setReadReceiptsEnabled] = useState(true);
  const [linkPreviewsEnabled, setLinkPreviewsEnabled] = useState(true);
  // Комментарий на русском: предпросмотр ссылки над полем ввода (DM/Room/Support) + “отправить без превью”.
  const [draftPreviewSuppressedUrl, setDraftPreviewSuppressedUrl] = useState('');
  const [roomDraftPreviewSuppressedUrl, setRoomDraftPreviewSuppressedUrl] = useState('');
  const draftFirstUrl = useMemo(() => (linkPreviewsEnabled ? extractFirstUrl(draft) : null), [draft, linkPreviewsEnabled]);
  const roomDraftFirstUrl = useMemo(() => (linkPreviewsEnabled ? extractFirstUrl(roomDraft) : null), [roomDraft, linkPreviewsEnabled]);
  const { preview: draftLinkPreview, loading: draftLinkPreviewLoading } = useDraftLinkPreview(
    draftFirstUrl && draftFirstUrl !== draftPreviewSuppressedUrl ? draftFirstUrl : null,
    linkPreviewsEnabled
  );
  const { preview: roomDraftLinkPreview, loading: roomDraftLinkPreviewLoading } = useDraftLinkPreview(
    roomDraftFirstUrl && roomDraftFirstUrl !== roomDraftPreviewSuppressedUrl ? roomDraftFirstUrl : null,
    linkPreviewsEnabled
  );
  useEffect(() => {
    if (draftPreviewSuppressedUrl && draftFirstUrl !== draftPreviewSuppressedUrl) setDraftPreviewSuppressedUrl('');
  }, [draftFirstUrl, draftPreviewSuppressedUrl]);
  useEffect(() => {
    if (roomDraftPreviewSuppressedUrl && roomDraftFirstUrl !== roomDraftPreviewSuppressedUrl) setRoomDraftPreviewSuppressedUrl('');
  }, [roomDraftFirstUrl, roomDraftPreviewSuppressedUrl]);
  const [compactMode, setCompactMode] = useState(false);
  const [fontScale, setFontScale] = useState(100);
  const [messageDensity, setMessageDensity] = useState<MessageDensity>('normal');
  const [messageTheme, setMessageTheme] = useState('custom');
  const [messageStackingEnabled, setMessageStackingEnabled] = useState(false);
  // Комментарий на русском: оформление аватара (онлайн: зелёный + свечение, офлайн: серый, без свечения).
  const [avatarFxColor, setAvatarFxColor] = useState('#22c55e');
  const [avatarFxThickness, setAvatarFxThickness] = useState(2);
  const [avatarFxGlow, setAvatarFxGlow] = useState(0);
  const [avatarFxOfflineColor, setAvatarFxOfflineColor] = useState('#64748b');
  // Комментарий на русском: "жидкое стекло" для пузырьков сообщений (кастомизация по пользователю).
  const [bubbleTransparency, setBubbleTransparency] = useState(82);
  const [bubbleBlur, setBubbleBlur] = useState(90);
  const [bubbleColor, setBubbleColor] = useState('#7dd3fc');
  // Комментарий на русском: отдельные цвета для "моих" и "чужих" сообщений (локально, без API).
  const [bubbleColorMe, setBubbleColorMe] = useState<string>('');
  const [bubbleColorPeer, setBubbleColorPeer] = useState<string>('');
  const [bubbleShadow, setBubbleShadow] = useState(35);
  const [bubbleShadowColor, setBubbleShadowColor] = useState('#0b1220');
  const [bubbleShadowEnabled, setBubbleShadowEnabled] = useState(true);
  // Комментарий на русском: настройки внутреннего свечения пузырька.
  const [bubbleGlowEnabled, setBubbleGlowEnabled] = useState(true);
  const [bubbleGlowColor, setBubbleGlowColor] = useState('#ffffff');
  const [bubbleGlowX, setBubbleGlowX] = useState(22);
  const [bubbleGlowY, setBubbleGlowY] = useState(18);
  const [bubbleGlowGyro, setBubbleGlowGyro] = useState(false);
  const [bubbleGlowGyroIntensity, setBubbleGlowGyroIntensity] = useState(1); // 1..5
  // Комментарий на русском: смещение от гироскопа (плавно интерполируется).
  const [gyroOffset, setGyroOffset] = useState({ x: 0, y: 0 });

  // Комментарий на русском: когда меняем цвет МОИХ сообщений — обновляем уже загруженные сообщения,
  // чтобы цвет применялся сразу (без перезагрузки истории).
  useEffect(() => {
    const myId = getMyUserId();
    if (!myId) return;
    // Комментарий на русском: если задан отдельный цвет "моих" — синхронизируем его, иначе базовый.
    const hex = String(bubbleColorMe || bubbleColor || '').trim().toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(hex)) return;
    const dmId = Number(selectedUser?.id || 0) || 0;
    const rid = Number(selectedRoom?.id || 0) || 0;
    const chatKey = rid ? `room:${rid}` : dmId ? `dm:${dmId}` : '';
    if (!chatKey) return;
    chatMessagesStore.getState().patchMessagesByUser(chatKey, myId, { bubble_color: hex } as any);
  }, [bubbleColor, bubbleColorMe, getMyUserId, selectedRoom?.id, selectedUser?.id]);

  // Комментарий на русском: цвет "собеседника" — локальный (только для этого устройства).
  useEffect(() => {
    const norm = (v: unknown) => {
      const s = String(v || '').trim().toLowerCase();
      return /^#[0-9a-f]{6}$/.test(s) ? s : '';
    };
    try {
      setBubbleColorPeer(norm(localStorage.getItem('lisad_bubble_color_peer')));
    } catch {}
  }, []);
  useEffect(() => {
    try {
      if (bubbleColorPeer) localStorage.setItem('lisad_bubble_color_peer', bubbleColorPeer);
      else localStorage.removeItem('lisad_bubble_color_peer');
    } catch {}
  }, [bubbleColorPeer]);

  // Комментарий на русском: параллакс свечения по гироскопу телефона.
  useEffect(() => {
    if (!bubbleGlowGyro || !bubbleGlowEnabled) {
      setGyroOffset({ x: 0, y: 0 });
      return;
    }
    if (typeof window === 'undefined' || !('DeviceOrientationEvent' in window)) return;

    let frameId = 0;
    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    // Комментарий на русском: интенсивность как множитель (1 = минимум, 5 = максимум).
    const intensity = Math.max(1, Math.min(5, bubbleGlowGyroIntensity));
    const BASE_SENSITIVITY = 0.35;
    const BASE_MAX_OFFSET = 18;
    const SENSITIVITY = BASE_SENSITIVITY * intensity;
    const MAX_OFFSET = BASE_MAX_OFFSET * intensity;
    const SMOOTHING = 0.08; // Комментарий на русском: плавность интерполяции.

    const handleOrientation = (e: DeviceOrientationEvent) => {
      const beta = e.beta ?? 0;
      const gamma = e.gamma ?? 0;
      targetX = Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, (gamma / 45) * MAX_OFFSET * (SENSITIVITY / intensity)));
      targetY = Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, ((beta - 45) / 45) * MAX_OFFSET * (SENSITIVITY / intensity)));
    };

    const animate = () => {
      currentX += (targetX - currentX) * SMOOTHING;
      currentY += (targetY - currentY) * SMOOTHING;
      if (Math.abs(currentX - targetX) > 0.01 || Math.abs(currentY - targetY) > 0.01) {
        setGyroOffset({ x: Math.round(currentX * 10) / 10, y: Math.round(currentY * 10) / 10 });
      }
      frameId = requestAnimationFrame(animate);
    };

    const startListening = () => {
      window.addEventListener('deviceorientation', handleOrientation, { passive: true });
      frameId = requestAnimationFrame(animate);
    };

    if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      (DeviceOrientationEvent as any).requestPermission().then((perm: string) => {
        if (perm === 'granted') startListening();
      }).catch(() => {});
    } else {
      startListening();
    }

    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
      if (frameId) cancelAnimationFrame(frameId);
      setGyroOffset({ x: 0, y: 0 });
    };
  }, [bubbleGlowGyro, bubbleGlowEnabled, bubbleGlowGyroIntensity]);

  // Комментарий на русском: читабельность текста в пузырьках (цвет + шрифт).
  const [bubbleTextColor, setBubbleTextColor] = useState('#ffffff');
  const [messageFont, setMessageFont] = useState<'system' | 'inter' | 'serif' | 'mono'>('system');
  // Комментарий на русском: выбранный раздел настроек для iOS-стиля (десктоп).
  const [settingsSection, setSettingsSection] = useState<MessengerSettingsSectionId>('profile');
  // Комментарий на русском: мини-профиль прямо в настройках мессенджера.
  const [myProfile, setMyProfile] = useState<any | null>(null);
  const myProfileDirtyRef = useRef(false);
  const myNicknameDirtyRef = useRef(false);
  const [myProfileForm, setMyProfileForm] = useState<MyProfileForm>(DEFAULT_MY_PROFILE_FORM);
  const [myNicknameDraft, setMyNicknameDraft] = useState('');
  const [profileBusy, setProfileBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  // Комментарий на русском: файл аватара, который надо обрезать перед загрузкой (единый кроппер по проекту).
  const [myAvatarCropFile, setMyAvatarCropFile] = useState<File | null>(null);
  const [settingsToast, setSettingsToast] = useState<string | null>(null);
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const authErrorShownRef = useRef(false);
  // Комментарий на русском: дебаунс сохранения списков (диалоги/комнаты), чтобы не затирать кэш при старте и не спамить IDB.
  const offlineChatsSaveTRef = useRef<number | null>(null);
  const offlineRoomsSaveTRef = useRef<number | null>(null);
  const [storageStats, setStorageStats] = useState<{
    used: number;
    quota: number;
    available: number;
    categories: StorageStat[];
  } | null>(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageError, setStorageError] = useState<string | null>(null);
  // Комментарий на русском: отдельный флаг для "очистка кэша", чтобы на iOS кнопка не блокировалась из-за долгого подсчёта.
  const [cachePurging, setCachePurging] = useState(false);
  // Комментарий на русском: на iOS confirm() может быть заблокирован, поэтому подтверждаем вторым нажатием.
  const [cachePurgeArmed, setCachePurgeArmed] = useState(false);
  const cachePurgeArmTRef = useRef<number | null>(null);

  // Комментарий на русском: сохраняем список диалогов для офлайна (после загрузки/гидрации).
  useEffect(() => {
    const uid = getMyUserId();
    if (!uid || loadingChats) return;
    if (offlineChatsSaveTRef.current != null) window.clearTimeout(offlineChatsSaveTRef.current);
    offlineChatsSaveTRef.current = window.setTimeout(() => {
      void chatKvSet(offlineConversationsKey(uid), conversations);
    }, 450);
  }, [conversations, getMyUserId, loadingChats]);

  // Комментарий на русском: сохраняем список комнат для офлайна (после загрузки/гидрации).
  useEffect(() => {
    const uid = getMyUserId();
    if (!uid || loadingRooms) return;
    if (offlineRoomsSaveTRef.current != null) window.clearTimeout(offlineRoomsSaveTRef.current);
    offlineRoomsSaveTRef.current = window.setTimeout(() => {
      void chatKvSet(offlineRoomsKey(uid), rooms as any);
    }, 450);
  }, [getMyUserId, loadingRooms, rooms]);

  const [chatSearch, setChatSearch] = useState('');
  // Комментарий на русском: поиск по сообщениям ВНУТРИ открытого чата (как в Telegram), только UI/навигация — без отдельной страницы.
  const [inChatSearchOpen, setInChatSearchOpen] = useState(false);
  const [inChatSearchQuery, setInChatSearchQuery] = useState('');
  const [inChatSearchActiveIdx, setInChatSearchActiveIdx] = useState(0);
  const inChatSearchInputRef = useRef<HTMLInputElement | null>(null);
  const inChatSearchListRef = useRef<HTMLDivElement | null>(null);
  const inChatSearchRootRef = useRef<HTMLDivElement | null>(null);
  // Комментарий на русском: UI-кнопка “+” и контекстное меню создания (как меню сообщения).
  const createBtnRef = useRef<HTMLButtonElement | null>(null);
  const createMenuRef = useRef<HTMLDivElement | null>(null);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [createMenuPos, setCreateMenuPos] = useState<{ left: number; top: number } | null>(null);

  // Контакты (друзья + заявки)
  const [friends, setFriends] = useState<User[]>([]);
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState<SearchUser[]>([]);
  const [userSearching, setUserSearching] = useState(false);
  // Комментарий на русском: выбранный контакт для вкладки "Контакты" (на десктопе профиль открываем справа).
  const [selectedContact, setSelectedContact] = useState<User | null>(null);
  // Комментарий на русском: данные профиля для правой колонки (десктоп), грузим по клику из списка.
  const [selectedContactProfile, setSelectedContactProfile] = useState<UserPreview | null>(null);
  const [selectedContactProfileLoading, setSelectedContactProfileLoading] = useState(false);
  // Комментарий на русском: ID раскрытого контакта для мобилки (расширяемая карточка с эффектом жидкого стекла).
  const [expandedContactId, setExpandedContactId] = useState<number | null>(null);
  const [expandedContactProfile, setExpandedContactProfile] = useState<UserPreview | null>(null);
  const [expandedContactLoading, setExpandedContactLoading] = useState(false);
  const contactsScrollRef = useRef<HTMLDivElement | null>(null);

  // Безопасность: blacklist + активные сессии
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [sessions, setSessions] = useState<AuthSession[]>([]);
  // Комментарий на русском: разделяем "это устройство" и остальные устройства (для UI и для logout-others).
  const currentSession = useMemo(() => sessions.find((s) => !!s.is_current) || null, [sessions]);
  const otherSessions = useMemo(() => sessions.filter((s) => !s.is_current), [sessions]);

  // Сотрудники (для роли "Сотрудник")
  const [employees, setEmployees] = useState<EmployeeUser[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  // Поддержка
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportInbox, setSupportInbox] = useState<Conversation[]>([]);
  const [supportTargetUser, setSupportTargetUser] = useState<User | null>(null); // для суперадмина: чей поток открыт
  const [supportMessages, setSupportMessages] = useState<DirectMessage[]>([]);
  const [supportDraft, setSupportDraft] = useState('');
  const [supportDraftPreviewSuppressedUrl, setSupportDraftPreviewSuppressedUrl] = useState('');
  const supportDraftFirstUrl = useMemo(
    () => (linkPreviewsEnabled ? extractFirstUrl(supportDraft) : null),
    [supportDraft, linkPreviewsEnabled]
  );
  const { preview: supportDraftLinkPreview, loading: supportDraftLinkPreviewLoading } = useDraftLinkPreview(
    supportDraftFirstUrl && supportDraftFirstUrl !== supportDraftPreviewSuppressedUrl ? supportDraftFirstUrl : null,
    linkPreviewsEnabled
  );
  useEffect(() => {
    if (supportDraftPreviewSuppressedUrl && supportDraftFirstUrl !== supportDraftPreviewSuppressedUrl) setSupportDraftPreviewSuppressedUrl('');
  }, [supportDraftFirstUrl, supportDraftPreviewSuppressedUrl]);
  const [supportUnread, setSupportUnread] = useState(0);
  const supportLastIdRef = useRef<number>(0);

  // Комментарий на русском: режим "Сайт/ошибки" в поддержке для суперадмина (вместо обычного пользователя).
  const [showErrorsChat, setShowErrorsChat] = useState(false);

  // Комментарий на русском: ошибки фронтенда (только для суперадминов).
  interface FrontendErrorItem {
    id: number;
    user_id: number | null;
    username: string | null;
    display_name: string | null;
    category: string;
    message: string;
    details: string | null;
    url: string | null;
    user_agent: string | null;
    stack: string | null;
    ip: string | null;
    created_at: string;
  }
  const [frontendErrors, setFrontendErrors] = useState<FrontendErrorItem[]>([]);
  const [errorsLoading, setErrorsLoading] = useState(false);
  const [errorsCategory, setErrorsCategory] = useState<string>('all');
  // Комментарий на русском: строка поиска по списку ошибок (фильтрация на клиенте).
  const [errorsSearch, setErrorsSearch] = useState('');

  const messagesScrollRef = useRef<HTMLDivElement>(null);
  // Комментарий на русском: ref для виртуализированного списка сообщений (react-virtuoso).
  const virtuosoRef = useRef<MessageListHandle>(null);
  // Комментарий на русском: ref для скролла списка чатов (sidebar) — для постоянного хранения позиции.
  const chatsListScrollRef = useRef<HTMLDivElement>(null);
  // Комментарий на русском: нижняя плавающая панель ввода (нужна, чтобы поднимать padding ленты при росте поля ввода).
  const composePanelRef = useRef<HTMLDivElement>(null);
  // Комментарий на русском: нижний "инсет" для ленты (под плавающую панель + 40px зазор до края).
  // Зачем: чтобы последнее сообщение не уезжало под compose/кнопки и не обрезалось.
  const [chatBottomInsetPx, setChatBottomInsetPx] = useState(0);
  const lastMessageIdRef = useRef<number>(0);
  // Комментарий на русском: текущий хэш открытого чата (для сохранения в кэш).
  const currentChatHashRef = useRef<string>('');
  const supportMessagesEndRef = useRef<HTMLDivElement>(null);
  const draftChatRef = useRef<HTMLTextAreaElement>(null);
  // Комментарий на русском: input для выбора фото (скрепка). Один на весь экран — работает и для лички, и для комнат.
  const photoInputRef = useRef<HTMLInputElement>(null);
  // Комментарий на русском: input для выбора файлов (документы/архивы).
  const fileInputRef = useRef<HTMLInputElement>(null);
  type PendingPhoto = {
    id: string;
    file: File;
    previewUrl: string;
    isGif: boolean;
  };
  type PendingFile = {
    id: string;
    file: File;
  };
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [pendingPhotoActiveIdx, setPendingPhotoActiveIdx] = useState<number>(0);
  const [pendingPhotoEditIdx, setPendingPhotoEditIdx] = useState<number>(0);
  const [pendingPhotoEditOpen, setPendingPhotoEditOpen] = useState(false);
  // Комментарий на русском: режим отправки фото (по умолчанию сжимаем).
  const [sendPhotosOriginal, setSendPhotosOriginal] = useState(false);
  // Комментарий на русском: refs для автофокуса ввода на десктопе (комнаты/поддержка).
  const supportDraftRef = useRef<HTMLTextAreaElement>(null);
  const roomDraftRef = useRef<HTMLTextAreaElement>(null);
  // Комментарий на русском: резерв — одиночный Ctrl вставляет перенос строки, если сочетания заблокированы браузером.
  const draftCtrlUsedRef = useRef(false);
  const roomCtrlUsedRef = useRef(false);
  const supportCtrlUsedRef = useRef(false);
  const hasDraftText = !!draft.trim();
  const hasRoomDraftText = !!roomDraft.trim();
  const pendingPhotoSafeIdx = Math.max(0, Math.min(pendingPhotoActiveIdx, Math.max(0, pendingPhotos.length - 1)));
  const pendingPhotoFile = pendingPhotos[pendingPhotoSafeIdx]?.file || null;
  const pendingPhotoPreviewUrl = pendingPhotos[pendingPhotoSafeIdx]?.previewUrl || '';
  const hasPendingPhoto = pendingPhotos.length > 0;
  const hasPendingFile = pendingFiles.length > 0;
  // Комментарий на русском: "активный" ввод — текст ИЛИ выбранное фото (нужно для общей логики кнопки микрофона/отправки).
  const activeHasText = !!selectedRoom ? (hasRoomDraftText || hasPendingPhoto || hasPendingFile) : (hasDraftText || hasPendingPhoto || hasPendingFile);
  const composeInChatRef = useRef(false);
  const selectedUserIdRef = useRef<number>(0);
  const selectedRoomIdRef = useRef<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const voiceChunksRef = useRef<Blob[]>([]);
  const voiceBytesRef = useRef(0);
  const voiceTooBigRef = useRef(false);
  const voiceStartedAtRef = useRef(0);
  const voiceSendRef = useRef(true);
  // Комментарий на русском: защита от setState после ухода со страницы/назад (onstop/onerror могут прилетать позже).
  const isMountedRef = useRef(true);
  const voiceLocalUrlsRef = useRef<Set<string>>(new Set());
  // Комментарий на русском: локальные blob: URL для фото (optimistic preview) — чистим при смене чата/размонтировании.
  const photoLocalUrlsRef = useRef<Set<string>>(new Set());
  const voiceCanvasRef = useRef<HTMLCanvasElement>(null);
  const voiceTimerRef = useRef<number | null>(null);
  const voiceRafRef = useRef<number | null>(null);
  const voiceAudioCtxRef = useRef<AudioContext | null>(null);
  // Комментарий на русском: “авто‑усиление” визуализации записи (делает волну чувствительнее и красивее).
  const voiceVizLevelRef = useRef(0.08);
  // Комментарий на русском: watchdog для детекции "мёртвой" записи (PWA iOS/Android — может не давать данные без ошибок).
  const voiceWatchdogRef = useRef<number | null>(null);
  const voiceGotDataRef = useRef(false);
  // Комментарий на русском: лимиты/качество голосовых (серверные лимиты отдельно в PHP).
  // Зачем: держим файл разумного размера, но качество — нормальное для речи.
  const VOICE_MAX_BYTES = 15 * 1024 * 1024; // 15MB
  const VOICE_AUDIO_BPS = 48000; // 48 kbps (нормально для речи)
  const VOICE_SLICE_MS = 250; // отдавать чанки, не держать всё в памяти до stop()
  const VOICE_MIN_MS = 650; // слишком короткие записи не отправляем (чтобы не ловить ошибки/пустые файлы)
  const VOICE_WATCHDOG_MS = 2500; // если за 2.5с не пришло данных — запись "мёртвая"
  // Комментарий на русском: glassmorphism кнопки для управления чатом (навигация, отправка, микрофон)
  const chatIconBtnClass =
    // Комментарий на русском: уменьшили размер кнопок на 40% (оставили 60% от исходного).
    'w-[2.8rem] h-[2.8rem] sm:w-[3.25rem] sm:h-[3.5rem] rounded-full glass-btn text-site-text text-lg leading-none flex items-center justify-center shrink-0 transition duration-150 active:scale-[0.85] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/40 disabled:opacity-60 disabled:cursor-not-allowed';

  // Комментарий на русском: кнопка "скрепка" — чуть меньше, чем кнопка "Назад", но стиль тот же.
  const chatAttachBtnClass =
    'w-[2.4rem] h-[2.4rem] sm:w-[2.9rem] sm:h-[3.1rem] rounded-full glass-btn text-site-text text-base leading-none flex items-center justify-center shrink-0 transition duration-150 active:scale-[0.85] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/40 disabled:opacity-60 disabled:cursor-not-allowed';

  // Комментарий на русском: монохромная иконка микрофона (для голосовых).
  const micIcon = (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3z" />
      <path d="M19 11a7 7 0 0 1-14 0" />
      <path d="M12 19v3" />
      <path d="M8 22h8" />
    </svg>
  );

  // Комментарий на русском: иконка скрепки (attach).
  const paperclipIcon = (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );

  // Комментарий на русском: при записи показываем “✓” (понятно, что это подтверждение/отправка записи).
  const rightBtnTitle = activeHasText
    ? (hasPendingPhoto
        ? `Отправить фото${pendingPhotos.length > 1 ? ` (${pendingPhotos.length})` : ''}`
        : hasPendingFile
          ? `Отправить файл${pendingFiles.length > 1 ? ` (${pendingFiles.length})` : ''}`
          : 'Отправить')
    : recording
      ? 'Отправить запись'
      : 'Записать голос';
  const rightBtnContent = activeHasText ? '➤' : recording ? '✓' : micIcon;

  const formatRecTime = (ms: number) => {
    const s = Math.max(0, (ms / 1000) | 0);
    const m = (s / 60) | 0;
    return `${m}:${String(s % 60).padStart(2, '0')}`;
  };

  const stopVoiceViz = useCallback(() => {
    // Комментарий на русском: стопаем график/таймер, чтобы не было утечек и “вечной” нагрузки на UI.
    if (voiceTimerRef.current) {
      window.clearInterval(voiceTimerRef.current);
      voiceTimerRef.current = null;
    }
    if (voiceWatchdogRef.current) {
      window.clearTimeout(voiceWatchdogRef.current);
      voiceWatchdogRef.current = null;
    }
    if (voiceRafRef.current != null) {
      cancelAnimationFrame(voiceRafRef.current);
      voiceRafRef.current = null;
    }
    try {
      voiceAudioCtxRef.current?.close();
    } catch {}
    voiceAudioCtxRef.current = null;
    voiceVizLevelRef.current = 0.08;
    // Комментарий на русском: на unmount не делаем setState (иначе возможны warnings в React).
    if (isMountedRef.current) setVoiceElapsedMs(0);
  }, []);

  const revokeVoiceLocalUrls = useCallback(() => {
    // Комментарий на русском: чистим blob: URL, чтобы не текла память (особенно на iOS).
    try {
      voiceLocalUrlsRef.current.forEach((u) => {
        if (typeof u === 'string' && u.startsWith('blob:')) URL.revokeObjectURL(u);
      });
    } catch {}
    voiceLocalUrlsRef.current.clear();
  }, []);

  const revokePhotoLocalUrls = useCallback(() => {
    // Комментарий на русском: чистим blob: URL фото (optimistic preview).
    try {
      photoLocalUrlsRef.current.forEach((u) => {
        if (typeof u === 'string' && u.startsWith('blob:')) URL.revokeObjectURL(u);
      });
    } catch {}
    photoLocalUrlsRef.current.clear();
  }, []);

  const clearPendingPhoto = useCallback(
    (opts?: { keepPreview?: boolean }) => {
      // Комментарий на русском: чистим выбранные фото (до 5), при keepPreview оставляем blob: URL для optimistic bubble.
      const keep = !!opts?.keepPreview;
      if (!keep) {
        try {
          for (const p of pendingPhotos) {
            const u = String(p?.previewUrl || '').trim();
            if (u && u.startsWith('blob:')) URL.revokeObjectURL(u);
            try {
              photoLocalUrlsRef.current.delete(u);
            } catch {}
          }
        } catch {}
      }
      setPendingPhotos([]);
      setPendingPhotoActiveIdx(0);
      setPendingPhotoEditIdx(0);
      try {
        if (photoInputRef.current) photoInputRef.current.value = '';
      } catch {}
    },
    [pendingPhotos]
  );

  const clearPendingFiles = useCallback(() => {
    setPendingFiles([]);
    try {
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {}
  }, []);

  const removePendingPhotoAt = useCallback((idx: number) => {
    // Комментарий на русском: удаляем одно фото из набора (не трогаем остальные).
    setPendingPhotos((prev) => {
      const p = prev[idx];
      if (!p) return prev;
      const u = String(p.previewUrl || '').trim();
      if (u && u.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(u);
        } catch {}
      }
      try {
        photoLocalUrlsRef.current.delete(u);
      } catch {}
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const openPendingPhotoEditor = useCallback(() => {
    const idx = pendingPhotoSafeIdx;
    const p = pendingPhotos[idx];
    if (!p?.file) return;
    if (p.isGif) {
      setError('GIF нельзя редактировать (анимация потеряется). Отправьте как есть.');
      setTimeout(() => setError(null), 2500);
      return;
    }
    setPendingPhotoEditIdx(idx);
    setPendingPhotoEditOpen(true);
  }, [pendingPhotos, pendingPhotoSafeIdx]);

  const replacePendingPhotoAt = useCallback((idx: number, f: File) => {
    // Комментарий на русском: заменяем файл и превью у конкретного фото после обрезки/правок.
    setPendingPhotos((prev) => {
      const p0 = prev[idx];
      if (!p0) return prev;
      const prevUrl = String(p0.previewUrl || '').trim();
      if (prevUrl) {
        try {
          if (prevUrl.startsWith('blob:')) URL.revokeObjectURL(prevUrl);
        } catch {}
        try {
          photoLocalUrlsRef.current.delete(prevUrl);
        } catch {}
      }
      const u = typeof URL !== 'undefined' ? URL.createObjectURL(f) : '';
      if (u) photoLocalUrlsRef.current.add(u);
      const t = String(f.type || '').toLowerCase();
      const n = String(f.name || '').toLowerCase();
      const isGif = t === 'image/gif' || n.endsWith('.gif');
      const out = [...prev];
      out[idx] = { ...p0, file: f, previewUrl: u, isGif };
      return out;
    });
    try {
      if (photoInputRef.current) photoInputRef.current.value = '';
    } catch {}
  }, []);

  const PendingPhotosBar = ({ disabled }: { disabled: boolean }) => {
    if (!hasPendingPhoto) return null;
    const activeIdx = pendingPhotoSafeIdx;
    const active = pendingPhotos[activeIdx];
    const count = pendingPhotos.length;

    return (
      <div className="mb-3 flex items-center gap-3 px-3 py-2 rounded-xl border border-site-border bg-site-surface/30">
        <div className="flex items-center gap-2 overflow-x-auto" data-scroll-lock-allow>
          {pendingPhotos.slice(0, 5).map((p, idx) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                if (idx === activeIdx) openPendingPhotoEditor();
                else setPendingPhotoActiveIdx(idx);
              }}
              disabled={disabled}
              className={`relative w-12 h-12 rounded-xl overflow-hidden border bg-black/20 shrink-0 block ${
                idx === activeIdx ? 'border-site-accent/60' : 'border-site-border'
              }`}
              title={idx === activeIdx ? 'Редактировать' : 'Выбрать'}
              aria-label={idx === activeIdx ? 'Редактировать фото' : 'Выбрать фото'}
            >
              {p.previewUrl ? (
                <img src={p.previewUrl} alt="Фото" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-site-text/60">Фото</div>
              )}
              {count > 1 && (
                <div className="absolute bottom-0 right-0 px-1 py-0.5 text-[10px] leading-none bg-black/60 text-site-text/90 rounded-tl-md">
                  {idx + 1}/{count}
                </div>
              )}
            </button>
          ))}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-site-text truncate">Фото{count > 1 ? ` (${count})` : ''}</div>
          <div className="text-[11px] text-site-muted truncate">{active?.file?.name || ''}</div>
        </div>
        <button
          type="button"
          onClick={() => setSendPhotosOriginal((v) => !v)}
          disabled={disabled}
          className="w-10 h-8 rounded-full glass-btn text-site-text/80 hover:text-site-text flex items-center justify-center transition active:scale-[0.92] text-[11px] font-semibold"
          title={sendPhotosOriginal ? 'Отправка без сжатия' : 'Отправка со сжатием'}
          aria-label={sendPhotosOriginal ? 'Фото без сжатия' : 'Фото со сжатием'}
        >
          {sendPhotosOriginal ? 'HD' : 'SD'}
        </button>
        <button
          type="button"
          onClick={openPendingPhotoEditor}
          disabled={disabled}
          className="w-8 h-8 rounded-full glass-btn text-site-text/80 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
          title="Редактировать"
          aria-label="Редактировать фото"
        >
          ✎
        </button>
        <button
          type="button"
          onClick={() => removePendingPhotoAt(activeIdx)}
          disabled={disabled}
          className="w-8 h-8 rounded-full glass-btn text-site-text/80 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
          title={count > 1 ? 'Убрать выбранное фото' : 'Убрать фото'}
          aria-label={count > 1 ? 'Убрать выбранное фото' : 'Убрать фото'}
        >
          ✕
        </button>
      </div>
    );
  };

  const PendingFilesBar = ({ disabled }: { disabled: boolean }) => {
    if (!hasPendingFile) return null;
    const count = pendingFiles.length;
    const first = pendingFiles[0]?.file;
    return (
      <div className="mb-3 flex items-center gap-3 px-3 py-2 rounded-xl border border-site-border bg-site-surface/30">
        <div className="w-12 h-12 rounded-xl border border-site-border bg-black/20 flex items-center justify-center shrink-0">
          <UiIcon name="FileText" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-site-text truncate">Файл{count > 1 ? ` (${count})` : ''}</div>
          <div className="text-[11px] text-site-muted truncate">{first?.name || ''}</div>
        </div>
        <button
          type="button"
          onClick={clearPendingFiles}
          disabled={disabled}
          className="w-8 h-8 rounded-full glass-btn text-site-text/80 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
          title={count > 1 ? 'Убрать файлы' : 'Убрать файл'}
          aria-label={count > 1 ? 'Убрать файлы' : 'Убрать файл'}
        >
          ✕
        </button>
      </div>
    );
  };

  const openPhotoPicker = useCallback(() => {
    if (recording || voiceSending) return;
    try {
      if (photoInputRef.current) photoInputRef.current.value = '';
    } catch {}
    photoInputRef.current?.click();
  }, [recording, voiceSending]);

  const openFilePicker = useCallback(() => {
    if (recording || voiceSending) return;
    try {
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {}
    fileInputRef.current?.click();
  }, [recording, voiceSending]);

  const handlePhotoSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputEl = e.currentTarget;
      const filesRaw = Array.from(inputEl.files || []);
      if (!filesRaw.length) return;

      const maxCount = 5;
      const maxBytes = 12 * 1024 * 1024; // 12MB (лимит на сервере такой же)

      // Комментарий на русском: чтобы не смешивать разные типы вложений — при выборе фото сбрасываем файлы.
      if (pendingFiles.length) clearPendingFiles();

      const free = Math.max(0, maxCount - pendingPhotos.length);
      if (free <= 0) {
        setError('Можно выбрать максимум 5 фото');
        setTimeout(() => setError(null), 2500);
        try {
          inputEl.value = '';
        } catch {}
        return;
      }

      const picked = filesRaw.slice(0, free);
      if (filesRaw.length > free) {
        setError('Можно выбрать максимум 5 фото');
        setTimeout(() => setError(null), 2500);
      }

      const additions: PendingPhoto[] = [];
      for (const fileRaw of picked) {
        const name = String(fileRaw?.name || '');
        const nameLower = name.toLowerCase();
        const ext = nameLower.includes('.') ? nameLower.split('.').pop() || '' : '';
        const mime = String(fileRaw?.type || '').toLowerCase();
        const isSvg = mime === 'image/svg+xml' || ext === 'svg';
        const isLikelyImage =
          (!isSvg && mime.startsWith('image/')) ||
          (!isSvg && ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'heic', 'heif', 'bmp', 'dib', 'tif', 'tiff'].includes(ext));

        // Комментарий на русском: иногда браузер отдаёт пустой MIME, поэтому страхуемся по расширению.
        if (!isLikelyImage) {
          const msg = `Недопустимый тип файла (${mime || ext || 'unknown'}). Разрешены: JPEG, PNG, GIF, WebP.`;
          setError(msg);
          try {
            logUploadError('Chat attachment rejected (type)', {
              kind: 'photo_select',
              mime,
              ext,
              name,
              size: fileRaw?.size || 0,
            });
          } catch {}
          setTimeout(() => setError(null), 2500);
          continue;
        }

        if ((fileRaw?.size || 0) > maxBytes) {
          const msg = 'Слишком большой файл. Максимум 12MB';
          setError(msg);
          try {
            logUploadError('Chat attachment rejected (too big)', {
              kind: 'photo_select',
              mime,
              ext,
              name,
              size: fileRaw?.size || 0,
              maxBytes,
            });
          } catch {}
          setTimeout(() => setError(null), 2500);
          continue;
        }

        let normalized: File = fileRaw;
        try {
          // Комментарий на русском: HEIF/HEIC приводим к JPEG сразу, чтобы превью/редактор работали везде.
          normalized = await ensureCanvasReadableImageFile(fileRaw);
        } catch (err: any) {
          const msg = 'Не удалось обработать фото (HEIF/HEIC). Попробуйте другое изображение.';
          setError(msg);
          try {
            logUploadError('Chat attachment normalize failed', { kind: 'photo_select', mime, ext, name, size: fileRaw?.size || 0 }, err as any);
          } catch {}
          setTimeout(() => setError(null), 2500);
          continue;
        }

        if ((normalized.size || 0) > maxBytes) {
          const msg = 'Фото получилось слишком большим после обработки. Выберите другое или обрежьте.';
          setError(msg);
          try {
            logUploadError('Chat attachment rejected after normalize (too big)', {
              kind: 'photo_select',
              mime,
              ext,
              name,
              size: normalized.size || 0,
              maxBytes,
            });
          } catch {}
          setTimeout(() => setError(null), 2500);
          continue;
        }

        let u = '';
        try {
          u = typeof URL !== 'undefined' ? URL.createObjectURL(normalized) : '';
        } catch (err: any) {
          const msg = 'Не удалось подготовить превью фото';
          setError(msg);
          try {
            logUploadError('Chat attachment preview failed', { kind: 'photo_select', mime, ext, name, size: normalized.size || 0 }, err as any);
          } catch {}
          setTimeout(() => setError(null), 2500);
          continue;
        }
        if (u) photoLocalUrlsRef.current.add(u);

        const t0 = String(normalized.type || '').toLowerCase();
        const n0 = String(normalized.name || '').toLowerCase();
        const isGif = t0 === 'image/gif' || n0.endsWith('.gif');
        additions.push({ id: makeClientId(), file: normalized, previewUrl: u, isGif });
      }

      try {
        inputEl.value = '';
      } catch {}
      if (!additions.length) return;

      setPendingPhotos((prev) => [...prev, ...additions].slice(0, maxCount));

      // Комментарий на русском: iOS — авто-открываем редактор только если выбрали ОДНО фото (иначе это раздражает при выборе пачки).
      try {
        if (pendingPhotos.length === 0 && additions.length === 1 && !additions[0].isGif) {
          const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';
          const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document);
          if (isIOS) {
            setPendingPhotoActiveIdx(0);
            setPendingPhotoEditIdx(0);
            setTimeout(() => setPendingPhotoEditOpen(true), 0);
          }
        }
      } catch {}

      // Комментарий на русском: в личке раскрываем поле ввода для подписи (caption).
      if (!selectedRoom) setComposeInChat(true);
      requestAnimationFrame(() => {
        try {
          if (selectedRoom) (roomDraftRef.current as any)?.focus?.({ preventScroll: true });
          else (draftChatRef.current as any)?.focus?.({ preventScroll: true });
        } catch {
          if (selectedRoom) roomDraftRef.current?.focus();
          else draftChatRef.current?.focus();
        }
      });
    },
    [clearPendingFiles, pendingFiles.length, pendingPhotos.length, selectedRoom]
  );

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputEl = e.currentTarget;
      const filesRaw = Array.from(inputEl.files || []);
      if (!filesRaw.length) return;

      const maxCount = 5;
      const maxBytes = 25 * 1024 * 1024; // 25MB

      // Комментарий на русском: чтобы не смешивать разные типы вложений — при выборе файлов сбрасываем фото.
      if (pendingPhotos.length) clearPendingPhoto();

      const free = Math.max(0, maxCount - pendingFiles.length);
      if (free <= 0) {
        setError('Можно выбрать максимум 5 файлов');
        setTimeout(() => setError(null), 2500);
        try {
          inputEl.value = '';
        } catch {}
        return;
      }

      const picked = filesRaw.slice(0, free);
      if (filesRaw.length > free) {
        setError('Можно выбрать максимум 5 файлов');
        setTimeout(() => setError(null), 2500);
      }

      const allowExt = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx', 'zip', 'rar', '7z']);
      const allowMime = new Set([
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/zip',
        'application/x-zip-compressed',
        'application/vnd.rar',
        'application/x-rar-compressed',
        'application/x-7z-compressed',
        'application/octet-stream', // Комментарий на русском: некоторые браузеры так отдают документы/архивы.
      ]);

      const additions: PendingFile[] = [];
      for (const fileRaw of picked) {
        const name = String(fileRaw?.name || '');
        const nameLower = name.toLowerCase();
        const ext = nameLower.includes('.') ? nameLower.split('.').pop() || '' : '';
        const mime = String(fileRaw?.type || '').toLowerCase();
        const size = Number(fileRaw?.size || 0) || 0;

        const okByExt = allowExt.has(ext);
        const okByMime = mime && allowMime.has(mime) && mime !== 'application/octet-stream';
        const okOctet = mime === 'application/octet-stream' && okByExt;
        if (!okByExt && !okByMime && !okOctet) {
          const msg = `Недопустимый файл (${ext || mime || 'unknown'}). Разрешены: PDF, Word, Excel, ZIP/RAR/7z.`;
          setError(msg);
          setTimeout(() => setError(null), 2500);
          continue;
        }

        if (size <= 0) {
          setError('Пустой файл');
          setTimeout(() => setError(null), 2500);
          continue;
        }
        if (size > maxBytes) {
          setError('Слишком большой файл. Максимум 25MB');
          setTimeout(() => setError(null), 2500);
          continue;
        }

        additions.push({ id: makeClientId(), file: fileRaw });
      }

      try {
        inputEl.value = '';
      } catch {}
      if (!additions.length) return;
      setPendingFiles((prev) => [...prev, ...additions].slice(0, maxCount));

      // Комментарий на русском: раскрываем поле ввода для подписи (caption).
      if (!selectedRoom) setComposeInChat(true);
      requestAnimationFrame(() => {
        try {
          if (selectedRoom) (roomDraftRef.current as any)?.focus?.({ preventScroll: true });
          else (draftChatRef.current as any)?.focus?.({ preventScroll: true });
        } catch {
          if (selectedRoom) roomDraftRef.current?.focus();
          else draftChatRef.current?.focus();
        }
      });
    },
    [clearPendingPhoto, pendingFiles.length, pendingPhotos.length, selectedRoom]
  );

  const stopVoiceStream = useCallback(() => {
    try {
      mediaStreamRef.current?.getTracks()?.forEach((t) => t.stop());
    } catch {}
    mediaStreamRef.current = null;
    stopVoiceViz();
  }, [stopVoiceViz]);

  const sendVoiceBlob = useCallback(
    async (blob: Blob) => {
      const toUserId = selectedUser?.id || 0;
      const roomId = selectedRoom?.id || 0;
      const isRoom = roomId > 0;
      const chatKey = isRoom ? `room:${roomId}` : `dm:${toUserId}`;
      if (!toUserId && !roomId) return;

      // Комментарий на русском: нормализуем MIME перед отправкой.
      // Зачем: некоторые браузеры отдают "audio/mp4;codecs=..." или даже "video/mp4" — а на PHP allowlist по точным значениям.
      const normalizeVoiceMime = (t: string) => {
        const raw = String(t || '').trim().toLowerCase();
        const base = raw.split(';', 1)[0]?.trim() || '';
        if (base === 'video/mp4') return 'audio/mp4';
        if (base === 'video/webm') return 'audio/webm';
        if (base === 'audio/x-m4a') return 'audio/mp4';
        return base;
      };

      setVoiceSending(true);
      // Комментарий на русском: на iOS/PWA upload иногда “подвисает” и блокирует кнопку микрофона (voiceSending=true навсегда).
      // Поэтому ставим таймаут и абортим запрос, чтобы UI гарантированно восстановился.
      const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
      let abortT: number | null = null;

      const replyToId = replyTo?.id || 0;
      const replyPreview = replyTo
        ? {
            id: replyTo.id,
            from_user: replyTo.from_user || null,
            message: replyTo.message || '',
            type: replyTo.type,
            voice_url: replyTo.voice_url ?? null,
            voice_mime: replyTo.voice_mime ?? null,
            created_at: replyTo.created_at || null,
          }
        : null;
      if (replyToId) setReplyTo(null);

      const localUrl = typeof URL !== 'undefined' ? URL.createObjectURL(blob) : '';
      if (localUrl) voiceLocalUrlsRef.current.add(localUrl);
      const optimisticId = Date.now();
      const clientId = makeClientId();

      try {
        const me = meRef.current || { id: 0, username: '', display_name: 'Вы', avatar_url: null };
        // Комментарий на русском: показываем голосовое у отправителя сразу, потом заменяем на URL с сервера.
        chatMessagesStore.getState().upsertMessages(
          chatKey,
          [
            {
              id: optimisticId,
              from_user: me,
              to_user: selectedUser || (me as any),
              message: '',
              created_at: new Date().toISOString(),
              // Комментарий на русском: "прочитано" — это receipt от получателя, не ставим true вручную.
              // Для комнат read-receipts не используем, для DM — тоже ждём обновления с сервера.
              is_read: false,
              status: 'sending',
              type: 'voice_message',
              client_id: clientId,
              reply_to: replyPreview,
              reactions: [],
              // Комментарий на русском: локальный blob URL для мгновенного воспроизведения (без ожидания сервера).
              local_voice_src: localUrl,
            } as any,
          ],
          { mode: 'append' }
        );
        requestAnimationFrame(() => {
          // Комментарий на русском: держим “низ” через CSS-скролл без конфликта с инициализацией (guard внутри forceScrollToBottom).
          forceScrollToBottom(toUserId);
          dmScrollBottomOffsetRef.current = 0;
          isNearBottomRef.current = true;
          setShowScrollToBottom(false);
          if (toUserId) writeDmScroll(toUserId, 0);
        });

        const fd = new FormData();
        const cleanMime = normalizeVoiceMime(blob.type);
        if (cleanMime) fd.append('voice_mime', cleanMime);
        fd.append('client_id', clientId);
        if (replyToId) fd.append('reply_to', String(replyToId));
        const ext = cleanMime.includes('ogg')
          ? 'ogg'
          : cleanMime.includes('mp4')
            ? 'm4a'
            : cleanMime.includes('mpeg')
              ? 'mp3'
              : cleanMime.includes('wav')
                ? 'wav'
              : 'webm';
        fd.append('voice', blob, `voice.${ext}`);
        if (!isRoom) fd.append('to_user_id', String(toUserId));

        if (ac) abortT = window.setTimeout(() => ac.abort(), 28000);
        const created = (await apiClient.request<any>(isRoom ? `/api/chat/rooms/${roomId}/messages/` : '/api/chat/direct-messages/', {
          method: 'POST',
          body: fd,
          signal: ac ? ac.signal : undefined,
        })) as any;

        try {
          // Комментарий на русском: у нас уже есть Blob — кладём его в кэш под URL сервера, без повторной загрузки.
          const v0 = typeof created?.voice_url === 'string' ? String(created.voice_url) : (typeof created?.message === 'string' ? String(created.message) : '');
          const isVoicePath = v0.includes('/chat_voice/') || v0.includes('/uploads/chat_voice/') || v0.includes('/media/chat_voice/');
          const v = isVoicePath ? v0 : '';
          const abs = resolveVoiceUrl(v);
          if (abs) void putVoiceBlobToCache(abs, blob, String(blob.type || ''));
        } catch {}

        const serverMsg: DirectMessage = {
          id: Number((created as any)?.id || 0) || optimisticId,
          message: String((created as any)?.message || 'Голосовое сообщение'),
          voice_url: typeof (created as any)?.voice_url === 'string' ? String((created as any).voice_url) : undefined,
          voice_mime: typeof (created as any)?.voice_mime === 'string' ? String((created as any).voice_mime) : undefined,
          local_voice_src: localUrl || undefined,
          created_at: String((created as any)?.created_at || new Date().toISOString()),
          type: String((created as any)?.type || 'voice_message'),
          client_id: ((created as any)?.client_id as any) ?? clientId,
        } as any;
        const up = chatMessagesStore.getState().upsertMessages(chatKey, [serverMsg], { mode: 'append', meId: me.id });
        if (up.maxServerId) lastMessageIdRef.current = Math.max(lastMessageIdRef.current, up.maxServerId);
        if (isRoom) loadRoomsThrottled();
        else loadConversationsThrottled();
      } catch (e: any) {
        chatMessagesStore.getState().patchMessageByClientId(chatKey, clientId, { status: 'error' } as any);
        const errName = String(e?.name || '');
        const details =
          errName === 'AbortError'
            ? 'Таймаут загрузки (попробуйте ещё раз)'
            : typeof e?.message === 'string' && e.message
              ? String(e.message)
              : 'Ошибка сети.';
        setError(`Не удалось отправить голосовое. ${details}`);
        setTimeout(() => setError(null), 2500);
      } finally {
        if (abortT != null) window.clearTimeout(abortT);
        abortT = null;
        setVoiceSending(false);
      }
    },
    // Комментарий на русском: loadConversations/loadRooms/writeDmScroll объявлены ниже по файлу, поэтому не держим их в deps (иначе TDZ/TS ошибка).
    [replyTo, selectedRoom?.id, selectedUser]
  );

  const sendPhotoMessage = useCallback(
    async () => {
      const photos = pendingPhotos;
      if (!photos.length) return;
      const toUserId = selectedUser?.id || 0;
      const roomId = selectedRoom?.id || 0;
      const isRoom = roomId > 0;
      const chatKey = isRoom ? `room:${roomId}` : `dm:${toUserId}`;
      if (!toUserId && !roomId) return;
      if (sending || voiceSending) return;

      // Комментарий на русском: фото не отправляем офлайн (иначе нужно хранить файл в IndexedDB и делать очередь).
      if (!isOnline) {
        setError('Фото нельзя отправить офлайн. Подключитесь к сети.');
        try {
          logUploadError('Cannot send photo while offline', {
            kind: 'photo_send',
            scope: isRoom ? 'room' : 'dm',
            toUserId,
            roomId,
            count: photos.length,
            size: photos[0]?.file?.size || 0,
            mime: String(photos[0]?.file?.type || ''),
            name: String(photos[0]?.file?.name || ''),
          });
        } catch {}
        setTimeout(() => setError(null), 2500);
        return;
      }

      const rawCaption = isRoom ? roomDraft : draft;
      let caption = '';
      if (rawCaption.trim()) {
        const v = validateMessage(rawCaption);
        if (!v.isValid) {
          setError(v.error || 'Ошибка валидации');
          setTimeout(() => setError(null), 2500);
          return;
        }
        caption = sanitizeMessage(rawCaption);
      }

      const me = meRef.current || { id: 0, username: '', display_name: 'Вы', avatar_url: null };
      const replyToId = replyTo?.id || 0;
      const replyPreview = replyTo
        ? {
            id: replyTo.id,
            from_user: replyTo.from_user || null,
            message: replyTo.message || '',
            type: replyTo.type,
            voice_url: replyTo.voice_url ?? null,
            voice_mime: replyTo.voice_mime ?? null,
            created_at: replyTo.created_at || null,
          }
        : null;
      if (replyToId) setReplyTo(null);

      const now = Date.now();
      const jobs = photos.slice(0, 5).map((p, i) => {
        const file0 = p.file;
        const clientId = makeClientId();
        const optimisticId = now + i;
        const localUrl = p.previewUrl || (typeof URL !== 'undefined' ? URL.createObjectURL(file0) : '');
        if (localUrl) photoLocalUrlsRef.current.add(localUrl);
        return { i, file0, clientId, optimisticId, localUrl };
      });

      // Комментарий на русском: optimistic сообщения (до 5).
      const optimisticBatch: any[] = jobs.map((j) => ({
        id: j.optimisticId,
        from_user: { ...me, id: me.id || 0 },
        to_user: isRoom ? (me as any) : selectedUser,
        message: j.i === 0 ? (caption || 'Фото') : 'Фото',
        created_at: new Date().toISOString(),
        is_read: false,
        status: 'sending',
        client_id: j.clientId,
        reply_to: j.i === 0 ? replyPreview : null,
        reactions: [],
        type: 'photo_message',
        local_photo_src: j.localUrl || null,
        attachments: [
          {
            id: 0,
            kind: 'photo',
            url: j.localUrl,
            mime: j.file0?.type || '',
            size: j.file0?.size || 0,
          },
        ],
      }));

      // Комментарий на русском: чистим панель выбора фото, но blob URL не ревокаем (он нужен для optimistic bubble).
      clearPendingPhoto({ keepPreview: true });
      if (isRoom) setRoomDraft('');
      else setDraft('');
      setError(null);

      // Комментарий на русском: прокрутка вниз после добавления сообщения.
      requestAnimationFrame(() => {
        // Комментарий на русском: держим “низ” через CSS-скролл без конфликта с первым скроллом на iOS.
        forceScrollToBottom(isRoom ? undefined : toUserId);
        dmScrollBottomOffsetRef.current = 0;
        isNearBottomRef.current = true;
        setShowScrollToBottom(false);
        if (!isRoom && toUserId) writeDmScroll(toUserId, 0);
      });

      setSending(true);
      chatMessagesStore.getState().upsertMessages(chatKey, optimisticBatch as any, { mode: 'append' });

      try {
        const endpoint = isRoom ? `/api/chat/rooms/${roomId}/messages/` : '/api/chat/direct-messages/';
        let hadSuccess = false;
        let shownError = false;

        for (const j of jobs) {
          const file0 = j.file0;
          const clientId = j.clientId;
          const optimisticId = j.optimisticId;
          const localUrl = j.localUrl;
          const isFirst = j.i === 0;

          const sendWithFile = async (f: File) => {
            const fd = new FormData();
            fd.append('client_id', clientId);
            if (isFirst && caption) fd.append('message', caption);
            if (isFirst && replyToId) fd.append('reply_to', String(replyToId));
            fd.append('photo', f, f.name || 'photo.jpg');
            if (!isRoom) fd.append('to_user_id', String(toUserId));
            return (await apiClient.request<any>(endpoint, { method: 'POST', body: fd })) as any;
          };

          // Комментарий на русском: заранее ужимаем фото под “безопасный” бюджет, чтобы не ловить лимиты PHP upload_max_filesize.
          // Параметры по умолчанию подбираются внутри optimizeImageForChatUpload по платформе.
          let created: any;
          try {
            // Комментарий на русском: режим "без сжатия" — отправляем исходник (но HEIC уже нормализован при выборе).
            let fileToSend: File = sendPhotosOriginal ? file0 : await optimizeImageForChatUpload(file0);
            try {
              created = await sendWithFile(fileToSend);
            } catch (e1: any) {
              const st = Number(e1?.status || 0) || 0;
              const msg = String(e1?.message || '').toLowerCase();
              const det = (e1?.details as any) || null;
              const maxAllowed = Number(det?.max_allowed_bytes || det?.maxAllowedBytes || 0) || 0;
              const uploadTooBig =
                st === 413 ||
                msg.includes('upload_max_filesize') ||
                msg.includes('post_max_size') ||
                msg.includes('слишком большой') ||
                (msg.includes('file') && msg.includes('not') && msg.includes('received')) ||
                msg.includes('файл фото не получен');

              if (uploadTooBig) {
                try {
                  const target = maxAllowed > 0 ? Math.max(180_000, Math.floor(maxAllowed * 0.9)) : 1_200_000;
                  const f2 = sendPhotosOriginal ? file0 : await optimizeImageForChatUpload(file0, { targetBytes: target, maxWidth: 1920, maxHeight: 1080 });
                  created = await sendWithFile(f2);
                } catch {
                  const target = maxAllowed > 0 ? Math.max(160_000, Math.floor(maxAllowed * 0.82)) : 900_000;
                  const f3 = sendPhotosOriginal ? file0 : await optimizeImageForChatUpload(file0, { targetBytes: target, maxWidth: 1600, maxHeight: 1600 });
                  created = await sendWithFile(f3);
                }
              } else {
                throw e1;
              }
            }

            hadSuccess = true;
            const serverMsg: DirectMessage = {
              id: Number((created as any)?.id || 0) || optimisticId,
              message: String((created as any)?.message || 'Фото'),
              created_at: String((created as any)?.created_at || new Date().toISOString()),
              type: String((created as any)?.type || 'photo_message'),
              attachments: Array.isArray((created as any)?.attachments) ? (created as any).attachments : undefined,
              // Комментарий на русском: оставляем local_photo_src как fallback (пока фото не загрузится по URL).
              local_photo_src: localUrl || undefined,
              client_id: ((created as any)?.client_id as any) ?? clientId,
            } as any;
            const up = chatMessagesStore.getState().upsertMessages(chatKey, [serverMsg], { mode: 'append', meId: me.id });
            if (up.maxServerId) lastMessageIdRef.current = Math.max(lastMessageIdRef.current, up.maxServerId);
          } catch (e: any) {
            chatMessagesStore.getState().patchMessageByClientId(chatKey, clientId, { status: 'error' } as any);
            if (!shownError) {
              shownError = true;
              const details = typeof e?.message === 'string' && e.message ? String(e.message) : 'Ошибка сети.';
              const hint = (e?.details && (e.details as any)?.hint) ? String((e.details as any).hint) : '';
              setError(`Не удалось отправить фото. ${details}${hint ? ` (${hint})` : ''}`);
              setTimeout(() => setError(null), 2500);
            }
            try {
              logUploadError('Failed to send photo message', {
                kind: 'photo_send',
                scope: isRoom ? 'room' : 'dm',
                toUserId,
                roomId,
                count: photos.length,
                size: file0?.size || 0,
                mime: String(file0?.type || ''),
                name: String(file0?.name || ''),
                captionLen: (isFirst ? caption.length : 0),
                replyToId: (isFirst ? replyToId : 0),
                errName: String(e?.name || ''),
                errMessage: String(e?.message || ''),
                httpStatus: Number(e?.status || 0) || undefined,
                serverHint: (e?.details as any)?.hint || undefined,
                serverMaxAllowedBytes: Number((e?.details as any)?.max_allowed_bytes || 0) || undefined,
              });
            } catch {}
          }
        }

        if (hadSuccess) {
          if (isRoom) loadRoomsThrottled();
          else loadConversationsThrottled();
        }
      } catch (e: any) {
        // ignore (ошибки обрабатываем по каждой картинке выше)
      } finally {
        setSending(false);
      }
    },
    // Комментарий на русском: loadConversations/loadRooms объявлены ниже по файлу, поэтому не держим их в deps (иначе TDZ/TS ошибка).
    [draft, setDraft, isOnline, pendingPhotos, replyTo, roomDraft, selectedRoom?.id, selectedUser, sending, sendPhotosOriginal, voiceSending]
  );

  const sendFileMessage = useCallback(
    async () => {
      const files = pendingFiles;
      if (!files.length) return;
      const toUserId = selectedUser?.id || 0;
      const roomId = selectedRoom?.id || 0;
      const isRoom = roomId > 0;
      const chatKey = isRoom ? `room:${roomId}` : `dm:${toUserId}`;
      if (!toUserId && !roomId) return;
      if (sending || voiceSending) return;

      // Комментарий на русском: файлы не отправляем офлайн (иначе нужна очередь).
      if (!isOnline) {
        setError('Файлы нельзя отправить офлайн. Подключитесь к сети.');
        setTimeout(() => setError(null), 2500);
        return;
      }

      const rawCaption = isRoom ? roomDraft : draft;
      let caption = '';
      if (rawCaption.trim()) {
        const v = validateMessage(rawCaption);
        if (!v.isValid) {
          setError(v.error || 'Ошибка валидации');
          setTimeout(() => setError(null), 2500);
          return;
        }
        caption = sanitizeMessage(rawCaption);
      }

      const me = meRef.current || { id: 0, username: '', display_name: 'Вы', avatar_url: null };
      const replyToId = replyTo?.id || 0;
      const replyPreview = replyTo
        ? {
            id: replyTo.id,
            from_user: replyTo.from_user || null,
            message: replyTo.message || '',
            type: replyTo.type,
            voice_url: replyTo.voice_url ?? null,
            voice_mime: replyTo.voice_mime ?? null,
            created_at: replyTo.created_at || null,
          }
        : null;
      if (replyToId) setReplyTo(null);

      const now = Date.now();
      const jobs = files.slice(0, 5).map((p, i) => {
        const file0 = p.file;
        const clientId = makeClientId();
        const optimisticId = now + i;
        return { i, file0, clientId, optimisticId };
      });

      // Комментарий на русском: чистим выбранные файлы.
      clearPendingFiles();
      if (isRoom) setRoomDraft('');
      else setDraft('');
      setError(null);

      requestAnimationFrame(() => {
        forceScrollToBottom(isRoom ? undefined : toUserId);
        dmScrollBottomOffsetRef.current = 0;
        isNearBottomRef.current = true;
        setShowScrollToBottom(false);
        if (!isRoom && toUserId) writeDmScroll(toUserId, 0);
      });

      setSending(true);
      chatMessagesStore.getState().upsertMessages(
        chatKey,
        jobs.map((j) => ({
          id: j.optimisticId,
          from_user: { ...me, id: me.id || 0 },
          to_user: isRoom ? (me as any) : selectedUser,
          message: j.i === 0 ? (caption || j.file0?.name || 'Файл') : (j.file0?.name || 'Файл'),
          created_at: new Date().toISOString(),
          is_read: false,
          status: 'sending' as const,
          client_id: j.clientId,
          reply_to: j.i === 0 ? replyPreview : null,
          reactions: [],
          type: 'file_message',
          attachments: [
            {
              id: 0,
              kind: 'file' as const,
              url: '',
              mime: j.file0?.type || '',
              size: j.file0?.size || 0,
              name: j.file0?.name || '',
            },
          ],
        } as DirectMessage)),
        { mode: 'append' }
      );

      try {
        const endpoint = isRoom ? `/api/chat/rooms/${roomId}/messages/` : '/api/chat/direct-messages/';
        let hadSuccess = false;
        let shownError = false;

        for (const j of jobs) {
          const file0 = j.file0;
          const clientId = j.clientId;
          const optimisticId = j.optimisticId;
          const isFirst = j.i === 0;

          try {
            const fd = new FormData();
            fd.append('client_id', clientId);
            if (isFirst && caption) fd.append('message', caption);
            if (isFirst && replyToId) fd.append('reply_to', String(replyToId));
            fd.append('file', file0, file0?.name || 'file');
            if (!isRoom) fd.append('to_user_id', String(toUserId));

            const created = (await apiClient.request<any>(endpoint, { method: 'POST', body: fd })) as any;
            hadSuccess = true;
            const serverMsg: DirectMessage = {
              id: Number((created as any)?.id || 0) || optimisticId,
              message: String((created as any)?.message || (file0?.name || 'Файл')),
              created_at: String((created as any)?.created_at || new Date().toISOString()),
              type: String((created as any)?.type || 'file_message'),
              attachments: Array.isArray((created as any)?.attachments) ? (created as any).attachments : undefined,
              client_id: ((created as any)?.client_id as any) ?? clientId,
            } as any;
            const up = chatMessagesStore.getState().upsertMessages(chatKey, [serverMsg], { mode: 'append', meId: me.id });
            if (up.maxServerId) lastMessageIdRef.current = Math.max(lastMessageIdRef.current, up.maxServerId);
          } catch (e: any) {
            chatMessagesStore.getState().patchMessageByClientId(chatKey, clientId, { status: 'error' } as any);
            if (!shownError) {
              shownError = true;
              const details = typeof e?.message === 'string' && e.message ? String(e.message) : 'Ошибка сети.';
              const hint = (e?.details && (e.details as any)?.hint) ? String((e.details as any).hint) : '';
              setError(`Не удалось отправить файл. ${details}${hint ? ` (${hint})` : ''}`);
              setTimeout(() => setError(null), 2500);
            }
          }
        }

        if (hadSuccess) {
          if (isRoom) loadRoomsThrottled();
          else loadConversationsThrottled();
        }
      } catch {
        // ignore
      } finally {
        setSending(false);
      }
    },
    // Комментарий на русском: loadConversations/loadRooms объявлены ниже по файлу, поэтому не держим их в deps (иначе TDZ/TS ошибка).
    [clearPendingFiles, draft, setDraft, isOnline, pendingFiles, replyTo, roomDraft, selectedRoom?.id, selectedUser, sending, voiceSending]
  );

  const startVoiceRecording = useCallback(async () => {
    // Комментарий на русском: голосовые доступны в личке и в комнате (группа/канал).
    if ((!selectedUser && !selectedRoom) || recording || voiceSending || activeHasText) return;
    if (typeof window === 'undefined') return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Голосовые не поддерживаются в этом браузере');
      setTimeout(() => setError(null), 2500);
      return;
    }

    try {
      try {
        // Комментарий на русском: при записи голосового закрываем клавиатуру.
        draftChatRef.current?.blur();
        roomDraftRef.current?.blur();
        supportDraftRef.current?.blur();
      } catch {}
      stopVoiceViz();
      // Комментарий на русском: базовые улучшения качества речи (браузер может игнорировать, но хуже не делает).
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } as any,
      });
      mediaStreamRef.current = stream;
      // Комментарий на русском: выбираем оптимальный формат по платформе.
      // iOS/Safari: mp4/aac, Android/Chrome: webm/opus (лучшее сжатие).
      const platform = detectAudioPlatform();
      const preferredMime = getPreferredAudioMime(platform);
      const candidates = platform === 'ios' || platform === 'safari'
        ? [
            'audio/mp4;codecs=mp4a.40.2',
            'audio/mp4',
            'audio/aac',
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/wav',
          ]
        : [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/ogg;codecs=opus',
            'audio/ogg',
            'audio/mp4;codecs=mp4a.40.2',
            'audio/mp4',
            'audio/wav',
          ];
      const isTypeSupported = (t: string) => {
        try {
          return typeof (MediaRecorder as any).isTypeSupported === 'function' ? !!(MediaRecorder as any).isTypeSupported(t) : false;
        } catch {
          return false;
        }
      };
      const pickMime = () => candidates.find(isTypeSupported) || '';
      let mime = pickMime();

      // Комментарий на русском: на некоторых Safari isTypeSupported врёт — поэтому делаем пробный конструктор.
      const makeRecorder = () => {
        const mimesToTry = mime ? [mime, 'audio/mp4', 'audio/mp4;codecs=mp4a.40.2', ''] : ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4', ''];
        for (const mt of mimesToTry) {
          // 1) Пробуем с bitrate (Chrome/Edge), 2) fallback без bitrate (Safari).
          const opts1: any = {};
          if (mt) opts1.mimeType = mt;
          opts1.audioBitsPerSecond = VOICE_AUDIO_BPS;
          try {
            return { rec: new MediaRecorder(stream, opts1), usedMime: mt || '' };
          } catch {}
          const opts2: any = {};
          if (mt) opts2.mimeType = mt;
          try {
            return { rec: new MediaRecorder(stream, opts2), usedMime: mt || '' };
          } catch {}
        }
        // Последняя попытка без опций (иногда работает только так)
        return { rec: new MediaRecorder(stream), usedMime: '' };
      };

      const { rec, usedMime } = makeRecorder();
      if (usedMime) mime = usedMime;

      voiceChunksRef.current = [];
      voiceBytesRef.current = 0;
      voiceTooBigRef.current = false;
      voiceStartedAtRef.current = Date.now();
      voiceSendRef.current = true;
      voiceGotDataRef.current = false;
      setVoiceElapsedMs(0);
      // Комментарий на русском: таймер записи.
      voiceTimerRef.current = window.setInterval(() => {
        const started = voiceStartedAtRef.current;
        if (started) setVoiceElapsedMs(Date.now() - started);
      }, 120);

      // Комментарий на русском: график звука (реальный waveform) через WebAudio.
      try {
        const AudioCtx = (window.AudioContext || (window as any).webkitAudioContext) as any;
        if (AudioCtx) {
          const ctx = new AudioCtx();
          voiceAudioCtxRef.current = ctx;
          const src = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 1024;
          analyser.smoothingTimeConstant = 0.85;
          src.connect(analyser);
          const data = new Uint8Array(analyser.fftSize);
          const draw = () => {
            const canvas = voiceCanvasRef.current;
            const g = canvas?.getContext?.('2d');
            if (!canvas || !g) {
              voiceRafRef.current = requestAnimationFrame(draw);
              return;
            }
            const w = canvas.clientWidth || 1;
            const h = canvas.clientHeight || 1;
            const dpr = window.devicePixelRatio || 1;
            const cw = Math.max(1, Math.floor(w * dpr));
            const ch = Math.max(1, Math.floor(h * dpr));
            if (canvas.width !== cw || canvas.height !== ch) {
              canvas.width = cw;
              canvas.height = ch;
            }
            g.setTransform(dpr, 0, 0, dpr, 0, 0);
            g.clearRect(0, 0, w, h);
            analyser.getByteTimeDomainData(data);

            // Комментарий на русском: считаем “уровень” сигнала и автоподстраиваем усиление (чувствительнее на тихой речи).
            let e = 0;
            for (let i = 0; i < data.length; i += 4) {
              const v = (data[i] - 128) / 128;
              e += v * v;
            }
            const rms = Math.sqrt(e / Math.max(1, (data.length / 4) | 0)); // 0..1
            const lvl0 = voiceVizLevelRef.current || 0.08;
            const lvl = lvl0 * 0.85 + rms * 0.15;
            voiceVizLevelRef.current = Math.max(0.02, Math.min(0.25, lvl));
            const boost = Math.min(4.0, 0.18 / Math.max(0.02, voiceVizLevelRef.current));

            // Комментарий на русском: рисуем “волны” как симметричные столбики с glow/градиентом (эффектнее).
            const bars = Math.max(28, Math.min(56, Math.round(w / 9)));
            const step = Math.max(1, Math.floor(data.length / bars));
            const gap = w < 420 ? 2 : 3;
            const barW = Math.max(2, (w - gap * (bars - 1)) / bars);
            const x0 = (w - (barW * bars + gap * (bars - 1))) / 2;
            const mid = h / 2;
            const maxH = h * 0.46;
            const grad = g.createLinearGradient(0, 0, w, 0);
            grad.addColorStop(0, 'rgba(248, 113, 113, 0.35)'); // red-400
            grad.addColorStop(0.5, 'rgba(244, 63, 94, 0.98)'); // rose-500
            grad.addColorStop(1, 'rgba(236, 72, 153, 0.55)'); // pink-500
            g.lineCap = 'round';
            g.lineWidth = barW;
            g.shadowColor = 'rgba(248, 113, 113, 0.55)';
            g.shadowBlur = Math.max(10, Math.min(18, barW * 3));
            g.strokeStyle = grad;
            g.beginPath();
            for (let i = 0; i < bars; i++) {
              const start = i * step;
              const end = Math.min(data.length, start + step);
              let mx = 0;
              for (let j = start; j < end; j += 2) {
                const v = Math.abs((data[j] - 128) / 128);
                if (v > mx) mx = v;
              }
              let a = Math.min(1, mx * boost);
              a = Math.pow(a, 0.65);
              const bh = Math.max(2, a * maxH);
              const x = x0 + i * (barW + gap) + barW / 2;
              g.moveTo(x, mid - bh);
              g.lineTo(x, mid + bh);
            }
            g.stroke();
            voiceRafRef.current = requestAnimationFrame(draw);
          };
          voiceRafRef.current = requestAnimationFrame(draw);
        }
      } catch {
        // ignore
      }
      rec.onerror = (ev: any) => {
        // Комментарий на русском: если MediaRecorder упал, обязательно освобождаем микрофон и сбрасываем UI,
        // иначе в iOS PWA запись может “сломаться” до перезапуска приложения.
        voiceSendRef.current = false;
        try {
          if (rec.state !== 'inactive') rec.stop();
        } catch {}
        stopVoiceStream();
        voiceStartedAtRef.current = 0;
        voiceChunksRef.current = [];
        voiceBytesRef.current = 0;
        voiceTooBigRef.current = false;
        if (!isMountedRef.current) return;
        setRecording(false);
        const err = (ev as any)?.error;
        const errName = String(err?.name || '');
        setError(errName ? `Ошибка записи голосового (${errName})` : 'Ошибка записи голосового');
        setTimeout(() => setError(null), 3500);
      };
      rec.ondataavailable = (e) => {
        const d = e.data;
        if (!d || !d.size) return;
        // Комментарий на русском: данные пришли — запись работает, сбрасываем watchdog.
        voiceGotDataRef.current = true;
        if (voiceWatchdogRef.current) {
          window.clearTimeout(voiceWatchdogRef.current);
          voiceWatchdogRef.current = null;
        }
        const next = voiceBytesRef.current + d.size;
        if (next > VOICE_MAX_BYTES) {
          voiceTooBigRef.current = true;
          voiceSendRef.current = false;
          try {
            rec.stop();
          } catch {}
          return;
        }
        voiceBytesRef.current = next;
        voiceChunksRef.current.push(d);
      };
      rec.onstop = () => {
        const chunks = voiceChunksRef.current;
        voiceChunksRef.current = [];
        stopVoiceStream();
        // Комментарий на русском: если уже ушли со страницы — просто гасим запись без UI-обновлений/отправки.
        if (!isMountedRef.current) {
          voiceStartedAtRef.current = 0;
          voiceSendRef.current = true;
          voiceTooBigRef.current = false;
          return;
        }
        setRecording(false);
        const durMs = Math.max(0, Date.now() - (voiceStartedAtRef.current || 0));
        voiceStartedAtRef.current = 0;
        const shouldSend = voiceSendRef.current;
        voiceSendRef.current = true;
        if (voiceTooBigRef.current) {
          voiceTooBigRef.current = false;
          setError('Голосовое слишком большое (лимит ~15MB). Запишите короче.');
          setTimeout(() => setError(null), 3500);
          return;
        }
        if (!shouldSend) return;
        if (durMs > 0 && durMs < VOICE_MIN_MS) {
          // Комментарий на русском: короткий “тап” — просто сбрасываем запись, без отправки и без мусора в чате.
          setError('Слишком короткая запись');
          setTimeout(() => setError(null), 1200);
          return;
        }
        // Комментарий на русском: если chunks пустые но запись шла долго — значит MediaRecorder не отдал данные (PWA баг).
        if (!chunks.length) {
          if (durMs >= VOICE_MIN_MS) {
            setError('Не удалось записать звук. Попробуйте перезапустить приложение.');
            setTimeout(() => setError(null), 3500);
          }
          return;
        }
        const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
        const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document);
        // Комментарий на русском: нормализуем тип записи (убираем ";codecs", video/* -> audio/*).
        // Зачем: иначе сервер может сохранить mp4 как .webm из-за strict allowlist.
        const normType = (() => {
          const raw = String((rec as any)?.mimeType || mime || '').trim().toLowerCase();
          const base = raw.split(';', 1)[0]?.trim() || '';
          if (base === 'video/mp4') return 'audio/mp4';
          if (base === 'video/webm') return 'audio/webm';
          if (base === 'audio/x-m4a') return 'audio/mp4';
          if (base.startsWith('audio/')) return base;
          // Комментарий на русском: если Safari не сообщил mimeType — на iOS безопаснее считать это mp4 (webm там часто не проигрывается).
          return isIOS ? 'audio/mp4' : 'audio/webm';
        })();
        const blob = new Blob(chunks, { type: normType });
        if (blob.size > VOICE_MAX_BYTES) {
          setError('Голосовое слишком большое (лимит ~15MB). Запишите короче.');
          setTimeout(() => setError(null), 3500);
          return;
        }
        if (blob.size < 500) {
          setError('Слишком короткая запись');
          setTimeout(() => setError(null), 1200);
          return;
        }
        void sendVoiceBlob(blob);
      };

      mediaRecorderRef.current = rec;
      setRecording(true);
      // Комментарий на русском: на iOS Safari start(timeslice) иногда падает — там безопаснее start() без аргумента.
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
      const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document);
      let startedWithTimeslice = false;
      if (isIOS) {
        rec.start();
        startedWithTimeslice = false;
      } else {
        try {
          rec.start(VOICE_SLICE_MS);
          startedWithTimeslice = true;
        } catch {
          rec.start();
          startedWithTimeslice = false;
        }
      }
      // Комментарий на русском: проверяем что запись реально стартовала (PWA на некоторых устройствах может молча не начать).
      setTimeout(() => {
        if (mediaRecorderRef.current === rec && rec.state !== 'recording' && isMountedRef.current) {
          voiceSendRef.current = false;
          try { rec.stop(); } catch {}
          stopVoiceStream();
          setRecording(false);
          setError('Не удалось начать запись. Проверьте разрешения микрофона.');
          setTimeout(() => setError(null), 3500);
        }
      }, 300);
      // Комментарий на русском: watchdog нужен только если есть timeslice (иначе ondataavailable придёт лишь в onstop, как на iOS).
      if (startedWithTimeslice) {
        voiceWatchdogRef.current = window.setTimeout(() => {
          if (!voiceGotDataRef.current && isMountedRef.current && mediaRecorderRef.current?.state === 'recording') {
            voiceSendRef.current = false;
            try { mediaRecorderRef.current?.stop(); } catch {}
            setError('Микрофон не передаёт звук. Перезапустите приложение или проверьте разрешения.');
            setTimeout(() => setError(null), 4000);
          }
        }, VOICE_WATCHDOG_MS);
      }
    } catch (e: any) {
      stopVoiceStream();
      setRecording(false);
      const name = String(e?.name || '');
      const msg = String(e?.message || '');
      // Комментарий на русском: даём пользователю точный диагноз (это сильно помогает на iOS/Chrome).
      let human = 'Нет доступа к микрофону';
      if (name === 'NotAllowedError' || name === 'SecurityError') human = 'Доступ к микрофону запрещён в браузере';
      else if (name === 'NotFoundError') human = 'Микрофон не найден';
      else if (name === 'NotSupportedError') human = 'Запись голосовых не поддерживается этим браузером';
      else if (msg.toLowerCase().includes('permissions policy')) human = 'Микрофон запрещён политикой сайта (Permissions-Policy)';
      // Комментарий на русском: в iOS у Safari и PWA (ярлык) разные разрешения микрофона.
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
        const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document);
        const isStandalone = typeof window !== 'undefined' && (!!(window.matchMedia?.('(display-mode: standalone)')?.matches) || !!(navigator as any)?.standalone);
        if (isIOS && isStandalone) human = 'Микрофон запрещён для “приложения lisad”. Откройте Настройки iOS → Safari → Микрофон и разрешите для lisad.kbsu.ru';
      }
      setError(human);
      setTimeout(() => setError(null), 2500);
    }
  }, [activeHasText, recording, selectedRoom?.id, selectedUser?.id, sendVoiceBlob, stopVoiceStream, stopVoiceViz, voiceSending]);

  const stopVoiceRecording = useCallback(
    (send: boolean) => {
      voiceSendRef.current = send;
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        try {
          rec.stop();
          return;
        } catch {}
      }
      voiceSendRef.current = true;
      stopVoiceStream();
      setRecording(false);
    },
    [stopVoiceStream]
  );

  const onRightBtnClick = () => {
    // Комментарий на русском: toggle-логика.
    // 1) Если выбрано фото — отправляем фото (caption берём из текущего draft).
    // 1.5) Если выбран файл — отправляем файл.
    // 2) Если есть текст — отправляем текст.
    // 2) Если идёт запись — останавливаем и отправляем голосовое.
    // 3) Иначе — начинаем запись.
    if (selectedRoom) {
      if (hasPendingPhoto) return void sendPhotoMessage();
      if (hasPendingFile) return void sendFileMessage();
      if (hasRoomDraftText) return void sendRoomMessage();
    } else {
    if (hasPendingPhoto) return void sendPhotoMessage();
    if (hasPendingFile) return void sendFileMessage();
    if (hasDraftText) return void sendMessage();
    }
    if (voiceSending) return;
    if (recording) return void stopVoiceRecording(true);
    void startVoiceRecording();
  };

  const openChatComposer = useCallback(() => {
    // Комментарий на русском: "ввод в чате" — прячем нижнее поле и переносим курсор в пузырь в ленте.
    flushSync(() => setComposeInChat(true)); // Комментарий на русском: монтируем пузырь синхронно, чтобы iOS дал открыть клавиатуру.
    // Комментарий на русском: скроллим через virtuoso (без прямого scrollTop).
    virtuosoRef.current?.scrollToBottom('auto');
    try {
      (draftChatRef.current as any)?.focus?.({ preventScroll: true });
    } catch {
      draftChatRef.current?.focus();
    }
  }, []);

  // Комментарий на русском: чтобы эмодзи/черновик не "залипали" при переключении диалогов.
  useEffect(() => {
    // Комментарий на русском: на мобилках не открываем клавиатуру автоматически — закрываем ввод при смене диалога.
    if (isMobileViewport()) setComposeInChat(false);
    setReplyTo(null);
    // Комментарий на русском: при смене диалога отменяем запись (если она идёт) и чистим локальные blob: URL.
    // Зачем: запись не должна самопроизвольно останавливаться при старте — только при смене контекста.
    stopVoiceRecording(false);
    revokeVoiceLocalUrls();
    // Комментарий на русском: чистим выбранное фото и blob: URL, чтобы не тянуть память и не отправить "не в тот чат".
    clearPendingPhoto();
    revokePhotoLocalUrls();
  }, [revokePhotoLocalUrls, selectedRoom?.id, selectedUser?.id, stopVoiceRecording, revokeVoiceLocalUrls]);
  useEffect(() => {
    if (!selectedUser) return;
    if (isMobileViewport()) return;
    // Комментарий на русском: на десктопе курсор сразу в поле печати.
    setComposeInChat(true);
    requestAnimationFrame(() => {
      try {
        (draftChatRef.current as any)?.focus?.({ preventScroll: true });
      } catch {
        draftChatRef.current?.focus();
      }
    });
  }, [selectedUser?.id]);
  useEffect(() => {
    if (!selectedRoom) return;
    if (isMobileViewport()) return;
    requestAnimationFrame(() => {
      try {
        (roomDraftRef.current as any)?.focus?.({ preventScroll: true });
      } catch {
        roomDraftRef.current?.focus();
      }
    });
  }, [selectedRoom?.id]);
  useEffect(() => {
    const isSuper = myIsSuperRef.current;
    const open = supportOpen && (!isSuper || !!supportTargetUser) && !(isSuper && showErrorsChat);
    if (!open) return;
    if (isMobileViewport()) return;
    requestAnimationFrame(() => {
      try {
        (supportDraftRef.current as any)?.focus?.({ preventScroll: true });
      } catch {
        supportDraftRef.current?.focus();
      }
    });
  }, [supportOpen, supportTargetUser?.id, showErrorsChat]);

  useEffect(() => {
    // Комментарий на русском: при переключении поддержки (или переключении на чат ошибок) чистим выбранное фото.
    // Зачем: чтобы случайно не отправить вложение “не туда” (особенно у суперадмина при смене треда).
    clearPendingPhoto();
    revokePhotoLocalUrls();
  }, [revokePhotoLocalUrls, showErrorsChat, supportOpen, supportTargetUser?.id]);
  useEffect(() => {
    // Комментарий на русском: при уходе со страницы чистим blob: URL.
    return () => {
      revokeVoiceLocalUrls();
      revokePhotoLocalUrls();
    };
  }, [revokePhotoLocalUrls, revokeVoiceLocalUrls]);

  // Комментарий на русском: старый механизм восстановления scrollTop удалён — виртуализация управляет позицией автоматически.

  useEffect(() => {
    // Комментарий на русском: при уходе со страницы/назад гарантированно останавливаем запись и освобождаем микрофон.
    // Зачем: MediaRecorder может успеть кинуть onstop позже, поэтому ставим isMountedRef=false раньше.
    return () => {
      isMountedRef.current = false;
      voiceSendRef.current = false; // не отправлять при принудительной остановке
      try {
        const rec = mediaRecorderRef.current;
        if (rec && rec.state !== 'inactive') rec.stop();
      } catch {}
      try {
        mediaStreamRef.current?.getTracks()?.forEach((t) => t.stop());
      } catch {}
      mediaStreamRef.current = null;
      stopVoiceViz();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Комментарий на русском: на iOS/Safari переход “назад” может идти через bfcache, поэтому слушаем pagehide.
    const onPageHide = () => {
      voiceSendRef.current = false;
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        try {
          rec.stop();
        } catch {}
      }
      try {
        mediaStreamRef.current?.getTracks()?.forEach((t) => t.stop());
      } catch {}
      mediaStreamRef.current = null;
      stopVoiceViz();
      if (isMountedRef.current) setRecording(false);
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [stopVoiceViz]);

  useEffect(() => {
    // Комментарий на русском: если потеряли фокус/страница ушла — запись отменяем (без отправки).
    const onVis = () => {
      if (document.hidden && recording) stopVoiceRecording(false);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [recording, stopVoiceRecording]);

  useEffect(() => {
    composeInChatRef.current = composeInChat;
  }, [composeInChat]);

  useEffect(() => {
    selectedUserIdRef.current = selectedUser?.id || 0;
  }, [selectedUser?.id]);
  useEffect(() => {
    selectedRoomIdRef.current = selectedRoom?.id || 0;
  }, [selectedRoom?.id]);

  // Комментарий на русском: авто-рост поля ввода — desktop: до 25 строк; phone/tablet: до 60% высоты чата (над клавиатурой).
  useLayoutEffect(() => {
    if (!composeInChat) return;
    const el = draftChatRef.current;
    if (!el) return;
    const chatH =
      messagesScrollRef.current?.clientHeight || Math.round(window.visualViewport?.height || window.innerHeight || 0);
    autosizeTextarea(el, calcComposeTextareaMaxHeightPx(el, chatH));
    // Комментарий на русском: НЕ трогаем scrollTop при наборе — это источник “дёрганий” на iOS.
  }, [draft, composeInChat]);

  // Комментарий на русском: тот же авто‑рост для комнат (поле ввода внизу).
  useLayoutEffect(() => {
    const el = roomDraftRef.current;
    if (!el) return;
    const chatH =
      messagesScrollRef.current?.clientHeight || Math.round(window.visualViewport?.height || window.innerHeight || 0);
    autosizeTextarea(el, calcComposeTextareaMaxHeightPx(el, chatH));
  }, [roomDraft, selectedRoom?.id]);

  // Комментарий на русском: авто‑рост поля ввода в поддержке (чтобы одинаково везде).
  useLayoutEffect(() => {
    const el = supportDraftRef.current;
    if (!el) return;
    const h = Math.round(window.visualViewport?.height || window.innerHeight || 0);
    autosizeTextarea(el, calcComposeTextareaMaxHeightPx(el, h));
  }, [supportDraft]);

  useEffect(() => {
    // Комментарий на русском: при изменении вьюпорта/клавиатуры пересчитываем высоту (иначе может “залипнуть” старый clamp).
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    let raf = 0;
    const apply = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const chatH = messagesScrollRef.current?.clientHeight || Math.round(vv?.height || window.innerHeight || 0);
        const dm = draftChatRef.current;
        if (dm) autosizeTextarea(dm, calcComposeTextareaMaxHeightPx(dm, chatH));
        const room = roomDraftRef.current;
        if (room) autosizeTextarea(room, calcComposeTextareaMaxHeightPx(room, chatH));
        const sup = supportDraftRef.current;
        if (sup) autosizeTextarea(sup, calcComposeTextareaMaxHeightPx(sup, Math.round(vv?.height || window.innerHeight || 0)));
      });
    };
    apply();
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);
    vv?.addEventListener('resize', apply);
    vv?.addEventListener('scroll', apply);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
      vv?.removeEventListener('resize', apply);
      vv?.removeEventListener('scroll', apply);
    };
  }, []);

  useEffect(() => {
    // Комментарий на русском:
    // считаем нижний инсет по высоте плавающей панели ввода.
    // Важно: это НЕ зависит от готовности scroller — иначе можно “промазать” первый расчёт.
    const panel = composePanelRef.current;
    if (!panel || typeof ResizeObserver === 'undefined') return;
    let raf = 0;
    const apply = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const base = isMobileViewport() ? 112 : 128; // pb-28 / pb-32
        const h = panel.offsetHeight || 0;
        // Комментарий на русском: дополнительный “зазор” снизу — чтобы самое новое сообщение было выше края на 40px.
        // Зачем: плавающие кнопки/панель ввода могут перекрывать край последнего пузыря.
        const extra = 40;
        const pad = Math.max(base + extra, h + 24 + extra);
        setChatBottomInsetPx(pad);
      });
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(panel);
    window.addEventListener('resize', apply);
    window.visualViewport?.addEventListener('resize', apply);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', apply);
      window.visualViewport?.removeEventListener('resize', apply);
      setChatBottomInsetPx(0);
    };
  }, [tab, selectedUser?.id, selectedRoom?.id, supportOpen, roomSettingsOpen]);

  useLayoutEffect(() => {
    if (!composeInChat) return;
    // Комментарий на русском: вместо прямого scrollTop используем virtuoso API.
    virtuosoRef.current?.scrollToBottom('auto');
    try {
      (draftChatRef.current as any)?.focus?.({ preventScroll: true });
    } catch {
      draftChatRef.current?.focus();
    }
  }, [composeInChat]);

  // Комментарий на русском: настройка приватности — скрывать/показывать превью текста в списке диалогов.
  useEffect(() => {
    try {
      const v = localStorage.getItem('lisad_show_message_preview');
      if (v === '0' || v === '1') setShowMessagePreview(v === '1');
    } catch {}
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem('lisad_show_message_preview', showMessagePreview ? '1' : '0');
    } catch {}
  }, [showMessagePreview]);

  // Комментарий на русском: настройка приватности — скрывать/показывать содержимое сообщений в уведомлениях.
  useEffect(() => {
    try {
      const v = localStorage.getItem('lisad_show_notification_content');
      if (v === '0' || v === '1') setShowNotificationContent(v === '1');
    } catch {}
  }, []);
  useEffect(() => {
    // Комментарий на русском: дублируем настройку в Cache Storage, чтобы Service Worker мог читать её без localStorage.
    // Дополнительно: прокидываем "активный чат", чтобы SW мог не показывать уведомления из открытого диалога, даже если URL без ?user=...
    const activeDmUserId = !supportOpen && !selectedRoom ? Number(selectedUser?.id || 0) : 0;
    const activeRoomId = !supportOpen && !!selectedRoom ? Number(selectedRoom?.id || 0) : 0;
    const activeSupportOpen = supportOpen ? 1 : 0;
    const activeSupportUserId = Number(supportTargetUser?.id || 0);
    try {
      localStorage.setItem('lisad_show_notification_content', showNotificationContent ? '1' : '0');
    } catch {}
    try {
      localStorage.setItem('lisad_active_dm_user_id', String(activeDmUserId || 0));
      localStorage.setItem('lisad_active_room_id', String(activeRoomId || 0));
      localStorage.setItem('lisad_active_support_open', activeSupportOpen ? '1' : '0');
      localStorage.setItem('lisad_active_support_user_id', String(activeSupportUserId || 0));
    } catch {}
    void (async () => {
      try {
        const c = (globalThis as any).caches;
        if (!c || typeof c.open !== 'function') return;
        const cache = await c.open('lisad_push_prefs_v1');
        const body = JSON.stringify({
          showNotificationContent: showNotificationContent ? 1 : 0,
          activeDmUserId: activeDmUserId || 0,
          activeRoomId: activeRoomId || 0,
          activeSupportOpen,
          activeSupportUserId: activeSupportUserId || 0,
        });
        await cache.put('/__lisad_push_prefs', new Response(body, { headers: { 'content-type': 'application/json' } }));
      } catch {
        // ignore
      }
    })();
  }, [showNotificationContent, selectedUser?.id, selectedRoom?.id, supportOpen, supportTargetUser?.id]);

  // Комментарий на русском: CSS-переменные для размера/плотности сообщений (минимум пропсов, максимум контроля).
  const dmStyle = useMemo(() => {
    const fsRaw = Number.isFinite(fontScale) ? fontScale : 100;
    const fs = Math.max(50, Math.min(200, fsRaw));
    const font = `${(0.875 * fs / 100).toFixed(3)}rem`; // 0.875rem = text-sm (≈14px при 16px root)
    const dens: MessageDensity = messageDensity === 'compact' || messageDensity === 'comfortable' ? messageDensity : 'normal';
    const padY = dens === 'compact' ? '0.375rem' : dens === 'comfortable' ? '0.625rem' : '0.5rem';
    const gap = dens === 'compact' ? '0.5rem' : dens === 'comfortable' ? '1rem' : '0.75rem';
    const leading = dens === 'compact' ? '1.4' : dens === 'comfortable' ? '1.6' : '1.5';

    const normHex = (v: unknown, fallback: string) => {
      const s = String(v || '').trim();
      return /^#[0-9a-fA-F]{6}$/.test(s) ? s.toLowerCase() : fallback;
    };
    const hexToTriplet = (hex: string, fallback: string) => {
      const h = normHex(hex, fallback);
      const r = parseInt(h.slice(1, 3), 16) || 0;
      const g = parseInt(h.slice(3, 5), 16) || 0;
      const b = parseInt(h.slice(5, 7), 16) || 0;
      return `${r} ${g} ${b}`;
    };

    // Комментарий на русском: параметры "жидкого стекла" для пузырьков сообщений (blur убран для оптимизации).
    const t = Number(bubbleTransparency);
    const transp = Number.isFinite(t) ? Math.max(0, Math.min(100, Math.round(t))) : 82;
    const alpha = Math.max(0, Math.min(1, (100 - transp) / 100));
    const sh = Number(bubbleShadow);
    const shadow = Number.isFinite(sh) ? Math.max(0, Math.min(100, Math.round(sh))) : 35;
    const shadowG = Math.max(0, Math.min(1, shadow / 100));
    const bubbleRgb = hexToTriplet(bubbleColor, '#7dd3fc');
    const shadowRgb = hexToTriplet(bubbleShadowColor, '#0b1220');
    const msgText = normHex(bubbleTextColor, '#ffffff');

    // Комментарий на русском: разные оттенки пузырьков для "я" и "собеседник".
    // Свои — чуть светлее; собеседник — темнее и с холодным оттенком.
    const parseTriplet = (t: string) => {
      const parts = String(t || '').trim().split(/\s+/).map((x) => Number(x));
      const r = Number.isFinite(parts[0]) ? Math.max(0, Math.min(255, Math.round(parts[0]!))) : 0;
      const g = Number.isFinite(parts[1]) ? Math.max(0, Math.min(255, Math.round(parts[1]!))) : 0;
      const b = Number.isFinite(parts[2]) ? Math.max(0, Math.min(255, Math.round(parts[2]!))) : 0;
      return [r, g, b] as [number, number, number];
    };
    const mixTriplet = (a: [number, number, number], b: [number, number, number], t: number) => {
      const k = Math.max(0, Math.min(1, Number(t) || 0));
      const mx = (x: number, y: number) => Math.max(0, Math.min(255, Math.round(x * (1 - k) + y * k)));
      return [mx(a[0], b[0]), mx(a[1], b[1]), mx(a[2], b[2])] as [number, number, number];
    };
    const baseRgb = parseTriplet(bubbleRgb);
    const meRgb =
      /^#[0-9a-fA-F]{6}$/.test(String(bubbleColorMe || '').trim())
        ? hexToTriplet(String(bubbleColorMe).trim(), '#7dd3fc')
        : bubbleRgb;
    const peerRgb =
      /^#[0-9a-fA-F]{6}$/.test(String(bubbleColorPeer || '').trim())
        ? hexToTriplet(String(bubbleColorPeer).trim(), '#7dd3fc')
        : mixTriplet(mixTriplet(baseRgb, [99, 102, 241], 0.18), [15, 23, 42], 0.55).join(' ');

    // Комментарий на русском: выбор красивого/читабельного шрифта (используем системные стеки, чтобы работало везде).
    const fontKey = String(messageFont || 'system').toLowerCase();
    const fontFamily =
      fontKey === 'inter'
        ? `"Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif`
        : fontKey === 'serif'
          ? `ui-serif, Georgia, Cambria, "Times New Roman", Times, serif`
          : fontKey === 'mono'
            ? `ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace`
            : `ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif`;
    return {
      fontFamily,
      ['--dm-font' as any]: font,
      ['--dm-pad-x' as any]: '1rem',
      ['--dm-pad-y' as any]: padY,
      ['--dm-gap' as any]: gap,
      ['--dm-leading' as any]: leading,
      ['--dm-msg-text' as any]: msgText,
      ['--dm-font-family' as any]: fontFamily,
      ['--mb-rgb' as any]: bubbleRgb,
      ['--mb-me-rgb' as any]: meRgb,
      ['--mb-peer-rgb' as any]: peerRgb,
      ['--mb-a' as any]: alpha.toFixed(3),
      ['--mb-me-a' as any]: alpha.toFixed(3),
      ['--mb-peer-a' as any]: alpha.toFixed(3),
      ['--mb-shadow-rgb' as any]: shadowRgb,
      ['--mb-shadow-g' as any]: shadowG.toFixed(3),
      ['--mb-shadow-on' as any]: bubbleShadowEnabled ? '1' : '0',
      ['--mb-glow-on' as any]: bubbleGlowEnabled ? '1' : '0',
      ['--mb-glow-rgb' as any]: hexToTriplet(bubbleGlowColor, '#ffffff'),
      // Комментарий на русском: применяем смещение от гироскопа к базовым координатам.
      ['--mb-glow-x' as any]: `${Math.max(0, Math.min(100, Math.round((Number(bubbleGlowX) || 22) + gyroOffset.x)))}%`,
      ['--mb-glow-y' as any]: `${Math.max(0, Math.min(100, Math.round((Number(bubbleGlowY) || 18) + gyroOffset.y)))}%`,
    } as any;
  }, [fontScale, messageDensity, bubbleTransparency, bubbleColor, bubbleColorMe, bubbleColorPeer, bubbleShadow, bubbleShadowColor, bubbleShadowEnabled, bubbleGlowEnabled, bubbleGlowColor, bubbleGlowX, bubbleGlowY, gyroOffset, bubbleTextColor, messageFont]);

  const chatRowPad = compactMode ? 'p-2' : 'p-3';
  const chatAvatarSize = compactMode ? 'w-9 h-9' : 'w-11 h-11';
  const chatSnippetText = compactMode ? 'text-xs' : 'text-sm';
  const selectedIsBlocked = !!selectedUser && blockedUsers.some((u) => (u?.id as number) === (selectedUser?.id as number));

  const canUseProjectContext = !editTarget && !replyTo && !hasPendingPhoto && !hasPendingFile && !recording && !voiceSending && !sending && !selectedRoom;
  const activeDraftProject = verifiedDraftProject?.key === dmDraft.key && verifiedDraftProject.revision === dmDraft.revision && verifiedDraftProject.project.id === dmDraft.projectId ? verifiedDraftProject.project : null;
  const offeredProject = pendingProjectContext?.key === dmDraft.key && pendingProjectContext.revision === dmDraft.revision ? pendingProjectContext.project : null;
  const requestedPeerId = parseContextId(searchParams.get('user'));
  const requestedProjectId = readContextProjectId(searchParams);

  useEffect(() => {
    if (!dmDraft.ready || !dmDraft.projectId || !selectedUser) return;
    let alive = true;
    const isCurrent = () => alive && dmDraft.isCurrent();
    void findDraftProject(selectedUser.id, dmDraft.projectId, (peer, limit, offset, projectId) => apiClient.getPublicProfileProjects(peer, limit, offset, projectId), isCurrent)
      .then((project) => {
        if (!isCurrent()) return;
        setVerifiedDraftProject(project ? { key: dmDraft.key, revision: dmDraft.revision, project } : null);
        if (!project) setContextNotice({ key: dmDraft.key, text: 'Не удалось проверить публичный проект. Уберите тег, чтобы отправить обычное сообщение.' });
      }).catch(() => {
        if (isCurrent()) setContextNotice({ key: dmDraft.key, text: 'Публичный проект недоступен. Текст сохранён; тег можно убрать.' });
      });
    return () => { alive = false; };
  }, [dmDraft.key, dmDraft.revision, dmDraft.projectId, dmDraft.ready, dmDraft.isCurrent, selectedUser?.id]);

  useEffect(() => {
    if (!dmDraft.ready || !requestedPeerId || !requestedProjectId || requestedPeerId !== selectedUser?.id) return;
    let alive = true;
    const isCurrent = () => alive && dmDraft.isCurrent();
    void findDraftProject(requestedPeerId, requestedProjectId, (peer, limit, offset, projectId) => apiClient.getPublicProfileProjects(peer, limit, offset, projectId), isCurrent)
      .then((project) => {
        if (!isCurrent()) return;
        if (project) setPendingProjectContext({ key: dmDraft.key, revision: dmDraft.revision, project });
        else setContextNotice({ key: dmDraft.key, text: 'Не удалось проверить контекст проекта. Можно написать без него.' });
        router.replace(`/messages?user=${requestedPeerId}`, { scroll: false });
      }).catch(() => {
        if (!isCurrent()) return;
        setContextNotice({ key: dmDraft.key, text: 'Контекст проекта не загрузился. Черновик не изменён.' });
        router.replace(`/messages?user=${requestedPeerId}`, { scroll: false });
      });
    return () => { alive = false; };
  }, [dmDraft.key, dmDraft.revision, dmDraft.ready, dmDraft.isCurrent, requestedPeerId, requestedProjectId, selectedUser?.id, router]);

  // Пункт 1: подгрузка истории вверх (пагинация) + контроль скролла (чтобы чат не прыгал)
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const hasMoreMessagesRef = useRef(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);
  // Комментарий на русском: якорь для точного восстановления позиции (устраняет “сдвиг” при подгрузке).
  // Комментарий: captureChatScrollAnchor удалён — react-virtuoso управляет позицией автоматически.

  // Комментарий: restoreChatScrollAnchor удалён — react-virtuoso управляет позицией автоматически.
  const isNearBottomRef = useRef(true);
  const dmScrollBottomOffsetRef = useRef(0);
  const dmScrollPersistedOffsetRef = useRef(0);
  const dmViewportChangeAtRef = useRef(0);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  // Комментарий на русском: ref для проверки изменения — не вызываем setState при том же значении (iOS momentum).
  const showScrollToBottomRef = useRef(false);

  const writeDmScroll = useCallback((userId: number, bottomOffset: number) => {
    // Комментарий на русском: храним смещение от низа — так позиция сохраняется при новых сообщениях и повороте экрана.
    try {
      const off = Math.max(0, Math.round(bottomOffset));
      dmScrollPersistedOffsetRef.current = off;
      localStorage.setItem(`lisad_dm_scroll_v1:${userId}`, String(off));
    } catch {}
  }, []);

  // Комментарий на русском: аналогичная функция для комнат.
  const writeRoomScroll = useCallback((roomId: number, bottomOffset: number) => {
    try {
      const off = Math.max(0, Math.round(bottomOffset));
      localStorage.setItem(`lisad_room_scroll_v1:${roomId}`, String(off));
    } catch {}
  }, []);
  const writeRoomScrollThrottled = useMemo(() => throttle(writeRoomScroll, 250), [writeRoomScroll]);

  // Комментарий на русском: сохранение/восстановление скролла списка чатов (sidebar).
  const writeChatsListScroll = useCallback((scrollTop: number) => {
    try {
      const v = Math.max(0, Math.round(scrollTop));
      localStorage.setItem('lisad_chats_list_scroll_v1', String(v));
    } catch {}
  }, []);
  const writeChatsListScrollThrottled = useMemo(() => throttle(writeChatsListScroll, 250), [writeChatsListScroll]);

  // Комментарий на русском: восстанавливаем скролл списка чатов при загрузке.
  useEffect(() => {
    const el = chatsListScrollRef.current;
    if (!el) return;
    try {
      const v = localStorage.getItem('lisad_chats_list_scroll_v1');
      const n = v != null ? Number(v) : 0;
      if (Number.isFinite(n) && n > 0) el.scrollTop = n;
    } catch {}
  }, []);
  const writeDmScrollThrottled = useMemo(() => throttle(writeDmScroll, 250), [writeDmScroll]);

  useEffect(() => {
    // перед уходом из диалога сохраняем позицию (иначе иногда улетает вверх при входе/выходе).
    const uid = selectedUser?.id || 0;
    if (!uid) return;
    const el = messagesScrollRef.current;
    if (!el) return;
    return () => {
      const offRaw = Math.max(0, Math.round(el.scrollHeight - el.clientHeight - el.scrollTop));
      // Комментарий на русском: небольшое смещение “от низа” считаем как 0 — иначе на iOS при возврате/resize чат может «прыгать» вверх.
      const off = offRaw < 120 ? 0 : offRaw;
      dmScrollBottomOffsetRef.current = off;
      dmScrollPersistedOffsetRef.current = off;
      isNearBottomRef.current = off < 120;
      writeDmScroll(uid, off);
    };
  }, [selectedUser?.id, writeDmScroll]);

  // Комментарий на русском: ручной restore scrollTop на resize удалён — на iOS это даёт “дёргания”.

  // Комментарий на русском: guard против «первого прыжка» — не дергаем scrollToBottom сразу после открытия чата.
  const virtuosoReadyRef = useRef(false);
  const scrollPendingRef = useRef(false);
  useEffect(() => {
    virtuosoReadyRef.current = false;
    scrollPendingRef.current = true;
    const t = setTimeout(() => {
      virtuosoReadyRef.current = true;
      // Комментарий на русском: если был запрос на скролл пока guard был активен — выполняем сейчас.
      if (scrollPendingRef.current) {
        scrollPendingRef.current = false;
        virtuosoRef.current?.scrollToBottom('auto');
      }
    }, 100);
    return () => clearTimeout(t);
  }, [selectedUser?.id, selectedRoom?.id]);

  const forceScrollToBottom = useCallback(
    (userId?: number) => {
      // Комментарий на русском: фикс iOS — “фиксируем низ” ДО любых resize/blur клавиатуры, иначе restore() может восстановить старый offset.
      dmScrollBottomOffsetRef.current = 0;
      dmScrollPersistedOffsetRef.current = 0;
      isNearBottomRef.current = true;
      setShowScrollToBottom(false);
      if (userId) writeDmScroll(userId, 0);

      // Комментарий на русском: scrollToBottom только после стабилизации DOM — иначе на iOS первый свайп «прыгает».
      if (!virtuosoReadyRef.current) {
        scrollPendingRef.current = true;
        return;
      }
      virtuosoRef.current?.scrollToBottom('auto');
    },
    [writeDmScroll]
  );

  // Пункт 3: online/typing (для открытого диалога)
  const [peerOnline, setPeerOnline] = useState(false);
  const [peerLastSeen, setPeerLastSeen] = useState<string | null>(null);
  const [peerTyping, setPeerTyping] = useState(false);
  // Комментарий на русском: тип устройства собеседника (для иконки рядом с именем).
  const [peerDeviceType, setPeerDeviceType] = useState<'mobile' | 'tablet' | 'desktop' | null>(null);

  // Push: статус подписки (вкл/выкл) для UI кнопки
  const [pushEnabled, setPushEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      const s = await getWebPushStatus();
      if (!alive) return;
      setPushEnabled(!!s.subscribed && s.permission === 'granted');
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Комментарий на русском: очистку кэшей после деплоя делаем глобально в app/providers.tsx.

  const apiCall = useCallback(apiCallRaw, []);

  const handleUnauthorized = useCallback(() => {
    if (authErrorShownRef.current) return;
    authErrorShownRef.current = true;
    setLoadError('Сессия истекла. Войдите снова.');
    try {
      clearAuth();
    } catch {}
    setIsAuthed(false);
  }, []);

  const apiCallWithTimeout = useCallback(
    async (url: string, options: RequestInit = {}, timeoutMs: number) => {
      const isAbortError = (e: unknown) => {
        const anyE = e as any;
        const name = typeof anyE?.name === 'string' ? String(anyE.name) : '';
        return name === 'AbortError';
      };
      const clearNetTimer = () => {
        if (netTimerRef.current != null) window.clearTimeout(netTimerRef.current);
        netTimerRef.current = null;
      };
      const markServerOk = () => {
        netAttemptRef.current = 0;
        netLoopRunningRef.current = false;
        clearNetTimer();
        setNetBanner('hidden');
      };
      const probeInternet = async (): Promise<boolean> => {
        try {
          const ac = new AbortController();
          const t = window.setTimeout(() => ac.abort(), 2500);
          try {
            // Комментарий на русском: "пинг" интернета. no-cors нужен, чтобы не упасть из-за CORS.
            await fetch('https://dns.google/generate_204', { method: 'GET', mode: 'no-cors', cache: 'no-store', signal: ac.signal });
            return true;
          } finally {
            window.clearTimeout(t);
          }
        } catch {
          return false;
        }
      };
      const probeServer = async (): Promise<boolean> => {
        try {
          const ac = new AbortController();
          const t = window.setTimeout(() => ac.abort(), 2500);
          try {
            const r = await fetch(`${API_URL}/api/updates`, { method: 'GET', cache: 'no-store', signal: ac.signal });
            return r.ok || r.status > 0;
          } finally {
            window.clearTimeout(t);
          }
        } catch {
          return false;
        }
      };
      const startNetLoop = () => {
        if (netLoopRunningRef.current) return;
        if (authErrorShownRef.current) return;
        netLoopRunningRef.current = true;
        netAttemptRef.current = 0;
        setNetBanner('loading');
        clearNetTimer();

        const step = async () => {
          if (!netLoopRunningRef.current) return;
          // Комментарий на русском: если браузер сам говорит "offline" — не ждём 15 попыток.
          if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            setNetBanner('offline');
            netLoopRunningRef.current = false;
            clearNetTimer();
            return;
          }

          const ok = await probeServer();
          if (ok) {
            markServerOk();
            return;
          }

          netAttemptRef.current += 1;
          if (netAttemptRef.current >= 15) {
            const internetOk = await probeInternet();
            if (!internetOk) {
              // Комментарий на русском: интернета нет — показываем коротко и без доп. текста.
              setNetBanner('offline');
              netLoopRunningRef.current = false;
              clearNetTimer();
              return;
            }
            // Комментарий на русском: интернет есть, но сервер недоступен — остаёмся в "Загрузка" и продолжаем попытки.
            netAttemptRef.current = 0;
          }

          netTimerRef.current = window.setTimeout(() => void step(), 2000);
        };

        void step();
      };
      const shouldMarkOffline = (e: unknown) => {
        // Комментарий на русском: abort/таймауты long-poll не считаем офлайном.
        if (isAbortError(e)) return false;
        // Комментарий на русском: офлайн помечаем только если браузер сам считает, что сети нет.
        try {
          if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
        } catch {}
        return false;
      };
      const ms = Number(timeoutMs || 0) || 0;
      if (!ms) {
        try {
          const res = await apiCall(url, options);
          // Комментарий на русском: успешный ответ (даже 4xx/5xx) = сеть/сервер доступны.
          setIsOnline((prev) => (prev ? prev : true));
          markServerOk();
          if (res.status === 401) handleUnauthorized();
          return res;
        } catch (e) {
          if (!isAbortError(e)) startNetLoop();
          if (shouldMarkOffline(e)) setIsOnline(false);
          throw e;
        }
      }
      // Комментарий на русском: если signal уже передан — уважаем его, иначе ставим свой таймаут.
      if ((options as any)?.signal) {
        try {
          const res = await apiCall(url, options);
          setIsOnline((prev) => (prev ? prev : true));
          markServerOk();
          if (res.status === 401) handleUnauthorized();
          return res;
        } catch (e) {
          // Комментарий на русском: отмена (cleanup) — не считаем проблемой сети.
          if (!isAbortError(e)) startNetLoop();
          if (shouldMarkOffline(e)) setIsOnline(false);
          throw e;
        }
      }
      const ac = new AbortController();
      const t = window.setTimeout(() => ac.abort(), ms);
      try {
        const res = await apiCall(url, { ...options, signal: ac.signal });
        setIsOnline((prev) => (prev ? prev : true));
        markServerOk();
        if (res.status === 401) handleUnauthorized();
        return res;
      } finally {
        window.clearTimeout(t);
      }
    },
    [apiCall, handleUnauthorized]
  );

  // Комментарий на русском: IndexedDB иногда может “подвиснуть” (blocked/upgrade), поэтому читаем кэш с таймаутом.
  const idbGetWithTimeout = useCallback(async <T,>(p: Promise<T>, ms: number): Promise<T | null> => {
    const timeoutMs = Math.max(200, Number(ms || 0) || 0);
    return await new Promise<T | null>((resolve) => {
      let done = false;
      const t = window.setTimeout(() => {
        if (done) return;
        done = true;
        resolve(null);
      }, timeoutMs);
      p.then((v) => {
        if (done) return;
        done = true;
        window.clearTimeout(t);
        resolve(v);
      }).catch(() => {
        if (done) return;
        done = true;
        window.clearTimeout(t);
        resolve(null);
      });
    });
  }, []);

  // Комментарий на русском: сбрасываем volatile-кэши при смене пользователя (RAM), чтобы не смешивать данные разных аккаунтов.
  const resetRamCachesForUser = useCallback((nextUid: number) => {
    activeUidRef.current = Number(nextUid || 0) || 0;
    try {
      chatMemRef.current.clear();
    } catch {}
    try {
      pendingMessagesRef.current.clear();
    } catch {}
    pendingSendingRef.current = false;
    setPendingCount(0);
    setConversations([]);
    setRooms([]);
    chatMessagesStore.setState({ chats: {} });
    setSelectedUser(null);
    setSelectedRoom(null);
    setSupportOpen(false);
    setSupportTargetUser(null);
    setSupportMessages([]);
    supportLastIdRef.current = 0;
    lastMessageIdRef.current = 0;
    currentChatHashRef.current = '';
  }, []);

  const hydrateOfflineForUser = useCallback(
    async (uid: number) => {
      const userId = Number(uid || 0) || 0;
      if (!userId) return;

      // Комментарий на русском: если uid изменился — обязательно чистим RAM перед гидратацией, иначе ChatMap смешается.
      if (activeUidRef.current !== userId) resetRamCachesForUser(userId);

      // Комментарий на русском: читаем всё ПАРАЛЛЕЛЬНО для мгновенной загрузки.
      const HYDRATE_TIMEOUT = 300; // 300мс — достаточно для локального IndexedDB

      // Запускаем все чтения одновременно
      const [cmResult, convResult, rmsResult, pendResult] = await Promise.all([
        idbGetWithTimeout(chatKvGet<any>(offlineChatMemKey(userId)), HYDRATE_TIMEOUT),
        idbGetWithTimeout(chatKvGet<Conversation[]>(offlineConversationsKey(userId)), HYDRATE_TIMEOUT),
        idbGetWithTimeout(chatKvGet<RoomConversation[]>(offlineRoomsKey(userId)), HYDRATE_TIMEOUT),
        idbGetWithTimeout(chatKvGet<PendingOutboxItem[]>(offlinePendingKey(userId)), HYDRATE_TIMEOUT),
      ]);

      // ChatMap — RAM-кэш сообщений
      try {
        const items = (cmResult?.value as any)?.items;
        if (Array.isArray(items) && items.length) {
          for (const it of items.slice(-32)) {
            const k = typeof it?.k === 'string' ? String(it.k) : '';
            if (!k || !(k.startsWith('dm:') || k.startsWith('room:'))) continue;
            const msgs = Array.isArray(it?.messages) ? (it.messages as any[]).slice(-CHAT_PAGE_LIMIT) : [];
            const lastId = Number(it?.lastId || 0) || 0;
            const hasMore = !!it?.hasMore;
            chatMemRef.current.set(k, { messages: msgs as any, lastId, hasMore });
          }
        }
      } catch {
        // ignore
      }

      // Conversations
      try {
        if (convResult?.value && Array.isArray(convResult.value) && convResult.value.length) {
          setConversations(convResult.value);
          setLoadingChats(false);
        }
      } catch {
        // ignore
      }

      // Rooms
      try {
        if (rmsResult?.value && Array.isArray(rmsResult.value) && rmsResult.value.length) {
          setRooms(rmsResult.value);
          setLoadingRooms(false);
        }
      } catch {
        // ignore
      }

      // Pending outbox
      try {
        const arr = pendResult?.value;
        if (Array.isArray(arr) && arr.length) {
          pendingMessagesRef.current.clear();
          for (const it of arr as any[]) {
            const cid = typeof it?.clientId === 'string' ? String(it.clientId) : '';
            const kind = it?.kind === 'room' ? 'room' : 'dm';
            const targetId = Number(it?.targetId || 0) || 0;
            const msg = typeof it?.message === 'string' ? String(it.message) : '';
            if (!cid || !targetId || !msg) continue;
            pendingMessagesRef.current.set(cid, {
              kind,
              targetId,
              message: msg,
              replyToId: Number(it?.replyToId || 0) || undefined,
              clientId: cid,
              createdAt: typeof it?.createdAt === 'string' ? String(it.createdAt) : new Date().toISOString(),
            });
          }
          setPendingCount(pendingMessagesRef.current.size);
        }
      } catch {
        // ignore
      }
    },
    [idbGetWithTimeout, resetRamCachesForUser, setConversations, setLoadingChats, setLoadingRooms, setRooms]
  );

  const upsertOfflineChatMessage = useCallback(async (uid: number, kind: 'dm' | 'room', targetId: number, msg: DirectMessage) => {
    const userId = Number(uid || 0) || 0;
    const tid = Number(targetId || 0) || 0;
    if (!userId || !tid) return;
    const key = kind === 'room' ? offlineRoomKey(userId, tid) : offlineDmKey(userId, tid);
    const cur = await chatKvGet<DirectMessage[]>(key);
    const prev = Array.isArray(cur?.value) ? (cur?.value as DirectMessage[]) : [];
    const cid = String((msg as any)?.client_id || '');
    let out = prev;
    if (cid) {
      const idx = prev.findIndex((m: any) => String(m?.client_id || '') === cid);
      out = idx >= 0 ? prev.map((m, i) => (i === idx ? ({ ...(m as any), ...(msg as any) } as any) : m)) : [...prev, msg];
    } else {
      out = [...prev, msg];
    }
    out = out.slice(-CHAT_OFFLINE_MAX_MESSAGES);
    void chatKvSet(key, out);
  }, []);

  const patchOfflineChatMessageByClientId = useCallback(
    async (
      uid: number,
      kind: 'dm' | 'room',
      targetId: number,
      clientId: string,
      patch: Partial<DirectMessage>
    ) => {
      const userId = Number(uid || 0) || 0;
      const tid = Number(targetId || 0) || 0;
      const cid = String(clientId || '').trim();
      if (!userId || !tid || !cid) return;
      const key = kind === 'room' ? offlineRoomKey(userId, tid) : offlineDmKey(userId, tid);
      const cur = await chatKvGet<DirectMessage[]>(key);
      const prev = Array.isArray(cur?.value) ? (cur?.value as DirectMessage[]) : [];
      const idx = prev.findIndex((m: any) => String(m?.client_id || '') === cid);
      if (idx < 0) return;
      const out = prev.map((m, i) => (i === idx ? ({ ...(m as any), ...(patch as any) } as any) : m));
      void chatKvSet(key, out.slice(-CHAT_OFFLINE_MAX_MESSAGES));
    },
    []
  );

  const patchOfflineChatMessageById = useCallback(
    async (uid: number, kind: 'dm' | 'room', targetId: number, messageId: number, patch: Partial<DirectMessage>) => {
      // Комментарий на русском: обновляем офлайн-снимок по server id (для реакций/редактирования без client_id).
      const userId = Number(uid || 0) || 0;
      const tid = Number(targetId || 0) || 0;
      const mid = Number(messageId || 0) || 0;
      if (!userId || !tid || !mid) return;
      const key = kind === 'room' ? offlineRoomKey(userId, tid) : offlineDmKey(userId, tid);
      const cur = await chatKvGet<DirectMessage[]>(key);
      const prev = Array.isArray(cur?.value) ? (cur?.value as DirectMessage[]) : [];
      if (!prev.length) return;

      let touched = false;
      const out = prev.map((m: any) => {
        const id = Number(m?.id || 0) || 0;
        if (id !== mid) return m;
        touched = true;
        return { ...(m as any), ...(patch as any) } as any;
      });
      if (!touched) return;
      void chatKvSet(key, out.slice(-CHAT_OFFLINE_MAX_MESSAGES));
    },
    []
  );

  const deleteOfflineChatMessage = useCallback(
    async (uid: number, kind: 'dm' | 'room', targetId: number, messageId: number, clientId?: string) => {
      // Комментарий на русском: удаляем из офлайн-снимка, чтобы сообщение не “воскресло” после перезагрузки.
      const userId = Number(uid || 0) || 0;
      const tid = Number(targetId || 0) || 0;
      const mid = Number(messageId || 0) || 0;
      const cid = String(clientId || '').trim();
      if (!userId || !tid) return;
      const key = kind === 'room' ? offlineRoomKey(userId, tid) : offlineDmKey(userId, tid);
      const cur = await chatKvGet<DirectMessage[]>(key);
      const prev = Array.isArray(cur?.value) ? (cur?.value as DirectMessage[]) : [];
      if (!prev.length) return;
      const out = prev.filter((m: any) => {
        if (cid && String(m?.client_id || '') === cid) return false;
        const id = Number(m?.id || 0) || 0;
        if (mid && id === mid) return false;
        return true;
      });
      void chatKvSet(key, out.slice(-CHAT_OFFLINE_MAX_MESSAGES));
    },
    []
  );

  const applyServerChatCommand = useCallback(
    (cmd: any) => {
      const t = typeof cmd?.type === 'string' ? String(cmd.type) : '';
      const isDelete = t === 'dm_message_deleted' || t === 'room_message_deleted';
      const isReaction = t === 'dm_message_reaction' || t === 'room_message_reaction';
      const isEdit = t === 'dm_message_edited' || t === 'room_message_edited';
      if (!isDelete && !isReaction && !isEdit) return;

      const messageId = Number(cmd?.message_id || 0) || 0;
      if (messageId <= 0) return;

      const kind: 'dm' | 'room' = t.startsWith('room_') ? 'room' : 'dm';
      const targetId = kind === 'room' ? (Number(cmd?.room_id || 0) || 0) : (Number(cmd?.peer_id || 0) || 0);
      if (targetId <= 0) return;

      const uid = getMyUserId();

      // ===== Редактирование =====
      if (isEdit) {
        const nextText = String((cmd as any)?.message ?? '').trim();
        const editedAt = (cmd as any)?.edited_at ?? null;
        if (nextText !== '') {
          if (uid) void patchOfflineChatMessageById(uid, kind, targetId, messageId, { message: nextText, edited_at: editedAt } as any);
          const memKey = `${kind}:${targetId}`;
          try {
            const cached = chatMemRef.current.get(memKey);
            const prevMsgs = Array.isArray((cached as any)?.messages) ? ((cached as any).messages as any[]) : [];
            if (prevMsgs.length) {
              const nextMsgs = prevMsgs.map((m: any) =>
                Number(m?.id || 0) === messageId ? { ...(m as any), message: nextText, edited_at: editedAt } : m
              );
              chatMemRef.current.set(memKey, { ...(cached as any), messages: nextMsgs as any });
              if (uid) schedulePersistChatMem(uid);
            }
          } catch {}
          const openRid = Number(selectedRoomIdRef.current || 0) || 0;
          const openDm = Number(selectedUserIdRef.current || 0) || 0;
          const isOpen = kind === 'room' ? openRid === targetId : openDm === targetId && openRid === 0;
          if (isOpen) {
          const chatKey = kind === 'room' ? `room:${targetId}` : `dm:${targetId}`;
          chatMessagesStore.getState().patchMessage(chatKey, messageId, { message: nextText, edited_at: editedAt } as any);
          }
        }
        return;
      }

      // ===== Реакции (обновление существующего сообщения) =====
      if (isReaction) {
        const reactionsRaw = (cmd as any)?.reactions;
        const reactions = Array.isArray(reactionsRaw) ? (reactionsRaw as any[]) : [];

        if (uid) void patchOfflineChatMessageById(uid, kind, targetId, messageId, { reactions } as any);

        // Комментарий на русском: обновляем ChatMap, чтобы реакция не “терялась” при быстром открытии чата.
        const memKey = `${kind}:${targetId}`;
        try {
          const cached = chatMemRef.current.get(memKey);
          const prevMsgs = Array.isArray((cached as any)?.messages) ? ((cached as any).messages as any[]) : [];
          if (prevMsgs.length) {
            const nextMsgs = prevMsgs.map((m: any) => (Number(m?.id || 0) === messageId ? { ...(m as any), reactions } : m));
            chatMemRef.current.set(memKey, { ...(cached as any), messages: nextMsgs as any });
            if (uid) schedulePersistChatMem(uid);
          }
        } catch {}

        const openRid = Number(selectedRoomIdRef.current || 0) || 0;
        const openDm = Number(selectedUserIdRef.current || 0) || 0;
        const isOpen = kind === 'room' ? openRid === targetId : openDm === targetId && openRid === 0;
        if (isOpen) {
          const chatKey = kind === 'room' ? `room:${targetId}` : `dm:${targetId}`;
          chatMessagesStore.getState().patchMessage(chatKey, messageId, { reactions } as any);
        }
        return;
      }

      // Комментарий на русском: delete-for-all — soft-delete (оставляем сообщение как “[Удалено]”).
      // Зачем: удаление синхронизируется как изменение сообщения (без исчезновения id из истории).
      const forAll = !!(cmd as any)?.for_all;
      if (forAll) {
        if (uid) void patchOfflineChatMessageById(uid, kind, targetId, messageId, { message: '[Удалено]', attachments: [], voice_url: null, voice_mime: null } as any);

        const memKey = `${kind}:${targetId}`;
        try {
          const cached = chatMemRef.current.get(memKey);
          const prevMsgs = Array.isArray((cached as any)?.messages) ? ((cached as any).messages as any[]) : [];
          if (prevMsgs.length) {
            const nextMsgs = prevMsgs.map((m: any) =>
              Number(m?.id || 0) === messageId ? { ...(m as any), message: '[Удалено]', attachments: [], voice_url: null, voice_mime: null } : m
            );
            chatMemRef.current.set(memKey, { ...(cached as any), messages: nextMsgs as any });
            if (uid) schedulePersistChatMem(uid);
          }
        } catch {}

        const openRid = Number(selectedRoomIdRef.current || 0) || 0;
        const openDm = Number(selectedUserIdRef.current || 0) || 0;
        const isOpen = kind === 'room' ? openRid === targetId : openDm === targetId && openRid === 0;
        if (isOpen) {
          const chatKey = kind === 'room' ? `room:${targetId}` : `dm:${targetId}`;
          chatMessagesStore.getState().patchMessage(
            chatKey,
            messageId,
            { message: '[Удалено]', attachments: [], voice_url: null, voice_mime: null } as any
          );
          if (kind === 'room') loadRoomsThrottled();
          else loadConversationsThrottled();
        }
        return;
      }

      if (uid) void deleteOfflineChatMessage(uid, kind, targetId, messageId);

      // Комментарий на русском: чистим ChatMap (иначе удалённое сообщение может “воскреснуть” при быстром открытии чата).
      const memKey = `${kind}:${targetId}`;
      try {
        const cached = chatMemRef.current.get(memKey);
        const prevMsgs = Array.isArray((cached as any)?.messages) ? ((cached as any).messages as any[]) : [];
        if (prevMsgs.length) {
          const nextMsgs = prevMsgs.filter((m: any) => Number(m?.id || 0) !== messageId);
          if (nextMsgs.length) {
            const maxServerId = nextMsgs.reduce((mx: number, m: any) => {
              const id = Number(m?.id || 0) || 0;
              return id > 0 && id < 1_000_000_000_000 && id > mx ? id : mx;
            }, 0);
            chatMemRef.current.set(memKey, { ...(cached as any), messages: nextMsgs as any, lastId: maxServerId || (cached as any)?.lastId || 0 });
          } else {
            chatMemRef.current.delete(memKey);
          }
          if (uid) schedulePersistChatMem(uid);
        }
      } catch {}

      const openRid = Number(selectedRoomIdRef.current || 0) || 0;
      const openDm = Number(selectedUserIdRef.current || 0) || 0;
      const isOpen = kind === 'room' ? openRid === targetId : openDm === targetId && openRid === 0;

      const updateListsFromTail = (tail: any | null) => {
        if (kind === 'room') {
          setRooms((prev) =>
            prev.map((c) => {
              const rid = Number(c?.room?.id || 0) || 0;
              if (rid !== targetId) return c;
              const lastId = Number((c as any)?.last_message?.id || 0) || 0;
              if (lastId !== messageId) return c;
              return {
                ...c,
                last_message: tail
                  ? { id: Number(tail.id || 0) || 0, from_user: tail.from_user, message: String(tail.message || ''), created_at: String(tail.created_at || '') }
                  : null,
              } as any;
            })
          );
          return;
        }

        setConversations((prev) => {
          const out: any[] = [];
          for (const c of prev as any[]) {
            const pid = Number(c?.user?.id || 0) || 0;
            if (pid !== targetId) {
              out.push(c);
              continue;
            }
            const lastId = Number(c?.last_message?.id || 0) || 0;
            if (lastId !== messageId) {
              out.push(c);
              continue;
            }
            if (!tail) continue; // Комментарий на русском: если хвоста нет — диалог “исчезнет” как на сервере.
            out.push({
              ...c,
              last_message: {
                id: Number(tail.id || 0) || 0,
                from_user: { id: Number(tail?.from_user?.id || 0) || 0 },
                to_user: { id: Number(tail?.to_user?.id || 0) || 0 },
                message: String(tail.message || ''),
                created_at: String(tail.created_at || ''),
                is_read: !!tail.is_read,
              },
            });
          }
          return out as any;
        });
      };

      if (isOpen) {
        const chatKey = kind === 'room' ? `room:${targetId}` : `dm:${targetId}`;
        const rm: any = getChatMessageById(chatKey, messageId) || null;
        try {
          const vu = typeof rm?.voice_url === 'string' ? String(rm.voice_url) : '';
          const raw = String(rm?.message || '');
          const eff = vu || raw;
          const isV = !!vu || (typeof rm?.type === 'string' && String(rm.type) === 'voice_message') || eff.includes('/chat_voice/');
          if (isV && eff && (eff.startsWith('/') || /^https?:\/\//.test(eff))) {
            const abs = resolveVoiceUrl(eff);
            void deleteCachedVoice(abs);
          }
        } catch {}
        chatMessagesStore.getState().removeMessage(chatKey, messageId);
        const tail = getChatMessagesTail(chatKey, 1)[0] || null;
        updateListsFromTail(tail);
        return;
      }

      // Комментарий на русском: чат не открыт — обновляем только списки по ChatMap-хвосту (если он есть).
      try {
        const tail = (chatMemRef.current.get(memKey) as any)?.messages?.slice?.(-1)?.[0] || null;
        updateListsFromTail(tail);
      } catch {}
    },
    [deleteOfflineChatMessage, getMyUserId]
  );

  const applyChatSettings = useCallback((s: any) => {
    setReadReceiptsEnabled(!!s?.read_receipts_enabled);
    setLinkPreviewsEnabled(!!s?.link_previews_enabled);
    setCompactMode(!!s?.compact_mode);
    const fs = Number(s?.font_scale ?? 100);
    // Комментарий на русском: даём размеру текста опуститься до 8px (≈57%) и выше 140% без "залипания".
    setFontScale(Number.isFinite(fs) ? Math.max(50, Math.min(200, fs)) : 100);
    const md = String(s?.message_density ?? 'normal') as MessageDensity;
    setMessageDensity(md === 'compact' || md === 'comfortable' ? md : 'normal');
    if (s && Object.prototype.hasOwnProperty.call(s, 'message_stacking_enabled')) {
      setMessageStackingEnabled(!!s?.message_stacking_enabled);
    }
    // Комментарий на русском: не затираем тему, если сервер не вернул поле (нет миграции).
    if (s && Object.prototype.hasOwnProperty.call(s, 'message_theme')) {
      const themeKey = String((s as any)?.message_theme ?? 'custom').toLowerCase();
      setMessageTheme(/^[a-z0-9_-]{1,32}$/.test(themeKey) ? themeKey : 'custom');
    }

    const bt = Number(s?.bubble_transparency ?? 82);
    setBubbleTransparency(Number.isFinite(bt) ? Math.max(0, Math.min(100, Math.round(bt))) : 82);
    const bb = Number(s?.bubble_blur ?? 90);
    setBubbleBlur(Number.isFinite(bb) ? Math.max(0, Math.min(100, Math.round(bb))) : 90);
    const bc = String(s?.bubble_color ?? '#7dd3fc');
    setBubbleColor(/^#[0-9a-fA-F]{6}$/.test(bc) ? bc.toLowerCase() : '#7dd3fc');
    // Комментарий на русском: не затираем override-цвета, если сервер не вернул поля (нет миграции).
    if (s && Object.prototype.hasOwnProperty.call(s, 'bubble_color_me')) {
      const bcm = String((s as any)?.bubble_color_me ?? '');
      setBubbleColorMe(/^#[0-9a-fA-F]{6}$/.test(bcm) ? bcm.toLowerCase() : '');
    }
    if (s && Object.prototype.hasOwnProperty.call(s, 'bubble_color_peer')) {
      const bcp = String((s as any)?.bubble_color_peer ?? '');
      setBubbleColorPeer(/^#[0-9a-fA-F]{6}$/.test(bcp) ? bcp.toLowerCase() : '');
    }
    const bs = Number(s?.bubble_shadow ?? 35);
    setBubbleShadow(Number.isFinite(bs) ? Math.max(0, Math.min(100, Math.round(bs))) : 35);
    const bsc = String(s?.bubble_shadow_color ?? '#0b1220');
    setBubbleShadowColor(/^#[0-9a-fA-F]{6}$/.test(bsc) ? bsc.toLowerCase() : '#0b1220');
    // Комментарий на русском: поля из миграции 039 — обновляем только если сервер вернул их.
    if (typeof s?.bubble_shadow_enabled === 'boolean') setBubbleShadowEnabled(s.bubble_shadow_enabled);
    if (typeof s?.bubble_glow_enabled === 'boolean') setBubbleGlowEnabled(s.bubble_glow_enabled);
    if (s?.bubble_glow_color != null) {
      const bgc = String(s.bubble_glow_color);
      setBubbleGlowColor(/^#[0-9a-fA-F]{6}$/.test(bgc) ? bgc.toLowerCase() : '#ffffff');
    }
    const bgx = Number(s?.bubble_glow_x ?? 22);
    setBubbleGlowX(Number.isFinite(bgx) ? Math.max(0, Math.min(100, Math.round(bgx))) : 22);
    const bgy = Number(s?.bubble_glow_y ?? 18);
    setBubbleGlowY(Number.isFinite(bgy) ? Math.max(0, Math.min(100, Math.round(bgy))) : 18);
    // Комментарий на русском: если миграция 039 не применена, сервер не вернёт эти поля — не сбрасываем.
    if (typeof s?.bubble_glow_gyro === 'boolean') setBubbleGlowGyro(s.bubble_glow_gyro);
    if (s?.bubble_glow_gyro_intensity != null) {
      const gyroInt = Number(s.bubble_glow_gyro_intensity);
      setBubbleGlowGyroIntensity(Number.isFinite(gyroInt) ? Math.max(1, Math.min(5, Math.round(gyroInt))) : 1);
    }
    const btc = String(s?.bubble_text_color ?? '#ffffff');
    setBubbleTextColor(/^#[0-9a-fA-F]{6}$/.test(btc) ? btc.toLowerCase() : '#ffffff');
    const mfRaw = String(s?.message_font ?? 'system').toLowerCase();
    const mf = mfRaw === 'inter' || mfRaw === 'serif' || mfRaw === 'mono' ? mfRaw : 'system';
    setMessageFont(mf as any);

    const c = String(s?.avatar_fx_color ?? '#22c55e');
    setAvatarFxColor(/^#[0-9a-fA-F]{6}$/.test(c) ? c.toLowerCase() : '#22c55e');
    const th = Number(s?.avatar_fx_thickness ?? 2);
    setAvatarFxThickness(Number.isFinite(th) ? Math.max(0, Math.min(10, Math.round(th))) : 2);
    const gl = Number(s?.avatar_fx_glow ?? 0);
    setAvatarFxGlow(Number.isFinite(gl) ? Math.max(0, Math.min(100, Math.round(gl))) : 0);
    // цвет офлайн-обрамления.
    const offC = String(s?.avatar_fx_offline_color ?? '#64748b');
    setAvatarFxOfflineColor(/^#[0-9a-fA-F]{6}$/.test(offC) ? offC.toLowerCase() : '#64748b');

    // Комментарий на русском: обновляем версию настроек для синхронизации между устройствами.
    const ver = Number(s?.settings_version || 0) || 0;
    if (ver > 0) settingsVersionRef.current = ver;

    setChatSettingsLoaded(true);
  }, []);

  const loadChatSettings = useCallback(async () => {
    const uid = getMyUserId();
    // Комментарий на русском: сначала показываем кэш (мгновенно), потом проверяем сервер.
    if (uid > 0) {
      try {
        const cached = await chatKvGet<any>(offlineSettingsKey(uid));
        if (cached?.value && typeof cached.value === 'object') {
          applyChatSettings(cached.value);
        }
      } catch {}
    }
    // Комментарий на русском: background sync — загружаем с сервера и обновляем кэш.
    try {
      const r = await apiCall('/api/chat/settings/');
      if (!r.ok) return;
      const data = await r.json();
      applyChatSettings(data);
      // Комментарий на русском: сохраняем в кэш для следующего открытия.
      if (uid > 0) {
        void chatKvSet(offlineSettingsKey(uid), data);
      }
    } catch {
      // ignore
    }
  }, [apiCall, applyChatSettings, getMyUserId]);

  const chatSettingsPatchIdRef = useRef(0);
  const updateChatSettings = useCallback(
    async (patch: Partial<MessengerSettings>, opts?: { silent?: boolean }) => {
      try {
        const reqId = ++chatSettingsPatchIdRef.current;
        const r = await apiCall('/api/chat/settings/', { method: 'PATCH', body: JSON.stringify(patch) });
        if (!r.ok) {
          if (!opts?.silent) {
            try {
              const data = (await r.json()) as any;
              const msg = String(data?.error || `Не удалось сохранить (HTTP ${r.status})`);
              setSettingsToast(msg);
              setTimeout(() => setSettingsToast(null), 2600);
            } catch {
              setSettingsToast(`Не удалось сохранить (HTTP ${r.status})`);
              setTimeout(() => setSettingsToast(null), 2600);
            }
          }
          return false;
        }
        const data = await r.json();
        // Комментарий на русском: защита от гонок — применяем только самый свежий ответ.
        if (reqId === chatSettingsPatchIdRef.current) {
          applyChatSettings(data);
          // Комментарий на русском: обновляем кэш после успешного PATCH.
          const uid = getMyUserId();
          if (uid > 0) void chatKvSet(offlineSettingsKey(uid), data);
        }
        return true;
      } catch {
        if (!opts?.silent) {
          setSettingsToast('Не удалось сохранить настройки');
          setTimeout(() => setSettingsToast(null), 2600);
        }
        return false;
      }
    },
    [apiCall, applyChatSettings, getMyUserId, setSettingsToast]
  );

  const calcStorageStats = useCallback(async () => {
    if (typeof window === 'undefined') return;
    setStorageLoading(true);
    setStorageError(null);
    try {
      const est = (await (navigator.storage?.estimate?.() ?? Promise.resolve(undefined))) as StorageEstimate | undefined;
      const quota = est?.quota || 0;
      const used = est?.usage || 0;
      const available = Math.max(0, quota - used);

      const buckets: Record<StorageCategoryId, number> = { photos: 0, audio: 0, video: 0, files: 0, other: 0 };
      const addSize = (url: string, ct: string, size: number) => {
        const kind = detectStorageKind(url, ct);
        buckets[kind] += size;
      };

      if (typeof caches !== 'undefined') {
        const cacheKeys = await caches.keys();
        for (const ck of cacheKeys) {
          const cache = await caches.open(ck);
          const reqs = await cache.keys();
          for (const req of reqs) {
            try {
              const res = await cache.match(req);
              if (!res) continue;
              const ct = res.headers.get('content-type') || '';
              let size = 0;
              try {
                size = (await res.clone().arrayBuffer()).byteLength;
              } catch {
                // ignore
              }
              addSize(req.url || '', ct, size);
            } catch {
              // ignore
            }
          }
        }
      }

      const roughStorageSize = (s: Storage | null | undefined) => {
        if (!s) return 0;
        let bytes = 0;
        for (let i = 0; i < s.length; i++) {
          const k = s.key(i) || '';
          try {
            const v = s.getItem(k) || '';
            bytes += (k.length + v.length) * 2; // UTF-16
          } catch {
            // ignore
          }
        }
        return bytes;
      };

      try {
        buckets.files += roughStorageSize(window.localStorage);
        buckets.files += roughStorageSize(window.sessionStorage);
      } catch {
        // ignore
      }

      // Комментарий на русском: добавляем размер постоянного IndexedDB кэша фотографий.
      try {
        const photoCacheSize = await getPhotoCacheSize();
        buckets.photos += photoCacheSize;
      } catch {
        // ignore
      }

      const categories = (Object.entries(buckets) as Array<[StorageCategoryId, number]>)
        .map(([id, size]) => ({
          id,
          size,
          label:
            id === 'photos'
              ? 'Фото'
              : id === 'audio'
                ? 'Аудио'
                : id === 'video'
                  ? 'Видео'
                  : id === 'files'
                    ? 'Файлы'
                    : 'Общие',
        }))
        .sort((a, b) => b.size - a.size);

      setStorageStats({ used, quota, available, categories });
    } catch {
      setStorageError('Не удалось получить объём');
    } finally {
      setStorageLoading(false);
    }
  }, []);

  const handlePurgeStorage = useCallback(async () => {
    if (cachePurging) return;

    const isIOS =
      typeof navigator !== 'undefined' &&
      (/iPad|iPhone|iPod/.test(navigator.userAgent || '') ||
        ((navigator.userAgent || '').includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document));

    if (!isIOS) {
      // Комментарий на русском: на десктопе confirm работает стабильно.
      if (!confirm('Очистить кэш мессенджера? Авторизация сохранится.')) return;
    } else {
      // Комментарий на русском: iOS/Safari/PWA — делаем подтверждение вторым нажатием.
      if (!cachePurgeArmed) {
        setCachePurgeArmed(true);
        setSettingsToast('Нажмите ещё раз: очистить кэш (авторизация сохранится)');
        if (cachePurgeArmTRef.current != null) window.clearTimeout(cachePurgeArmTRef.current);
        cachePurgeArmTRef.current = window.setTimeout(() => {
          cachePurgeArmTRef.current = null;
          setCachePurgeArmed(false);
        }, 2200);
        return;
      }
      setCachePurgeArmed(false);
      if (cachePurgeArmTRef.current != null) window.clearTimeout(cachePurgeArmTRef.current);
      cachePurgeArmTRef.current = null;
    }

    setCachePurging(true);
    setStorageError(null);
    try {
      await purgeVoiceCache();
      await purgeChatPhotoCache();
      await clearAllPhotoCache(); // Комментарий на русском: очищаем постоянный IndexedDB кэш фотографий.
      await purgeSiteCache();
      // Комментарий на русском: очищаем офлайн-данные чатов и очередь (авторизация сохранится).
      const uid = getMyUserId();
      if (uid) await chatKvClearUser(uid);
      chatMemRef.current.clear();
      pendingMessagesRef.current.clear();
      setPendingCount(0);
      setConversations([]);
      setRooms([]);
      chatMessagesStore.setState({ chats: {} });
      setSettingsToast('Кэш очищен');
      setTimeout(() => setSettingsToast(null), 1800);
      void calcStorageStats();
    } catch {
      setStorageError('Не удалось очистить кэш');
    } finally {
      setCachePurging(false);
    }
  }, [cachePurging, cachePurgeArmed, calcStorageStats]);

  // ползунки эффектов не должны спамить PATCH на каждый пиксель.
  const saveAvatarFxDebounced = useMemo(
    () =>
      debounce((next: { color: string; thickness: number; glow: number; offlineColor: string }) => {
        void updateChatSettings({
          avatar_fx_color: next.color,
          avatar_fx_thickness: next.thickness,
          avatar_fx_glow: next.glow,
          avatar_fx_offline_color: next.offlineColor,
        } as any);
      }, 350),
    [updateChatSettings]
  );

  // Комментарий на русском: ползунки "жидкого стекла" тоже не должны спамить PATCH на каждый пиксель. blur убран для оптимизации.
  const saveBubbleGlassDebounced = useMemo(
    () =>
      debounce((next: {
        transparency: number;
        color: string;
        colorMe?: string;
        colorPeer?: string;
        shadow: number;
        shadowColor: string;
        shadowEnabled: boolean;
        glowEnabled: boolean;
        glowColor: string;
        glowX: number;
        glowY: number;
      }) => {
        void updateChatSettings({
          bubble_transparency: next.transparency,
          bubble_color: next.color,
          ...(typeof next.colorMe === 'string' ? { bubble_color_me: next.colorMe } : {}),
          ...(typeof next.colorPeer === 'string' ? { bubble_color_peer: next.colorPeer } : {}),
          bubble_shadow: next.shadow,
          bubble_shadow_color: next.shadowColor,
          bubble_shadow_enabled: next.shadowEnabled,
          bubble_glow_enabled: next.glowEnabled,
          bubble_glow_color: next.glowColor,
          bubble_glow_x: next.glowX,
          bubble_glow_y: next.glowY,
        } as any);
      }, 350),
    [updateChatSettings]
  );

  // Комментарий на русском: настройки текста (цвет/шрифт) тоже сохраняем с дебаунсом, чтобы input[type=color] не спамил API.
  const saveMessageTextStyleDebounced = useMemo(
    () =>
      debounce((next: { textColor: string; font: 'system' | 'inter' | 'serif' | 'mono' }) => {
        void updateChatSettings({
          bubble_text_color: next.textColor,
          message_font: next.font,
        } as any);
      }, 350),
    [updateChatSettings]
  );

  const saveMessageThemeDebounced = useMemo(
    () =>
      debounce((theme: string) => {
        const t = String(theme || 'custom').toLowerCase();
        void updateChatSettings({ message_theme: /^[a-z0-9_-]{1,32}$/.test(t) ? t : 'custom' } as any, { silent: true });
      }, 350),
    [updateChatSettings]
  );

  const markThemeCustom = useCallback(() => {
    if (messageTheme === 'custom') return;
    setMessageTheme('custom');
    saveMessageThemeDebounced('custom');
  }, [messageTheme, saveMessageThemeDebounced]);

  const MESSAGE_THEMES = useMemo(
    () =>
      ([
        { id: 'black', label: 'Black', base: '#0b1220', me: '#111827', peer: '#0f172a', text: '#ffffff', transp: 88, glow: false, glowColor: '#ffffff' },
        { id: 'white', label: 'White', base: '#f8fafc', me: '#ffffff', peer: '#e2e8f0', text: '#0b1220', transp: 60, glow: false, glowColor: '#ffffff' },
        { id: 'noir_neon', label: 'Noir Neon', base: '#0b1220', me: '#22c55e', peer: '#f472b6', text: '#ffffff', transp: 78, glow: true, glowColor: '#22d3ee' },
        { id: 'hi_contrast', label: 'Hi Contrast', base: '#111827', me: '#facc15', peer: '#38bdf8', text: '#ffffff', transp: 76, glow: true, glowColor: '#fde047' },
        { id: 'terminal', label: 'Terminal', base: '#0b1220', me: '#22c55e', peer: '#a3e635', text: '#e5e7eb', transp: 80, glow: true, glowColor: '#86efac' },
        { id: 'aurora', label: 'Aurora', base: '#22d3ee', me: '#60a5fa', peer: '#34d399', text: '#0b1220', transp: 78, glow: true, glowColor: '#a7f3d0' },
        { id: 'midnight', label: 'Midnight', base: '#60a5fa', me: '#a78bfa', peer: '#38bdf8', text: '#ffffff', transp: 86, glow: true, glowColor: '#93c5fd' },
        { id: 'sakura', label: 'Sakura', base: '#fb7185', me: '#fda4af', peer: '#c4b5fd', text: '#0b1220', transp: 76, glow: true, glowColor: '#fbcfe8' },
        { id: 'sunset', label: 'Sunset', base: '#fb923c', me: '#f97316', peer: '#fb7185', text: '#0b1220', transp: 74, glow: true, glowColor: '#fed7aa' },
        { id: 'emerald', label: 'Emerald', base: '#34d399', me: '#22c55e', peer: '#2dd4bf', text: '#0b1220', transp: 78, glow: true, glowColor: '#86efac' },
        { id: 'violet', label: 'Violet', base: '#a78bfa', me: '#8b5cf6', peer: '#f472b6', text: '#ffffff', transp: 84, glow: true, glowColor: '#ddd6fe' },
        { id: 'ocean', label: 'Ocean', base: '#38bdf8', me: '#0ea5e9', peer: '#22d3ee', text: '#0b1220', transp: 80, glow: true, glowColor: '#bae6fd' },
        { id: 'graphite', label: 'Graphite', base: '#94a3b8', me: '#64748b', peer: '#a1a1aa', text: '#0b1220', transp: 82, glow: false, glowColor: '#ffffff' },
        { id: 'cyber', label: 'Cyber', base: '#22c55e', me: '#a3e635', peer: '#f472b6', text: '#0b1220', transp: 70, glow: true, glowColor: '#86efac' },
        { id: 'solar', label: 'Solar', base: '#facc15', me: '#fde047', peer: '#fb923c', text: '#0b1220', transp: 72, glow: true, glowColor: '#fef08a' },
      ] as const),
    []
  );

  const applyMessageThemePreset = useCallback(
    async (id: string) => {
      const theme = MESSAGE_THEMES.find((t) => t.id === id);
      if (!theme) return;
      setMessageTheme(theme.id);
      setBubbleTransparency(theme.transp);
      // Комментарий на русском: bubble_color — это "мои" (то, что видят другие в комнатах).
      setBubbleColor(theme.me);
      setBubbleColorMe(theme.me);
      setBubbleColorPeer(theme.peer);
      setBubbleGlowEnabled(!!theme.glow);
      setBubbleGlowColor(theme.glowColor);
      setBubbleTextColor(theme.text);
      setBubbleShadowEnabled(true);

      const ok = await updateChatSettings({
        message_theme: theme.id,
        bubble_transparency: theme.transp,
        bubble_color: theme.me,
        bubble_color_me: theme.me,
        bubble_color_peer: theme.peer,
        bubble_glow_enabled: !!theme.glow,
        bubble_glow_color: theme.glowColor,
        bubble_text_color: theme.text,
        bubble_shadow_enabled: true,
      } as any, { silent: true });
      if (!ok) {
        // Комментарий на русском: не откатываем UI — иначе выглядит как "сброс". Сообщение об ошибке покажет updateChatSettings.
      }
    },
    [
      MESSAGE_THEMES,
      messageTheme,
      updateChatSettings,
    ]
  );

  const loadBlockedUsers = useCallback(async () => {
    try {
      const r = await apiCall('/api/chat/friends/blocked/');
      if (!r.ok) return;
      const data = (await r.json()) as any;
      if (Array.isArray(data)) setBlockedUsers(data as any);
    } catch {
      // ignore
    }
  }, [apiCall]);

  const loadSessions = useCallback(async () => {
    try {
      const refresh = getRefreshToken() || '';
      const r = await apiCall('/api/auth/sessions/', {
        headers: refresh ? { 'X-Refresh-Token': refresh } : undefined,
      });
      if (!r.ok) return;
      const data = (await r.json()) as any;
      if (Array.isArray(data)) setSessions(data as any);
    } catch {
      // ignore
    }
  }, [apiCall]);

  const loadMyProfile = useCallback(async () => {
    try {
      const r = await apiCall('/api/auth/me');
      if (!r.ok) return;
      const u = (await r.json()) as any;
      setMyProfile(u);
      if (!myNicknameDirtyRef.current) setMyNicknameDraft(String(u?.nickname || u?.username || ''));
      if (!myProfileDirtyRef.current) {
        setMyProfileForm({
          first_name: String(u?.first_name || ''),
          last_name: String(u?.last_name || ''),
          middle_name: String(u?.middle_name || ''),
          bio: String(u?.bio || ''),
          phone: String(u?.phone || ''),
          position: String(u?.position || ''),
          organization: String(u?.organization || ''),
          website: String(u?.website || ''),
          github_username: String(u?.github_username || ''),
          telegram_username: String(u?.telegram_username || ''),
          work_time: String(u?.work_time || ''),
        });
      }
      try {
        setStoredUser(u);
      } catch {}
      if (u?.id) {
        meRef.current = {
          id: (u.id as number) | 0,
          username: String(u.username || ''),
          nickname: String(u.nickname || ''),
          display_name: String(u.display_name || u.username || 'Вы'),
          avatar_url: u.avatar_url ?? null,
        } as any;
      }
    } catch {
      // ignore
    }
  }, [apiCall]);

  const normalizeNicknameDraft = useCallback((raw: string) => {
    // Комментарий на русском: UI поддерживает ввод с "@", но на API отправляем без "@"
    const s = String(raw || '').trim();
    return (s.startsWith('@') ? s.slice(1) : s).trim();
  }, []);

  const saveMyNickname = useCallback(async () => {
    const nick = normalizeNicknameDraft(myNicknameDraft);
    if (!nick) {
      setSettingsToast('Введите @username');
      setTimeout(() => setSettingsToast(null), 2500);
      return;
    }
    setProfileBusy(true);
    try {
      const resp = await apiClient.updateProfile({ nickname: nick } as any);
      const u = (resp as any)?.user || null;
      if (u) {
        setMyProfile(u);
        try {
          setStoredUser(u);
        } catch {}
        myNicknameDirtyRef.current = false;
        setMyNicknameDraft(String(u?.nickname || u?.username || ''));
        if (u?.id) {
          meRef.current = {
            id: (u.id as number) | 0,
            username: String(u.username || ''),
            nickname: String(u.nickname || ''),
            display_name: String(u.display_name || u.username || 'Вы'),
            avatar_url: u.avatar_url ?? null,
          } as any;
        }
      }
      setSettingsToast('Публичный @username обновлён');
      setTimeout(() => setSettingsToast(null), 1800);
    } catch (e: any) {
      const status = (e && typeof e === 'object' ? (e as any).status : undefined) as number | undefined;
      const details = (e && typeof e === 'object' ? (e as any).details : undefined) as any;
      const nextAt = details?.next_available_at ? String(details.next_available_at) : '';
      if (status === 429 && nextAt) {
        setSettingsToast(`Можно менять @username раз в сутки. Следующая попытка: ${new Date(nextAt).toLocaleString('ru-RU')}`);
        setTimeout(() => setSettingsToast(null), 7000);
      } else {
        setSettingsToast(e?.message || 'Не удалось обновить @username');
        setTimeout(() => setSettingsToast(null), 2500);
      }
    } finally {
      setProfileBusy(false);
    }
  }, [myNicknameDraft, normalizeNicknameDraft]);

  const saveMyProfileForm = useCallback(async () => {
    setProfileBusy(true);
    try {
      const resp = await apiClient.updateProfile(myProfileForm as any);
      const u = (resp as any)?.user || null;
      if (u) {
        setMyProfile(u);
        try {
          setStoredUser(u);
        } catch {}
        myProfileDirtyRef.current = false;
        if (u?.id) {
          meRef.current = {
            id: (u.id as number) | 0,
            username: String(u.username || ''),
            nickname: String(u.nickname || ''),
            display_name: String(u.display_name || u.username || 'Вы'),
            avatar_url: u.avatar_url ?? null,
          } as any;
        }
      }
      setSettingsToast('Профиль обновлён');
      setTimeout(() => setSettingsToast(null), 1800);
    } catch (e: any) {
      setSettingsToast(e?.message || 'Не удалось обновить профиль');
      setTimeout(() => setSettingsToast(null), 2500);
    } finally {
      setProfileBusy(false);
    }
  }, [myProfileForm]);

  const resetMyProfileForm = useCallback(() => {
    myProfileDirtyRef.current = false;
    const u = myProfile || {};
    setMyProfileForm({
      first_name: String(u?.first_name || ''),
      last_name: String(u?.last_name || ''),
      middle_name: String(u?.middle_name || ''),
      bio: String(u?.bio || ''),
      phone: String(u?.phone || ''),
      position: String(u?.position || ''),
      organization: String(u?.organization || ''),
      website: String(u?.website || ''),
      github_username: String(u?.github_username || ''),
      telegram_username: String(u?.telegram_username || ''),
      work_time: String(u?.work_time || ''),
    });
  }, [myProfile]);

  const uploadMyAvatar = useCallback(async (file: File) => {
    setAvatarBusy(true);
    try {
      const resp = await apiClient.uploadAvatar(file);
      const u = (resp as any)?.user || null;
      if (u) {
        setMyProfile(u);
        setStoredUser(u);
        if (u?.id) {
          meRef.current = {
            id: (u.id as number) | 0,
            username: String(u.username || ''),
            nickname: String(u.nickname || ''),
            display_name: String(u.display_name || u.username || 'Вы'),
            avatar_url: u.avatar_url ?? null,
          } as any;
        }
      } else {
        void loadMyProfile();
      }
      setSettingsToast('Аватар обновлён');
      setTimeout(() => setSettingsToast(null), 1800);
    } catch {
      setSettingsToast('Не удалось загрузить аватар');
      setTimeout(() => setSettingsToast(null), 2500);
    } finally {
      setAvatarBusy(false);
    }
  }, [loadMyProfile]);

  const handleMyAvatarSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    try {
      e.currentTarget.value = '';
    } catch {}
    if (!file) return;

    // Комментарий на русском: фото для обрезки можно брать до 10MB, дальше — ошибка.
    if (file.size > 10 * 1024 * 1024) {
      setSettingsToast('Размер файла не должен превышать 10MB');
      setTimeout(() => setSettingsToast(null), 2500);
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setSettingsToast('Недопустимый тип файла. Разрешены: JPEG, PNG, GIF, WebP');
      setTimeout(() => setSettingsToast(null), 2500);
      return;
    }

    setMyAvatarCropFile(file);
  }, []);

  const deleteMyAvatar = useCallback(async () => {
    setAvatarBusy(true);
    try {
      await apiClient.deleteAvatar();
      void loadMyProfile();
      setSettingsToast('Аватар удалён');
      setTimeout(() => setSettingsToast(null), 1800);
    } catch {
      setSettingsToast('Не удалось удалить аватар');
      setTimeout(() => setSettingsToast(null), 2500);
    } finally {
      setAvatarBusy(false);
    }
  }, [loadMyProfile]);

  const logoutOtherDevices = useCallback(async () => {
    // Комментарий на русском: "выйти везде кроме этого устройства" = отозвать чужие refresh tokens, но сохранить текущий.
    try {
      const refresh = getRefreshToken() || '';
      if (!refresh) {
        setSettingsToast('Не удалось определить текущую сессию (нет refresh токена)');
        setTimeout(() => setSettingsToast(null), 2500);
        return;
      }
      const r = await apiCall('/api/auth/logout-others/', { method: 'POST', body: JSON.stringify({ refresh }) });
      if (!r.ok) {
        setSettingsToast('Не удалось выйти на других устройствах');
        setTimeout(() => setSettingsToast(null), 2500);
        return;
      }
      setSettingsToast('Вы вышли на других устройствах');
      setTimeout(() => setSettingsToast(null), 1800);
      void loadSessions();
    } catch {
      setSettingsToast('Не удалось выйти на других устройствах');
      setTimeout(() => setSettingsToast(null), 2500);
    }
  }, [apiCall, loadSessions]);

  const blockUser = useCallback(
    async (userId: number) => {
      if (!userId) return;
      try {
        const r = await apiCall(`/api/chat/friends/${userId}/block/`, { method: 'POST' });
        if (r.ok) void loadBlockedUsers();
      } catch {
        // ignore
      }
    },
    [apiCall, loadBlockedUsers]
  );

  const unblockUser = useCallback(
    async (userId: number) => {
      if (!userId) return;
      try {
        const r = await apiCall(`/api/chat/friends/${userId}/unblock/`, { method: 'DELETE' });
        if (r.ok) void loadBlockedUsers();
      } catch {
        // ignore
      }
    },
    [apiCall, loadBlockedUsers]
  );

  // Комментарий на русском: даём пользователю способ включить уведомления прямо внутри мессенджера (т.к. плавающая кнопка скрыта на /messages).
  const requestBrowserNotifications = useCallback(async () => {
    try {
      const status = await getWebPushStatus();
      if (!status.supported) {
        const msg = 'Push не поддерживается в этом браузере. На iPhone: добавьте сайт "На экран Домой".';
        setError(msg);
        setTimeout(() => setError(null), 3500);
        try {
          alert(msg);
        } catch {}
        return;
      }
      if (status.permission === 'denied') {
        const msg = 'Уведомления заблокированы в браузере. Разрешите их в настройках сайта.';
        setError(msg);
        setTimeout(() => setError(null), 3500);
        try {
          alert(msg);
        } catch {}
        return;
      }
      if (status.subscribed && status.permission === 'granted') {
        const r = await disableWebPush(API_URL);
        if (r.ok) {
          setPushEnabled(false);
          setError('Push отключены');
          setTimeout(() => setError(null), 1200);
        }
        return;
      }

      const r = await enableWebPush(API_URL);
      if (r.ok) {
        setPushEnabled(true);
        setError('Push включены');
        setTimeout(() => setError(null), 1200);
        return;
      }
      const msg = r.error === 'no_public_key'
        ? 'Push на сервере не настроен (нет VAPID ключей)'
        : r.error === 'no_service_worker'
          ? 'Service Worker недоступен (push невозможен)'
          : r.error === 'no_push_manager'
            ? 'Push не поддерживается на этом устройстве'
            : r.error === 'permission_denied'
              ? 'Нет разрешения на уведомления'
              : 'Не удалось включить push-уведомления';
      setError(msg);
      setTimeout(() => setError(null), 2500);
      try {
        alert(msg);
      } catch {}
    } catch {
      // ignore
    }
  }, []);

  // Пункт 3: heartbeat online (без WS)
  useEffect(() => {
    if (!isAuthed) return;
    void apiCall('/api/chat/status/online/', { method: 'POST', body: JSON.stringify({ is_online: true }) });
    const t = setInterval(() => void apiCall('/api/chat/status/online/', { method: 'POST', body: JSON.stringify({ is_online: true }) }), 25000);
    const onHide = () => void apiCall('/api/chat/status/online/', { method: 'POST', body: JSON.stringify({ is_online: false }) });
    window.addEventListener('pagehide', onHide);
    return () => {
      clearInterval(t);
      window.removeEventListener('pagehide', onHide);
      void apiCall('/api/chat/status/online/', { method: 'POST', body: JSON.stringify({ is_online: false }) });
    };
  }, [apiCall, isAuthed]);

  const pingTyping = useMemo(
    () =>
      throttle((toUserId: number) => {
        void apiCall('/api/chat/status/typing/', { method: 'POST', body: JSON.stringify({ to_user_id: toUserId }) });
      }, 1200),
    [apiCall]
  );

  const loadConversations = useCallback(async () => {
    const uid = getMyUserId();
    let hasOffline = false;
    if (uid) {
      try {
        const cached = await idbGetWithTimeout(chatKvGet<Conversation[]>(offlineConversationsKey(uid)), 300);
        if (cached?.value && Array.isArray(cached.value) && cached.value.length) {
          setConversations(cached.value);
          hasOffline = true;
          setLoadingChats(false);
        }
      } catch {
        // ignore
      }
    }
    try {
      const res = await apiCallWithTimeout('/api/chat/conversations/', {}, CHAT_LOAD_TIMEOUT_MS);
      if (res.status === 401) return;
      if (!res.ok) {
        let msg = `Не удалось загрузить чаты (HTTP ${res.status})`;
        try {
          const data = await res.json();
          if (data?.error) msg = String(data.error);
          if (data?.hint) msg = `${msg}. ${String(data.hint)}`;
            if (data?.details) msg = `${msg}. ${String(data.details)}`;
        } catch {
          // ignore
        }
        // Комментарий на русском: если есть офлайн-кэш и сервер временно недоступен — не блокируем пользователя ошибкой.
        const transient = res.status >= 500 || res.status === 0 || res.status === 408 || res.status === 429;
        if (!hasOffline || !transient) setLoadError(msg);
        return;
      }
      const data = await res.json();
      if (Array.isArray(data)) {
        setConversations(data);
        if (uid) void chatKvSet(offlineConversationsKey(uid), data);
        // Комментарий на русском: сохраняем хэши чатов для синхронизации.
        if (uid) {
          for (const conv of data) {
            const peerId = Number((conv as any)?.user?.id || 0) || 0;
            const hash = (conv as any)?.chat_hash || '';
            if (peerId > 0 && hash) {
              void chatKvSet(offlineChatHashKey(uid, 'dm', peerId), hash);
            }
          }
        }
      }
      setLoadError(null);
    } catch {
      if (!hasOffline) setLoadError('Не удалось загрузить чаты (ошибка сети)');
    } finally {
      setLoadingChats(false);
    }
  }, [apiCallWithTimeout, getMyUserId, idbGetWithTimeout]);

  // Комментарий на русском: префетчим “каркас” самых свежих чатов (текст + размеры вложений), чтобы открывались без пауз.
  const prefetchRef = useRef<{ uid: number; started: boolean }>({ uid: 0, started: false });
  useEffect(() => {
    if (!isAuthed) return;
    if (loadingChats) return;
    if (!Array.isArray(conversations) || conversations.length === 0) return;
    const uid = getMyUserId();
    if (!uid) return;

    if (prefetchRef.current.uid !== uid) prefetchRef.current = { uid, started: false };
    if (prefetchRef.current.started) return;
    prefetchRef.current.started = true;

    let cancelled = false;
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    void (async () => {
      const top = (conversations || []).slice(0, CHAT_PREFETCH_TOP_CHATS);
      for (let i = 0; i < top.length; i += 1) {
        if (cancelled) return;
        if (typeof document !== 'undefined' && document.hidden) {
          await sleep(900);
          continue;
        }
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

        const peerId = Number((top[i] as any)?.user?.id || 0) || 0;
        if (!peerId) continue;
        if (selectedUserIdRef.current === peerId) continue;

        const key = offlineDmKey(uid, peerId);
        const cached = await chatKvGet<DirectMessage[]>(key);
        const arr0 = cached?.value;
        if (Array.isArray(arr0) && arr0.length >= CHAT_PAGE_LIMIT) {
          await sleep(CHAT_PREFETCH_DELAY_MS);
          continue;
        }

        try {
          const qs = new URLSearchParams();
          qs.set('limit', String(CHAT_PAGE_LIMIT));
          const res = await apiCallWithTimeout(`/api/chat/messages/${peerId}/?${qs.toString()}`, {}, CHAT_LOAD_TIMEOUT_MS);
          if (!res.ok) {
            await sleep(CHAT_PREFETCH_DELAY_MS);
            continue;
          }
          const data = await res.json();
          // Комментарий на русском: также сохраняем фон чата в IndexedDB (чтобы офлайн открывался с тем же фоном).
          try {
            if (!Array.isArray(data)) {
              const wp = (data as any)?.wallpaper;
              const nextWp =
                wp && typeof wp === 'object' && typeof (wp as any)?.id === 'number' && typeof (wp as any)?.url === 'string'
                  ? { id: Number((wp as any).id || 0) || 0, url: String((wp as any).url || ''), blur: Number((wp as any).blur || 0) || 0 }
                  : null;
              void chatKvSet(offlineDmWallpaperKey(uid, peerId), nextWp && nextWp.id > 0 && nextWp.url ? nextWp : null);
            }
          } catch {
            // ignore
          }
          const raw: any[] = Array.isArray(data) ? data : (data?.messages || []);
          const normalized = raw.map((m) => m as DirectMessage).filter((m) => m && typeof m.id === 'number').reverse();
          if (!normalized.length) {
            await sleep(CHAT_PREFETCH_DELAY_MS);
            continue;
          }
          // Комментарий на русском: кладём только первые 40 (последние по времени), без “истории”, чтобы не грузить iOS памятью.
          void chatKvSet(key, normalized.slice(-CHAT_PAGE_LIMIT));
          // Комментарий на русском: держим небольшой RAM-кэш для мгновенного открытия без похода в IndexedDB.
          const maxServerId = normalized.reduce((mx, m: any) => {
            const id = Number(m?.id || 0) || 0;
            return id > 0 && id < 1_000_000_000_000 && id > mx ? id : mx;
          }, 0);
          const memKey = `dm:${peerId}`;
          try {
            if (chatMemRef.current.has(memKey)) chatMemRef.current.delete(memKey);
          } catch {}
          chatMemRef.current.set(memKey, { messages: normalized.slice(-CHAT_PAGE_LIMIT), lastId: maxServerId, hasMore: true });
          if (chatMemRef.current.size > CHAT_MAP_MAX_SIZE) {
            const first = chatMemRef.current.keys().next().value as string | undefined;
            if (first) chatMemRef.current.delete(first);
          }
          schedulePersistChatMem(uid);
        } catch {
          // ignore
        }

        await sleep(CHAT_PREFETCH_DELAY_MS);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiCallWithTimeout, conversations, getMyUserId, isAuthed, loadingChats]);

  // Комментарий на русском: префетчим комнаты (rooms) аналогично DM.
  const prefetchRoomsRef = useRef<{ uid: number; started: boolean }>({ uid: 0, started: false });
  useEffect(() => {
    if (!isAuthed) return;
    if (loadingRooms) return;
    if (!Array.isArray(rooms) || rooms.length === 0) return;
    const uid = getMyUserId();
    if (!uid) return;

    if (prefetchRoomsRef.current.uid !== uid) prefetchRoomsRef.current = { uid, started: false };
    if (prefetchRoomsRef.current.started) return;
    prefetchRoomsRef.current.started = true;

    let cancelled = false;
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    void (async () => {
      // Комментарий на русском: даём приоритет DM — ждём немного перед стартом.
      await sleep(800);
      const top = (rooms || []).slice(0, CHAT_PREFETCH_TOP_CHATS);
      for (let i = 0; i < top.length; i += 1) {
        if (cancelled) return;
        if (typeof document !== 'undefined' && document.hidden) {
          await sleep(900);
          continue;
        }
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

        const roomId = Number((top[i] as any)?.room?.id || 0) || 0;
        if (!roomId) continue;
        if (selectedRoomIdRef.current === roomId) continue;

        const key = offlineRoomKey(uid, roomId);
        const cached = await chatKvGet<DirectMessage[]>(key);
        const arr0 = cached?.value;
        if (Array.isArray(arr0) && arr0.length >= CHAT_PAGE_LIMIT) {
          await sleep(CHAT_PREFETCH_DELAY_MS);
          continue;
        }

        try {
          const qs = new URLSearchParams();
          qs.set('limit', String(CHAT_PAGE_LIMIT));
          const res = await apiCallWithTimeout(`/api/chat/rooms/${roomId}/messages/?${qs.toString()}`, {}, CHAT_LOAD_TIMEOUT_MS);
          if (!res.ok) {
            await sleep(CHAT_PREFETCH_DELAY_MS);
            continue;
          }
          const data = await res.json();
          // Комментарий на русском: сохраняем обои комнаты в IndexedDB.
          try {
            if (!Array.isArray(data)) {
              const wp = (data as any)?.wallpaper;
              const nextWp =
                wp && typeof wp === 'object' && typeof (wp as any)?.id === 'number' && typeof (wp as any)?.url === 'string'
                  ? { id: Number((wp as any).id || 0) || 0, url: String((wp as any).url || ''), blur: Number((wp as any).blur || 0) || 0 }
                  : null;
              void chatKvSet(offlineRoomWallpaperKey(uid, roomId), nextWp && nextWp.id > 0 && nextWp.url ? nextWp : null);
            }
          } catch {
            // ignore
          }
          const raw: any[] = Array.isArray(data) ? data : (data?.messages || []);
          const normalized = raw.map((m) => m as DirectMessage).filter((m) => m && typeof m.id === 'number').reverse();
          if (!normalized.length) {
            await sleep(CHAT_PREFETCH_DELAY_MS);
            continue;
          }
          void chatKvSet(key, normalized.slice(-CHAT_PAGE_LIMIT));
          // Комментарий на русском: RAM-кэш для мгновенного открытия.
          const maxServerId = normalized.reduce((mx, m: any) => {
            const id = Number(m?.id || 0) || 0;
            return id > 0 && id < 1_000_000_000_000 && id > mx ? id : mx;
          }, 0);
          const memKey = `room:${roomId}`;
          try {
            if (chatMemRef.current.has(memKey)) chatMemRef.current.delete(memKey);
          } catch {}
          chatMemRef.current.set(memKey, { messages: normalized.slice(-CHAT_PAGE_LIMIT), lastId: maxServerId, hasMore: true });
          if (chatMemRef.current.size > CHAT_MAP_MAX_SIZE) {
            const first = chatMemRef.current.keys().next().value as string | undefined;
            if (first) chatMemRef.current.delete(first);
          }
          schedulePersistChatMem(uid);
        } catch {
          // ignore
        }

        await sleep(CHAT_PREFETCH_DELAY_MS);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiCallWithTimeout, rooms, getMyUserId, isAuthed, loadingRooms]);

  // Комментарий на русском: IntersectionObserver для prefetch соседних чатов при скролле.
  const prefetchOnScrollRef = useRef<Set<string>>(new Set());
  const prefetchChat = useCallback(
    async (type: 'dm' | 'room', id: number) => {
      const uid = getMyUserId();
      if (!uid || !id) return;
      const cacheKey = `${type}:${id}`;
      if (prefetchOnScrollRef.current.has(cacheKey)) return;
      prefetchOnScrollRef.current.add(cacheKey);

      const offKey = type === 'dm' ? offlineDmKey(uid, id) : offlineRoomKey(uid, id);
      const cached = await chatKvGet<DirectMessage[]>(offKey);
      if (cached?.value && Array.isArray(cached.value) && cached.value.length >= CHAT_PAGE_LIMIT) return;

      // Комментарий на русском: уже есть в RAM — пропускаем.
      const memKey = `${type === 'dm' ? 'dm' : 'room'}:${id}`;
      if (chatMemRef.current.has(memKey)) return;

      try {
        const endpoint = type === 'dm' ? `/api/chat/messages/${id}/` : `/api/chat/rooms/${id}/messages/`;
        const qs = new URLSearchParams();
        qs.set('limit', String(CHAT_PAGE_LIMIT));
        const res = await apiCallWithTimeout(`${endpoint}?${qs.toString()}`, {}, CHAT_LOAD_TIMEOUT_MS);
        if (!res.ok) return;
        const data = await res.json();
        const raw: any[] = Array.isArray(data) ? data : (data?.messages || []);
        const normalized = raw.map((m) => m as DirectMessage).filter((m) => m && typeof m.id === 'number').reverse();
        if (!normalized.length) return;
        void chatKvSet(offKey, normalized.slice(-CHAT_PAGE_LIMIT));
        const maxServerId = normalized.reduce((mx, m: any) => {
          const n = Number(m?.id || 0) || 0;
          return n > 0 && n < 1_000_000_000_000 && n > mx ? n : mx;
        }, 0);
        try {
          if (chatMemRef.current.has(memKey)) chatMemRef.current.delete(memKey);
        } catch {}
        chatMemRef.current.set(memKey, { messages: normalized.slice(-CHAT_PAGE_LIMIT), lastId: maxServerId, hasMore: true });
        if (chatMemRef.current.size > CHAT_MAP_MAX_SIZE) {
          const first = chatMemRef.current.keys().next().value as string | undefined;
          if (first) chatMemRef.current.delete(first);
        }
        schedulePersistChatMem(uid);
      } catch {
        // ignore
      }
    },
    [apiCallWithTimeout, getMyUserId]
  );

  // Комментарий на русском: prefetch соседей через requestIdleCallback при видимости элемента.
  const observedNodesRef = useRef<WeakSet<HTMLElement>>(new WeakSet());
  const observePrefetch = useCallback(
    (node: HTMLElement | null, type: 'dm' | 'room', id: number, neighbors: number[]) => {
      if (!node || typeof IntersectionObserver === 'undefined') return;
      // Комментарий на русском: не создаём повторный observer для уже обработанного элемента.
      if (observedNodesRef.current.has(node)) return;
      observedNodesRef.current.add(node);
      const obs = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting) return;
          obs.disconnect();
          // Комментарий на русском: низкоприоритетная загрузка соседей.
          const schedule = typeof requestIdleCallback !== 'undefined' ? requestIdleCallback : (cb: () => void) => setTimeout(cb, 50);
          schedule(() => {
            for (const nid of neighbors) {
              void prefetchChat(type, nid);
            }
          });
        },
        { rootMargin: '200px' }
      );
      obs.observe(node);
    },
    [prefetchChat]
  );

  const loadRooms = useCallback(async () => {
    const uid = getMyUserId();
    if (uid) {
      try {
        const cached = await idbGetWithTimeout(chatKvGet<RoomConversation[]>(offlineRoomsKey(uid)), 300);
        if (cached?.value && Array.isArray(cached.value) && cached.value.length) {
          setRooms(cached.value as any);
          setLoadingRooms(false);
        }
      } catch {
        // ignore
      }
    }
    try {
      const res = await apiCallWithTimeout('/api/chat/rooms/', {}, CHAT_LOAD_TIMEOUT_MS);
      if (res.status === 401) return;
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) {
        setRooms(data as any);
        if (uid) void chatKvSet(offlineRoomsKey(uid), data);
        // Комментарий на русском: сохраняем хэши комнат для синхронизации.
        if (uid) {
          for (const rc of data) {
            const roomId = Number((rc as any)?.room?.id || 0) || 0;
            const hash = (rc as any)?.chat_hash || '';
            if (roomId > 0 && hash) {
              void chatKvSet(offlineChatHashKey(uid, 'room', roomId), hash);
            }
          }
        }
      }
    } catch {
      // Комментарий на русском: комнаты не должны ломать основной мессенджер — молча остаёмся без них.
    } finally {
      setLoadingRooms(false);
    }
  }, [apiCallWithTimeout, getMyUserId, idbGetWithTimeout]);

  // Комментарий на русском: дедупликация частых вызовов (poll + уведомления + действия пользователя).
  const loadConversationsThrottled = useMemo(
    () => throttle(() => void loadConversations(), 1200),
    [loadConversations]
  );
  const loadRoomsThrottled = useMemo(() => throttle(() => void loadRooms(), 1200), [loadRooms]);


  const loadFriends = useCallback(async () => {
    try {
      const res = await apiCall('/api/chat/friends/');
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) setFriends(data);
    } catch {
      // ignore
    }
  }, [apiCall]);

  const loadEmployees = useCallback(async () => {
    // Список сотрудников нужен только для роли "Сотрудник" (и админов, но UI включаем только сотруднику)
    setEmployeesLoading(true);
    try {
      const res = await apiCall('/api/users/employees/');
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) setEmployees(data);
    } catch {
      // ignore
    } finally {
      setEmployeesLoading(false);
    }
  }, [apiCall]);

  const loadFriendRequests = useCallback(async () => {
    try {
      const res = await apiCall('/api/chat/friends/requests/');
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) setFriendRequests(data);
    } catch {
      // ignore
    }
  }, [apiCall]);

  const loadSupportUnread = useCallback(async () => {
    try {
      const res = await apiCall('/api/chat/support/unread/');
      if (!res.ok) return;
      const data = await res.json();
      const unread = Number(data?.unread || 0);
      setSupportUnread(unread);
    } catch {
      // ignore
    }
  }, [apiCall]);

  const loadSupportInbox = useCallback(async () => {
    // Только для суперадмина
    try {
      const res = await apiCall('/api/chat/support/inbox/?limit=100');
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) setSupportInbox(data);
    } catch {
      // ignore
    }
  }, [apiCall]);

  // Комментарий на русском: загрузка ошибок фронтенда (только для суперадмина).
  const loadFrontendErrors = useCallback(async (category?: string) => {
    setErrorsLoading(true);
    try {
      const cat = category || errorsCategory;
      const qs = cat && cat !== 'all' ? `?category=${encodeURIComponent(cat)}&limit=200` : '?limit=200';
      const res = await apiCall(`/api/errors/list${qs}`);
      if (!res.ok) {
        setErrorsLoading(false);
        return;
      }
      const data = await res.json();
      if (Array.isArray(data?.errors)) setFrontendErrors(data.errors);
    } catch {
      // ignore
    }
    setErrorsLoading(false);
  }, [apiCall, errorsCategory]);

  const loadSupportMessages = useCallback(
    async (userId?: number, afterId?: number) => {
      try {
        const qs = new URLSearchParams();
        qs.set('limit', '120');
        if (afterId && afterId > 0) qs.set('after', String(afterId));

        const isSuper = myIsSuperRef.current;
        const url = isSuper && userId
          ? `/api/chat/support/messages/${userId}/?${qs.toString()}`
          : `/api/chat/support/messages/?${qs.toString()}`;

        const res = await apiCall(url);
        if (!res.ok) return;
        const data = await res.json();
        const arr: any[] = Array.isArray(data) ? data : (data?.messages || []);
        const normalized = arr
          .map((m) => m as DirectMessage)
          .filter((m) => m && typeof m.id === 'number')
          .reverse();

        if (afterId && afterId > 0) {
          if (!normalized.length) return;
          
          // Комментарий на русском: обновляем supportLastIdRef СРАЗУ, до setSupportMessages, чтобы следующий poll не дублировал запросы.
          const maxNewId = normalized.reduce((mx, m: any) => Math.max(mx, Number(m?.id || 0) || 0), 0);
          if (maxNewId > supportLastIdRef.current) {
            supportLastIdRef.current = maxNewId;
          }
          
          const isSuper = myIsSuperRef.current;
          let hasNewMessages = false;
          
          setSupportMessages((prev) => {
            const seen = new Set(prev.map((x) => x.id));
            const fresh = normalized.filter((x) => !seen.has(x.id));
            
            if (fresh.length > 0) {
              hasNewMessages = true;
            }
            
            // Комментарий на русском: вибро на входящие в поддержку (best-effort).
            if (fresh.length) {
              const hasIncoming = fresh.some((m: any) => {
                const fromId = Number(m?.from_user?.id || 0) || 0;
                // Для суперадмина: входящие = сообщения пользователя (from_id != 0).
                // Для обычного пользователя: входящие = сообщения поддержки (from_id == 0).
                return isSuper ? fromId !== 0 : fromId === 0;
              });
              if (hasIncoming) {
                setTimeout(() => {
                  try {
                    const v = (globalThis as any)?.navigator?.vibrate;
                    if (!document.hidden && typeof v === 'function') v.call(navigator, [16, 26, 10]);
                  } catch {}
                }, 0);
              }
            }
            return [...prev, ...fresh];
          });
          
          // Комментарий на русском: обновляем inbox/unread счетчики, чтобы список поддержки показывал актуальные данные.
          if (hasNewMessages) {
            void loadSupportUnread();
            if (myIsSuperRef.current) void loadSupportInbox();
          }
          
          return;
        }

        setSupportMessages(normalized);
        supportLastIdRef.current = normalized[normalized.length - 1]?.id || 0;
      } catch {
        // ignore
      }
    },
    [apiCall, loadSupportInbox, loadSupportUnread]
  );

  const sendSupportMessage = useCallback(async () => {
    if (sending) return;
    const raw = supportDraft;
    const v = validateMessage(raw);
    if (!v.isValid) {
      setError(v.error || 'Ошибка валидации');
      setTimeout(() => setError(null), 2500);
      return;
    }
    const clean = sanitizeMessage(raw);
    if (!clean) return;
    const linkPreviewDisabled = !!supportDraftFirstUrl && supportDraftFirstUrl === supportDraftPreviewSuppressedUrl;

    setSupportDraft('');
    setSupportDraftPreviewSuppressedUrl('');
    setSending(true);
    setError(null);

    // Оптимистично добавляем сообщение
    const isSuper = myIsSuperRef.current;
    const target = isSuper ? supportTargetUser : null;
    if (isSuper && !target) {
      setSending(false);
      setError('Выберите обращение слева');
      setTimeout(() => setError(null), 2500);
      return;
    }

    const me = meRef.current || { id: 0, username: '', display_name: 'Вы' };
    const supportUser: User = { id: 0, username: 'support', display_name: 'Поддержка', avatar_url: null };
    const clientId = makeClientId();
    const optimisticId = Date.now();
    const optimistic: DirectMessage = {
      id: optimisticId,
      // Для суперадмина "свои" сообщения идут от Поддержки (id=0), чтобы диалог рисовался корректно.
      from_user: isSuper ? supportUser : { ...me, id: me.id || 0 },
      to_user: isSuper ? (target as User) : supportUser,
      message: clean,
      created_at: new Date().toISOString(),
      // Комментарий на русском: read-receipts в поддержке не используем, не ставим is_read=true вручную.
      is_read: false,
      status: 'sending',
      client_id: clientId,
      link_preview_disabled: linkPreviewDisabled,
    };
    setSupportMessages((prev) => [...prev, optimistic]);

    try {
      const targetId = isSuper ? target?.id : undefined;
      const url = isSuper && targetId
        ? `/api/chat/support/messages/${targetId}/`
        : '/api/chat/support/messages/';

      const res = await apiCall(url, {
        method: 'POST',
        body: JSON.stringify({ message: clean, client_id: clientId, link_preview_disabled: linkPreviewDisabled }),
      });
      if (!res.ok) {
        setSupportMessages((prev) => prev.map((m) => (m.id === optimisticId ? { ...m, status: 'error' } : m)));
        let msg = 'Не удалось отправить сообщение в поддержку';
        try {
          const data = (await res.json()) as any;
          if (data?.error) msg = String(data.error);
          if (data?.hint) msg = `${msg}. ${String(data.hint)}`;
        } catch {
          // ignore
        }
        setError(msg);
        setTimeout(() => setError(null), 2500);
        return;
      }

      const created = (await res.json()) as any;
      setSupportMessages((prev) =>
        dedupeLastById(
          prev.map((m) =>
            (((m as any)?.client_id && (m as any)?.client_id === clientId) || m.id === optimisticId)
              ? {
                  ...m,
                  id: Number(created?.id || m.id),
                  message: typeof created?.message === 'string' ? String(created.message) : m.message,
                  created_at: String(created?.created_at || m.created_at),
                  status: undefined,
                  type: typeof created?.type === 'string' ? String(created.type) : (m as any)?.type,
                  attachments: Array.isArray(created?.attachments) ? created.attachments : (m as any)?.attachments,
                  client_id: (created?.client_id as any) ?? (m as any)?.client_id ?? clientId,
                }
              : m
          )
        )
      );
      supportLastIdRef.current = Math.max(supportLastIdRef.current, Number(created?.id || 0));
      void loadSupportUnread();
      if (myIsSuperRef.current) void loadSupportInbox();
    } catch {
      setSupportMessages((prev) => prev.map((m) => (m.id === optimisticId ? { ...m, status: 'error' } : m)));
      setError('Ошибка сети');
      setTimeout(() => setError(null), 2500);
    } finally {
      setSending(false);
    }
  }, [
    apiCall,
    loadSupportInbox,
    loadSupportUnread,
    sending,
    supportDraft,
    supportDraftFirstUrl,
    supportDraftPreviewSuppressedUrl,
    supportTargetUser,
  ]);

  const sendSupportPhotoMessage = useCallback(async () => {
    const photos = pendingPhotos;
    if (!photos.length) return;
    if (sending) return;

    // Комментарий на русском: фото не отправляем офлайн (иначе нужно хранить файл в IndexedDB).
    if (!isOnline) {
      setError('Фото нельзя отправить офлайн. Подключитесь к сети.');
      try {
        logUploadError('Cannot send support photo while offline', {
          kind: 'photo_send',
          scope: 'support',
          isSuper: myIsSuperRef.current,
          targetUserId: (myIsSuperRef.current ? supportTargetUser?.id : 0) || 0,
          count: photos.length,
          size: photos[0]?.file?.size || 0,
          mime: String(photos[0]?.file?.type || ''),
          name: String(photos[0]?.file?.name || ''),
        });
      } catch {}
      setTimeout(() => setError(null), 2500);
      return;
    }

    const isSuper = myIsSuperRef.current;
    const target = isSuper ? supportTargetUser : null;
    if (isSuper && !target) {
      setError('Выберите обращение слева');
      setTimeout(() => setError(null), 2500);
      return;
    }

    const rawCaption = supportDraft;
    let caption = '';
    if (rawCaption.trim()) {
      const v = validateMessage(rawCaption);
      if (!v.isValid) {
        setError(v.error || 'Ошибка валидации');
        setTimeout(() => setError(null), 2500);
        return;
      }
      caption = sanitizeMessage(rawCaption);
    }

    const me = meRef.current || { id: 0, username: '', display_name: 'Вы' };
    const supportUser: User = { id: 0, username: 'support', display_name: 'Поддержка', avatar_url: null };
    const now = Date.now();
    const jobs = photos.slice(0, 5).map((p, i) => {
      const file0 = p.file;
      const clientId = makeClientId();
      const optimisticId = now + i;
      const localUrl = p.previewUrl || (typeof URL !== 'undefined' ? URL.createObjectURL(file0) : '');
      if (localUrl) photoLocalUrlsRef.current.add(localUrl);
      return { i, file0, clientId, optimisticId, localUrl };
    });

    const optimisticBatch: any[] = jobs.map((j) => ({
      id: j.optimisticId,
      from_user: isSuper ? supportUser : { ...me, id: me.id || 0 },
      to_user: isSuper ? (target as User) : supportUser,
      message: j.i === 0 ? (caption || 'Фото') : 'Фото',
      created_at: new Date().toISOString(),
      is_read: false,
      status: 'sending',
      client_id: j.clientId,
      type: 'photo_message',
      local_photo_src: j.localUrl || null,
      attachments: [
        {
          id: 0,
          kind: 'photo',
          url: j.localUrl,
          mime: j.file0?.type || '',
          size: j.file0?.size || 0,
        },
      ],
    }));

    // Комментарий на русском: чистим панель выбора фото, но blob URL не ревокаем (он нужен для optimistic bubble).
    clearPendingPhoto({ keepPreview: true });
    setSupportDraft('');
    setSending(true);
    setError(null);
    setSupportMessages((prev) => [...prev, ...optimisticBatch]);
    requestAnimationFrame(() => supportMessagesEndRef.current?.scrollIntoView({ behavior: 'auto' }));

    try {
      const targetId = isSuper ? target?.id : undefined;
      const url = isSuper && targetId ? `/api/chat/support/messages/${targetId}/` : '/api/chat/support/messages/';
      let hadSuccess = false;
      let shownError = false;

      for (const j of jobs) {
        const file0 = j.file0;
        const clientId = j.clientId;
        const optimisticId = j.optimisticId;
        const localUrl = j.localUrl;
        const isFirst = j.i === 0;

        try {
          let fileToSend: File = file0;
          // Комментарий на русском: GIF не оптимизируем через canvas (иначе потеряем анимацию).
          if (!photos[j.i]?.isGif) {
            try {
              fileToSend = await optimizeImageForChatUpload(file0);
            } catch {
              fileToSend = file0;
            }
          }

          const fd = new FormData();
          fd.append('client_id', clientId);
          if (isFirst && caption) fd.append('message', caption);
          fd.append('photo', fileToSend, fileToSend.name || 'photo.jpg');

          const res = await apiCall(url, { method: 'POST', body: fd });
          if (!res.ok) {
            setSupportMessages((prev) =>
              prev.map((m: any) =>
                ((m as any)?.client_id && (m as any)?.client_id === clientId) || m.id === optimisticId ? { ...m, status: 'error' } : m
              )
            );

            if (!shownError) {
              shownError = true;
              let msg = 'Не удалось отправить фото в поддержку';
              try {
                const data = (await res.json()) as any;
                if (data?.error) msg = String(data.error);
                if (data?.hint) msg = `${msg}. ${String(data.hint)}`;
              } catch {}
              setError(msg);
              setTimeout(() => setError(null), 2500);
            }

            try {
              logUploadError('Failed to send support photo (HTTP)', {
                kind: 'photo_send',
                scope: 'support',
                isSuper,
                targetUserId: target?.id || 0,
                count: photos.length,
                size: file0.size || 0,
                mime: String(file0.type || ''),
                name: String(file0.name || ''),
                captionLen: isFirst ? caption.length : 0,
                httpStatus: res.status,
              });
            } catch {}

            continue;
          }

          const created = (await res.json()) as any;
          hadSuccess = true;
          setSupportMessages((prev) =>
            dedupeLastById(
              prev.map((m: any) =>
                ((m as any)?.client_id && (m as any)?.client_id === clientId) || m.id === optimisticId
                  ? {
                      ...m,
                      id: Number(created?.id || m.id),
                      message: String(created?.message || m.message || 'Фото'),
                      created_at: String(created?.created_at || m.created_at),
                      status: undefined,
                      type: String(created?.type || 'photo_message'),
                      attachments: Array.isArray(created?.attachments) ? created.attachments : (m as any)?.attachments,
                      local_photo_src: localUrl || (m as any)?.local_photo_src,
                      client_id: (created?.client_id as any) ?? (m as any)?.client_id ?? clientId,
                    }
                  : m
              )
            )
          );
          supportLastIdRef.current = Math.max(supportLastIdRef.current, Number(created?.id || 0));
        } catch {
          setSupportMessages((prev) =>
            prev.map((m: any) =>
              ((m as any)?.client_id && (m as any)?.client_id === clientId) || m.id === optimisticId ? { ...m, status: 'error' } : m
            )
          );
          if (!shownError) {
            shownError = true;
            setError('Ошибка сети');
            setTimeout(() => setError(null), 2500);
          }
          try {
            logUploadError('Failed to send support photo (network)', {
              kind: 'photo_send',
              scope: 'support',
              isSuper: myIsSuperRef.current,
              targetUserId: (myIsSuperRef.current ? supportTargetUser?.id : 0) || 0,
              count: photos.length,
              size: file0.size || 0,
              mime: String(file0.type || ''),
              name: String(file0.name || ''),
              captionLen: isFirst ? caption.length : 0,
            });
          } catch {}
        }
      }

      if (hadSuccess) {
        void loadSupportUnread();
        if (myIsSuperRef.current) void loadSupportInbox();
      }
    } catch {
      // ignore (ошибки обрабатываем выше по каждой картинке)
    } finally {
      setSending(false);
    }
  }, [apiCall, clearPendingPhoto, isOnline, loadSupportInbox, loadSupportUnread, pendingPhotos, sending, supportDraft, supportTargetUser]);

  const loadMessages = useCallback(
    async (userId: number, afterId?: number, skipCache?: boolean) => {
      setLoadingMessages(!afterId);
      const chatKey = `dm:${userId}`;
      const uid = getMyUserId();
      let offlineSnap: DirectMessage[] | null = null;
      // Комментарий на русском: skipCache=true — кэш устарел (хэш не совпал), сразу идём на сервер.
      if (!afterId && !skipCache) {
        // Комментарий на русском: сначала пробуем RAM-кэш (ChatMap) — он мгновенный и не зависит от IndexedDB.
        try {
          const mem = chatMemRef.current.get(`dm:${userId}` as any) as any;
          const arr = Array.isArray(mem?.messages) ? (mem.messages as DirectMessage[]) : null;
          if (arr && arr.length) {
            offlineSnap = arr;
            chatMessagesStore.getState().setChatSnapshot(chatKey, arr, { hasMore: typeof mem?.hasMore === 'boolean' ? !!mem.hasMore : true });
            const maxServerId = arr.reduce((mx, m: any) => {
              const id = Number(m?.id || 0) || 0;
              return id > 0 && id < 1_000_000_000_000 && id > mx ? id : mx;
            }, 0);
            lastMessageIdRef.current = maxServerId;
            const hm = typeof mem?.hasMore === 'boolean' ? !!mem.hasMore : true;
            hasMoreMessagesRef.current = hm;
            setHasMoreMessages(hm);
            chatMessagesStore.getState().setHasMore(chatKey, hm);
            chatMessagesStore.getState().setLastServerId(chatKey, maxServerId);
            setLoadError(null);
            requestAnimationFrame(() => forceScrollToBottom(userId));
            setLoadingMessages(false);
          }
        } catch {
          // ignore
        }

        // Комментарий на русском: затем читаем IndexedDB (но с таймаутом, чтобы не “висеть”).
        if (uid) {
          const cached = await idbGetWithTimeout(chatKvGet<DirectMessage[]>(offlineDmKey(uid, userId)), 500);
          const arr = cached?.value;
          if (Array.isArray(arr) && arr.length) {
            offlineSnap = arr as DirectMessage[];
            chatMessagesStore.getState().setChatSnapshot(chatKey, offlineSnap, { hasMore: true });
          // Комментарий на русском: lastMessageIdRef держим только по серверным id (оптимистичные Date.now() не подходят).
          const maxServerId = offlineSnap.reduce((mx, m: any) => {
            const id = Number(m?.id || 0) || 0;
            return id > 0 && id < 1_000_000_000_000 && id > mx ? id : mx;
          }, 0);
          lastMessageIdRef.current = maxServerId;
          // Комментарий на русском: офлайн-снимок не знает `has_more`.
          // На iOS/PWA сеть/таймауты часто приводят к сценарию “показали кэш и навсегда запретили историю”.
          // Поэтому даём шанс подгрузить историю: включаем hasMore, а сервер на первом же запросе вернёт пусто → и мы выключим.
          const guessHasMore = true;
          hasMoreMessagesRef.current = guessHasMore;
          setHasMoreMessages(guessHasMore);
          chatMessagesStore.getState().setHasMore(chatKey, guessHasMore);
          chatMessagesStore.getState().setLastServerId(chatKey, maxServerId);
          setLoadError(null);
          const savedOffset = (() => {
            try {
              const v = localStorage.getItem(`lisad_dm_scroll_v1:${userId}`);
              const n = v != null ? Number(v) : NaN;
              return Number.isFinite(n) ? Math.max(0, Math.round(n)) : null;
            } catch {
              return null;
            }
          })();
          const offRaw = savedOffset ?? 0;
          const off = offRaw < 120 ? 0 : offRaw;
          if (savedOffset == null || off === 0) writeDmScroll(userId, 0);
          // Комментарий на русском: старое восстановление scrollTop удалено — открываем диалог у низа через virtuoso.
          requestAnimationFrame(() => forceScrollToBottom(userId));
          setLoadingMessages(false);
        }
          // Комментарий на русском: восстанавливаем фон DM из IndexedDB (даже офлайн), тоже с таймаутом.
          try {
            const cachedWp = await idbGetWithTimeout(chatKvGet<any>(offlineDmWallpaperKey(uid, userId)), 2500);
            const wpRaw = cachedWp?.value;
            const nextWp =
              wpRaw && typeof wpRaw === 'object' && typeof (wpRaw as any)?.id === 'number' && typeof (wpRaw as any)?.url === 'string'
                ? {
                    id: Number((wpRaw as any).id || 0) || 0,
                    url: String((wpRaw as any).url || ''),
                    blur: Number((wpRaw as any).blur || 0) || 0,
                    brightness: Number((wpRaw as any).brightness || 100) || 100,
                  }
                : null;
            if (selectedUserIdRef.current === userId) {
              setDmWallpaper(nextWp && nextWp.id > 0 && nextWp.url ? nextWp : null);
            }
          } catch {
            // ignore
          }
        }
      }
      try {
        const qs = new URLSearchParams();
        qs.set('limit', String(CHAT_PAGE_LIMIT));
        if (afterId && afterId > 0) qs.set('after', String(afterId));
        const res = await apiCallWithTimeout(`/api/chat/messages/${userId}/?${qs.toString()}`, {}, CHAT_LOAD_TIMEOUT_MS);
        if (!res.ok) {
          let msg = `Не удалось загрузить сообщения (HTTP ${res.status})`;
          try {
            const data = await res.json();
            if (data?.error) msg = String(data.error);
            if (data?.hint) msg = `${msg}. ${String(data.hint)}`;
            if (data?.details) msg = `${msg}. ${String(data.details)}`;
          } catch {
            // ignore
          }
          setLoadError(msg);
          return;
        }
        const data = await res.json();
        // Комментарий на русском: персональный фон чата и входящий запрос (если есть).
        if (!Array.isArray(data)) {
          const wp = (data as any)?.wallpaper;
          const nextWp =
            wp && typeof wp === 'object' && typeof (wp as any)?.id === 'number' && typeof (wp as any)?.url === 'string'
              ? {
                  id: Number((wp as any).id || 0) || 0,
                  url: String((wp as any).url || ''),
                  blur: Number((wp as any).blur || 0) || 0,
                  brightness: Number((wp as any).brightness || 100) || 100,
                }
              : null;
          if (uid) void chatKvSet(offlineDmWallpaperKey(uid, userId), nextWp && nextWp.id > 0 && nextWp.url ? nextWp : null);
          if (selectedUserIdRef.current === userId) {
            setDmWallpaper(nextWp && nextWp.id > 0 && nextWp.url ? nextWp : null);
            const pr = (data as any)?.pending_wallpaper_request;
            setDmWallpaperRequest(pr && typeof pr === 'object' && typeof (pr as any)?.id === 'number' ? pr : null);
          }
        }
        const arr: any[] = Array.isArray(data) ? data : (data?.messages || []);
        const normalized = arr
          .map((m) => m as DirectMessage)
          .filter((m) => m && typeof m.id === 'number')
          // API отдаёт DESC, приводим к ASC (для нормального скролла/append)
          .reverse();

        if (afterId && afterId > 0) {
          if (!normalized.length) return;
          const shouldStick = isNearBottomRef.current;
          const up = chatMessagesStore.getState().upsertMessages(chatKey, normalized, {
            mode: 'append',
            meId: meRef.current?.id || 0,
          });
          if (up.maxServerId) lastMessageIdRef.current = Math.max(lastMessageIdRef.current, up.maxServerId);

          // Комментарий на русском: обновляем список диалогов, чтобы last_message и unread_count были актуальными.
          if (up.addedIds.length || up.changedIds.length || up.replacedIds.length) {
            loadConversationsThrottled();
          }
          if (up.hasIncoming) {
            // Комментарий на русском: best-effort вибрация (на iOS может не сработать).
            setTimeout(() => {
              try {
                const v = (globalThis as any)?.navigator?.vibrate;
                if (!document.hidden && typeof v === 'function') v.call(navigator, [18, 30, 12]);
              } catch {}
            }, 0);
          }
          
          if (shouldStick) {
            requestAnimationFrame(() => {
              if (selectedUserIdRef.current !== userId) return;
              forceScrollToBottom(userId);
            });
          }
          return;
        }

        // Комментарий на русском: синхронизируем кэш голосовых по “снимку” чата.
        // Зачем: если сообщение удалено на сервере — удаляем и локальный файл (без ложных удалений из-за limit/pagination).
        setTimeout(() => {
          try {
            const key = `lisad_voice_seen_v2:${userId}`;
            const prevRaw = JSON.parse(localStorage.getItem(key) || '[]') as any[];
            const map = new Map<number, string>();
            if (Array.isArray(prevRaw)) {
              for (const it of prevRaw) {
                if (Array.isArray(it) && it.length === 2) {
                  const id = Number(it[0] || 0);
                  const u = typeof it[1] === 'string' ? String(it[1]) : '';
                  if (id > 0 && u) map.set(id, u);
                }
              }
            }

            const ids = new Set<number>();
            let minId = Number.MAX_SAFE_INTEGER;
            for (const m of normalized as any[]) {
              const id = Number((m as any)?.id || 0);
              if (id > 0) {
                ids.add(id);
                if (id < minId) minId = id;
              }
              const vu = typeof (m as any)?.voice_url === 'string' ? String((m as any).voice_url) : '';
              const raw = String((m as any)?.message || '');
              const eff = vu || raw;
              const isV =
                !!vu || (typeof (m as any)?.type === 'string' && String((m as any).type) === 'voice_message') || eff.includes('/chat_voice/');
              if (!isV) continue;
              if (!eff || !(eff.startsWith('/') || /^https?:\/\//.test(eff))) continue;
              const abs = resolveVoiceUrl(eff);
              if (abs && id > 0) map.set(id, abs);
            }
            if (minId === Number.MAX_SAFE_INTEGER) minId = 0;

            // Комментарий на русском: удаляем только те, что "должны" попадать в текущий снимок (id >= minId), но пропали => удалены на сервере.
            for (const [id, u] of Array.from(map.entries())) {
              if (minId && id >= minId && !ids.has(id)) {
                void deleteCachedVoice(u);
                map.delete(id);
              }
            }

            const out = Array.from(map.entries())
              .sort((a, b) => a[0] - b[0])
              .slice(-360);
            localStorage.setItem(key, JSON.stringify(out));
          } catch {}
        }, 0);

        const up = chatMessagesStore.getState().upsertMessages(chatKey, normalized, {
          mode: 'append',
          meId: meRef.current?.id || 0,
        });
        if (up.maxServerId) lastMessageIdRef.current = Math.max(lastMessageIdRef.current, up.maxServerId);
        // Комментарий на русском: бэк может отдавать либо объект `{ messages, has_more }`, либо просто массив сообщений.
        // Для массива берём best-effort: если пришло >= limit, значит “скорее всего” есть ещё.
        const hm = !Array.isArray(data) ? !!data?.has_more : normalized.length >= CHAT_PAGE_LIMIT;
        hasMoreMessagesRef.current = hm;
        setHasMoreMessages(hm);
        chatMessagesStore.getState().setHasMore(chatKey, hm);
        setLoadError(null);
        const savedOffset = (() => {
          try {
            const v = localStorage.getItem(`lisad_dm_scroll_v1:${userId}`);
            const n = v != null ? Number(v) : NaN;
            return Number.isFinite(n) ? Math.max(0, Math.round(n)) : null;
          } catch {
            return null;
          }
        })();
        const offRaw = savedOffset ?? 0;
        // Комментарий на русском: если сохранённый оффсет совсем небольшой — открываем диалог внизу (это убирает “прыжки” на iOS).
        const off = offRaw < 120 ? 0 : offRaw;
        if (savedOffset == null || off === 0) writeDmScroll(userId, 0);
        // Комментарий на русском: старое восстановление scrollTop удалено — открываем диалог у низа через virtuoso.
        requestAnimationFrame(() => forceScrollToBottom(userId));

        // Комментарий на русском: сохраняем снимок (включая pending) для офлайна.
        if (uid) void chatKvSet(offlineDmKey(uid, userId), getChatMessagesTail(chatKey, CHAT_OFFLINE_MAX_MESSAGES));
        // Комментарий на русском: обновляем хэш чата из ответа сервера.
        const serverHash = String((data as any)?.chat_hash || '') || '';
        if (serverHash) {
          currentChatHashRef.current = serverHash;
          if (uid) void chatKvSet(offlineChatHashKey(uid, 'dm', userId), serverHash);
        }
      } catch {
        if (!offlineSnap) setLoadError('Не удалось загрузить сообщения (ошибка сети)');
      } finally {
        setLoadingMessages(false);
      }
    },
    [apiCallWithTimeout, forceScrollToBottom, getMyUserId, idbGetWithTimeout, loadConversations, writeDmScroll]
  );

  const loadRoomMessages = useCallback(
    async (roomId: number, afterId?: number, skipCache?: boolean) => {
      // Комментарий на русском: afterId должен быть "серверным" id (INT). Если сюда попал optimistic Date.now() (13 цифр),
      // polling сломается (id > after никогда не найдётся). Поэтому жёстко нормализуем.
      const safeAfter = afterId && afterId > 0 && afterId < 1_000_000_000_000 ? afterId : 0;
      setLoadingMessages(!safeAfter);
      const chatKey = `room:${roomId}`;
      const uid = getMyUserId();
      let offlineSnap: DirectMessage[] | null = null;
      // Комментарий на русском: skipCache=true — кэш устарел (хэш не совпал), сразу идём на сервер.
      if (!safeAfter && !skipCache) {
        // Комментарий на русском: сначала RAM-кэш (ChatMap) — мгновенный.
        try {
          const mem = chatMemRef.current.get(`room:${roomId}` as any) as any;
          const arr = Array.isArray(mem?.messages) ? (mem.messages as DirectMessage[]) : null;
          if (arr && arr.length) {
            offlineSnap = arr;
            chatMessagesStore.getState().setChatSnapshot(chatKey, arr, { hasMore: typeof mem?.hasMore === 'boolean' ? !!mem.hasMore : true });
            requestAnimationFrame(() => forceScrollToBottom());
            const maxServerId = arr.reduce((mx, m: any) => {
              const id = Number(m?.id || 0) || 0;
              return id > 0 && id < 1_000_000_000_000 && id > mx ? id : mx;
            }, 0);
            lastMessageIdRef.current = maxServerId;
            const hm = typeof mem?.hasMore === 'boolean' ? !!mem.hasMore : true;
            hasMoreMessagesRef.current = hm;
            setHasMoreMessages(hm);
            chatMessagesStore.getState().setHasMore(chatKey, hm);
            chatMessagesStore.getState().setLastServerId(chatKey, maxServerId);
            setLoadingMessages(false);
          }
        } catch {
          // ignore
        }

        if (uid) {
          const cached = await idbGetWithTimeout(chatKvGet<DirectMessage[]>(offlineRoomKey(uid, roomId)), 500);
          const arr = cached?.value;
          if (Array.isArray(arr) && arr.length) {
            offlineSnap = arr as DirectMessage[];
            chatMessagesStore.getState().setChatSnapshot(chatKey, offlineSnap, { hasMore: true });
          // Комментарий на русском: комнаты открываем снизу через virtuoso (без ручного scrollTop).
          requestAnimationFrame(() => forceScrollToBottom());
          const maxServerId = offlineSnap.reduce((mx, m: any) => {
            const id = Number(m?.id || 0) || 0;
            return id > 0 && id < 1_000_000_000_000 && id > mx ? id : mx;
          }, 0);
          lastMessageIdRef.current = maxServerId;
          // Комментарий на русском: офлайн-снимок не содержит `has_more`, но историю блокировать нельзя (iOS/PWA).
          // Даём шанс подгрузить историю: если её нет — сервер быстро вернёт пусто и мы выключим.
          const guessHasMore = true;
          hasMoreMessagesRef.current = guessHasMore;
          setHasMoreMessages(guessHasMore);
          chatMessagesStore.getState().setHasMore(chatKey, guessHasMore);
          chatMessagesStore.getState().setLastServerId(chatKey, maxServerId);
          setLoadingMessages(false);
        }
          // Комментарий на русском: восстанавливаем фон комнаты из IndexedDB (даже офлайн), тоже с таймаутом.
          try {
            const cachedWp = await idbGetWithTimeout(chatKvGet<any>(offlineRoomWallpaperKey(uid, roomId)), 2500);
            const wpRaw = cachedWp?.value;
            const nextWp =
              wpRaw && typeof wpRaw === 'object' && typeof (wpRaw as any)?.id === 'number' && typeof (wpRaw as any)?.url === 'string'
                ? {
                    id: Number((wpRaw as any).id || 0) || 0,
                    url: String((wpRaw as any).url || ''),
                    blur: Number((wpRaw as any).blur || 0) || 0,
                    brightness: Number((wpRaw as any).brightness || 100) || 100,
                  }
                : null;
            if (selectedRoomIdRef.current === roomId) {
              setRoomWallpaper(nextWp && nextWp.id > 0 && nextWp.url ? nextWp : null);
            }
          } catch {
            // ignore
          }
        }
      }
      try {
        const qs = new URLSearchParams();
        qs.set('limit', String(CHAT_PAGE_LIMIT));
        if (safeAfter > 0) qs.set('after', String(safeAfter));
        const res = await apiCallWithTimeout(`/api/chat/rooms/${roomId}/messages/?${qs.toString()}`, {}, CHAT_LOAD_TIMEOUT_MS);
        if (!res.ok) return;
        const data = await res.json();
        // Комментарий на русском: общий фон комнаты (если есть).
        if (!Array.isArray(data)) {
          const wp = (data as any)?.wallpaper;
          const nextWp =
            wp && typeof wp === 'object' && typeof (wp as any)?.id === 'number' && typeof (wp as any)?.url === 'string'
              ? {
                  id: Number((wp as any).id || 0) || 0,
                  url: String((wp as any).url || ''),
                  blur: Number((wp as any).blur || 0) || 0,
                  brightness: Number((wp as any).brightness || 100) || 100,
                }
              : null;
          if (uid) void chatKvSet(offlineRoomWallpaperKey(uid, roomId), nextWp && nextWp.id > 0 && nextWp.url ? nextWp : null);
          if (selectedRoomIdRef.current === roomId) {
            setRoomWallpaper(nextWp && nextWp.id > 0 && nextWp.url ? nextWp : null);
          }
        }
        const arr: any[] = Array.isArray(data) ? data : (data?.messages || []);
        // Комментарий на русском: бэк может отдавать либо объект `{ messages, has_more }`, либо массив сообщений.
        // Для массива берём best-effort: если пришло >= limit, значит “скорее всего” есть ещё.
        const hm = !Array.isArray(data) ? !!data?.has_more : arr.length >= CHAT_PAGE_LIMIT;

        // Комментарий на русском: приводим room-message к формату DirectMessage, чтобы переиспользовать UI пузырьков.
        const me = meRef.current || { id: 0, username: '', display_name: 'Вы' };
        // Комментарий на русском: API комнат отдаёт DESC — приводим к ASC для корректного порядка.
        const normalized = arr
          .map((m) => {
            const mm = (m as any) || {};
            const from = mm?.from_user || {};
            const id = Number(mm?.id || 0) || 0;
            if (!id) return null;
            // Комментарий на русском: reply_to и реакции сервер отдаёт уже готовыми — важно НЕ терять это при reopen чата.
            const replyRaw = mm?.reply_to;
            const replyObj = replyRaw && typeof replyRaw === 'object' ? (replyRaw as any) : null;
            const reactionsRaw = mm?.reactions;
            const reactions = Array.isArray(reactionsRaw) ? (reactionsRaw as any[]) : [];
            // Комментарий на русском: вложения (фото/файлы) — иначе фото в группах/каналах превращается в текст "Фото" после перезагрузки.
            const atts = Array.isArray(mm?.attachments) ? (mm.attachments as any[]) : [];
            return {
              id,
              from_user: {
                id: Number(from?.id || 0) || 0,
                username: String(from?.username || ''),
                display_name: String(from?.display_name || from?.username || ''),
                avatar_url: (from?.avatar_url ?? null) as any,
                // Комментарий на русском: для комнат бэк уже отдаёт online/fx — сохраняем, иначе UI “обеднеет” после перезагрузки.
                avatar_fx_color: (from?.avatar_fx_color ?? null) as any,
                avatar_fx_thickness: (from?.avatar_fx_thickness ?? null) as any,
                avatar_fx_glow: (from?.avatar_fx_glow ?? null) as any,
                avatar_fx_offline_color: (from?.avatar_fx_offline_color ?? null) as any,
                // Комментарий на русском: в комнатах цвет сообщений собеседника берём из его настроек (а не из наших).
                bubble_color: (from?.bubble_color ?? null) as any,
                is_online: typeof from?.is_online === 'boolean' ? (from.is_online as any) : undefined,
                last_seen: (from?.last_seen ?? null) as any,
              },
              to_user: me as any,
              message: String(mm?.message || ''),
              created_at: String(mm?.created_at || ''),
              // Комментарий на русском: в комнатах не показываем read-receipts (иначе будут “вечные” галочки).
              is_read: false,
              client_id: mm?.client_id ? String(mm.client_id) : null,
              // Комментарий на русском: голосовые/тип сообщения (как в личке) — иначе после reopen голосовые могут рендериться “как текст”.
              type: mm?.type ? String(mm.type) : undefined,
              voice_url: (mm?.voice_url ?? null) as any,
              voice_mime: (mm?.voice_mime ?? null) as any,
              reply_to: replyObj,
              reactions,
              attachments: atts,
            } as DirectMessage;
          })
          .filter(Boolean)
          .reverse() as DirectMessage[];

        if (safeAfter > 0) {
          if (!normalized.length) return;
          
          // Комментарий на русском: обновляем lastMessageIdRef СРАЗУ, до setMessages, чтобы следующий poll не запросил те же сообщения.
          const maxNewId = normalized.reduce((mx, m: any) => Math.max(mx, Number(m?.id || 0) || 0), 0);
          if (maxNewId > lastMessageIdRef.current) {
            lastMessageIdRef.current = maxNewId;
          }
          
          const shouldStick = isNearBottomRef.current;
          // Комментарий на русском: строим карту userId -> bubble_color из свежих сообщений.
          const freshBubbleColorMap = new Map<number, string>();
          for (const n of normalized as any[]) {
            const uid = Number(n?.from_user?.id || 0) || 0;
            const bc = String(n?.from_user?.bubble_color || '').trim().toLowerCase();
            if (uid > 0 && /^#[0-9a-f]{6}$/.test(bc)) freshBubbleColorMap.set(uid, bc);
          }
          const up = chatMessagesStore.getState().upsertMessages(chatKey, normalized, {
            mode: 'append',
            meId: meRef.current?.id || 0,
          });
          if (up.addedIds.length) loadRoomsThrottled();
          if (freshBubbleColorMap.size > 0) {
            for (const [uid, color] of Array.from(freshBubbleColorMap.entries())) {
              chatMessagesStore.getState().patchMessagesByUser(chatKey, uid, { bubble_color: color } as any);
            }
          }
          if (up.maxServerId) lastMessageIdRef.current = Math.max(lastMessageIdRef.current, up.maxServerId);
          if (shouldStick) {
            requestAnimationFrame(() => {
              if (selectedRoomIdRef.current !== roomId) return;
              forceScrollToBottom();
            });
          }
          return;
        }

        const up = chatMessagesStore.getState().upsertMessages(chatKey, normalized, {
          mode: 'append',
          meId: meRef.current?.id || 0,
        });
        const freshBubbleColorMap = new Map<number, string>();
        for (const n of normalized as any[]) {
          const uid = Number(n?.from_user?.id || 0) || 0;
          const bc = String(n?.from_user?.bubble_color || '').trim().toLowerCase();
          if (uid > 0 && /^#[0-9a-f]{6}$/.test(bc)) freshBubbleColorMap.set(uid, bc);
        }
        if (freshBubbleColorMap.size > 0) {
          for (const [uid, color] of Array.from(freshBubbleColorMap.entries())) {
            chatMessagesStore.getState().patchMessagesByUser(chatKey, uid, { bubble_color: color } as any);
          }
        }
        // Комментарий на русском: после сетевой загрузки комнаты держим “низ” через virtuoso (без ручного scrollTop).
        requestAnimationFrame(() => forceScrollToBottom());
        if (up.maxServerId) lastMessageIdRef.current = Math.max(lastMessageIdRef.current, up.maxServerId);
        // Комментарий на русском: для комнат тоже держим has_more (нужно для подгрузки истории при скролле вверх).
        hasMoreMessagesRef.current = hm;
        setHasMoreMessages(hm);
        chatMessagesStore.getState().setHasMore(chatKey, hm);
        if (uid) void chatKvSet(offlineRoomKey(uid, roomId), getChatMessagesTail(chatKey, CHAT_OFFLINE_MAX_MESSAGES));
        // Комментарий на русском: обновляем хэш комнаты из ответа сервера.
        const serverHash = String((data as any)?.chat_hash || '') || '';
        if (serverHash) {
          currentChatHashRef.current = serverHash;
          if (uid) void chatKvSet(offlineChatHashKey(uid, 'room', roomId), serverHash);
        }
      } catch {
        // Комментарий на русском: не пугаем пользователя — комнаты могут быть не мигрированы на старых БД.
      } finally {
        setLoadingMessages(false);
      }
    },
    [apiCallWithTimeout, forceScrollToBottom, getMyUserId, idbGetWithTimeout, loadRooms]
  );

  const loadOlderMessages = useCallback(async () => {
    if (!selectedUser) return;
    if (!hasMoreMessagesRef.current) return;
    if (loadingOlderRef.current) return;

    const chatKey = `dm:${selectedUser.id}`;
    const beforeId = getChatOldestServerId(chatKey);
    if (!beforeId) return;

    loadingOlderRef.current = true;
    setLoadingOlder(true);

    try {
      const qs = new URLSearchParams();
      qs.set('limit', String(CHAT_PAGE_LIMIT));
      qs.set('before', String(beforeId));
      // Комментарий на русском: iOS/PWA может “подвисать” на fetch без таймаута → используем общий таймаут как и при первичной загрузке.
      const res = await apiCallWithTimeout(`/api/chat/messages/${selectedUser.id}/?${qs.toString()}`, {}, CHAT_LOAD_TIMEOUT_MS);
      if (!res.ok) return;

      const data = await res.json();
      const arr: any[] = Array.isArray(data) ? data : (data?.messages || []);
      const normalized = arr.map((m) => m as DirectMessage).filter((m) => m && typeof m.id === 'number').reverse();
      // Комментарий на русском: если ответ массивом — `has_more` нет, считаем по размеру страницы.
      const hm = !Array.isArray(data) ? !!data?.has_more : normalized.length >= CHAT_PAGE_LIMIT;

      if (!normalized.length) {
        hasMoreMessagesRef.current = false;
        setHasMoreMessages(false);
        chatMessagesStore.getState().setHasMore(chatKey, false);
        return;
      }

      // Комментарий на русском: react-virtuoso через firstItemIndex автоматически сохраняет позицию скролла.
      chatMessagesStore.getState().upsertMessages(chatKey, normalized, { mode: 'prepend', meId: meRef.current?.id || 0 });

      hasMoreMessagesRef.current = hm;
      setHasMoreMessages(hm);
      chatMessagesStore.getState().setHasMore(chatKey, hm);
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [apiCallWithTimeout, selectedUser]);

  const loadOlderRoomMessages = useCallback(async () => {
    if (!selectedRoom) return;
    if (!hasMoreMessagesRef.current) return;
    if (loadingOlderRef.current) return;

    const chatKey = `room:${selectedRoom.id}`;
    // Комментарий на русском: берём минимальный серверный id из стора.
    const beforeId = getChatOldestServerId(chatKey);
    if (!beforeId) return;

    loadingOlderRef.current = true;
    setLoadingOlder(true);

    try {
      const qs = new URLSearchParams();
      qs.set('limit', String(CHAT_PAGE_LIMIT));
      qs.set('before', String(beforeId));
      const res = await apiCallWithTimeout(`/api/chat/rooms/${selectedRoom.id}/messages/?${qs.toString()}`, {}, CHAT_LOAD_TIMEOUT_MS);
      if (!res.ok) return;

      const data = await res.json();
      const arr: any[] = Array.isArray(data) ? data : (data?.messages || []);
      // Комментарий на русском: если ответ массивом — `has_more` нет, считаем по размеру страницы.
      const hm = !Array.isArray(data) ? !!data?.has_more : arr.length >= CHAT_PAGE_LIMIT;

      // Комментарий на русском: нормализуем room-message → DirectMessage (как в loadRoomMessages).
      const me = meRef.current || { id: 0, username: '', display_name: 'Вы' };
      // Комментарий на русском: API комнат отдаёт DESC — приводим к ASC для корректного prepend.
      const normalized = arr
        .map((m) => {
          const mm = (m as any) || {};
          const from = mm?.from_user || {};
          const id = Number(mm?.id || 0) || 0;
          if (!id) return null;
          const replyRaw = mm?.reply_to;
          const replyObj = replyRaw && typeof replyRaw === 'object' ? (replyRaw as any) : null;
          const reactionsRaw = mm?.reactions;
          const reactions = Array.isArray(reactionsRaw) ? (reactionsRaw as any[]) : [];
          const atts = Array.isArray(mm?.attachments) ? (mm.attachments as any[]) : [];
          return {
            id,
            from_user: {
              id: Number(from?.id || 0) || 0,
              username: String(from?.username || ''),
              display_name: String(from?.display_name || from?.username || ''),
              avatar_url: (from?.avatar_url ?? null) as any,
              avatar_fx_color: (from?.avatar_fx_color ?? null) as any,
              avatar_fx_thickness: (from?.avatar_fx_thickness ?? null) as any,
              avatar_fx_glow: (from?.avatar_fx_glow ?? null) as any,
              avatar_fx_offline_color: (from?.avatar_fx_offline_color ?? null) as any,
              // Комментарий на русском: в комнатах цвет сообщений собеседника берём из его настроек (а не из наших).
              bubble_color: (from?.bubble_color ?? null) as any,
              is_online: typeof from?.is_online === 'boolean' ? (from.is_online as any) : undefined,
              last_seen: (from?.last_seen ?? null) as any,
            },
            to_user: me as any,
            message: String(mm?.message || ''),
            created_at: String(mm?.created_at || ''),
            is_read: false,
            client_id: mm?.client_id ? String(mm.client_id) : null,
            type: mm?.type ? String(mm.type) : undefined,
            voice_url: (mm?.voice_url ?? null) as any,
            voice_mime: (mm?.voice_mime ?? null) as any,
            reply_to: replyObj,
            reactions,
            attachments: atts,
          } as DirectMessage;
        })
        .filter(Boolean)
        .reverse() as DirectMessage[];

      if (!normalized.length) {
        hasMoreMessagesRef.current = false;
        setHasMoreMessages(false);
        chatMessagesStore.getState().setHasMore(chatKey, false);
        return;
      }

      chatMessagesStore.getState().upsertMessages(chatKey, normalized, { mode: 'prepend', meId: meRef.current?.id || 0 });

      hasMoreMessagesRef.current = hm;
      setHasMoreMessages(hm);
      chatMessagesStore.getState().setHasMore(chatKey, hm);
      // Комментарий на русском: react-virtuoso через firstItemIndex автоматически сохраняет позицию скролла.
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [apiCallWithTimeout, selectedRoom]);

  // Комментарий на русском: handleMessagesScroll и ResizeObserver удалены — react-virtuoso управляет скроллом автоматически.

  const pollReadReceipts = useCallback(
    async (userId: number) => {
      const meId = meRef.current?.id || 0;
      if (!meId) return;
      try {
        const r = await apiCall(`/api/chat/messages/${userId}/receipts/`);
        if (!r.ok) return;
        const rec = (await r.json()) as any;
        const readUpTo = Number(rec?.read_up_to || 0);
        if (!readUpTo) return;
        const chatKey = `dm:${userId}`;
        const snap = getChatMessagesSnapshot(chatKey);
        for (const m of snap) {
          if (m?.from_user?.id === meId && m?.to_user?.id === userId && m.id <= readUpTo) {
            chatMessagesStore.getState().patchMessage(chatKey, m.id, { is_read: true } as any);
          }
        }
      } catch {
        // ignore
      }
    },
    [apiCall]
  );

  const selectUser = useCallback(
    (u: User, expectedHash?: string) => {
      // Если был открыт режим "Поддержка" — возвращаемся к обычным чатам.
      setSupportOpen(false);
      setSupportTargetUser(null);
      setSupportMessages([]);
      supportLastIdRef.current = 0;

      // Комментарий на русском: старый флаг chatScrollReady удалён (virtuoso убирает “flash” без скрытия контейнера).

      // Комментарий на русском: обновляем refs синхронно, чтобы scroll-restore (useLayoutEffect) не видел "старый" чат.
      selectedRoomIdRef.current = 0;
      selectedUserIdRef.current = Number(u?.id || 0) || 0;
      setSelectedRoom(null);
      setSelectedUser(u);
      // Комментарий на русском: сбрасываем UI фона при переключении чата (чтобы не мигал фон от прошлого диалога).
      setDmWallpaper(null);
      setDmWallpaperRequest(null);
      setDmWallpaperSrc('');
      setRoomWallpaper(null);
      setRoomWallpaperSrc('');
      setDmWallpaperOpen(false);
      setDmWallpaperFile(null);
      setDmWallpaperBlurDraft(0);
      setDmWallpaperToast(null);
      setDmWallpaperBusy(false);
      // Комментарий на русском: сбрасываем профиль пользователя при переключении чата.
      setPeerProfileOpen(false);
      setPeerProfileTab('voice');
      setPeerProfileDragY(0);
      setPeerProfilePullY(0);
      setPeerProfileMenu(null);
      setPeerMuteContent(false);
      setPeerMuteSound(false);
      setMediaOwnerFilter('all');
      setMediaTypeFilter('all');
      setFilesOwnerFilter('all');
      // Комментарий на русском: если мы зашли в чат — сразу сбрасываем бейдж непрочитанных (optimistic),
      // а сервер всё равно отметит прочитанным при загрузке сообщений.
      setConversations((prev) => prev.map((c) => (c?.user?.id === u.id ? { ...c, unread_count: 0 } : c)));
      setError(null);
      const key = `dm:${Number(u.id || 0) || 0}`;
      const cached = key ? chatMemRef.current.get(key) : null;
      // Комментарий на русском: если expectedHash передан и не совпадает с кэшированным — кэш устарел, игнорируем.
      // Это решает проблему "в списке вижу новое сообщение, а в чате старые".
      const cacheValid = !expectedHash || !cached?.chatHash || cached.chatHash === expectedHash;
      if (cached?.messages?.length && cacheValid) {
        // Комментарий на русском: открываем чат снизу (как Telegram), чтобы медиа не “подгружалось сверху”.
        const savedOffset = (() => {
          try {
            const v = localStorage.getItem(`lisad_dm_scroll_v1:${u.id}`);
            const n = v != null ? Number(v) : NaN;
            return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
          } catch {
            return 0;
          }
        })();
        const off = savedOffset < 120 ? 0 : savedOffset;
        // Комментарий на русском: старое восстановление scrollTop удалено — открываем диалог у низа через virtuoso.
        requestAnimationFrame(() => forceScrollToBottom(Number(u.id || 0) || 0));
        lastMessageIdRef.current = Number(cached.lastId || 0) || 0;
        currentChatHashRef.current = cached.chatHash || '';
        // Комментарий на русском: старые кэши могли сохранить hasMore=false (или вообще не считать hasMore для DM).
        // Для iOS/PWA используем best-effort: если в кэше набралось >= limit — значит “скорее всего” есть ещё.
        const guessHasMore = !!cached.hasMore || (Array.isArray(cached.messages) && cached.messages.length >= CHAT_PAGE_LIMIT);
        hasMoreMessagesRef.current = guessHasMore;
        setHasMoreMessages(guessHasMore);
        chatMessagesStore.getState().setChatSnapshot(key, cached.messages as any, { hasMore: guessHasMore, lastServerId: lastMessageIdRef.current });
        // Комментарий на русском: восстанавливаем фон DM из IndexedDB сразу (чтобы не ждать poll).
        try {
          const uid = getMyUserId();
          const peerId = Number(u.id || 0) || 0;
          if (uid && peerId) {
            void (async () => {
              const cachedWp = await chatKvGet<any>(offlineDmWallpaperKey(uid, peerId));
              const wpRaw = cachedWp?.value;
              const nextWp =
                wpRaw && typeof wpRaw === 'object' && typeof (wpRaw as any)?.id === 'number' && typeof (wpRaw as any)?.url === 'string'
                  ? {
                      id: Number((wpRaw as any).id || 0) || 0,
                      url: String((wpRaw as any).url || ''),
                      blur: Number((wpRaw as any).blur || 0) || 0,
                      brightness: Number((wpRaw as any).brightness || 100) || 100,
                    }
                  : null;
              if (selectedUserIdRef.current === peerId) {
                setDmWallpaper(nextWp && nextWp.id > 0 && nextWp.url ? nextWp : null);
              }
            })();
          }
        } catch {}
        // Комментарий на русском: не перезагружаем весь чат — догружаем только новые.
        void loadMessages(u.id, lastMessageIdRef.current);
        return;
      }
      // Комментарий на русском: кэш устарел или отсутствует — загружаем с нуля, пропуская кэш.
      chatMessagesStore.getState().clearChat(key);
      lastMessageIdRef.current = 0;
      currentChatHashRef.current = '';
      hasMoreMessagesRef.current = false;
      setHasMoreMessages(false);
      // skipCache=true — не читать IndexedDB, сразу идти на сервер
      void loadMessages(u.id, undefined, true);
    },
    [loadMessages]
  );

  const selectRoom = useCallback(
    (r: ChatRoom, expectedHash?: string) => {
      // Комментарий на русском: комнаты не смешиваем с DM — явно очищаем выбранного собеседника.
      setSupportOpen(false);
      setSupportTargetUser(null);
      setSupportMessages([]);
      supportLastIdRef.current = 0;

      // Комментарий на русском: старый флаг chatScrollReady удалён (virtuoso убирает “flash” без скрытия контейнера).

      // Комментарий на русском: обновляем refs синхронно, чтобы scroll-restore (useLayoutEffect) не видел "старый" чат.
      selectedUserIdRef.current = 0;
      selectedRoomIdRef.current = Number(r?.id || 0) || 0;
      setSelectedUser(null);
      // Комментарий на русском: сбрасываем UI фона при переключении чата (чтобы не мигал фон от прошлого диалога).
      setDmWallpaper(null);
      setDmWallpaperRequest(null);
      setDmWallpaperSrc('');
      setRoomWallpaper(null);
      setRoomWallpaperSrc('');
      setDmWallpaperOpen(false);
      setDmWallpaperFile(null);
      setDmWallpaperBlurDraft(0);
      setDmWallpaperToast(null);
      setDmWallpaperBusy(false);
      setSelectedRoom(r);
      // Комментарий на русском: если мы зашли в комнату — сразу сбрасываем бейдж непрочитанных (optimistic).
      setRooms((prev) => prev.map((c) => (c?.room?.id === r.id ? { ...c, unread_count: 0 } : c)));
      setRoomDraft('');
      setError(null);
      const key = `room:${Number(r.id || 0) || 0}`;
      const cached = key ? chatMemRef.current.get(key) : null;
      // Комментарий на русском: если expectedHash передан и не совпадает с кэшированным — кэш устарел.
      const cacheValid = !expectedHash || !cached?.chatHash || cached.chatHash === expectedHash;
      if (cached?.messages?.length && cacheValid) {
        // Комментарий на русском: читаем сохранённый скролл для комнаты (как для DM).
        const savedOffset = (() => {
          try {
            const v = localStorage.getItem(`lisad_room_scroll_v1:${r.id}`);
            const n = v != null ? Number(v) : NaN;
            return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
          } catch {
            return 0;
          }
        })();
        const off = savedOffset < 120 ? 0 : savedOffset;
        // Комментарий на русском: старое восстановление scrollTop удалено — открываем комнату у низа через virtuoso.
        requestAnimationFrame(() => forceScrollToBottom());
        const maxServerId = (cached.messages as any[]).reduce((mx: number, m: any) => {
          const id = Number(m?.id || 0) || 0;
          return id > 0 && id < 1_000_000_000_000 && id > mx ? id : mx;
        }, 0);
        lastMessageIdRef.current = maxServerId;
        currentChatHashRef.current = cached.chatHash || '';
        // Комментарий на русском: старые кэши могли сохранить hasMore=false (пока для комнат не считалось).
        // Поэтому, если сообщений >= page_limit, разрешаем попытку подгрузки истории (сервер сам вернёт пусто и мы выключим).
        const guessHasMore = !!cached.hasMore || (Array.isArray(cached.messages) && cached.messages.length >= CHAT_PAGE_LIMIT);
        hasMoreMessagesRef.current = guessHasMore;
        setHasMoreMessages(guessHasMore);
        chatMessagesStore.getState().setChatSnapshot(key, cached.messages as any, { hasMore: guessHasMore, lastServerId: maxServerId });
        // Комментарий на русском: восстанавливаем фон комнаты из IndexedDB сразу (чтобы не ждать poll).
        try {
          const uid = getMyUserId();
          const rid0 = Number(r.id || 0) || 0;
          if (uid && rid0) {
            void (async () => {
              const cachedWp = await chatKvGet<any>(offlineRoomWallpaperKey(uid, rid0));
              const wpRaw = cachedWp?.value;
              const nextWp =
                wpRaw && typeof wpRaw === 'object' && typeof (wpRaw as any)?.id === 'number' && typeof (wpRaw as any)?.url === 'string'
                  ? {
                      id: Number((wpRaw as any).id || 0) || 0,
                      url: String((wpRaw as any).url || ''),
                      blur: Number((wpRaw as any).blur || 0) || 0,
                      brightness: Number((wpRaw as any).brightness || 100) || 100,
                    }
                  : null;
              if (selectedRoomIdRef.current === rid0) {
                setRoomWallpaper(nextWp && nextWp.id > 0 && nextWp.url ? nextWp : null);
              }
            })();
          }
        } catch {}
        // Комментарий на русском: не перезагружаем весь чат — догружаем только новые.
        void loadRoomMessages(r.id, lastMessageIdRef.current);
        return;
      }
      chatMessagesStore.getState().clearChat(key);
      lastMessageIdRef.current = 0;
      currentChatHashRef.current = '';
      hasMoreMessagesRef.current = false;
      setHasMoreMessages(false);
      // skipCache=true — не читать IndexedDB, сразу идти на сервер
      void loadRoomMessages(r.id, undefined, true);
    },
    [loadRoomMessages]
  );

  const ensureUserProfile = useCallback(
    async (id: number): Promise<User | null> => {
      try {
        // Комментарий на русском: используем безопасный предпросмотр профиля (учёт приватности).
        const res = await apiCall(`/api/users/preview/${id}/`, { method: 'GET' });
        if (!res.ok) return null;
        const u = (await res.json()) as any;
        if (!u?.id) return null;
        return {
          id: (u.id as number) | 0,
          username: String(u.username || ''),
          display_name: String(u.display_name || u.username || ''),
          avatar_url: u.avatar_display_url ?? u.avatar_url ?? null,
          avatar_fx_color: u.avatar_fx_color ?? null,
          avatar_fx_thickness: u.avatar_fx_thickness ?? null,
          avatar_fx_glow: u.avatar_fx_glow ?? null,
          avatar_fx_offline_color: u.avatar_fx_offline_color ?? null,
        };
      } catch {
        return null;
      }
    },
    [apiCall]
  );

  // Инициализация (авторизация + данные)
  useEffect(() => {
    let token = '';
    try {
      token = getAccessToken() || '';
      setIsAuthed(!!token);
      const rawUser = getStoredUserRaw();
      if (rawUser) {
        const u = JSON.parse(rawUser);
        if (u?.id) {
          meRef.current = {
            id: (u.id as number) | 0,
            username: String(u.username || ''),
            nickname: String(u.nickname || ''),
            display_name: String(u.display_name || u.username || 'Вы'),
            avatar_url: u.avatar_url ?? null,
          };
        }
        // Роль/права нужны для "Сотрудники" и "Поддержка"
        myRoleRef.current = String(u?.role?.name || u?.role_name || '');
        myIsSuperRef.current = !!u?.is_superuser;
      }

      // Комментарий на русском: поднимаем офлайн-кэш/очередь ДО сетевых запросов, чтобы мессенджер открывался даже без интернета.
      const uid = getMyUserId();
      if (uid) void hydrateOfflineForUser(uid);
    } catch {
      setIsAuthed(false);
    } finally {
      setAuthChecked(true);
    }

    if (!token) {
      // Комментарий на русском: если токена нет — не делаем запросы и не зависаем в "Загрузка чатов".
      setLoadingChats(false);
      return;
    }

    // Комментарий на русском: первичная вкладка должна открыться максимально быстро —
    // второстепенные запросы запускаем с небольшой задержкой, чтобы не "душить" сеть/CPU.
    const isEmployee = myRoleRef.current === 'Сотрудник';
    let t1: number | undefined;
    let t2: number | undefined;

    if (isEmployee) {
      setTab('employees');
      void loadEmployees();
      t1 = window.setTimeout(() => {
        loadConversationsThrottled();
        loadRoomsThrottled();
      }, 200);
    } else {
      loadConversationsThrottled();
      loadRoomsThrottled();
    }

    // Комментарий на русском: настройки мессенджера грузим отдельно — они влияют на UI (плотность/компактность/ссылки).
    void loadChatSettings();

    t2 = window.setTimeout(() => {
      void loadFriends();
      void loadFriendRequests();
      void loadSupportUnread();
    }, 250);

    return () => {
      if (t1) window.clearTimeout(t1);
      if (t2) window.clearTimeout(t2);
    };
  }, [
    getMyUserId,
    hydrateOfflineForUser,
    loadConversationsThrottled,
    loadEmployees,
    loadFriendRequests,
    loadFriends,
    loadRoomsThrottled,
    loadSupportUnread,
  ]);

  useEffect(() => {
    if (!isAuthed || tab !== 'friends') return;
    const timer = window.setInterval(() => void loadFriends(), 25000);
    return () => window.clearInterval(timer);
  }, [isAuthed, loadFriends, tab]);

  // Комментарий на русском: синхронизация данных пользователя при изменении в других местах (настройки профиля).
  useEffect(() => {
    const onAuthChanged = () => {
      // Комментарий на русском: авторизация/пользователь могли смениться (логин/логаут/смена профиля) — синхронизируем и чистим RAM.
      try {
        const token = getAccessToken() || '';
        setIsAuthed(!!token);
      } catch {}

      const raw = getStoredUserRaw();
      if (!raw) {
        // Комментарий на русском: логаут — убираем данные прошлого пользователя из памяти.
        if (activeUidRef.current) resetRamCachesForUser(0);
        meRef.current = null;
        myRoleRef.current = '';
        myIsSuperRef.current = false;
        setMyProfile((prev: any) => (prev ? null : prev));
        return;
      }
      try {
        const u = JSON.parse(raw);
        const nextId = Number(u?.id || 0) || 0;
        if (nextId > 0 && activeUidRef.current > 0 && nextId !== activeUidRef.current) {
          resetRamCachesForUser(nextId);
          void hydrateOfflineForUser(nextId);
          loadConversationsThrottled();
          loadRoomsThrottled();
        }
        if (nextId > 0) {
          meRef.current = {
            id: (nextId as number) | 0,
            username: String(u.username || ''),
            nickname: String(u.nickname || ''),
            display_name: String(u.display_name || u.username || 'Вы'),
            avatar_url: u.avatar_url ?? null,
          };
          myRoleRef.current = String(u?.role?.name || u?.role_name || '');
          myIsSuperRef.current = !!u?.is_superuser;
          // Комментарий на русском: обновляем myProfile только если он уже загружен.
          setMyProfile((prev: any) => (prev ? { ...prev, ...u } : prev));
        }
      } catch {}
    };
    window.addEventListener('lisad_auth_changed', onAuthChanged);
    return () => window.removeEventListener('lisad_auth_changed', onAuthChanged);
  }, []);

  // Комментарий на русском: безопасность/настройки подгружаем только когда пользователь реально открыл вкладку "Настройки".
  useEffect(() => {
    if (!isAuthed) return;
    if (tab !== 'settings') return;
    void loadBlockedUsers();
    void loadSessions();
    if (!myProfile) void loadMyProfile();
  }, [isAuthed, loadBlockedUsers, loadMyProfile, loadSessions, myProfile, tab]);

  useEffect(() => {
    if (!isAuthed) return;
    if (tab !== 'settings') return;
    if (settingsSection !== 'storage') return;
    void calcStorageStats();
  }, [calcStorageStats, isAuthed, settingsSection, tab]);

  // Для суперадмина: inbox поддержки
  useEffect(() => {
    if (!isAuthed) return;
    if (!myIsSuperRef.current) return;
    void loadSupportInbox();
  }, [isAuthed, loadSupportInbox]);

  // Poll поддержки (unread + inbox)
  useEffect(() => {
    if (!isAuthed) return;
    const ms = pageVisible ? 12000 : 60000;
    const t = setInterval(() => {
      void loadSupportUnread();
      if (myIsSuperRef.current) void loadSupportInbox();
    }, ms);
    return () => clearInterval(t);
  }, [isAuthed, loadSupportInbox, loadSupportUnread, pageVisible]);

  // Комментарий на русском: глобальный long-poll списка чатов (DM + rooms) вместо частого setInterval.
  const listSyncStateRef = useRef<{ uid: number; hash: string }>({ uid: 0, hash: '' });
  useEffect(() => {
    if (!isAuthed) return;
    if (!pageVisible) return;
    let cancelled = false;
    const ac = new AbortController();
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    const run = async () => {
      const uid = getMyUserId();
      if (!uid) return;

      // Комментарий на русском: восстанавливаем локальный хэш списка (чтобы первый long-poll мог ждать, а не всегда "changed=true").
      if (listSyncStateRef.current.uid !== uid) {
        listSyncStateRef.current = { uid, hash: '' };
        try {
          const cached = await chatKvGet<string>(offlineChatListHashKey(uid));
          listSyncStateRef.current.hash = String(cached?.value || '') || '';
        } catch {
          // ignore
        }
      }

      let localHash = listSyncStateRef.current.hash || '';

      while (!cancelled) {
        try {
          // Комментарий на русском: если пользователь сменился — выходим (дальше запустится новый эффект).
          const uidNow = getMyUserId();
          if (!uidNow || uidNow !== uid) return;

          const qs = new URLSearchParams();
          if (localHash) qs.set('local_hash', localHash);
          qs.set('wait_ms', '25000');

          const res = await apiCallWithTimeout(`/api/chat/list-sync/?${qs.toString()}`, { method: 'GET', signal: ac.signal }, 26000);
          if (cancelled) return;
          if (res.status === 401) return;
          if (!res.ok) {
            await sleep(4000);
            continue;
          }

          const data = (await res.json()) as any;
          const serverHash = String(data?.server_hash || '') || '';
          const changed = !!data?.changed || (localHash && serverHash && serverHash !== localHash);

          if (serverHash) {
            localHash = serverHash;
            listSyncStateRef.current = { uid, hash: serverHash };
            try {
              void chatKvSet(offlineChatListHashKey(uid), serverHash);
            } catch {}
          }

          if (changed) {
            loadConversationsThrottled();
            loadRoomsThrottled();
            void loadSupportUnread();
          }

          await sleep(250);
        } catch {
          await sleep(4000);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      try {
        ac.abort();
      } catch {}
    };
  }, [apiCallWithTimeout, getMyUserId, isAuthed, loadConversationsThrottled, loadRoomsThrottled, loadSupportUnread, pageVisible]);

  useEffect(() => {
    if (!isAuthed) return;
    // Комментарий на русском: чтобы новые сообщения/комнаты подтягивались сразу, не дожидаясь poll.
    return subscribeUpdates(({ changes }) => {
      if (!changes.messages) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      loadConversationsThrottled();
      loadRoomsThrottled();
      void loadSupportUnread();
      // Комментарий на русском: обновляем сообщения в открытом чате при получении уведомления.
      const userId = selectedUserIdRef.current;
      const roomId = selectedRoomIdRef.current;
      if (userId > 0) {
        void loadMessages(userId, lastMessageIdRef.current);
      } else if (roomId > 0) {
        void loadRoomMessages(roomId, lastMessageIdRef.current);
      }
    });
  }, [isAuthed, loadConversationsThrottled, loadRoomsThrottled, loadSupportUnread, loadMessages, loadRoomMessages]);

  // Комментарий на русском: синхронизация настроек при возврате на вкладку (между устройствами).
  const settingsSyncLastRef = useRef(0);
  useEffect(() => {
    if (!isAuthed) return;
    if (!pageVisible) return;
    // Комментарий на русском: throttle — не чаще раза в 30 секунд.
    const now = Date.now();
    if (now - settingsSyncLastRef.current < 30_000) return;
    settingsSyncLastRef.current = now;
    void loadChatSettings();
  }, [isAuthed, loadChatSettings, pageVisible]);

  // Poll новых сообщений в открытом чате
  useEffect(() => {
    if (!selectedUser) return;
    if (!pageVisible) return;
    if (!isAuthed) return;
    let cancelled = false;
    const ac = new AbortController();
    let eventId = 0;

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    const run = async () => {
      // Комментарий на русском: восстанавливаем курсор синхронизации и хэш из IndexedDB.
      let localHash = '';
      try {
        const uid = getMyUserId();
        if (uid) {
          const cached = await chatKvGet<number>(offlineSyncCursorKey(uid, 'dm', selectedUser.id));
          const v = Number(cached?.value || 0) || 0;
          if (v > 0) eventId = v;
          const hashCached = await chatKvGet<string>(offlineChatHashKey(uid, 'dm', selectedUser.id));
          localHash = String(hashCached?.value || '') || '';
        }
      } catch {
        // ignore
      }
      while (!cancelled) {
        try {
          const qs = new URLSearchParams();
          qs.set('kind', 'dm');
          qs.set('target_id', String(selectedUser.id));
          qs.set('after_message_id', String(Number(lastMessageIdRef.current || 0) || 0));
          qs.set('after_event_id', String(Number(eventId || 0) || 0));
          qs.set('wait_ms', '30000');
          // Комментарий на русском: отправляем локальный хэш для сравнения с серверным.
          if (localHash) qs.set('local_hash', localHash);

          const res = await apiCallWithTimeout(`/api/chat/sync/?${qs.toString()}`, { method: 'GET', signal: ac.signal }, 31000);
          if (cancelled) return;
          if (res.status === 401) return;

          if (res.status === 204) {
            // Комментарий на русском: таймаут long-poll — просто ждём и повторяем.
            await sleep(4000);
            continue;
          }
          if (!res.ok) {
            await sleep(4000);
            continue;
          }

          const data = (await res.json()) as any;

          // Комментарий на русском: обновляем локальный хэш из ответа сервера.
          const serverHash = String(data?.server_hash || '') || '';
          if (serverHash) {
            localHash = serverHash;
            currentChatHashRef.current = serverHash;
            try {
              const uid = getMyUserId();
              if (uid) void chatKvSet(offlineChatHashKey(uid, 'dm', selectedUser.id), serverHash);
            } catch {}
          }

          // Комментарий на русском: force_reload — полная перезагрузка чата при рассинхроне (сервер перезапускался/долгий офлайн).
          // Сбрасываем кэш и lastMessageIdRef, чтобы не мержить старые сообщения с новыми.
          if (data?.force_reload) {
            eventId = 0;
            lastMessageIdRef.current = 0;
            chatMessagesStore.getState().clearChat(`dm:${selectedUser.id}`);
            // Комментарий на русском: очищаем кэш чата в IndexedDB.
            try {
              const uid = getMyUserId();
              if (uid) {
                void chatKvSet(offlineDmKey(uid, selectedUser.id), []);
                void chatKvSet(offlineSyncCursorKey(uid, 'dm', selectedUser.id), 0);
              }
            } catch {}
            // skipCache=true — кэш устарел
            void loadMessages(selectedUser.id, undefined, true);
            await sleep(2000);
            continue;
          }

          const nextEvent = Number(data?.next_event_id || 0) || 0;
          if (nextEvent > eventId) {
            eventId = nextEvent;
            try {
              const uid = getMyUserId();
              if (uid) void chatKvSet(offlineSyncCursorKey(uid, 'dm', selectedUser.id), eventId);
            } catch {}
          }

          const events = Array.isArray(data?.events) ? (data.events as any[]) : [];
          for (const e of events) {
            if (!e || typeof e !== 'object') continue;
            applyServerChatCommand(e);
          }

          if (data?.new_messages) {
            void loadMessages(selectedUser.id, lastMessageIdRef.current);
          }

          await sleep(4000);
        } catch {
          // Комментарий на русском: сеть/Abort — не спамим, просто повторяем с паузой.
          await sleep(4000);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      try {
        ac.abort();
      } catch {}
    };
  }, [applyServerChatCommand, apiCallWithTimeout, isAuthed, loadMessages, pageVisible, selectedUser]);

  // Poll новых сообщений в открытой комнате (группа/канал/помощник)
  useEffect(() => {
    if (!selectedRoom) return;
    if (!pageVisible) return;
    if (!isAuthed) return;
    let cancelled = false;
    const ac = new AbortController();
    let eventId = 0;

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    const run = async () => {
      // Комментарий на русском: восстанавливаем курсор синхронизации и хэш из IndexedDB.
      let localHash = '';
      try {
        const uid = getMyUserId();
        if (uid) {
          const cached = await chatKvGet<number>(offlineSyncCursorKey(uid, 'room', selectedRoom.id));
          const v = Number(cached?.value || 0) || 0;
          if (v > 0) eventId = v;
          const hashCached = await chatKvGet<string>(offlineChatHashKey(uid, 'room', selectedRoom.id));
          localHash = String(hashCached?.value || '') || '';
        }
      } catch {
        // ignore
      }
      while (!cancelled) {
        try {
          const qs = new URLSearchParams();
          qs.set('kind', 'room');
          qs.set('target_id', String(selectedRoom.id));
          qs.set('after_message_id', String(Number(lastMessageIdRef.current || 0) || 0));
          qs.set('after_event_id', String(Number(eventId || 0) || 0));
          qs.set('wait_ms', '30000');
          // Комментарий на русском: отправляем локальный хэш для сравнения с серверным.
          if (localHash) qs.set('local_hash', localHash);

          const res = await apiCallWithTimeout(`/api/chat/sync/?${qs.toString()}`, { method: 'GET', signal: ac.signal }, 31000);
          if (cancelled) return;
          if (res.status === 401) return;

          if (res.status === 204) {
            await sleep(4000);
            continue;
          }
          if (!res.ok) {
            await sleep(4000);
            continue;
          }

          const data = (await res.json()) as any;

          // Комментарий на русском: обновляем локальный хэш из ответа сервера.
          const serverHash = String(data?.server_hash || '') || '';
          if (serverHash) {
            localHash = serverHash;
            currentChatHashRef.current = serverHash;
            try {
              const uid = getMyUserId();
              if (uid) void chatKvSet(offlineChatHashKey(uid, 'room', selectedRoom.id), serverHash);
            } catch {}
          }

          // Комментарий на русском: force_reload — полная перезагрузка комнаты при рассинхроне.
          // Сбрасываем кэш и lastMessageIdRef, чтобы не мержить старые сообщения с новыми.
          if (data?.force_reload) {
            eventId = 0;
            lastMessageIdRef.current = 0;
            chatMessagesStore.getState().clearChat(`room:${selectedRoom.id}`);
            // Комментарий на русском: очищаем кэш комнаты в IndexedDB.
            try {
              const uid = getMyUserId();
              if (uid) {
                void chatKvSet(offlineRoomKey(uid, selectedRoom.id), []);
                void chatKvSet(offlineSyncCursorKey(uid, 'room', selectedRoom.id), 0);
              }
            } catch {}
            // skipCache=true — кэш устарел
            void loadRoomMessages(selectedRoom.id, undefined, true);
            await sleep(2000);
            continue;
          }

          const nextEvent = Number(data?.next_event_id || 0) || 0;
          if (nextEvent > eventId) {
            eventId = nextEvent;
            try {
              const uid = getMyUserId();
              if (uid) void chatKvSet(offlineSyncCursorKey(uid, 'room', selectedRoom.id), eventId);
            } catch {}
          }

          const events = Array.isArray(data?.events) ? (data.events as any[]) : [];
          for (const e of events) {
            if (!e || typeof e !== 'object') continue;
            applyServerChatCommand(e);
          }

          if (data?.new_messages) {
            void loadRoomMessages(selectedRoom.id, lastMessageIdRef.current);
          }

          await sleep(4000);
        } catch {
          await sleep(4000);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      try {
        ac.abort();
      } catch {}
    };
  }, [applyServerChatCommand, apiCallWithTimeout, isAuthed, loadRoomMessages, pageVisible, selectedRoom]);

  // Комментарий на русском: голосовые теперь сохраняются через HTTP (PHP API) и приходят через обычный poll истории.

  // Пункт 2: read-receipts (обновление статусов исходящих сообщений у отправителя)
  useEffect(() => {
    if (!selectedUser) return;
    if (!pageVisible) return;
    void pollReadReceipts(selectedUser.id);
    const t = setInterval(() => void pollReadReceipts(selectedUser.id), 5000);
    return () => clearInterval(t);
  }, [selectedUser, pollReadReceipts, pageVisible]);

  // Пункт 3: статус собеседника + typing (без WS)
  useEffect(() => {
    if (!selectedUser) {
      setPeerOnline(false);
      setPeerLastSeen(null);
      setPeerTyping(false);
      setPeerDeviceType(null);
      return;
    }
    if (!pageVisible) return;
    const uid = selectedUser.id;
    let alive = true;
    const load = async () => {
      try {
        const res = await apiCall(`/api/chat/status/user/${uid}/`);
        if (!res.ok) return;
        const data = (await res.json()) as any;
        if (!alive) return;
        setPeerOnline(!!data?.is_online);
        setPeerLastSeen(data?.last_seen ? String(data.last_seen) : null);
        setPeerTyping(!!data?.is_typing);
        const dt = String(data?.device_type || '').toLowerCase();
        setPeerDeviceType(dt === 'mobile' || dt === 'tablet' || dt === 'desktop' ? (dt as any) : null);
        setSelectedUser((prev) => (prev?.id === uid ? { ...prev, game_presence: data?.game_presence ?? null } : prev));
      } catch {
        // ignore
      }
    };
    void load();
    const t = setInterval(load, 2500);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [apiCall, pageVisible, selectedUser?.id]);

  // Poll поддержки (без WS, через PHP API)
  useEffect(() => {
    if (!supportOpen) return;
    if (myIsSuperRef.current && !supportTargetUser) return;
    if (!pageVisible) return;

    // Комментарий на русском: немедленный вызов при активации (возврат на страницу).
    // Не вызываем если after=0 — первую загрузку запускает открытие чата поддержки.
    const after = supportLastIdRef.current;
    if (after > 0) {
      void loadSupportMessages(myIsSuperRef.current ? supportTargetUser?.id : undefined, after);
    }

    const t = setInterval(() => {
      const afterNow = supportLastIdRef.current;
      void loadSupportMessages(myIsSuperRef.current ? supportTargetUser?.id : undefined, afterNow);
    }, POLL_MESSAGES_MS);

    return () => clearInterval(t);
  }, [supportOpen, supportTargetUser, loadSupportMessages, pageVisible]);

  // Комментарий на русском: автоскролл для личных сообщений делаем точечно (в loadMessages/send),
  // чтобы при подгрузке истории вверх чат не "прыгал".

  useEffect(() => {
    if (!supportOpen) return;
    if (myIsSuperRef.current && !supportTargetUser) return;
    supportMessagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [supportMessages, supportOpen, supportTargetUser]);

  // Открытие чата из URL (?user=ID), без позднего переключения после нового перехода.
  const urlUserRef = useRef<string | null>(null);
  const requestedUser = searchParams.get('user');
  useEffect(() => {
    const id = parseContextId(requestedUser);
    if (!isAuthed || !id || urlUserRef.current === requestedUser) return;
    let alive = true;
    const previousPeerId = selectedUserIdRef.current;

    const fromConvs = conversations.find((c) => c.user?.id === id)?.user;
    const fromFriends = friends.find((f) => f.id === id);
    if (fromConvs) {
      urlUserRef.current = requestedUser;
      selectUser(fromConvs);
      return;
    }
    if (fromFriends) {
      urlUserRef.current = requestedUser;
      selectUser(fromFriends);
      return;
    }

    (async () => {
      const profile = await ensureUserProfile(id);
      if (alive && profile && selectedUserIdRef.current === previousPeerId) {
        urlUserRef.current = requestedUser;
        selectUser(profile);
      }
    })();
    return () => { alive = false; };
  }, [requestedUser, isAuthed, conversations, friends, ensureUserProfile, selectUser]);

  // Открытие комнаты из URL (?room=ID)
  const urlRoomRef = useRef<string | null>(null);
  useEffect(() => {
    const r = searchParams.get('room');
    if (r) urlRoomRef.current = r;
  }, [searchParams]);

  useEffect(() => {
    if (!urlRoomRef.current) return;
    const id = parseInt(urlRoomRef.current, 10);
    if (!id || Number.isNaN(id)) return;

    const fromRooms = rooms.find((c: any) => Number(c?.room?.id || 0) === id)?.room;
    if (fromRooms) {
      urlRoomRef.current = null;
      selectRoom(fromRooms);
      return;
    }

    (async () => {
      try {
        const res = await apiCall(`/api/chat/rooms/${id}/meta/`);
        if (!res.ok) return;
        const data = (await res.json()) as any;
        const room = data?.room;
        if (room?.id) {
          urlRoomRef.current = null;
          selectRoom(room);
        }
      } catch {
        // ignore
      }
    })();
  }, [apiCall, rooms, selectRoom]);

  // Открытие поддержки из URL (?support=1[&support_user=ID])
  const urlSupportHandledRef = useRef(false);
  useEffect(() => {
    if (urlSupportHandledRef.current) return;
    if (!isAuthed) return;

    const s = searchParams.get('support');
    if (s !== '1') return;
    urlSupportHandledRef.current = true;

    setSupportOpen(true);
    // Комментарий на русском: при входе в поддержку сразу убираем бейдж (optimistic).
    setSupportUnread(0);
    setSelectedUser(null);
    setSelectedRoom(null);
    chatMessagesStore.setState({ chats: {} });
    lastMessageIdRef.current = 0;
    setError(null);
    setSupportMessages([]);
    supportLastIdRef.current = 0;

    const supportUserParam = searchParams.get('support_user');
    const supportUserId = supportUserParam ? parseInt(supportUserParam, 10) : 0;

    void loadSupportUnread();

    if (myIsSuperRef.current) {
      void loadSupportInbox();
      if (supportUserId > 0) {
        (async () => {
          const profile = await ensureUserProfile(supportUserId);
          if (!profile) return;
          setSupportTargetUser(profile);
          setSupportMessages([]);
          supportLastIdRef.current = 0;
          void loadSupportMessages(profile.id);
        })();
      } else {
        setSupportTargetUser(null);
      }
    } else {
      setSupportTargetUser(null);
      void loadSupportMessages();
    }
  }, [ensureUserProfile, isAuthed, loadSupportInbox, loadSupportMessages, loadSupportUnread, searchParams]);

  const filteredConversations = useMemo(() => {
    const q = chatSearch.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => {
      const name = (c.user?.display_name || c.user?.username || '').toLowerCase();
      const last = (c.last_message?.message || '').toLowerCase();
      return name.includes(q) || last.includes(q);
    });
  }, [conversations, chatSearch]);

  const filteredRooms = useMemo(() => {
    const q = chatSearch.trim().toLowerCase();
    if (!q) return rooms;
    return rooms.filter((c) => {
      const title = (c.room?.title || '').toLowerCase();
      const last = (c.last_message?.message || '').toLowerCase();
      return title.includes(q) || last.includes(q);
    });
  }, [rooms, chatSearch]);

  const closeCreateMenu = useCallback(() => setCreateMenuOpen(false), []);

  const recalcCreateMenuPos = useCallback(() => {
    const btn = createBtnRef.current;
    const menu = createMenuRef.current;
    if (!btn || !menu) return;
    const br = btn.getBoundingClientRect();
    const mr = menu.getBoundingClientRect();
    const pad = 8;
    const gap = 8;

    // Комментарий на русском: выравниваем по правому краю кнопки, меню — ниже кнопки.
    let left = br.right - mr.width;
    left = Math.max(pad, Math.min(left, window.innerWidth - pad - mr.width));

    let top = br.bottom + gap;
    if (top + mr.height > window.innerHeight - pad) {
      top = br.top - gap - mr.height;
    }
    top = Math.max(pad, Math.min(top, window.innerHeight - pad - mr.height));

    setCreateMenuPos({ left: Math.round(left), top: Math.round(top) });
  }, []);

  useLayoutEffect(() => {
    if (!createMenuOpen) {
      setCreateMenuPos(null);
      return;
    }
    recalcCreateMenuPos();
  }, [createMenuOpen, recalcCreateMenuPos]);

  useEffect(() => {
    if (!createMenuOpen) return;
    const onResize = () => recalcCreateMenuPos();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [createMenuOpen, recalcCreateMenuPos]);

  useEffect(() => {
    if (!createMenuOpen) return;
    const onPointerDown = (e: Event) => {
      const t = e.target as Node | null;
      if (t && createMenuRef.current?.contains(t)) return;
      if (t && createBtnRef.current?.contains(t)) return;
      closeCreateMenu();
    };
    const onAnyScroll = () => closeCreateMenu();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCreateMenu();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('scroll', onAnyScroll, true);
    document.addEventListener('wheel', onAnyScroll, { capture: true, passive: true });
    document.addEventListener('touchmove', onAnyScroll, { capture: true, passive: true });
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('scroll', onAnyScroll, true);
      document.removeEventListener('wheel', onAnyScroll, true as any);
      document.removeEventListener('touchmove', onAnyScroll, true as any);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [closeCreateMenu, createMenuOpen]);

  const createRoom = useCallback(
    async (type: RoomType) => {
      closeCreateMenu();
      const def = type === 'channel' ? 'Новый канал' : type === 'assistant' ? 'Помощник' : 'Новая группа';
      const title =
        type === 'assistant'
          ? def
          : String(prompt(type === 'channel' ? 'Название канала' : 'Название группы', def) || '').trim();
      if (!title) return;

      try {
        const res = await apiCall('/api/chat/rooms/', { method: 'POST', body: JSON.stringify({ type, title }) });
        if (!res.ok) {
          let msg = `Не удалось создать (HTTP ${res.status})`;
          try {
            const data = await res.json();
            if (data?.error) msg = String(data.error);
            if (data?.hint) msg = `${msg}. ${String(data.hint)}`;
          } catch {
            // ignore
          }
          setError(msg);
          setTimeout(() => setError(null), 2500);
          return;
        }
        const data = (await res.json()) as any;
        const r = data?.room;
        const rid = Number(r?.id || 0) || 0;
        if (!rid) return;
        const created: ChatRoom = { id: rid, type: (r?.type as RoomType) || type, title: String(r?.title || title) };
        loadRoomsThrottled();
        selectRoom(created);
        // Комментарий на русском: после создания пользователь сразу хочет печатать в новой комнате —
        // форсим фокус в поле ввода (в том числе на мобилке).
        requestAnimationFrame(() => {
          try {
            (roomDraftRef.current as any)?.focus?.({ preventScroll: true });
          } catch {
            roomDraftRef.current?.focus();
          }
        });
      } catch {
        setError('Ошибка сети');
        setTimeout(() => setError(null), 2500);
      }
    },
    [apiCall, closeCreateMenu, loadRoomsThrottled, selectRoom]
  );

  const roomCanManage = !!roomMeta && (roomMeta.my_role === 'owner' || roomMeta.my_role === 'admin');
  const roomMemberIdSet = useMemo(() => new Set(roomMembers.map((m) => m.user?.id || 0).filter((x) => x > 0)), [roomMembers]);
  const roomProjectIdSet = useMemo(() => new Set(roomProjects.map((p) => p.id || 0).filter((x) => x > 0)), [roomProjects]);

  const closeMemberAddMenu = useCallback(() => setMemberAddOpen(false), []);
  const closeProjectAddMenu = useCallback(() => setProjectAddOpen(false), []);

  const toggleMemberAddMenu = useCallback(() => {
    // Комментарий на русском: одновременно держим открытым только один picker.
    setProjectAddOpen(false);
    setMemberAddOpen((v) => {
      const next = !v;
      if (next) {
        setMemberAddQuery('');
        setMemberAddResults([]);
        setMemberAddLoading(false);
      }
      return next;
    });
  }, []);

  const toggleProjectAddMenu = useCallback(() => {
    setMemberAddOpen(false);
    setProjectAddOpen((v) => {
      const next = !v;
      if (next) {
        setProjectAddQuery('');
        setProjectAddLoading(false);
      }
      return next;
    });
  }, []);

  const recalcMemberAddPos = useCallback(() => {
    const btn = memberAddBtnRef.current;
    const menu = memberAddMenuRef.current;
    if (!btn || !menu) return;
    const br = btn.getBoundingClientRect();
    const mr = menu.getBoundingClientRect();
    const pad = 8;
    const gap = 8;
    let left = br.right - mr.width;
    left = Math.max(pad, Math.min(left, window.innerWidth - pad - mr.width));
    let top = br.bottom + gap;
    if (top + mr.height > window.innerHeight - pad) top = br.top - gap - mr.height;
    top = Math.max(pad, Math.min(top, window.innerHeight - pad - mr.height));
    setMemberAddPos({ left: Math.round(left), top: Math.round(top) });
  }, []);

  const recalcProjectAddPos = useCallback(() => {
    const btn = projectAddBtnRef.current;
    const menu = projectAddMenuRef.current;
    if (!btn || !menu) return;
    const br = btn.getBoundingClientRect();
    const mr = menu.getBoundingClientRect();
    const pad = 8;
    const gap = 8;
    let left = br.right - mr.width;
    left = Math.max(pad, Math.min(left, window.innerWidth - pad - mr.width));
    let top = br.bottom + gap;
    if (top + mr.height > window.innerHeight - pad) top = br.top - gap - mr.height;
    top = Math.max(pad, Math.min(top, window.innerHeight - pad - mr.height));
    setProjectAddPos({ left: Math.round(left), top: Math.round(top) });
  }, []);

  useLayoutEffect(() => {
    if (!memberAddOpen) {
      setMemberAddPos(null);
      return;
    }
    recalcMemberAddPos();
  }, [memberAddOpen, recalcMemberAddPos]);
  useLayoutEffect(() => {
    if (!projectAddOpen) {
      setProjectAddPos(null);
      return;
    }
    recalcProjectAddPos();
  }, [projectAddOpen, recalcProjectAddPos]);

  useEffect(() => {
    if (!memberAddOpen) return;
    const onResize = () => recalcMemberAddPos();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [memberAddOpen, recalcMemberAddPos]);
  useEffect(() => {
    if (!projectAddOpen) return;
    const onResize = () => recalcProjectAddPos();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [projectAddOpen, recalcProjectAddPos]);

  useEffect(() => {
    if (!memberAddOpen) return;
    const onPointerDown = (e: Event) => {
      const t = e.target as Node | null;
      if (t && memberAddMenuRef.current?.contains(t)) return;
      if (t && memberAddBtnRef.current?.contains(t)) return;
      closeMemberAddMenu();
    };
    const onAnyScroll = (e: Event) => {
      // Комментарий на русском: iOS при фокусе input может дергать scroll/viewport => меню закрывалось и клавиатура “моргала”.
      // Поэтому если фокус сейчас внутри меню — не закрываем.
      const ae = (typeof document !== 'undefined' ? (document.activeElement as Node | null) : null) || null;
      if (ae && memberAddMenuRef.current?.contains(ae)) return;
      const t = e.target as Node | null;
      if (t && memberAddMenuRef.current?.contains(t)) return;
      if (t && memberAddBtnRef.current?.contains(t)) return;
      closeMemberAddMenu();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMemberAddMenu();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('scroll', onAnyScroll, true);
    document.addEventListener('wheel', onAnyScroll, { capture: true, passive: true });
    document.addEventListener('touchmove', onAnyScroll, { capture: true, passive: true });
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('scroll', onAnyScroll, true);
      document.removeEventListener('wheel', onAnyScroll, true as any);
      document.removeEventListener('touchmove', onAnyScroll, true as any);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [closeMemberAddMenu, memberAddOpen]);

  useEffect(() => {
    if (!projectAddOpen) return;
    const onPointerDown = (e: Event) => {
      const t = e.target as Node | null;
      if (t && projectAddMenuRef.current?.contains(t)) return;
      if (t && projectAddBtnRef.current?.contains(t)) return;
      closeProjectAddMenu();
    };
    const onAnyScroll = (e: Event) => {
      // Комментарий на русском: iOS при фокусе input может дергать scroll/viewport => меню закрывалось и клавиатура “моргала”.
      // Поэтому если фокус сейчас внутри меню — не закрываем.
      const ae = (typeof document !== 'undefined' ? (document.activeElement as Node | null) : null) || null;
      if (ae && projectAddMenuRef.current?.contains(ae)) return;
      const t = e.target as Node | null;
      if (t && projectAddMenuRef.current?.contains(t)) return;
      if (t && projectAddBtnRef.current?.contains(t)) return;
      closeProjectAddMenu();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeProjectAddMenu();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('scroll', onAnyScroll, true);
    document.addEventListener('wheel', onAnyScroll, { capture: true, passive: true });
    document.addEventListener('touchmove', onAnyScroll, { capture: true, passive: true });
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('scroll', onAnyScroll, true);
      document.removeEventListener('wheel', onAnyScroll, true as any);
      document.removeEventListener('touchmove', onAnyScroll, true as any);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [closeProjectAddMenu, projectAddOpen]);

  useEffect(() => {
    if (!memberAddOpen) return;
    requestAnimationFrame(() => memberAddInputRef.current?.focus());
  }, [memberAddOpen]);
  useEffect(() => {
    if (!projectAddOpen) return;
    requestAnimationFrame(() => projectAddInputRef.current?.focus());
  }, [projectAddOpen]);

  // Комментарий на русском: поиск пользователей для добавления в группу/канал.
  useEffect(() => {
    if (!memberAddOpen) return;
    const q = memberAddQuery.trim();
    if (q.length < 2) {
      setMemberAddResults([]);
      setMemberAddLoading(false);
      return;
    }

    const ac = new AbortController();
    const t = window.setTimeout(() => {
      setMemberAddLoading(true);
      apiCall(`/api/chat/users/search/?q=${encodeURIComponent(q)}`, { signal: ac.signal })
        .then(async (res) => {
          if (!res.ok) return [];
          try {
            const data = (await res.json()) as any;
            return Array.isArray(data) ? (data as SearchUser[]) : [];
          } catch {
            return [];
          }
        })
        .then((items) => {
          if (ac.signal.aborted) return;
          setMemberAddResults(items || []);
        })
        .finally(() => {
          if (ac.signal.aborted) return;
          setMemberAddLoading(false);
        });
    }, 180);

    return () => {
      window.clearTimeout(t);
      ac.abort();
    };
  }, [apiCall, memberAddOpen, memberAddQuery]);

  const loadRoomMeta = useCallback(
    async (roomId: number, opts?: { syncDrafts?: boolean }) => {
      try {
        const res = await apiCall(`/api/chat/rooms/${roomId}/meta/`);
        if (!res.ok) return;
        const data = (await res.json()) as any;
        const r = data?.room || {};
        const id = Number(r?.id || 0) || 0;
        if (!id) return;
        const meta = {
          id,
          type: (String(r?.type || 'group') as RoomType) || 'group',
          title: String(r?.title || ''),
          description: String(r?.description || ''),
          avatar_url: r?.avatar_url ? String(r.avatar_url) : null,
          members_count: Number(data?.members_count || 0) || 0,
          online_count: Number(data?.online_count || 0) || 0,
          my_role: (String(data?.my_role || '') as any) || 'member',
        };
        setRoomMeta(meta);
        if (opts?.syncDrafts) {
          setRoomTitleDraft(meta.title);
          setRoomDescDraft(meta.description);
        }
        setSelectedRoom((prev) => (prev && prev.id === id ? { ...prev, title: meta.title, type: meta.type } : prev));
      } catch {
        // ignore
      }
    },
    [apiCall]
  );

  const saveRoomMeta = useCallback(async () => {
    if (!selectedRoom) return;
    if (!roomCanManage) return;
    const title = String(roomTitleDraft || '').trim();
    const description = String(roomDescDraft || '').trim();
    if (!title) return;
    setRoomMetaBusy(true);
    try {
      const res = await apiCall(`/api/chat/rooms/${selectedRoom.id}/meta/`, {
        method: 'PATCH',
        body: JSON.stringify({ title, description }),
      });
      if (!res.ok) return;
      void loadRoomMeta(selectedRoom.id, { syncDrafts: true });
      loadRoomsThrottled();
    } finally {
      setRoomMetaBusy(false);
    }
  }, [apiCall, loadRoomMeta, loadRoomsThrottled, roomCanManage, roomDescDraft, roomTitleDraft, selectedRoom]);

  const pushRoomToast = useCallback((msg: string) => {
    setRoomSettingsToast(msg);
    window.setTimeout(() => setRoomSettingsToast(null), 2500);
  }, []);

  const openRoomAvatarPicker = useCallback(() => {
    if (!selectedRoom) return;
    if (!roomCanManage) return;
    if (selectedRoom.type === 'assistant') return;
    // Комментарий на русском: закрываем прочие выпадающие меню, чтобы фото-кроппер/инпут не конфликтовали по z-index.
    setMemberAddOpen(false);
    setProjectAddOpen(false);
    roomAvatarInputRef.current?.click();
  }, [roomCanManage, selectedRoom]);

  const handleRoomAvatarSelect = useCallback(
    (e: any) => {
      const file: File | undefined = e?.target?.files?.[0];
      try {
        if (roomAvatarInputRef.current) roomAvatarInputRef.current.value = '';
      } catch {}
      if (!file) return;

      // Комментарий на русском: ограничения как в профиле.
      if (file.size > 10 * 1024 * 1024) {
        pushRoomToast('Размер файла не должен превышать 10MB');
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        pushRoomToast('Недопустимый тип файла. Разрешены: JPEG, PNG, GIF, WebP');
        return;
      }

      setRoomAvatarCropFile(file);
    },
    [pushRoomToast]
  );

  const uploadRoomAvatar = useCallback(
    async (croppedFile: File) => {
      if (!selectedRoom) return;
      if (!roomCanManage) return;
      if (selectedRoom.type === 'assistant') return;

      setRoomAvatarUploading(true);
      setRoomSettingsToast(null);

      try {
        const fd = new FormData();
        fd.append('avatar', croppedFile);
        const res = await apiCall(`/api/chat/rooms/${selectedRoom.id}/avatar/`, { method: 'POST', body: fd });
        if (!res.ok) {
          let msg = `Не удалось загрузить фото (HTTP ${res.status})`;
          try {
            const data = await res.json();
            const e = data?.detail || data?.error;
            if (e) msg = String(e);
            if (data?.hint) msg = `${msg}. ${String(data.hint)}`;
          } catch {
            // ignore
          }
          pushRoomToast(msg);
          return;
        }
        const data = (await res.json()) as any;
        const url = typeof data?.avatar_url === 'string' ? String(data.avatar_url) : '';
        if (url) {
          setRoomMeta((prev) => (prev && prev.id === selectedRoom.id ? { ...prev, avatar_url: url } : prev));
        }
        void loadRoomMeta(selectedRoom.id, { syncDrafts: true });
        pushRoomToast('Фото сохранено');
      } catch {
        pushRoomToast('Ошибка сети');
      } finally {
        setRoomAvatarUploading(false);
      }
    },
    [apiCall, loadRoomMeta, pushRoomToast, roomCanManage, selectedRoom]
  );

  const loadRoomMembers = useCallback(
    async (roomId: number) => {
      try {
        const res = await apiCall(`/api/chat/rooms/${roomId}/members/`);
        if (!res.ok) return;
        const data = (await res.json()) as any;
        if (Array.isArray(data)) setRoomMembers(data as any);
      } catch {
        // ignore
      }
    },
    [apiCall]
  );

  const addRoomMemberByUserId = useCallback(
    async (userId: number) => {
      if (!selectedRoom) return false;
      if (!roomCanManage) return false;
      const uid = Number(userId || 0) || 0;
      if (uid <= 0) return false;
      if (roomMemberIdSet.has(uid)) return true; // уже есть
      try {
        const res = await apiCall(`/api/chat/rooms/${selectedRoom.id}/members/`, {
          method: 'POST',
          body: JSON.stringify({ user_id: uid }),
        });
        if (!res.ok) return false;
        void loadRoomMembers(selectedRoom.id);
        void loadRoomMeta(selectedRoom.id, { syncDrafts: false });
        return true;
      } catch {
        return false;
      }
    },
    [apiCall, loadRoomMembers, loadRoomMeta, roomCanManage, roomMemberIdSet, selectedRoom]
  );

  const pickRoomMember = useCallback(
    async (u: SearchUser) => {
      const uid = Number(u?.id || 0) || 0;
      if (uid <= 0) return;
      const ok = await addRoomMemberByUserId(uid);
      if (!ok) return;
      closeMemberAddMenu();
      setMemberAddQuery('');
      setMemberAddResults([]);
    },
    [addRoomMemberByUserId, closeMemberAddMenu]
  );

  const loadRoomProjects = useCallback(
    async (roomId: number) => {
      try {
        const res = await apiCall(`/api/chat/rooms/${roomId}/projects/`);
        if (!res.ok) return;
        const data = (await res.json()) as any;
        if (Array.isArray(data)) setRoomProjects(data as any);
      } catch {
        // ignore
      }
    },
    [apiCall]
  );

  const loadMyProjects = useCallback(async () => {
    try {
      const res = await apiCall('/api/projects/my/');
      if (!res.ok) return;
      const data = (await res.json()) as any;
      if (!Array.isArray(data)) return;
      const out = data
        .map((p: any) => ({
          id: Number(p?.id || 0) || 0,
          name: String(p?.name || ''),
          status: p?.status ? String(p.status) : undefined,
          progress: typeof p?.progress === 'number' ? p.progress : Number(p?.progress || 0) || 0,
        }))
        .filter((p: any) => p.id > 0 && p.name);
      setMyProjects(out);
    } catch {
      // ignore
    }
  }, [apiCall]);

  const addRoomProjectById = useCallback(
    async (projectId: number) => {
      if (!selectedRoom) return false;
      if (!roomCanManage) return false;
      const pid = Number(projectId || 0) || 0;
      if (pid <= 0) return false;
      if (roomProjectIdSet.has(pid)) return true;
      try {
        const res = await apiCall(`/api/chat/rooms/${selectedRoom.id}/projects/`, {
          method: 'POST',
          body: JSON.stringify({ project_id: pid }),
        });
        if (!res.ok) return false;
        void loadRoomProjects(selectedRoom.id);
        return true;
      } catch {
        return false;
      }
    },
    [apiCall, loadRoomProjects, roomCanManage, roomProjectIdSet, selectedRoom]
  );

  const projectAddCandidates = useMemo(() => {
    const q = projectAddQuery.trim().toLowerCase();
    const base = myProjects.filter((p) => p.id > 0 && !roomProjectIdSet.has(p.id));
    if (!q) return base;
    return base.filter((p) => (p.name || '').toLowerCase().includes(q) || String(p.id).includes(q));
  }, [myProjects, projectAddQuery, roomProjectIdSet]);

  // Комментарий на русском: при открытии выбора проекта подтягиваем "мои проекты" (участник/владелец).
  useEffect(() => {
    if (!projectAddOpen) return;
    if (myProjects.length) return;
    setProjectAddLoading(true);
    Promise.resolve(loadMyProjects()).finally(() => setProjectAddLoading(false));
  }, [loadMyProjects, myProjects.length, projectAddOpen]);

  const pickRoomProject = useCallback(
    async (projectId: number) => {
      const pid = Number(projectId || 0) || 0;
      if (pid <= 0) return;
      const ok = await addRoomProjectById(pid);
      if (!ok) return;
      closeProjectAddMenu();
      setProjectAddQuery('');
    },
    [addRoomProjectById, closeProjectAddMenu]
  );

  const loadRoomAssets = useCallback(
    async (roomId: number) => {
      try {
        const p = await apiCall(`/api/chat/rooms/${roomId}/assets/?kind=photo`);
        if (p.ok) {
          const data = (await p.json()) as any;
          if (Array.isArray(data)) {
            const arr = data
              .map((x: any) => ({ id: Number(x?.id || 0) || 0, url: String(x?.url || ''), created_at: String(x?.created_at || '') }))
              .filter((x: any) => x.id > 0 && x.url);
            setRoomPhotos(arr);
          }
        }
        const f = await apiCall(`/api/chat/rooms/${roomId}/assets/?kind=file`);
        if (f.ok) {
          const data = (await f.json()) as any;
          if (Array.isArray(data)) {
            const arr = data
              .map((x: any) => ({
                id: Number(x?.id || 0) || 0,
                url: String(x?.url || ''),
                name: x?.name ? String(x.name) : undefined,
                created_at: String(x?.created_at || ''),
              }))
              .filter((x: any) => x.id > 0 && x.url);
            setRoomFiles(arr);
          }
        }
      } catch {
        // ignore
      }
    },
    [apiCall]
  );

  // Комментарий на русском: при смене комнаты сбрасываем настройки/кэш данных, чтобы не показывать “чужое”.
  useEffect(() => {
    setRoomSettingsOpen(false);
    setRoomSettingsTab('members');
    setRoomMeta(null);
    setRoomMembers([]);
    setRoomProjects([]);
    setRoomPhotos([]);
    setRoomFiles([]);
    setRoomTitleDraft('');
    setRoomDescDraft('');
    setRoomMetaBusy(false);
    setRoomAvatarCropFile(null);
    setRoomAvatarUploading(false);
    setRoomSettingsToast(null);
    setMemberAddOpen(false);
    setMemberAddPos(null);
    setMemberAddQuery('');
    setMemberAddLoading(false);
    setMemberAddResults([]);
    setProjectAddOpen(false);
    setProjectAddPos(null);
    setProjectAddQuery('');
    setProjectAddLoading(false);
  }, [selectedRoom?.id]);

  useEffect(() => {
    // Комментарий на русском: если закрыли настройки комнаты — закрываем и выпадающие меню.
    if (roomSettingsOpen) return;
    setMemberAddOpen(false);
    setProjectAddOpen(false);
    setRoomAvatarCropFile(null);
  }, [roomSettingsOpen]);

  useEffect(() => {
    // Комментарий на русском: при переключении вкладок закрываем нерелевантные выпадающие меню.
    if (roomSettingsTab !== 'members') setMemberAddOpen(false);
    if (roomSettingsTab !== 'projects') setProjectAddOpen(false);
  }, [roomSettingsTab]);

  // Комментарий на русском: при открытии настроек подгружаем всё для вкладок (мемберы/проекты/медиа/файлы).
  useEffect(() => {
    if (!roomSettingsOpen) return;
    const rid = selectedRoom?.id || 0;
    if (!rid) return;
    void loadRoomMeta(rid, { syncDrafts: true });
    void loadRoomMembers(rid);
    void loadRoomProjects(rid);
    void loadRoomAssets(rid);
  }, [loadRoomAssets, loadRoomMembers, loadRoomMeta, loadRoomProjects, roomSettingsOpen, selectedRoom?.id]);

  // Комментарий на русском: для "таблетки" в чате нужны счётчики участников/онлайн — грузим meta при выборе комнаты.
  useEffect(() => {
    const rid = selectedRoom?.id || 0;
    if (!rid) return;
    // Важно: не перетираем черновики, если пользователь редактирует настройки.
    if (roomSettingsOpen) return;
    void loadRoomMeta(rid, { syncDrafts: false });
  }, [loadRoomMeta, roomSettingsOpen, selectedRoom?.id]);

  const msgKeyOf = useCallback((m: DirectMessage) => {
    const cid = (m as any)?.client_id;
    return cid ? String(cid) : String(m?.id || '');
  }, []);

  const toggleSelectMessage = useCallback(
    (m: DirectMessage) => {
      const key = msgKeyOf(m);
      if (!key) return;
      setSelectedMsgKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    },
    [msgKeyOf]
  );

  const clearSelectedMessages = useCallback(() => setSelectedMsgKeys([]), []);

  const scrollToMessageById = useCallback((id: number) => {
    const mid = Number(id || 0) || 0;
    if (mid <= 0) return;
    // Комментарий на русском: используем virtuoso для скролла к сообщению.
    if (virtuosoRef.current) {
      virtuosoRef.current.scrollToMessageId(mid, 'smooth');
    }
  }, []);

  const togglePinMessage = useCallback(
    (m: DirectMessage) => {
      const id = Number((m as any)?.id || 0) || 0;
      if (id <= 0) return;
      const key = selectedRoom?.id
        ? `lisad_pin_room:${selectedRoom.id}`
        : selectedUser?.id
          ? `lisad_pin_dm:${selectedUser.id}`
          : '';
      if (!key) return;
      const next = pinnedMessageId === id ? 0 : id;
      setPinnedMessageId(next);
      try {
        if (next > 0) localStorage.setItem(key, String(next));
        else localStorage.removeItem(key);
      } catch {
        // ignore
      }
    },
    [pinnedMessageId, selectedRoom?.id, selectedUser?.id]
  );

  const cancelEditMessage = useCallback(() => {
    setEditTarget(null);
    if (selectedRoom) setRoomDraft(editPrevRoomDraftRef.current || '');
    else setDraft(editPrevDraftRef.current || '');
    editPrevDraftRef.current = '';
    editPrevRoomDraftRef.current = '';
  }, [selectedRoom?.id, setDraft]);

  const startEditMessage = useCallback(
    (m: DirectMessage) => {
      const meId = meRef.current?.id || 0;
      const fromId = Number((m as any)?.from_user?.id || 0) || 0;
      if (!meId || fromId !== meId) return;

      const isVoice =
        String((m as any)?.type || '') === 'voice_message' ||
        !!(m as any)?.voice_url ||
        String((m as any)?.message || '').includes('/chat_voice/');
      if (isVoice) return;

      const raw = String((m as any)?.message || '');
      if (!raw.trim()) return;

      setReplyTo(null);
      setEditTarget(m);

      if (selectedRoom) {
        editPrevRoomDraftRef.current = roomDraft;
        setRoomDraft(raw);
        requestAnimationFrame(() => {
          try {
            (roomDraftRef.current as any)?.focus?.({ preventScroll: true });
          } catch {
            roomDraftRef.current?.focus();
          }
        });
        return;
      }

      editPrevDraftRef.current = draft;
      setDraft(raw);
      flushSync(() => setComposeInChat(true));
      requestAnimationFrame(() => {
        try {
          (draftChatRef.current as any)?.focus?.({ preventScroll: true });
        } catch {
          draftChatRef.current?.focus();
        }
      });
    },
    [draft, setDraft, roomDraft, selectedRoom?.id, selectedUser?.id]
  );

  const inChatSearch = useMemo(() => {
    const q0 = inChatSearchQuery.trim();
    const q = q0.toLowerCase();
    if (!q) return { q: '', items: [] as Array<{ id: number; snippet: string; count: number }>, total: 0 };

    const countOcc = (hay: string, needle: string) => {
      let c = 0;
      let p = 0;
      while (true) {
        const i = hay.indexOf(needle, p);
        if (i < 0) break;
        c += 1;
        p = i + needle.length;
      }
      return c;
    };

    const items: Array<{ id: number; snippet: string; count: number }> = [];
    let total = 0;
    const chatKey = selectedRoom?.id ? `room:${selectedRoom.id}` : selectedUser?.id ? `dm:${selectedUser.id}` : '';
    const snap = chatKey ? getChatMessagesSnapshot(chatKey) : [];
    for (const m of snap) {
      const raw = String((m as any)?.message || '');
      if (!raw) continue;
      const isVoice =
        String((m as any)?.type || '') === 'voice_message' ||
        !!(m as any)?.voice_url ||
        raw.includes('/chat_voice/') ||
        raw.includes('/uploads/chat_voice/') ||
        raw.includes('/media/chat_voice/');
      if (isVoice) continue;

      const low = raw.toLowerCase();
      if (!low.includes(q)) continue;
      const cnt = countOcc(low, q);
      if (cnt <= 0) continue;
      total += cnt;

      const first = low.indexOf(q);
      const start = Math.max(0, first - 28);
      const end = Math.min(raw.length, first + q.length + 44);
      const chunk = raw.slice(start, end).replace(/\s+/g, ' ').trim();
      const snippet = `${start > 0 ? '…' : ''}${chunk}${end < raw.length ? '…' : ''}`;
      items.push({ id: Number((m as any)?.id || 0) || 0, snippet, count: cnt });
    }
    return { q: q0, items: items.filter((x) => x.id > 0), total };
  }, [inChatSearchQuery, selectedRoom?.id, selectedUser?.id]);

  useEffect(() => {
    // Комментарий на русском: "тап снаружи" закрывает поиск (нужно для мобильных: 1 тап = открыть поиск, 2 тап = открыть профиль/настройки).
    if (!inChatSearchOpen) return;
    const root = inChatSearchRootRef.current;
    if (!root) return;
    const onDown = (e: Event) => {
      const t = e.target as Node | null;
      if (t && root.contains(t)) return;
      setInChatSearchOpen(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('touchstart', onDown, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('touchstart', onDown, true);
    };
  }, [inChatSearchOpen]);

  useEffect(() => {
    // Комментарий на русском: если пришёл запрос на установку фона — раскрываем "каплю" вниз автоматически,
    // чтобы вместо поиска появились кнопки принять/отклонить.
    const hasReq = !!dmWallpaperRequest && !!selectedUser && !selectedRoom;
    if (!hasReq) return;
    setInChatSearchOpen(true);
    setInChatSearchQuery('');
    setInChatSearchActiveIdx(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.id, selectedRoom?.id, (dmWallpaperRequest as any)?.id]);

  useEffect(() => {
    if (inChatSearchActiveIdx <= 0) return;
    if (inChatSearchActiveIdx < inChatSearch.items.length) return;
    setInChatSearchActiveIdx(0);
  }, [inChatSearch.items.length, inChatSearchActiveIdx]);

  const goToInChatSearchResult = useCallback(
    (idx: number) => {
      const items = inChatSearch.items;
      if (!items.length) return;
      const nextIdx = ((idx % items.length) + items.length) % items.length;
      const id = Number(items[nextIdx]?.id || 0) || 0;
      if (id <= 0) return;
      setInChatSearchActiveIdx(nextIdx);
      requestAnimationFrame(() => {
        scrollToMessageById(id);
        const root = inChatSearchListRef.current;
        const row = root ? (root.querySelector(`[data-inchat-find-id="${id}"]`) as HTMLElement | null) : null;
        row?.scrollIntoView?.({ block: 'nearest' } as any);
      });
    },
    [inChatSearch.items, scrollToMessageById]
  );

  const goInChatSearchPrev = useCallback(
    () => goToInChatSearchResult(inChatSearchActiveIdx - 1),
    [goToInChatSearchResult, inChatSearchActiveIdx]
  );
  const goInChatSearchNext = useCallback(
    () => goToInChatSearchResult(inChatSearchActiveIdx + 1),
    [goToInChatSearchResult, inChatSearchActiveIdx]
  );

  const openForwardModal = useCallback((items: DirectMessage[]) => {
    const list = (items || []).filter(Boolean);
    if (!list.length) return;
    setForwardItems(list);
    setForwardQuery('');
    setForwardOpen(true);
  }, []);

  const closeForwardModal = useCallback(() => {
    setForwardOpen(false);
    setForwardItems([]);
    setForwardQuery('');
    setForwardSending(false);
  }, []);

  const openForwardSelected = useCallback(() => {
    if (!selectedMsgKeys.length) return;
    // Комментарий на русском: собираем по ключам в порядке выбора (и не теряем сообщения, если ключ “переехал” с id на client_id).
    const chatKey = selectedRoom?.id ? `room:${selectedRoom.id}` : selectedUser?.id ? `dm:${selectedUser.id}` : '';
    const snap = chatKey ? getChatMessagesSnapshot(chatKey) : [];
    const byKey = new Map<string, DirectMessage>();
    for (const m of snap) {
      const k = msgKeyOf(m);
      if (k && !byKey.has(k)) byKey.set(k, m);
    }
    const out: DirectMessage[] = [];
    const seen = new Set<string>();
    for (const k0 of selectedMsgKeys) {
      const k = String(k0 || '');
      let m = byKey.get(k);
      if (!m) {
        const id = Number(k);
        if (Number.isFinite(id) && id > 0) m = snap.find((x) => Number((x as any)?.id || 0) === id);
      }
      if (!m) continue;
      const kk = msgKeyOf(m);
      if (kk && seen.has(kk)) continue;
      if (kk) seen.add(kk);
      out.push(m);
    }
    openForwardModal(out);
  }, [msgKeyOf, openForwardModal, selectedMsgKeys, selectedRoom?.id, selectedUser?.id]);

  const openForwardOne = useCallback((m: DirectMessage) => openForwardModal([m]), [openForwardModal]);

  const forwardCandidates = useMemo(() => {
    const q = forwardQuery.trim().toLowerCase();
    const excludeId = selectedUser?.id || 0;
    const base = conversations.filter((c) => (c.user?.id || 0) !== excludeId);
    if (!q) return base;
    return base.filter((c) => {
      const name = (c.user?.display_name || c.user?.username || '').toLowerCase();
      const last = (c.last_message?.message || '').toLowerCase();
      return name.includes(q) || last.includes(q);
    });
  }, [conversations, forwardQuery, selectedUser?.id]);

  const extractForwardVoiceUrl = useCallback((m: DirectMessage) => {
    // Комментарий на русском: для пересылки голосовых нужен "чистый" URL файла (без текста/префиксов).
    // Иначе бэк запишет voice_url как весь текст, и плеер не найдёт источник.
    const vu0 = typeof (m as any)?.voice_url === 'string' ? String((m as any).voice_url) : '';
    const raw0 = String((m as any)?.message || '');
    const pick = (s: string) => {
      const t = String(s || '').trim();
      if (!t) return '';
      if (t.startsWith('/uploads/chat_voice/') || t.startsWith('/media/chat_voice/')) return t;
      if (/^https?:\/\//.test(t)) return t;
      // Комментарий на русском: если это "Переслано...\nhttps://..." — берём первую ссылку.
      return extractFirstUrl(t) || '';
    };
    const cand = pick(vu0) || pick(raw0);
    if (!cand || !cand.includes('/chat_voice/')) return '';
    // Комментарий на русском: если это наша абсолютная ссылка — сохраняем только путь (/uploads/...), чтобы не зашивать домен.
    if (/^https?:\/\//.test(cand)) {
      try {
        const p = new URL(cand).pathname || '';
        if (p.startsWith('/uploads/chat_voice/') || p.startsWith('/media/chat_voice/')) return p;
      } catch {}
    }
    return cand;
  }, []);

  const buildForwardText = useCallback((m: DirectMessage) => {
    // Комментарий на русском: текстовые сообщения пересылаем как текст.
    // Голосовые пересылаем отдельно как voice_message (см. forwardToUser).
    const from = m?.from_user?.display_name || m?.from_user?.username || 'Пользователь';
    const raw = String(m?.message || '');
    return sanitizeMessage(`↪️ Переслано от ${from}\n${raw}`);
  }, []);

  const forwardToUser = useCallback(
    async (toUserId: number) => {
      if (!toUserId || forwardSending) return;
      setForwardSending(true);
      try {
        for (const m of forwardItems) {
          const raw = String((m as any)?.message || '');
          const vu = extractForwardVoiceUrl(m);
          const isVoice = raw.startsWith('__voice__:') || !!vu || String((m as any)?.type || '') === 'voice_message';

          // Комментарий на русском: если это голосовое — отправляем как голосовое (message = URL файла).
          // Зачем: бэк сам распознает /uploads/chat_voice/* и вернёт нормальный voice_message с voice_url.
          const text = isVoice && vu ? vu : buildForwardText(m);
          if (!text) continue;
          const clientId = makeClientId();
          const createdAt = new Date().toISOString();
          if (!isOnline) {
            pendingMessagesRef.current.set(clientId, { kind: 'dm', targetId: toUserId, message: text, clientId, createdAt });
            continue;
          }
          try {
            const res = await apiCallWithTimeout(
              '/api/chat/direct-messages/',
              {
              method: 'POST',
              body: JSON.stringify({ to_user_id: toUserId, message: text, client_id: clientId }),
              },
              CHAT_SEND_TIMEOUT_MS
            );
            if (!res.ok) pendingMessagesRef.current.set(clientId, { kind: 'dm', targetId: toUserId, message: text, clientId, createdAt });
          } catch {
            pendingMessagesRef.current.set(clientId, { kind: 'dm', targetId: toUserId, message: text, clientId, createdAt });
          }
        }
        setPendingCount(pendingMessagesRef.current.size);
        closeForwardModal();
        setSelectedMsgKeys([]);
        loadConversationsThrottled();
      } finally {
        setForwardSending(false);
      }
    },
    [
      apiCallWithTimeout,
      buildForwardText,
      closeForwardModal,
      extractForwardVoiceUrl,
      forwardItems,
      forwardSending,
      isOnline,
      loadConversationsThrottled,
    ]
  );

  const deleteMessage = useCallback(
    async (messageId: number, forAll: boolean) => {
      try {
        // Комментарий на русском: оптимистичные сообщения (Date.now()) ещё не на сервере — удаляем локально:
        // - убираем из UI
        // - убираем из очереди (если там есть)
        // - чистим офлайн-снимок (IndexedDB)
        if (messageId > 1_000_000_000_000) {
          const uid = getMyUserId();
          const ridNow = Number(selectedRoomIdRef.current || 0) || 0;
          const dmNow = Number(selectedUserIdRef.current || 0) || 0;
          const kind: 'dm' | 'room' = ridNow ? 'room' : 'dm';
          const tid = ridNow || dmNow;
          const chatKey = ridNow ? `room:${ridNow}` : `dm:${dmNow}`;
          const rm: any = getChatMessageById(chatKey, messageId) || null;
          const cid = String(rm?.client_id || '').trim();
          const localVoice = typeof rm?.local_voice_src === 'string' ? String(rm.local_voice_src) : '';
          if (cid) {
            pendingMessagesRef.current.delete(cid);
            setPendingCount(pendingMessagesRef.current.size);
          }
          if (uid && tid) void deleteOfflineChatMessage(uid, kind, tid, messageId, cid || undefined);
          // Комментарий на русском: если это voice с blob: — освобождаем память (best-effort).
          try {
            if (localVoice && localVoice.startsWith('blob:')) {
              URL.revokeObjectURL(localVoice);
              voiceLocalUrlsRef.current.delete(localVoice);
            }
          } catch {}
          chatMessagesStore.getState().removeMessage(chatKey, messageId);
          return;
        }

        const rid = selectedRoom?.id || 0;
        const url = rid
          ? `/api/chat/rooms/${rid}/messages/${messageId}/delete/?for_all=${forAll ? 1 : 0}`
          : `/api/chat/message/${messageId}/delete/?for_all=${forAll ? 1 : 0}`;
        const res = await apiCall(url, {
          method: 'DELETE',
        });
        if (!res.ok) return;
        // Комментарий на русском: применяем “команду” локально (обновляет UI + офлайн-кэш/ChatMap) без лишних запросов.
        applyServerChatCommand({
          type: rid ? 'room_message_deleted' : 'dm_message_deleted',
          room_id: rid || undefined,
          peer_id: rid ? undefined : (Number(selectedUserIdRef.current || 0) || 0),
          message_id: messageId,
          for_all: !!forAll,
        });
      } catch {
        // ignore
      }
    },
    [apiCall, applyServerChatCommand, deleteOfflineChatMessage, getMyUserId, selectedRoom?.id]
  );

  useEffect(() => {
    // Комментарий на русском: команды из push-sw.js (postMessage) — удаление/синхронизация без polling.
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;
    const onMsg = (e: MessageEvent) => {
      const d: any = (e as any)?.data;
      if (!d || d.type !== 'lisad_push') return;
      const payload = d.data || {};
      if (!payload || typeof payload !== 'object') return;
      if (!payload.cmd) return;
      applyServerChatCommand(payload.cmd);
    };
    try {
      navigator.serviceWorker.addEventListener('message', onMsg as any);
    } catch {
      return;
    }
    return () => {
      try {
        navigator.serviceWorker.removeEventListener('message', onMsg as any);
      } catch {}
    };
  }, [applyServerChatCommand]);

  const reactToMessage = useCallback(
    async (messageId: number, emoji: string) => {
      // Комментарий на русском: реакции ставим только для серверных сообщений (не для оптимистичных Date.now()).
      const id = Number(messageId || 0);
      const emo = String(emoji || '').trim();
      if (!id || id > 1_000_000_000_000 || !emo) return;
      try {
        const rid = selectedRoom?.id || 0;
        const url = rid ? `/api/chat/rooms/${rid}/messages/${id}/reaction/` : `/api/chat/message/${id}/reaction/`;
        const res = await apiCall(url, {
          method: 'POST',
          body: JSON.stringify({ reaction: emo }),
        });
        if (!res.ok) return;
        const updated = (await res.json()) as any;
        const chatKey = rid ? `room:${rid}` : `dm:${Number(selectedUser?.id || 0) || 0}`;
        if (chatKey) chatMessagesStore.getState().patchMessage(chatKey, id, updated as any);
        if (rid) loadRoomsThrottled();
        else loadConversationsThrottled();
      } catch {
        // ignore
      }
    },
    [apiCall, loadConversationsThrottled, loadRoomsThrottled, selectedRoom?.id]
  );

  const startReply = useCallback(
    (msg: DirectMessage) => {
      // Комментарий на русском: свайп по сообщению выставляет reply_to и сразу открывает ввод.
      setReplyTo(msg);
      if (selectedRoom) {
        requestAnimationFrame(() => {
          try {
            (roomDraftRef.current as any)?.focus?.({ preventScroll: true });
          } catch {
            roomDraftRef.current?.focus();
          }
        });
        return;
      }
      openChatComposer();
    },
    [openChatComposer, selectedRoom?.id]
  );

  const clearChat = useCallback(async () => {
    if (!selectedUser) return;
    if (!confirm(`Очистить чат с ${selectedUser.display_name || selectedUser.username}? Сообщения будут удалены только у вас.`)) {
      return;
    }
    try {
      const res = await apiCall(`/api/chat/messages/${selectedUser.id}/clear/`, { method: 'DELETE' });
      if (!res.ok) return;
      // Комментарий на русском: это явное действие пользователя — чистим фото-кэш этого чата.
      const chatKey = `dm:${Number(selectedUser.id || 0) || 0}`;
      void clearPhotoCacheByChat(chatKey);
      const snap = getChatMessagesSnapshot(chatKey);
      setTimeout(() => {
        try {
          for (const m of snap as any[]) {
            const vu = typeof m?.voice_url === 'string' ? String(m.voice_url) : '';
            const raw = String(m?.message || '');
            const eff = vu || raw;
            const isV = !!vu || (typeof m?.type === 'string' && String(m.type) === 'voice_message') || eff.includes('/chat_voice/');
            if (!isV) continue;
            if (!eff || !(eff.startsWith('/') || /^https?:\/\//.test(eff))) continue;
            const abs = resolveVoiceUrl(eff);
            void deleteCachedVoice(abs);
          }
          localStorage.removeItem(`lisad_voice_seen_v2:${selectedUser.id}`);
        } catch {}
      }, 0);
      chatMessagesStore.getState().clearChat(chatKey);
      setSelectedUser(null);
      loadConversationsThrottled();
    } catch {
      // ignore
    }
  }, [apiCall, selectedUser, loadConversationsThrottled]);

  const openDmWallpaperModal = useCallback(() => {
    if (!selectedUser && !selectedRoom) return;
    setDmWallpaperToast(null);
    setDmWallpaperFile(null);
    const curBlur = selectedRoom ? Number(roomWallpaper?.blur || 0) || 0 : Number(dmWallpaper?.blur || 0) || 0;
    const curBrightness = selectedRoom ? Number(roomWallpaper?.brightness || 100) || 100 : Number(dmWallpaper?.brightness || 100) || 100;
    setDmWallpaperBlurDraft(curBlur);
    setDmWallpaperBrightnessDraft(curBrightness);
    setDmWallpaperOpen(true);
  }, [dmWallpaper?.blur, dmWallpaper?.brightness, roomWallpaper?.blur, roomWallpaper?.brightness, selectedRoom, selectedUser]);

  const closeDmWallpaperModal = useCallback(() => {
    setDmWallpaperOpen(false);
    setWallpaperCropFile(null);
    setDmWallpaperFile(null);
    setDmWallpaperToast(null);
    setDmWallpaperBusy(false);
  }, []);

  const closePhotoViewer = useCallback(() => {
    setPhotoViewerOpen(false);
    setPhotoViewerUrl('');
    setPhotoViewerList([]);
    setPhotoViewerIndex(0);
    setPhotoViewerSrc('');
    photoViewerKeyRef.current = '';
    try {
      const prev = photoViewerObjUrlRef.current;
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
    } catch {}
    photoViewerObjUrlRef.current = '';
    setPhotoViewerDragging(false);
    setPhotoViewerDragY(0);
    photoViewerDragYRef.current = 0;
    setPhotoViewerScale(1);
    setPhotoViewerTranslate({ x: 0, y: 0 });
    photoViewerPinchRef.current = null;
  }, []);

  const openPhotoViewerList = useCallback((urls: string[], startUrl: string) => {
    const list = (urls || []).filter(Boolean);
    const idx = Math.max(0, list.findIndex((u) => String(u) === String(startUrl)));
    // Комментарий на русском: сбрасываем src текущей картинки, иначе на мгновение “залипает” прошлое фото.
    setPhotoViewerSrc('');
    photoViewerKeyRef.current = '';
    try {
      const prev = photoViewerObjUrlRef.current;
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
    } catch {}
    photoViewerObjUrlRef.current = '';
    setPhotoViewerList(list);
    setPhotoViewerIndex(idx >= 0 ? idx : 0);
    setPhotoViewerUrl(list[idx >= 0 ? idx : 0] || String(startUrl || ''));
    setPhotoViewerOpen(true);
    setPhotoViewerDragging(false);
    setPhotoViewerDragY(0);
    photoViewerDragYRef.current = 0;
    setPhotoViewerScale(1);
    setPhotoViewerTranslate({ x: 0, y: 0 });
    photoViewerPinchRef.current = null;
  }, []);

  const openPhotoViewerSingle = useCallback((url: string) => {
    openPhotoViewerList([url], url);
  }, [openPhotoViewerList]);

  // Комментарий на русском: обратная совместимость (старые вызовы openPhotoViewer(url)).
  const openPhotoViewer = openPhotoViewerSingle;

  const resolveChatMediaUrl = useCallback((src: string) => {
    const u = String(src || '').trim();
    if (!u || u.startsWith('blob:') || u.startsWith('data:') || /^https?:\/\//.test(u)) return u;
    // Комментарий на русском: поддерживаем относительные пути и без ведущего "/".
    if (u.startsWith('/')) return `${API_URL}${u}`;
    return `${API_URL}/${u}`.replace(/\/{2,}/g, '/').replace(':/', '://');
  }, []);

  // Комментарий на русском: чистим локальный кэш src для сетки медиа (blob url), чтобы не было мерцаний/утечек.
  const clearPeerMediaSrcCache = useCallback(() => {
    try {
      for (const u of Array.from(mediaObjUrlMapRef.current.values())) {
        if (u && u.startsWith('blob:')) URL.revokeObjectURL(u);
      }
    } catch {}
    mediaObjUrlMapRef.current.clear();
    mediaLoadingRef.current.clear();
    setMediaSrcByUrl({});
  }, []);

  // Комментарий на русском: загружаем фото для плитки (CacheStorage -> fetch) и кладём src в локальный кэш.
  const ensurePeerMediaPhotoSrc = useCallback(
    async (rawUrl: string) => {
      const url = resolveChatMediaUrl(String(rawUrl || '').trim());
      if (!url) return;
      if (url.startsWith('blob:') || url.startsWith('data:')) {
        setMediaSrcByUrl((prev) => (prev[url] ? prev : { ...prev, [url]: url }));
        return;
      }
      if (mediaLoadingRef.current.has(url)) return;
      if (mediaObjUrlMapRef.current.has(url)) return;
      mediaLoadingRef.current.add(url);
      try {
        const preload = (u: string) =>
          new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = u;
          });

        const cached = await getCachedChatPhotoBlob(url);
        if (cached) {
          const obj = URL.createObjectURL(cached);
          await preload(obj);
          mediaObjUrlMapRef.current.set(url, obj);
          setMediaSrcByUrl((prev) => (prev[url] ? prev : { ...prev, [url]: obj }));
          return;
        }
        // Комментарий на русском: если кэша нет — НЕ скачиваем автоматически (только по кнопке ⬇).
      } catch {
        // ignore
      } finally {
        mediaLoadingRef.current.delete(url);
      }
    },
    [resolveChatMediaUrl]
  );

  // Комментарий на русском: ручная загрузка фото (как у голосовых) — по кнопке ⬇ на каркасе.
  const downloadPeerMediaPhoto = useCallback(
    async (rawUrl: string) => {
      const url = resolveChatMediaUrl(String(rawUrl || '').trim());
      if (!url) return;
      if (mediaLoadingRef.current.has(url)) return;
      mediaLoadingRef.current.add(url);
      try {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        const token = getAccessToken() || '';
        const headers: Record<string, string> = { Accept: 'image/*,*/*;q=0.8' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(url, { method: 'GET', credentials: 'include', headers });
        if (!res.ok) return;
        const ct = String(res.headers.get('content-type') || '').toLowerCase();
        if (!ct.startsWith('image/')) return;
        const blob = await res.blob();
        void putChatPhotoBlobToCache(url, blob, ct);
        const obj = URL.createObjectURL(blob);
        const img = new Image();
        await new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = obj;
        });
        mediaObjUrlMapRef.current.set(url, obj);
        setMediaSrcByUrl((prev) => (prev[url] ? prev : { ...prev, [url]: obj }));
      } catch {
        // ignore
      } finally {
        mediaLoadingRef.current.delete(url);
      }
    },
    [resolveChatMediaUrl]
  );

  // Комментарий на русском: собираем индекс фото текущего чата для пролистывания (работает и для DM, и для комнат).
  const getChatPhotoIndex = useCallback(() => {
    const out: Array<{ messageId: number; url: string; createdAt: string }> = [];
    const chatKey = selectedRoom?.id ? `room:${selectedRoom.id}` : selectedUser?.id ? `dm:${selectedUser.id}` : '';
    const snap = chatKey ? getChatMessagesSnapshot(chatKey) : [];
    for (const m of snap) {
      const atts = (m as any).attachments as Array<{ url?: string; kind?: string; mime?: string }> | undefined;
      if (!atts?.length) continue;
      for (const a of atts) {
        const mime = String((a as any)?.mime || '').toLowerCase();
        const kind = String((a as any)?.kind || '').toLowerCase();
        const raw = String((a as any)?.url || '').trim();
        if (!raw) continue;
        const isPhoto = kind === 'photo' || kind === 'image' || mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|avif)$/i.test(raw);
        if (!isPhoto) continue;
        out.push({ messageId: Number((m as any)?.id || 0) || 0, url: resolveChatMediaUrl(raw), createdAt: String((m as any)?.created_at || '') });
        break;
      }
    }
    // Комментарий на русском: порядок как в чате (messages уже в правильном порядке), не сортируем.
    return out.filter((x) => !!x.messageId && !!x.url);
  }, [resolveChatMediaUrl, selectedRoom?.id, selectedUser?.id]);

  const openChatPhotoViewer = useCallback(
    (photoUrl: string, messageId: number) => {
      const chatPhotoIndex = getChatPhotoIndex();
      const list = chatPhotoIndex.map((x) => x.url);
      const start = resolveChatMediaUrl(photoUrl);
      let idx = chatPhotoIndex.findIndex((x) => x.messageId === messageId);
      if (idx < 0) idx = list.findIndex((u) => String(u) === String(start));
      const startUrl = list[idx >= 0 ? idx : 0] || start;
      openPhotoViewerList(list.length ? list : [start], startUrl);
    },
    [getChatPhotoIndex, openPhotoViewerList, resolveChatMediaUrl]
  );

  // Комментарий на русском: при закрытии профиля чистим медиа-кэш (и blob url), чтобы не было утечек и “живых” обновлений.
  useEffect(() => {
    if (peerProfileOpen) return;
    peerMediaIndexKeyRef.current = '';
    setPeerMediaIndex([]);
    setMediaVisibleCount(100);
    clearPeerMediaSrcCache();
  }, [peerProfileOpen, clearPeerMediaSrcCache]);

  // Комментарий на русском: строим индекс всех медиа 1 раз при входе во вкладку (стабильная сетка без “обновлений”).
  useEffect(() => {
    if (!peerProfileOpen || peerProfileTab !== 'media') return;
    if (!selectedUser && !selectedRoom) return;
    const key = selectedRoom ? `r:${selectedRoom.id}` : `u:${selectedUser?.id}`;
    if (peerMediaIndexKeyRef.current === key && peerMediaIndex.length) return;
    peerMediaIndexKeyRef.current = key;
    clearPeerMediaSrcCache();
    setMediaVisibleCount(100);
    const myId = meRef.current?.id || 0;
    const all: PeerMediaItem[] = [];
    // Комментарий на русском: берём “снимок” сообщений на момент открытия вкладки.
    const chatKey = selectedRoom?.id ? `room:${selectedRoom.id}` : selectedUser?.id ? `dm:${selectedUser.id}` : '';
    const snap = chatKey ? getChatMessagesSnapshot(chatKey) : [];
    snap.forEach((m) => {
      const isMe = m.from_user?.id === myId;
      const atts = (m as any).attachments as Array<{ id: number; url: string; kind: string; mime?: string }> | undefined;
      if (!atts?.length) return;
      atts.forEach((a) => {
        const mime = String(a.mime || '').toLowerCase();
        const url = resolveChatMediaUrl(String(a.url || ''));
        const kind = String(a.kind || '').toLowerCase();
        const isPhoto = kind === 'image' || mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|avif)$/i.test(url);
        const isVideo = kind === 'video' || mime.startsWith('video/') || /\.(mp4|mov|webm|mkv)$/i.test(url);
        if (!isPhoto && !isVideo) return;
        all.push({ id: a.id, url, kind: isVideo ? 'video' : 'photo', isMe, createdAt: m.created_at, mime });
      });
    });
    all.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')) || (Number(b.id || 0) - Number(a.id || 0)));
    setPeerMediaIndex(all);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peerProfileOpen, peerProfileTab, selectedUser?.id, selectedRoom?.id, resolveChatMediaUrl]);

  // Комментарий на русском: при смене фильтров начинаем показ снова с первых 40.
  useEffect(() => {
    if (!peerProfileOpen || peerProfileTab !== 'media') return;
    setMediaVisibleCount(100);
  }, [mediaOwnerFilter, mediaTypeFilter, peerProfileOpen, peerProfileTab]);

  // Комментарий на русском: подгружаем фото для видимых плиток (по мере пролистывания), пачками по 40.
  useEffect(() => {
    if (!peerProfileOpen || peerProfileTab !== 'media') return;
    const filtered = peerMediaIndex.filter((item) => {
      if (mediaOwnerFilter === 'me' && !item.isMe) return false;
      if (mediaOwnerFilter === 'peer' && item.isMe) return false;
      if (mediaTypeFilter === 'photo' && item.kind !== 'photo') return false;
      if (mediaTypeFilter === 'video' && item.kind !== 'video') return false;
      return true;
    });
    const end = Math.max(0, mediaVisibleCount || 0);
    const start = Math.max(0, end - 100);
    // Комментарий на русском: грузим ровно “следующую пачку” (100) по мере увеличения visibleCount.
    const batch = filtered.slice(start, start + 100).filter((x) => x.kind === 'photo');
    batch.forEach((x) => void ensurePeerMediaPhotoSrc(x.url));
  }, [peerProfileOpen, peerProfileTab, peerMediaIndex, mediaOwnerFilter, mediaTypeFilter, mediaVisibleCount, ensurePeerMediaPhotoSrc]);

  // Комментарий на русском: загрузка src для текущего photo viewer (CacheStorage -> fetch), иначе <img> в браузере не имеет Authorization.
  useEffect(() => {
    if (!photoViewerOpen) return;
    const url = resolveChatMediaUrl(photoViewerUrl);
    if (!url) return;
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      setPhotoViewerSrc(url);
      photoViewerKeyRef.current = url;
      return;
    }
    if (photoViewerKeyRef.current === url && photoViewerSrc && photoViewerSrc.startsWith('blob:')) return;
    let cancelled = false;
    void (async () => {
      try {
        const cached = await getCachedChatPhotoBlob(url);
        if (cached && !cancelled) {
          const obj = URL.createObjectURL(cached);
          const img = new Image();
          await new Promise<void>((resolve) => {
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = obj;
          });
          if (cancelled) return;
          try {
            const prev = photoViewerObjUrlRef.current;
            if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
          } catch {}
          photoViewerObjUrlRef.current = obj;
          photoViewerKeyRef.current = url;
          setPhotoViewerSrc(obj);
          return;
        }
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        const token = getAccessToken() || '';
        const headers: Record<string, string> = { Accept: 'image/*,*/*;q=0.8' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(url, { method: 'GET', credentials: 'include', headers });
        if (!res.ok) return;
        const ct = String(res.headers.get('content-type') || '').toLowerCase();
        if (!ct.startsWith('image/')) return;
        const blob = await res.blob();
        if (cancelled) return;
        void putChatPhotoBlobToCache(url, blob, ct);
        const obj = URL.createObjectURL(blob);
        const img = new Image();
        await new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = obj;
        });
        if (cancelled) return;
        try {
          const prev = photoViewerObjUrlRef.current;
          if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        } catch {}
        photoViewerObjUrlRef.current = obj;
        photoViewerKeyRef.current = url;
        setPhotoViewerSrc(obj);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [photoViewerOpen, photoViewerUrl, photoViewerSrc, resolveChatMediaUrl]);

  const goPhotoPrev = useCallback(() => {
    const list = photoViewerList || [];
    if (!list.length) return;
    setPhotoViewerIndex((i) => {
      const next = Math.max(0, i - 1);
      setPhotoViewerSrc('');
      photoViewerKeyRef.current = '';
      try {
        const prev = photoViewerObjUrlRef.current;
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      } catch {}
      photoViewerObjUrlRef.current = '';
      setPhotoViewerUrl(list[next] || '');
      setPhotoViewerDragY(0);
      photoViewerDragYRef.current = 0;
      setPhotoViewerScale(1);
      setPhotoViewerTranslate({ x: 0, y: 0 });
      photoViewerPinchRef.current = null;
      return next;
    });
  }, [photoViewerList]);

  const goPhotoNext = useCallback(() => {
    const list = photoViewerList || [];
    if (!list.length) return;
    setPhotoViewerIndex((i) => {
      const next = Math.min(list.length - 1, i + 1);
      setPhotoViewerSrc('');
      photoViewerKeyRef.current = '';
      try {
        const prev = photoViewerObjUrlRef.current;
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      } catch {}
      photoViewerObjUrlRef.current = '';
      setPhotoViewerUrl(list[next] || '');
      setPhotoViewerDragY(0);
      photoViewerDragYRef.current = 0;
      setPhotoViewerScale(1);
      setPhotoViewerTranslate({ x: 0, y: 0 });
      photoViewerPinchRef.current = null;
      return next;
    });
  }, [photoViewerList]);

  useEffect(() => {
    if (!photoViewerOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePhotoViewer();
      if (e.key === 'ArrowLeft') goPhotoPrev();
      if (e.key === 'ArrowRight') goPhotoNext();
    };
    try {
      window.addEventListener('keydown', onKeyDown);
    } catch {}
    return () => {
      try {
        window.removeEventListener('keydown', onKeyDown);
      } catch {}
    };
  }, [photoViewerOpen, closePhotoViewer, goPhotoPrev, goPhotoNext]);

  // Комментарий на русском: портал просмотрщика фото должен рендериться И в DM, И в группах (а не только в ветке selectedUser).
  const renderPhotoViewerPortal = useCallback(() => {
    if (!photoViewerOpen) return null;
    if (typeof document === 'undefined' || !document.body) return null;
    return createPortal(
      (() => {
        const dy = Math.max(0, Math.round(photoViewerDragY || 0));
        const dragK = Math.min(1, dy / 240);
        const bgOpacity = Math.max(0.18, 0.75 - dragK * 0.45);
        return (
          <div className="fixed inset-0 z-[12000]">
            <div
              className="absolute inset-0 backdrop-blur-md"
              style={{ backgroundColor: `rgba(0,0,0,${bgOpacity})` }}
              onClick={closePhotoViewer}
              aria-hidden="true"
            />
            <div className="absolute z-[12002] flex items-center gap-2 bottom-4 left-1/2 -translate-x-1/2 sm:top-3 sm:right-3 sm:left-auto sm:bottom-auto sm:translate-x-0">
              {/* Комментарий на русском: зум кнопками — только планшеты/десктоп (на телефоне оставляем pinch). */}
              <div className="hidden sm:flex items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPhotoViewerScale((s) => {
                      const next = Math.max(1, Math.min(4, s - 0.5));
                      if (next <= 1) setPhotoViewerTranslate({ x: 0, y: 0 });
                      return next;
                    });
                  }}
                  className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92] text-xl font-bold"
                  title="Уменьшить"
                  aria-label="Уменьшить"
                >
                  −
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPhotoViewerScale(1);
                    setPhotoViewerTranslate({ x: 0, y: 0 });
                  }}
                  className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
                  title="Сбросить масштаб"
                  aria-label="Сбросить масштаб"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPhotoViewerScale((s) => Math.min(4, s + 0.5));
                  }}
                  className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92] text-xl font-bold"
                  title="Увеличить"
                  aria-label="Увеличить"
                >
                  +
                </button>
                <div className="w-px h-6 bg-site-text/20" />
              </div>
              <button
                type="button"
                onClick={async (e) => {
                  e.stopPropagation();
                  const raw = String(photoViewerUrl || '').trim();
                  if (!raw) return;
                  try {
                    const url = resolveChatMediaUrl(raw);
                    const token = getAccessToken() || '';
                    const headers: Record<string, string> = { Accept: 'image/*,*/*;q=0.8' };
                    if (token) headers.Authorization = `Bearer ${token}`;
                    const res = await fetch(url, { method: 'GET', credentials: 'include', headers });
                    if (!res.ok) return;
                    const blob = await res.blob();
                    const obj = URL.createObjectURL(blob);
                    try {
                      const a = document.createElement('a');
                      a.href = obj;
                      a.download = `photo_${Date.now()}.jpg`;
                      a.rel = 'noopener noreferrer';
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                    } finally {
                      try { URL.revokeObjectURL(obj); } catch {}
                    }
                  } catch {
                    // ignore
                  }
                }}
                disabled={!photoViewerUrl}
                className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92] disabled:opacity-50 disabled:cursor-not-allowed"
                title="Скачать"
              >
                <UiIcon name="ArrowDown" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  closePhotoViewer();
                }}
                className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
                title="Закрыть"
              >
                ✕
              </button>
            </div>
            <div
              className="absolute inset-0 z-[12001] flex items-center justify-center px-0 py-0 sm:p-10"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => {
                if (e.touches.length === 2) {
                  const t1 = e.touches[0], t2 = e.touches[1];
                  const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                  const centerX = (t1.clientX + t2.clientX) / 2, centerY = (t1.clientY + t2.clientY) / 2;
                  const img = photoViewerImgRef.current;
                  if (img) {
                    const rect = img.getBoundingClientRect();
                    photoViewerPinchRef.current = {
                      distance: dist,
                      center: { x: centerX - (rect.left + rect.width / 2), y: centerY - (rect.top + rect.height / 2) },
                      initialScale: photoViewerScale,
                      initialTranslate: { ...photoViewerTranslate },
                    };
                  }
                } else if (e.touches.length === 1 && photoViewerScale <= 1) {
                  const t = e.touches[0];
                  photoViewerPointerIdRef.current = t.identifier;
                  photoViewerStartRef.current = { x: t.clientX, y: t.clientY };
                  setPhotoViewerDragging(true);
                  photoViewerSwipeRef.current = { startX: t.clientX, startY: t.clientY, dx: 0, dy: 0 };
                }
              }}
              onTouchMove={(e) => {
                e.preventDefault();
                if (e.touches.length === 2 && photoViewerPinchRef.current) {
                  const t1 = e.touches[0], t2 = e.touches[1];
                  const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                  const pinch = photoViewerPinchRef.current;
                  const scale = Math.max(1, Math.min(4, (pinch.initialScale * dist) / pinch.distance));
                  setPhotoViewerScale(scale);
                  if (scale > 1) {
                    const centerX = (t1.clientX + t2.clientX) / 2, centerY = (t1.clientY + t2.clientY) / 2;
                    const img = photoViewerImgRef.current;
                    if (img) {
                      const rect = img.getBoundingClientRect();
                      const dx = centerX - (rect.left + rect.width / 2) - pinch.center.x;
                      const dy = centerY - (rect.top + rect.height / 2) - pinch.center.y;
                      setPhotoViewerTranslate({ x: pinch.initialTranslate.x + dx / scale, y: pinch.initialTranslate.y + dy / scale });
                    }
                  } else {
                    setPhotoViewerTranslate({ x: 0, y: 0 });
                  }
                  return;
                }
                if (e.touches.length === 1 && photoViewerScale <= 1 && photoViewerDragging) {
                  const t = e.touches[0];
                  if (photoViewerPointerIdRef.current !== t.identifier) return;
                  const st = photoViewerStartRef.current;
                  if (!st) return;
                  const dx = t.clientX - st.x, dyRaw = t.clientY - st.y;
                  if (photoViewerSwipeRef.current) {
                    photoViewerSwipeRef.current.dx = dx;
                    photoViewerSwipeRef.current.dy = dyRaw;
                  }
                  if (Math.abs(dx) > Math.abs(dyRaw) * 1.1) return; // Комментарий на русском: горизонтальный свайп — листаем фото.
                  photoViewerDragYRef.current = Math.max(0, dyRaw);
                  setPhotoViewerDragY(photoViewerDragYRef.current);
                }
              }}
              onTouchEnd={(e) => {
                if (e.touches.length < 2) photoViewerPinchRef.current = null;
                if (e.touches.length === 0 && photoViewerDragging && photoViewerScale <= 1) {
                  photoViewerPointerIdRef.current = null;
                  photoViewerStartRef.current = null;
                  setPhotoViewerDragging(false);
                  const dyFinal = photoViewerDragYRef.current || 0;
                  const sw = photoViewerSwipeRef.current;
                  photoViewerSwipeRef.current = null;
                  const dxFinal = sw?.dx || 0;
                  const dy0 = sw?.dy || 0;
                  if (Math.abs(dxFinal) > 80 && Math.abs(dxFinal) > Math.abs(dy0) * 1.1) {
                    if (dxFinal < 0) goPhotoNext();
                    else goPhotoPrev();
                    photoViewerDragYRef.current = 0;
                    setPhotoViewerDragY(0);
                    return;
                  }
                  if (dyFinal > 140) closePhotoViewer();
                  else {
                    photoViewerDragYRef.current = 0;
                    setPhotoViewerDragY(0);
                  }
                }
              }}
              onTouchCancel={() => {
                photoViewerPinchRef.current = null;
                photoViewerPointerIdRef.current = null;
                photoViewerStartRef.current = null;
                setPhotoViewerDragging(false);
                photoViewerDragYRef.current = 0;
                setPhotoViewerDragY(0);
                photoViewerSwipeRef.current = null;
              }}
            >
              <div
                className="max-w-full max-h-full"
                onPointerDown={(e) => {
                  if (e.pointerType !== 'touch' || photoViewerScale > 1) return;
                  photoViewerPointerIdRef.current = e.pointerId;
                  photoViewerStartRef.current = { x: e.clientX, y: e.clientY };
                  setPhotoViewerDragging(true);
                  try { (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId); } catch {}
                }}
                onPointerMove={(e) => {
                  if (!photoViewerDragging || photoViewerScale > 1 || photoViewerPointerIdRef.current !== e.pointerId) return;
                  const st = photoViewerStartRef.current;
                  if (!st) return;
                  const dx = e.clientX - st.x, dyRaw = e.clientY - st.y;
                  if (Math.abs(dx) > Math.abs(dyRaw) * 1.1) return;
                  photoViewerDragYRef.current = Math.max(0, dyRaw);
                  setPhotoViewerDragY(photoViewerDragYRef.current);
                }}
                onPointerUp={(e) => {
                  if (photoViewerPointerIdRef.current !== e.pointerId) return;
                  try { (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId); } catch {}
                  photoViewerPointerIdRef.current = null;
                  photoViewerStartRef.current = null;
                  setPhotoViewerDragging(false);
                  const dyFinal = photoViewerDragYRef.current || 0;
                  if (dyFinal > 140 && photoViewerScale <= 1) closePhotoViewer();
                  else { photoViewerDragYRef.current = 0; setPhotoViewerDragY(0); }
                }}
                onPointerCancel={(e) => {
                  if (photoViewerPointerIdRef.current !== e.pointerId) return;
                  photoViewerPointerIdRef.current = null;
                  photoViewerStartRef.current = null;
                  setPhotoViewerDragging(false);
                  photoViewerDragYRef.current = 0;
                  setPhotoViewerDragY(0);
                }}
                style={{
                  touchAction: 'none',
                  transform: `translate3d(${photoViewerTranslate.x}px, ${photoViewerDragY + photoViewerTranslate.y}px, 0) scale(${photoViewerScale})`,
                  transition: photoViewerDragging || photoViewerPinchRef.current ? 'none' : 'transform 180ms ease-out',
                }}
              >
                {photoViewerSrc ? (
                  <img
                    ref={photoViewerImgRef}
                    src={photoViewerSrc}
                    alt="Фото"
                    className="max-w-[100vw] max-h-[100vh] sm:max-w-[min(92vw,980px)] sm:max-h-[86vh] object-contain rounded-none sm:rounded-2xl border-0 sm:border sm:border-site-border shadow-[0_25px_90px_rgba(0,0,0,0.65)]"
                    draggable={false}
                    style={{ touchAction: 'none' }}
                  />
                ) : (
                  <div className="w-[min(92vw,680px)] aspect-square rounded-2xl border border-site-border bg-black/20 flex items-center justify-center">
                    <span className="w-7 h-7 border-2 border-site-border border-t-white/80 rounded-full animate-spin" />
                  </div>
                )}
              </div>
            </div>

            {/* Комментарий на русском: десктоп — кнопки пролистывания (влево/вправо). */}
            {photoViewerList.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); goPhotoPrev(); }}
                  disabled={photoViewerIndex <= 0}
                  className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 z-[12003] w-12 h-12 rounded-full glass-btn items-center justify-center text-site-text/90 hover:text-site-text transition disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Предыдущее"
                  aria-label="Предыдущее фото"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); goPhotoNext(); }}
                  disabled={photoViewerIndex >= photoViewerList.length - 1}
                  className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 z-[12003] w-12 h-12 rounded-full glass-btn items-center justify-center text-site-text/90 hover:text-site-text transition disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Следующее"
                  aria-label="Следующее фото"
                >
                  ›
                </button>
              </>
            )}
          </div>
        );
      })(),
      document.body
    );
  }, [
    closePhotoViewer,
    goPhotoNext,
    goPhotoPrev,
    photoViewerDragY,
    photoViewerDragging,
    photoViewerIndex,
    photoViewerList.length,
    photoViewerPinchRef,
    photoViewerScale,
    photoViewerSrc,
    photoViewerTranslate,
    photoViewerUrl,
  ]);

  const applyDmWallpaperForMe = useCallback(async () => {
    if (!selectedUser || selectedRoom) return;
    if (dmWallpaperBusy) return;
    const peerId = Number(selectedUser.id || 0) || 0;
    if (!peerId) return;
    const uid = getMyUserId();

    setDmWallpaperBusy(true);
    setDmWallpaperToast(null);
    try {
      if (!dmWallpaperFile) {
        // Только обновляем настройки (размытие/яркость).
        if (!dmWallpaper?.id) {
          setDmWallpaperToast('Сначала выберите фон');
          return;
        }
        const res = await apiCall(`/api/chat/messages/${peerId}/wallpaper/`, {
          method: 'PATCH',
          body: JSON.stringify({
            blur: Number(dmWallpaperBlurDraft || 0) || 0,
            brightness: Number(dmWallpaperBrightnessDraft || 100) || 100,
          }),
        });
        if (!res.ok) {
          let msg = `Не удалось обновить настройки (HTTP ${res.status})`;
          try {
            const data = await res.json();
            if (data?.error) msg = String(data.error);
            if (data?.hint) msg = `${msg}. ${String(data.hint)}`;
          } catch {}
          throw new Error(msg);
        }
        const data = (await res.json()) as any;
        const wp = data?.wallpaper;
        if (wp && typeof wp.id === 'number' && typeof wp.url === 'string') {
          const next = {
            id: Number(wp.id || 0) || 0,
            url: String(wp.url || ''),
            blur: Number(wp.blur || 0) || 0,
            brightness: Number(wp.brightness || 100) || 100,
          };
          setDmWallpaper(next);
          if (uid) void chatKvSet(offlineDmWallpaperKey(uid, peerId), next && next.id > 0 && next.url ? next : null);
        }
        setDmWallpaperToast('Настройки фона обновлены');
        setTimeout(() => setDmWallpaperToast(null), 1600);
        return;
      }

      let fileToSend: File = dmWallpaperFile;
      try {
        fileToSend = await optimizeImageForChatUpload(dmWallpaperFile);
      } catch {
        // ignore: отправим оригинал
      }

      const fd = new FormData();
      fd.append('blur', String(Number(dmWallpaperBlurDraft || 0) || 0));
      fd.append('brightness', String(Number(dmWallpaperBrightnessDraft || 100) || 100));
      fd.append('wallpaper', fileToSend, fileToSend.name || 'wallpaper.jpg');

      const res = await apiCall(`/api/chat/messages/${peerId}/wallpaper/`, { method: 'POST', body: fd });
      if (!res.ok) {
        let msg = `Не удалось обновить фон (HTTP ${res.status})`;
        try {
          const data = await res.json();
          if (data?.error) msg = String(data.error);
          if (data?.hint) msg = `${msg}. ${String(data.hint)}`;
        } catch {}
        throw new Error(msg);
      }
      const data = (await res.json()) as any;
      const wp = data?.wallpaper;
      if (wp && typeof wp.id === 'number' && typeof wp.url === 'string') {
        const next = {
          id: Number(wp.id || 0) || 0,
          url: String(wp.url || ''),
          blur: Number(wp.blur || 0) || 0,
          brightness: Number(wp.brightness || 100) || 100,
        };
        setDmWallpaper(next);
        if (uid) void chatKvSet(offlineDmWallpaperKey(uid, peerId), next && next.id > 0 && next.url ? next : null);
      } else {
        setDmWallpaper(null);
        if (uid) void chatKvSet(offlineDmWallpaperKey(uid, peerId), null);
      }
      setDmWallpaperOpen(false);
      setDmWallpaperFile(null);
      setDmWallpaperToast('Фон обновлён');
      setTimeout(() => setDmWallpaperToast(null), 1600);
    } catch (e: any) {
      setDmWallpaperToast(String(e?.message || 'Не удалось обновить фон'));
      setTimeout(() => setDmWallpaperToast(null), 2200);
    } finally {
      setDmWallpaperBusy(false);
    }
  }, [
    apiCall,
    dmWallpaper?.id,
    dmWallpaperBlurDraft,
    dmWallpaperBrightnessDraft,
    dmWallpaperBusy,
    dmWallpaperFile,
    getMyUserId,
    selectedRoom,
    selectedUser,
  ]);

  const sendDmWallpaperRequestToPeer = useCallback(async () => {
    if (!selectedUser) return;
    if (dmWallpaperBusy) return;
    const peerId = Number(selectedUser.id || 0) || 0;
    if (!peerId) return;

    setDmWallpaperBusy(true);
    setDmWallpaperToast(null);
    try {
      const fd = new FormData();
      fd.append('blur', String(Number(dmWallpaperBlurDraft || 0) || 0));
      fd.append('brightness', String(Number(dmWallpaperBrightnessDraft || 100) || 100));
      if (dmWallpaperFile) {
        let fileToSend: File = dmWallpaperFile;
        try {
          fileToSend = await optimizeImageForChatUpload(dmWallpaperFile);
        } catch {}
        fd.append('wallpaper', fileToSend, fileToSend.name || 'wallpaper.jpg');
      } else {
        // Комментарий на русском: если новый файл не выбран — предлагаем текущий установленный фон (без повторной загрузки).
        const directMediaId = Number((dmWallpaper as any)?.id || 0) || 0;
        const url = String((dmWallpaper as any)?.url || '').trim();
        const m = url.match(/[?&]media_id=(\d+)/i);
        const urlMediaId = m ? Number(m[1] || 0) || 0 : 0;
        const mediaId = directMediaId || urlMediaId || 0;
        if (!mediaId) {
          setDmWallpaperToast('Сначала выберите/установите фон, чтобы предложить его');
          setTimeout(() => setDmWallpaperToast(null), 2200);
          return;
        }
        fd.append('media_id', String(mediaId));
      }
      const res = await apiCall(`/api/chat/messages/${peerId}/wallpaper-request/`, { method: 'POST', body: fd });
      if (!res.ok) {
        let msg = `Не удалось отправить запрос (HTTP ${res.status})`;
        try {
          const data = await res.json();
          if (data?.error) msg = String(data.error);
          if (data?.hint) msg = `${msg}. ${String(data.hint)}`;
        } catch {}
        throw new Error(msg);
      }
      setDmWallpaperToast('Запрос отправлен');
      setTimeout(() => setDmWallpaperToast(null), 1800);
      // Комментарий на русском: картинку оставляем в модалке, чтобы пользователь мог ещё раз применить себе/изменить.
    } catch (e: any) {
      setDmWallpaperToast(String(e?.message || 'Не удалось отправить запрос'));
      setTimeout(() => setDmWallpaperToast(null), 2200);
    } finally {
      setDmWallpaperBusy(false);
    }
  }, [apiCall, dmWallpaper?.url, dmWallpaperBlurDraft, dmWallpaperBrightnessDraft, dmWallpaperBusy, dmWallpaperFile, selectedUser]);

  const clearDmWallpaper = useCallback(async () => {
    if (!selectedUser || selectedRoom) return;
    if (dmWallpaperBusy) return;
    const peerId = Number(selectedUser.id || 0) || 0;
    if (!peerId) return;
    const uid = getMyUserId();
    setDmWallpaperBusy(true);
    setDmWallpaperToast(null);
    try {
      const res = await apiCall(`/api/chat/messages/${peerId}/wallpaper/`, { method: 'DELETE' });
      if (!res.ok) return;
      setDmWallpaper(null);
      setDmWallpaperRequest(null);
      setDmWallpaperSrc('');
      if (uid) void chatKvSet(offlineDmWallpaperKey(uid, peerId), null);
      setDmWallpaperToast('Фон удалён');
      setTimeout(() => setDmWallpaperToast(null), 1600);
    } catch {
      // ignore
    } finally {
      setDmWallpaperBusy(false);
    }
  }, [apiCall, dmWallpaperBusy, getMyUserId, selectedRoom, selectedUser]);

  const applyRoomWallpaper = useCallback(async () => {
    if (!selectedRoom) return;
    if (dmWallpaperBusy) return;
    const roomId = Number(selectedRoom.id || 0) || 0;
    if (!roomId) return;
    const uid = getMyUserId();

    if (!roomCanManage) {
      setDmWallpaperToast('Только администратор/владелец комнаты может менять фон');
      setTimeout(() => setDmWallpaperToast(null), 2000);
      return;
    }

    setDmWallpaperBusy(true);
    setDmWallpaperToast(null);
    try {
      if (!dmWallpaperFile) {
        if (!roomWallpaper?.id) {
          setDmWallpaperToast('Сначала выберите фон');
          return;
        }
        const res = await apiCall(`/api/chat/rooms/${roomId}/wallpaper/`, {
          method: 'PATCH',
          body: JSON.stringify({
            blur: Number(dmWallpaperBlurDraft || 0) || 0,
            brightness: Number(dmWallpaperBrightnessDraft || 100) || 100,
          }),
        });
        if (!res.ok) {
          let msg = `Не удалось обновить настройки (HTTP ${res.status})`;
          try {
            const data = await res.json();
            if (data?.error) msg = String(data.error);
            if (data?.hint) msg = `${msg}. ${String(data.hint)}`;
          } catch {}
          throw new Error(msg);
        }
        const data = (await res.json()) as any;
        const wp = data?.wallpaper;
        if (wp && typeof wp.id === 'number' && typeof wp.url === 'string') {
          const next = {
            id: Number(wp.id || 0) || 0,
            url: String(wp.url || ''),
            blur: Number(wp.blur || 0) || 0,
            brightness: Number(wp.brightness || 100) || 100,
          };
          setRoomWallpaper(next);
          if (uid) void chatKvSet(offlineRoomWallpaperKey(uid, roomId), next && next.id > 0 && next.url ? next : null);
        }
        setDmWallpaperToast('Настройки фона обновлены');
        setTimeout(() => setDmWallpaperToast(null), 1600);
        return;
      }

      let fileToSend: File = dmWallpaperFile;
      try {
        fileToSend = await optimizeImageForChatUpload(dmWallpaperFile);
      } catch {
        // ignore
      }

      const fd = new FormData();
      fd.append('blur', String(Number(dmWallpaperBlurDraft || 0) || 0));
      fd.append('brightness', String(Number(dmWallpaperBrightnessDraft || 100) || 100));
      fd.append('wallpaper', fileToSend, fileToSend.name || 'wallpaper.jpg');

      const res = await apiCall(`/api/chat/rooms/${roomId}/wallpaper/`, { method: 'POST', body: fd });
      if (!res.ok) {
        let msg = `Не удалось обновить фон (HTTP ${res.status})`;
        try {
          const data = await res.json();
          if (data?.error) msg = String(data.error);
          if (data?.hint) msg = `${msg}. ${String(data.hint)}`;
        } catch {}
        throw new Error(msg);
      }
      const data = (await res.json()) as any;
      const wp = data?.wallpaper;
      if (wp && typeof wp.id === 'number' && typeof wp.url === 'string') {
        const next = {
          id: Number(wp.id || 0) || 0,
          url: String(wp.url || ''),
          blur: Number(wp.blur || 0) || 0,
          brightness: Number(wp.brightness || 100) || 100,
        };
        setRoomWallpaper(next);
        if (uid) void chatKvSet(offlineRoomWallpaperKey(uid, roomId), next && next.id > 0 && next.url ? next : null);
      } else {
        setRoomWallpaper(null);
        if (uid) void chatKvSet(offlineRoomWallpaperKey(uid, roomId), null);
      }
      setDmWallpaperOpen(false);
      setDmWallpaperFile(null);
      setDmWallpaperToast('Фон обновлён');
      setTimeout(() => setDmWallpaperToast(null), 1600);
    } catch (e: any) {
      setDmWallpaperToast(String(e?.message || 'Не удалось обновить фон'));
      setTimeout(() => setDmWallpaperToast(null), 2200);
    } finally {
      setDmWallpaperBusy(false);
    }
  }, [
    apiCall,
    dmWallpaperBlurDraft,
    dmWallpaperBrightnessDraft,
    dmWallpaperBusy,
    dmWallpaperFile,
    getMyUserId,
    roomCanManage,
    roomWallpaper?.id,
    selectedRoom,
  ]);

  const applyWallpaperAndClose = useCallback(async () => {
    if (dmWallpaperBusy) return;

    // Комментарий на русском: если это комната, но нет прав — просто закрываем.
    if (selectedRoom && !roomCanManage) {
      closeDmWallpaperModal();
      return;
    }

    const draftBlur = Number(dmWallpaperBlurDraft || 0) || 0;
    const draftBrightness = Number(dmWallpaperBrightnessDraft || 100) || 100;
    const hasFile = !!dmWallpaperFile;

    if (selectedRoom) {
      const cur = roomWallpaper;
      const hasExisting = !!cur?.id;
      const curBlur = Number(cur?.blur || 0) || 0;
      const curBrightness = Number(cur?.brightness || 100) || 100;
      const dirty = hasFile || (hasExisting && (draftBlur !== curBlur || draftBrightness !== curBrightness));
      if (dirty) {
        await applyRoomWallpaper();
      }
      closeDmWallpaperModal();
      return;
    }

    const cur = dmWallpaper;
    const hasExisting = !!cur?.id;
    const curBlur = Number(cur?.blur || 0) || 0;
    const curBrightness = Number(cur?.brightness || 100) || 100;
    const dirty = hasFile || (hasExisting && (draftBlur !== curBlur || draftBrightness !== curBrightness));
    if (dirty) {
      await applyDmWallpaperForMe();
    }
    closeDmWallpaperModal();
  }, [
    applyDmWallpaperForMe,
    applyRoomWallpaper,
    closeDmWallpaperModal,
    dmWallpaper,
    dmWallpaperBlurDraft,
    dmWallpaperBrightnessDraft,
    dmWallpaperBusy,
    dmWallpaperFile,
    roomCanManage,
    roomWallpaper,
    selectedRoom,
  ]);

  const applyActiveWallpaperTuning = useCallback(async (opts?: { silent?: boolean }) => {
    // Комментарий на русском: без загрузки картинки, только blur/brightness.
    const silent = !!opts?.silent;
    if (dmWallpaperBusy) return;
    const uid = getMyUserId();
    const blur = Math.max(0, Math.min(30, Number(dmWallpaperBlurDraft || 0) || 0));
    const brightness = Math.max(50, Math.min(150, Number(dmWallpaperBrightnessDraft || 100) || 100));

    try {
      if (selectedRoom) {
        const roomId = Number(selectedRoom.id || 0) || 0;
        if (!roomId) return;
        if (!roomWallpaper?.id) {
          if (!silent) {
            setSettingsToast('Сначала выберите фон комнаты');
            setTimeout(() => setSettingsToast(null), 1800);
          }
          return;
        }
        if (!roomCanManage) {
          if (!silent) {
            setSettingsToast('Только администратор/владелец комнаты может менять фон');
            setTimeout(() => setSettingsToast(null), 2200);
          }
          return;
        }

        setDmWallpaperBusy(true);
        const res = await apiCall(`/api/chat/rooms/${roomId}/wallpaper/`, {
          method: 'PATCH',
          body: JSON.stringify({ blur, brightness }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as any;
        const wp = data?.wallpaper;
        if (wp && typeof wp.id === 'number' && typeof wp.url === 'string') {
          const next = {
            id: Number(wp.id || 0) || 0,
            url: String(wp.url || ''),
            blur: Number(wp.blur || 0) || 0,
            brightness: Number(wp.brightness || 100) || 100,
          };
          setRoomWallpaper(next);
          if (uid) void chatKvSet(offlineRoomWallpaperKey(uid, roomId), next && next.id > 0 && next.url ? next : null);
        }
        if (!silent) {
          setSettingsToast('Настройки фона сохранены');
          setTimeout(() => setSettingsToast(null), 1400);
        }
        return;
      }

      if (selectedUser) {
        const peerId = Number(selectedUser.id || 0) || 0;
        if (!peerId) return;
        if (!dmWallpaper?.id) {
          if (!silent) {
            setSettingsToast('Сначала выберите фон личного чата');
            setTimeout(() => setSettingsToast(null), 1800);
          }
          return;
        }

        setDmWallpaperBusy(true);
        const res = await apiCall(`/api/chat/messages/${peerId}/wallpaper/`, {
          method: 'PATCH',
          body: JSON.stringify({ blur, brightness }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as any;
        const wp = data?.wallpaper;
        if (wp && typeof wp.id === 'number' && typeof wp.url === 'string') {
          const next = {
            id: Number(wp.id || 0) || 0,
            url: String(wp.url || ''),
            blur: Number(wp.blur || 0) || 0,
            brightness: Number(wp.brightness || 100) || 100,
          };
          setDmWallpaper(next);
          if (uid) void chatKvSet(offlineDmWallpaperKey(uid, peerId), next && next.id > 0 && next.url ? next : null);
        }
        if (!silent) {
          setSettingsToast('Настройки фона сохранены');
          setTimeout(() => setSettingsToast(null), 1400);
        }
      }
    } catch {
      if (!silent) {
        setSettingsToast('Не удалось сохранить настройки фона');
        setTimeout(() => setSettingsToast(null), 2000);
      }
    } finally {
      setDmWallpaperBusy(false);
    }
  }, [
    apiCall,
    dmWallpaper?.id,
    dmWallpaperBlurDraft,
    dmWallpaperBrightnessDraft,
    dmWallpaperBusy,
    getMyUserId,
    roomCanManage,
    roomWallpaper?.id,
    selectedRoom,
    selectedUser,
  ]);

  // Комментарий на русском: авто-сохранение настроек фона при изменении ползунков (без кнопки "Сохранить").
  const autoApplyWallpaperTuningDebounced = useMemo(
    () =>
      debounce(() => {
        void applyActiveWallpaperTuning({ silent: true });
      }, 450),
    [applyActiveWallpaperTuning]
  );
  useEffect(() => {
    const allow = (tab === 'settings' && settingsSection === 'interface') || roomSettingsOpen;
    if (!allow) return;
    if (dmWallpaperBusy) return;
    if (dmWallpaperFile) return; // Комментарий на русском: при загрузке файла не трогаем tuning-патчи.

    const canApplyRoom = !!selectedRoom && !!roomWallpaper?.id && !!roomCanManage;
    const canApplyDm = !selectedRoom && !!selectedUser && !!dmWallpaper?.id;
    if (!canApplyRoom && !canApplyDm) return;

    autoApplyWallpaperTuningDebounced();
    return () => {
      try {
        (autoApplyWallpaperTuningDebounced as any).cancel?.();
      } catch {}
    };
  }, [
    autoApplyWallpaperTuningDebounced,
    dmWallpaper?.id,
    dmWallpaperBlurDraft,
    dmWallpaperBrightnessDraft,
    dmWallpaperBusy,
    dmWallpaperFile,
    roomCanManage,
    roomSettingsOpen,
    roomWallpaper?.id,
    selectedRoom,
    selectedUser,
    settingsSection,
    tab,
  ]);

  const clearRoomWallpaper = useCallback(async () => {
    if (!selectedRoom) return;
    if (dmWallpaperBusy) return;
    const roomId = Number(selectedRoom.id || 0) || 0;
    if (!roomId) return;
    const uid = getMyUserId();

    if (!roomCanManage) {
      setDmWallpaperToast('Только администратор/владелец комнаты может менять фон');
      setTimeout(() => setDmWallpaperToast(null), 2000);
      return;
    }

    setDmWallpaperBusy(true);
    setDmWallpaperToast(null);
    try {
      const res = await apiCall(`/api/chat/rooms/${roomId}/wallpaper/`, { method: 'DELETE' });
      if (!res.ok) return;
      setRoomWallpaper(null);
      setRoomWallpaperSrc('');
      if (uid) void chatKvSet(offlineRoomWallpaperKey(uid, roomId), null);
      setDmWallpaperToast('Фон удалён');
      setTimeout(() => setDmWallpaperToast(null), 1600);
    } catch {
      // ignore
    } finally {
      setDmWallpaperBusy(false);
    }
  }, [apiCall, dmWallpaperBusy, getMyUserId, roomCanManage, selectedRoom]);

  const respondDmWallpaperRequest = useCallback(
    async (accept: boolean) => {
      if (!selectedUser || selectedRoom) return;
      if (dmWallpaperBusy) return;
      const peerId = Number(selectedUser.id || 0) || 0;
      const reqId = Number((dmWallpaperRequest as any)?.id || 0) || 0;
      if (!peerId || !reqId) return;
      const uid = getMyUserId();

      setDmWallpaperBusy(true);
      setDmWallpaperToast(null);
      try {
        const action = accept ? 'accept' : 'reject';
        const res = await apiCall(`/api/chat/messages/${peerId}/wallpaper-request/${reqId}/${action}/`, { method: 'POST' });
        if (!res.ok) {
          let msg = `Не удалось обработать запрос (HTTP ${res.status})`;
          try {
            const data = await res.json();
            if (data?.error) msg = String(data.error);
          } catch {}
          throw new Error(msg);
        }
        const data = (await res.json()) as any;
        if (accept) {
          const wp = data?.wallpaper;
          if (wp && typeof wp.id === 'number' && typeof wp.url === 'string') {
            const next = {
              id: Number(wp.id || 0) || 0,
              url: String(wp.url || ''),
              blur: Number(wp.blur || 0) || 0,
              brightness: Number(wp.brightness || 100) || 100,
            };
            setDmWallpaper(next);
            if (uid) void chatKvSet(offlineDmWallpaperKey(uid, peerId), next && next.id > 0 && next.url ? next : null);
          }
          setDmWallpaperToast('Фон применён');
        } else {
          setDmWallpaperToast('Запрос отклонён');
        }
        // Комментарий на русском: закрываем раскрытую "каплю" (там был запрос).
        setInChatSearchOpen(false);
        setDmWallpaperRequest(null);
        setTimeout(() => setDmWallpaperToast(null), 1600);
      } catch (e: any) {
        setDmWallpaperToast(String(e?.message || 'Не удалось обработать запрос'));
        setTimeout(() => setDmWallpaperToast(null), 2200);
      } finally {
        setDmWallpaperBusy(false);
      }
    },
    [apiCall, dmWallpaperBusy, dmWallpaperRequest, getMyUserId, selectedRoom, selectedUser]
  );

  // Комментарий на русском: отправка одного сообщения из очереди (для retry при восстановлении сети).
  const sendPendingMessage = useCallback(
    async (pendingMsg: PendingOutboxItem) => {
      try {
        const uid = getMyUserId();
        const kind = pendingMsg?.kind === 'room' ? 'room' : 'dm';
        const tid = Number(pendingMsg?.targetId || 0) || 0;
        const cid = String(pendingMsg?.clientId || '');
        if (!tid || !cid) return false;

        const url = kind === 'room' ? `/api/chat/rooms/${tid}/messages/` : '/api/chat/direct-messages/';
        const payload =
          kind === 'room'
            ? {
                message: pendingMsg.message,
                client_id: cid,
                reply_to: pendingMsg.replyToId || undefined,
                link_preview_disabled: !!pendingMsg.linkPreviewDisabled,
              }
            : {
                to_user_id: tid,
                message: pendingMsg.message,
                client_id: cid,
                reply_to: pendingMsg.replyToId || undefined,
                link_preview_disabled: !!pendingMsg.linkPreviewDisabled,
              };

        const res = await apiCallWithTimeout(url, { method: 'POST', body: JSON.stringify(payload) }, CHAT_SEND_TIMEOUT_MS);
      if (res.ok) {
        const created = (await res.json()) as any;
          const newId = Number(created?.id || 0) || 0;
          const newCreatedAt = String(created?.created_at || pendingMsg.createdAt || new Date().toISOString());

          const chatKey = kind === 'room' ? `room:${tid}` : `dm:${tid}`;
          chatMessagesStore.getState().patchMessageByClientId(
            chatKey,
            cid,
            { id: newId || undefined, status: undefined, created_at: newCreatedAt } as any
          );

          if (uid) void patchOfflineChatMessageByClientId(uid, kind, tid, cid, { id: newId || undefined, created_at: newCreatedAt, status: undefined } as any);
          pendingMessagesRef.current.delete(cid);
        setPendingCount(pendingMessagesRef.current.size);
        return true;
      }

        // Комментарий на русском: постоянные ошибки (4xx кроме 429) — не ретраим бесконечно, помечаем ошибкой.
        const st = Number(res.status || 0) || 0;
        const permanent = st >= 400 && st < 500 && st !== 429;
        if (permanent) {
          const chatKey = kind === 'room' ? `room:${tid}` : `dm:${tid}`;
          chatMessagesStore.getState().patchMessageByClientId(chatKey, cid, { status: 'error' } as any);
          if (uid) void patchOfflineChatMessageByClientId(uid, kind, tid, cid, { status: 'error' } as any);
          pendingMessagesRef.current.delete(cid);
          setPendingCount(pendingMessagesRef.current.size);
        }
      } catch {
        // ignore
      }
    return false;
    },
    [apiCallWithTimeout, getMyUserId, patchOfflineChatMessageByClientId]
  );

  // Комментарий на русском: при восстановлении сети отправляем накопившиеся сообщения.
  useEffect(() => {
    if (!isOnline || pendingCount === 0) return;
    if (pendingSendingRef.current) return;
    pendingSendingRef.current = true;
    const sendAll = async () => {
      try {
      const pending = Array.from(pendingMessagesRef.current.values());
      for (const msg of pending) {
        await sendPendingMessage(msg);
        // Комментарий на русском: небольшая пауза между отправками.
        await new Promise((r) => setTimeout(r, 200));
      }
      loadConversationsThrottled();
      } finally {
        pendingSendingRef.current = false;
      }
    };
    void sendAll();
  }, [isOnline, loadConversationsThrottled, pendingCount, sendPendingMessage]);

  const sendMessage = useCallback(async (text?: string) => {
    if (!selectedUser || sending || !dmDraft.isCurrent()) return;
    const chatKey = `dm:${selectedUser.id}`;
    // Комментарий на русском: если включен режим редактирования — не создаём новое сообщение, а обновляем существующее.
    if (editTarget) {
      const editId = Number((editTarget as any)?.id || 0) || 0;
      if (editId > 0) {
        const rawEdit = typeof text === 'string' ? text : draft;
        const vEdit = validateMessage(rawEdit);
        if (!vEdit.isValid) {
          setError(vEdit.error || 'Ошибка валидации');
          setTimeout(() => setError(null), 2500);
          return;
        }
        const cleanEdit = sanitizeMessage(rawEdit);
        if (!cleanEdit) return;

        const prevText = String((editTarget as any)?.message || '');
        const optimisticEditedAt = new Date().toISOString();
        setDraft('');
        setError(null);
        setSending(true);
        chatMessagesStore.getState().patchMessage(chatKey, editId, { message: cleanEdit, edited_at: optimisticEditedAt } as any);
        setEditTarget(null);

        try {
          const res = await apiCall(`/api/chat/message/${editId}/edit/`, {
            method: 'PATCH',
            body: JSON.stringify({ message: cleanEdit }),
          });
          if (!res.ok) {
            let msg = `Не удалось изменить (HTTP ${res.status})`;
            try {
              const data = (await res.json()) as any;
              if (data?.error) msg = String(data.error);
            } catch {}
            throw new Error(msg);
          }
          const updated = (await res.json()) as any;
          chatMessagesStore.getState().patchMessage(chatKey, editId, {
            message: String(updated?.message || cleanEdit),
            edited_at: String(updated?.edited_at || optimisticEditedAt),
          } as any);
          loadConversationsThrottled();
        } catch (e: any) {
          chatMessagesStore.getState().patchMessage(chatKey, editId, { message: prevText } as any);
          if (dmDraft.isCurrent()) {
            setError(String(e?.message || 'Не удалось изменить сообщение'));
            setTimeout(() => setError(null), 2500);
            // Возвращаем редактор только в тот же диалог и ту же сессию черновика.
            setEditTarget(editTarget);
            setDraft(cleanEdit);
          }
        } finally {
          setSending(false);
        }
        return;
      }
    }
    let raw = typeof text === 'string' ? text : draft;
    const includeProject = canUseProjectContext && typeof text !== 'string' && Boolean(dmDraft.projectId);
    if (includeProject) {
      if (!activeDraftProject) {
        setError('Проект ещё не проверен. Дождитесь проверки или уберите тег.');
        return;
      }
      try { raw = serializeContextMessage(raw, activeDraftProject, window.location.origin); }
      catch (reason) { setError(reason instanceof Error ? reason.message : 'Не удалось добавить контекст проекта.'); return; }
    }
    const v = validateMessage(raw);
    if (!v.isValid) {
      setError(v.error || 'Ошибка валидации');
      setTimeout(() => setError(null), 2500);
      return;
    }

    const clean = sanitizeMessage(raw);
    if (!clean) return;

    // Комментарий на русском: если превью ссылок глобально выключены или пользователь нажал “отправить без превью” для текущей ссылки.
    const linkPreviewDisabled = !linkPreviewsEnabled || (!!draftFirstUrl && draftFirstUrl === draftPreviewSuppressedUrl);

    const me = meRef.current || { id: 0, username: '', display_name: 'Вы' };
    const optimisticId = Date.now();
    const clientId = makeClientId();
    const replyToId = replyTo?.id || 0;
    const replyPreview = replyTo
      ? {
          id: replyTo.id,
          from_user: replyTo.from_user || null,
          message: replyTo.message || '',
          type: replyTo.type,
          voice_url: replyTo.voice_url ?? null,
          voice_mime: replyTo.voice_mime ?? null,
          created_at: replyTo.created_at || null,
        }
      : null;
    // Комментарий на русском: статус "pending" для офлайн-сообщений, "sending" для онлайн.
    const msgStatus = isOnline ? 'sending' : 'pending';
    const optimistic: DirectMessage = {
      id: optimisticId,
      from_user: { ...me, id: me.id || 0 },
      to_user: selectedUser,
      message: clean,
      created_at: new Date().toISOString(),
      is_read: false,
      status: msgStatus,
      client_id: clientId,
      link_preview_disabled: linkPreviewDisabled,
      reply_to: replyPreview,
      reactions: [],
    };

    setDraft('');
    setDraftPreviewSuppressedUrl('');
    if (includeProject) {
      dmDraft.setProjectId(null);
      setVerifiedDraftProject(null);
    }
    if (replyToId) setReplyTo(null);
    // Комментарий на русском: всегда фиксируем “низ” ДО blur клавиатуры — иначе iOS может восстановить старый offset и «подпрыгнуть» вверх.
    forceScrollToBottom(selectedUser.id);
    // Комментарий на русском: на мобилке оставляем прежнее поведение (прячем ввод), на десктопе — оставляем открытым и возвращаем фокус.
    const isMobile = isMobileViewport();
    setComposeInChat(!isMobile);
    if (!isMobile) {
      try {
        (draftChatRef.current as any)?.focus?.({ preventScroll: true });
      } catch {
        draftChatRef.current?.focus();
      }
    } else {
      try {
        draftChatRef.current?.blur();
      } catch {}
    }
    setError(null);
    setSending(true);
    chatMessagesStore.getState().upsertMessages(chatKey, [optimistic], { mode: 'append' });

    // Комментарий на русском: сразу фиксируем в офлайн-хранилище, чтобы сообщение не пропало при закрытии вкладки.
    const uid = getMyUserId();
    if (uid) void upsertOfflineChatMessage(uid, 'dm', selectedUser.id, optimistic);

    // Комментарий на русском: если офлайн — добавляем в очередь и выходим.
    if (!isOnline) {
      pendingMessagesRef.current.set(clientId, {
        kind: 'dm',
        targetId: selectedUser.id,
        message: clean,
        replyToId: replyToId || undefined,
        clientId,
        createdAt: optimistic.created_at,
        linkPreviewDisabled: linkPreviewDisabled,
      });
      setPendingCount(pendingMessagesRef.current.size);
      if (uid) void chatKvSet(offlinePendingKey(uid), Array.from(pendingMessagesRef.current.values()));
      setSending(false);
      return;
    }

    try {
      const res = await apiCallWithTimeout('/api/chat/direct-messages/', {
        method: 'POST',
        body: JSON.stringify({
          to_user_id: selectedUser.id,
          message: clean,
          client_id: clientId,
          reply_to: replyToId || undefined,
          link_preview_disabled: linkPreviewDisabled,
        }),
      }, CHAT_SEND_TIMEOUT_MS);
      if (!res.ok) {
        const st = Number(res.status || 0) || 0;
        const transient = st >= 500 || st === 0 || st === 408 || st === 429;
        if (transient) {
          // Комментарий на русском: временная проблема сети/сервера — ставим в очередь.
        pendingMessagesRef.current.set(clientId, {
            kind: 'dm',
            targetId: selectedUser.id,
          message: clean,
            replyToId: replyToId || undefined,
          clientId,
            createdAt: optimistic.created_at,
            linkPreviewDisabled: linkPreviewDisabled,
        });
        setPendingCount(pendingMessagesRef.current.size);
          if (uid) void chatKvSet(offlinePendingKey(uid), Array.from(pendingMessagesRef.current.values()));
        chatMessagesStore.getState().patchMessage(chatKey, optimisticId, { status: 'pending' } as any);
          setError('Сообщение будет отправлено при восстановлении связи');
          setTimeout(() => setError(null), 3000);
          return;
        }

        // Комментарий на русском: постоянная ошибка — не ставим в очередь.
      chatMessagesStore.getState().patchMessage(chatKey, optimisticId, { status: 'error' } as any);
        let msg = `Не удалось отправить (HTTP ${st || res.status})`;
        try {
          const data = (await res.json()) as any;
          if (data?.error) msg = String(data.error);
          if (data?.hint) msg = `${msg}. ${String(data.hint)}`;
        } catch {}
        setError(msg);
        setTimeout(() => setError(null), 3000);
        return;
      }
      const created = (await res.json()) as any;
      const serverMsg: DirectMessage = {
        id: Number((created as any)?.id || 0) || optimisticId,
        created_at: String((created as any)?.created_at || optimistic.created_at || new Date().toISOString()),
        client_id: ((created as any)?.client_id as any) ?? clientId,
      } as any;
      const up = chatMessagesStore.getState().upsertMessages(chatKey, [serverMsg], { mode: 'append', meId: me.id });
      if (up.maxServerId) lastMessageIdRef.current = Math.max(lastMessageIdRef.current, up.maxServerId);
      loadConversationsThrottled();
      lastMessageIdRef.current = Math.max(lastMessageIdRef.current, (created?.id as number) || 0);
    } catch {
      // Комментарий на русском: ошибка сети/таймаут — ставим в очередь.
      pendingMessagesRef.current.set(clientId, {
        kind: 'dm',
        targetId: selectedUser.id,
        message: clean,
        replyToId: replyToId || undefined,
        clientId,
        createdAt: optimistic.created_at,
        linkPreviewDisabled,
      });
      setPendingCount(pendingMessagesRef.current.size);
      if (uid) void chatKvSet(offlinePendingKey(uid), Array.from(pendingMessagesRef.current.values()));
      chatMessagesStore.getState().patchMessage(chatKey, optimisticId, { status: 'pending' } as any);
      setError('Сообщение будет отправлено при восстановлении связи');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSending(false);
    }
  }, [
    apiCallWithTimeout,
    dmDraft.isCurrent,
    dmDraft.projectId,
    dmDraft.setProjectId,
    activeDraftProject,
    canUseProjectContext,
    setDraft,
    draft,
    draftFirstUrl,
    draftPreviewSuppressedUrl,
    editTarget,
    forceScrollToBottom,
    getMyUserId,
    loadConversations,
    replyTo,
    selectedUser,
    sending,
    linkPreviewsEnabled,
    upsertOfflineChatMessage,
  ]);

  const sendRoomMessage = useCallback(async () => {
    if (!selectedRoom || sending) return;
    const chatKey = `room:${selectedRoom.id}`;
    // Комментарий на русском: режим редактирования в комнате — обновляем существующее сообщение.
    if (editTarget) {
      const editId = Number((editTarget as any)?.id || 0) || 0;
      if (editId > 0) {
        const vEdit = validateMessage(roomDraft);
        if (!vEdit.isValid) {
          setError(vEdit.error || 'Ошибка валидации');
          setTimeout(() => setError(null), 2500);
          return;
        }
        const cleanEdit = sanitizeMessage(roomDraft);
        if (!cleanEdit) return;

        const prevText = String((editTarget as any)?.message || '');
        const optimisticEditedAt = new Date().toISOString();
        setRoomDraft('');
        setError(null);
        setSending(true);
        chatMessagesStore.getState().patchMessage(chatKey, editId, { message: cleanEdit, edited_at: optimisticEditedAt } as any);
        setEditTarget(null);

        try {
          const res = await apiCall(`/api/chat/rooms/${selectedRoom.id}/messages/${editId}/edit/`, {
            method: 'PATCH',
            body: JSON.stringify({ message: cleanEdit }),
          });
          if (!res.ok) {
            let msg = `Не удалось изменить (HTTP ${res.status})`;
            try {
              const data = (await res.json()) as any;
              if (data?.error) msg = String(data.error);
            } catch {}
            throw new Error(msg);
          }
          const updated = (await res.json()) as any;
          chatMessagesStore.getState().patchMessage(chatKey, editId, {
            message: String(updated?.message || cleanEdit),
            edited_at: String(updated?.edited_at || optimisticEditedAt),
          } as any);
          loadRoomsThrottled();
        } catch (e: any) {
          chatMessagesStore.getState().patchMessage(chatKey, editId, { message: prevText } as any);
          setError(String(e?.message || 'Не удалось изменить сообщение'));
          setTimeout(() => setError(null), 2500);
          setEditTarget(editTarget);
          setRoomDraft(cleanEdit);
        } finally {
          setSending(false);
        }
        return;
      }
    }
    const v = validateMessage(roomDraft);
    if (!v.isValid) {
      setError(v.error || 'Ошибка валидации');
      setTimeout(() => setError(null), 2500);
      return;
    }
    const clean = sanitizeMessage(roomDraft);
    if (!clean) return;
    const linkPreviewDisabled = !!roomDraftFirstUrl && roomDraftFirstUrl === roomDraftPreviewSuppressedUrl;

    const me = meRef.current || { id: 0, username: '', display_name: 'Вы' };
    const optimisticId = Date.now();
    const clientId = makeClientId();
    const replyToId = replyTo?.id || 0;
    const replyPreview = replyTo
      ? {
          id: replyTo.id,
          from_user: replyTo.from_user || null,
          message: replyTo.message || '',
          type: replyTo.type,
          voice_url: replyTo.voice_url ?? null,
          voice_mime: replyTo.voice_mime ?? null,
          created_at: replyTo.created_at || null,
        }
      : null;
    // Комментарий на русском: статус "pending" для офлайн-сообщений, "sending" для онлайн.
    const msgStatus = isOnline ? 'sending' : 'pending';
    const optimistic: DirectMessage = {
      id: optimisticId,
      from_user: { ...me, id: me.id || 0 },
      to_user: me as any,
      message: clean,
      created_at: new Date().toISOString(),
      // Комментарий на русском: в комнатах read-receipts не используем.
      is_read: false,
      status: msgStatus,
      client_id: clientId,
      link_preview_disabled: linkPreviewDisabled,
      reactions: [],
      reply_to: replyPreview,
    };

    setRoomDraft('');
    setRoomDraftPreviewSuppressedUrl('');
    if (replyToId) setReplyTo(null);
    // Комментарий на русском: на десктопе сразу возвращаем фокус в поле ввода после отправки.
    const isMobile = isMobileViewport();
    if (!isMobile) {
      try {
        (roomDraftRef.current as any)?.focus?.({ preventScroll: true });
      } catch {
        roomDraftRef.current?.focus();
      }
    } else {
      try {
        roomDraftRef.current?.blur();
      } catch {}
    }
    setError(null);
    setSending(true);
    chatMessagesStore.getState().upsertMessages(chatKey, [optimistic], { mode: 'append' });
    // Комментарий на русском: после отправки в комнате держим “низ”, чтобы чат не прыгал на мобилках.
    forceScrollToBottom();

    // Комментарий на русском: сразу фиксируем в офлайн-хранилище, чтобы не потерять при закрытии вкладки.
    const uid = getMyUserId();
    if (uid) void upsertOfflineChatMessage(uid, 'room', selectedRoom.id, optimistic);

    // Комментарий на русском: если офлайн — добавляем в очередь и выходим.
    if (!isOnline) {
      pendingMessagesRef.current.set(clientId, {
        kind: 'room',
        targetId: selectedRoom.id,
        message: clean,
        replyToId: replyToId || undefined,
        clientId,
        createdAt: optimistic.created_at,
        linkPreviewDisabled: linkPreviewDisabled,
      });
      setPendingCount(pendingMessagesRef.current.size);
      if (uid) void chatKvSet(offlinePendingKey(uid), Array.from(pendingMessagesRef.current.values()));
      setSending(false);
      return;
    }

    try {
      const res = await apiCallWithTimeout(`/api/chat/rooms/${selectedRoom.id}/messages/`, {
        method: 'POST',
        body: JSON.stringify({
          message: clean,
          client_id: clientId,
          reply_to: replyToId || undefined,
          link_preview_disabled: linkPreviewDisabled,
        }),
      }, CHAT_SEND_TIMEOUT_MS);
      if (!res.ok) {
        const st = Number(res.status || 0) || 0;
        const transient = st >= 500 || st === 0 || st === 408 || st === 429;
        if (transient) {
          pendingMessagesRef.current.set(clientId, {
            kind: 'room',
            targetId: selectedRoom.id,
            message: clean,
            replyToId: replyToId || undefined,
            clientId,
            createdAt: optimistic.created_at,
            linkPreviewDisabled: linkPreviewDisabled,
          });
          setPendingCount(pendingMessagesRef.current.size);
          if (uid) void chatKvSet(offlinePendingKey(uid), Array.from(pendingMessagesRef.current.values()));
          chatMessagesStore.getState().patchMessageByClientId(chatKey, clientId, { status: 'pending' } as any);
          setError('Сообщение будет отправлено при восстановлении связи');
          setTimeout(() => setError(null), 3000);
          return;
        }

        chatMessagesStore.getState().patchMessageByClientId(chatKey, clientId, { status: 'error' } as any);
        let msg = `Не удалось отправить (HTTP ${st || res.status})`;
        try {
          const data = (await res.json()) as any;
          if (data?.error) msg = String(data.error);
          if (data?.hint) msg = `${msg}. ${String(data.hint)}`;
        } catch {}
        setError(msg);
        setTimeout(() => setError(null), 3000);
        return;
      }
      const created = (await res.json()) as any;
      const serverId = Number(created?.id || 0) || 0;
      const newId = serverId || optimisticId;
      const serverMsg: DirectMessage = {
        id: newId,
        created_at: String(created?.created_at || optimistic.created_at || new Date().toISOString()),
        client_id: (created?.client_id as any) ?? clientId,
      } as any;
      const up = chatMessagesStore.getState().upsertMessages(chatKey, [serverMsg], { mode: 'append', meId: me.id });
      if (up.maxServerId) lastMessageIdRef.current = Math.max(lastMessageIdRef.current, up.maxServerId);
      // Комментарий на русском: lastMessageIdRef держим только по серверным id (иначе poll "умирает").
      if (serverId > 0) lastMessageIdRef.current = Math.max(lastMessageIdRef.current, serverId);
      loadRoomsThrottled();
    } catch {
      // Комментарий на русском: ошибка сети/таймаут — ставим в очередь.
      pendingMessagesRef.current.set(clientId, {
        kind: 'room',
        targetId: selectedRoom.id,
        message: clean,
        replyToId: replyToId || undefined,
        clientId,
        createdAt: optimistic.created_at,
      });
      setPendingCount(pendingMessagesRef.current.size);
      if (uid) void chatKvSet(offlinePendingKey(uid), Array.from(pendingMessagesRef.current.values()));
      chatMessagesStore.getState().patchMessageByClientId(chatKey, clientId, { status: 'pending' } as any);
      setError('Сообщение будет отправлено при восстановлении связи');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSending(false);
    }
  }, [
    apiCallWithTimeout,
    editTarget,
    forceScrollToBottom,
    getMyUserId,
    loadRooms,
    replyTo,
    roomDraft,
    roomDraftFirstUrl,
    roomDraftPreviewSuppressedUrl,
    selectedRoom,
    sending,
    upsertOfflineChatMessage,
  ]);

  const sendFriendRequest = useCallback(
    async (toUserId: number) => {
      try {
        const res = await apiCall('/api/chat/friends/request/', {
          method: 'POST',
          body: JSON.stringify({ to_user_id: toUserId }),
        });
        if (!res.ok) return;
        setUserResults((prev) => prev.map((u) => (u.id === toUserId ? { ...u, friendship_status: 'outgoing_pending' } : u)));
      } catch {
        // ignore
      }
    },
    [apiCall]
  );

  const handleFriendRequest = useCallback(
    async (friendshipId: number, action: 'accept' | 'decline') => {
      try {
        const res = await apiCall(`/api/chat/friends/request/${friendshipId}/${action}/`, { method: 'POST' });
        if (!res.ok) return;
        setFriendRequests((prev) => prev.filter((r) => r.id !== friendshipId));
        if (action === 'accept') {
          void loadFriends();
          loadConversationsThrottled();
        }
      } catch {
        // ignore
      }
    },
    [apiCall, loadFriends, loadConversationsThrottled]
  );

  const removeFriend = useCallback(
    async (userId: number) => {
      if (!confirm('Удалить пользователя из друзей?')) return;
      try {
        const res = await apiCall(`/api/chat/friends/${userId}/remove/`, { method: 'DELETE' });
        if (!res.ok) return;
        setFriends((prev) => prev.filter((f) => f.id !== userId));
        loadConversationsThrottled();
      } catch {
        // ignore
      }
    },
    [apiCall, loadConversationsThrottled]
  );

  // Комментарий на русском: поиск пользователей для "Контакты" (поле поиска всегда сверху).
  useEffect(() => {
    if (tab !== 'friends') return;
    const q = userQuery.trim();
    if (q.length < 2) {
      setUserResults([]);
      setUserSearching(false);
      return;
    }
    const t = setTimeout(async () => {
      setUserSearching(true);
      try {
        const res = await apiCall(`/api/chat/users/search/?q=${encodeURIComponent(q)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data)) setUserResults(data);
      } catch {
        // ignore
      } finally {
        setUserSearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [apiCall, tab, userQuery]);

  // Комментарий на русском: на десктопе справа показываем полноценный профиль выбранного контакта (без перехода по страницам).
  useEffect(() => {
    if (tab !== 'friends') return;
    const id = selectedContact?.id || 0;
    if (!id) {
      setSelectedContactProfile(null);
      setSelectedContactProfileLoading(false);
      return;
    }

    // Комментарий на русском: не грузим «встроенный» профиль на мобильной — там профиль открывается отдельной страницей.
    const isDesktop = typeof window !== 'undefined' && !!window.matchMedia?.('(min-width: 640px)').matches;
    if (!isDesktop) return;

    let alive = true;
    setSelectedContactProfile(null);
    setSelectedContactProfileLoading(true);
    (async () => {
      try {
        const p = await apiClient.getUserPreview(id);
        if (!alive) return;
        setSelectedContactProfile(p);
      } catch {
        if (!alive) return;
        setSelectedContactProfile(null);
      } finally {
        if (alive) setSelectedContactProfileLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [selectedContact?.id, tab]);

  const isSuperAdmin = myIsSuperRef.current;
  const isEmployee = myRoleRef.current === 'Сотрудник';
  const supportChatOpen = supportOpen && (!isSuperAdmin || !!supportTargetUser);
  // Комментарий на русском: правая панель ошибок открыта (для суперадмина).
  const errorsChatOpen = supportOpen && isSuperAdmin && showErrorsChat;
  const mobileChatOpen = !!selectedUser || !!selectedRoom || supportChatOpen || errorsChatOpen;
  // Комментарий на русском: жест "с левого края вправо" — быстрый выход из чата (как в приложениях).
  const edgeSwipeRef = useRef({ x: 0, y: 0, t: 0, edge: false });
  const backToChats = () => {
    const chatKey = selectedRoom?.id ? `room:${selectedRoom.id}` : selectedUser?.id ? `dm:${selectedUser.id}` : '';
    setTab('chats');
    setSupportOpen(false);
    setSupportTargetUser(null);
    setSupportMessages([]);
    supportLastIdRef.current = 0;
    setSelectedUser(null);
    setSelectedRoom(null);
    if (chatKey) chatMessagesStore.getState().clearChat(chatKey);
    lastMessageIdRef.current = 0;
  };
  const handleEdgeSwipeStart = (e: any) => {
    if (!isMobileLikeViewport() || !mobileChatOpen) return;
    const t = e?.touches?.[0];
    if (!t) return;
    edgeSwipeRef.current.x = t.clientX || 0;
    edgeSwipeRef.current.y = t.clientY || 0;
    edgeSwipeRef.current.t = Date.now();
    edgeSwipeRef.current.edge = (t.clientX || 0) <= 24;
  };
  const handleEdgeSwipeEnd = (e: any) => {
    const s = edgeSwipeRef.current;
    if (!s.edge) return;
    s.edge = false;
    const t = e?.changedTouches?.[0];
    if (!t) return;
    const dx = (t.clientX || 0) - s.x;
    const dy = Math.abs((t.clientY || 0) - s.y);
    const dt = Date.now() - s.t;
    if (dx >= 70 && dy <= 70 && dt <= 800) backToChats();
  };

  const handleMyNicknameDraftChange = useCallback((value: string) => {
    myNicknameDirtyRef.current = true;
    setMyNicknameDraft(value);
  }, []);

  const handleMyProfileFieldChange = useCallback((field: keyof MyProfileForm, value: string) => {
    myProfileDirtyRef.current = true;
    setMyProfileForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Комментарий на русском: общий контент выбранного раздела настроек (рендерим и на мобилке, и справа на десктопе).
  const settingsSectionContent = (
    <>
      {/* ===== ПРОФИЛЬ ===== */}
      {settingsSection === 'profile' && (
        <MessengerProfileSettingsSection
          myProfile={myProfile as any}
          currentUser={meRef.current as any}
          avatarFxColor={avatarFxColor}
          avatarFxThickness={avatarFxThickness}
          avatarFxGlow={avatarFxGlow}
          avatarFxOfflineColor={avatarFxOfflineColor}
          avatarBusy={avatarBusy}
          profileBusy={profileBusy}
          canDeleteAvatar={!!(myProfile?.avatar_url || meRef.current?.avatar_url)}
          nicknameDraft={myNicknameDraft}
          canSaveNickname={!!normalizeNicknameDraft(myNicknameDraft)}
          profileForm={myProfileForm}
          onAvatarSelect={handleMyAvatarSelect}
          onDeleteAvatar={() => void deleteMyAvatar()}
          onNicknameChange={handleMyNicknameDraftChange}
          onSaveNickname={() => void saveMyNickname()}
          onProfileFieldChange={handleMyProfileFieldChange}
          onSaveProfile={() => void saveMyProfileForm()}
          onResetProfile={resetMyProfileForm}
        />
      )}

      {/* ===== ЭФФЕКТЫ АВАТАРА ===== */}
      {settingsSection === 'avatar' && (
        <>
          <div className="text-xs text-site-muted px-1 sm:hidden">Эффекты аватара</div>
          <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Предпросмотр</div>
                <div className="text-xs text-site-muted truncate">Онлайн + свечение, офлайн без свечения</div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <AvatarFx
                    sizeClass="w-11 h-11"
                    avatarUrl={(myProfile?.avatar_url ?? meRef.current?.avatar_url ?? null) as any}
                    alt="Онлайн"
                    fallback=""
                    isOnline={true}
                    fxColor={avatarFxColor}
                    fxThickness={avatarFxThickness}
                    fxGlow={avatarFxGlow}
                    fxOfflineColor={avatarFxOfflineColor}
                  />
                  <div className="text-[11px] text-site-success">● онлайн</div>
                </div>
                <div className="flex items-center gap-2">
                  <AvatarFx
                    sizeClass="w-11 h-11"
                    avatarUrl={(myProfile?.avatar_url ?? meRef.current?.avatar_url ?? null) as any}
                    alt="Офлайн"
                    fallback=""
                    isOnline={false}
                    fxColor={avatarFxColor}
                    fxThickness={avatarFxThickness}
                    fxGlow={avatarFxGlow}
                    fxOfflineColor={avatarFxOfflineColor}
                  />
                  <div className="text-[11px] text-site-muted">○ офлайн</div>
                </div>
              </div>
            </div>

            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Цвет онлайн</div>
                <div className="text-xs text-site-muted truncate">{avatarFxColor}</div>
              </div>
              <input
                type="color"
                value={avatarFxColor}
                onChange={(e) => {
                  const next = String(e.target.value || '#22c55e');
                  setAvatarFxColor(next);
                  saveAvatarFxDebounced({ color: next, thickness: avatarFxThickness, glow: avatarFxGlow, offlineColor: avatarFxOfflineColor });
                }}
                className="h-9 w-12 bg-transparent border border-site-border rounded-lg"
                aria-label="Цвет обрамления онлайн"
              />
            </div>

            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Толщина онлайн</div>
                <div className="text-xs text-site-muted truncate">{avatarFxThickness}px</div>
              </div>
              <div className="flex items-center gap-3 w-48">
                <input
                  type="range"
                  min={0}
                  max={10}
                  value={avatarFxThickness}
                  onChange={(e) => {
                    const next = Number(e.target.value || 0) || 0;
                    setAvatarFxThickness(next);
                    saveAvatarFxDebounced({ color: avatarFxColor, thickness: next, glow: avatarFxGlow, offlineColor: avatarFxOfflineColor });
                  }}
                  className="flex-1"
                  aria-label="Толщина обрамления онлайн"
                />
                <div className="w-10 text-right text-xs text-site-muted">{avatarFxThickness}px</div>
              </div>
            </div>

            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Свечение онлайн</div>
                <div className="text-xs text-site-muted truncate">{avatarFxGlow}%</div>
              </div>
              <div className="flex items-center gap-3 w-48">
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={avatarFxGlow}
                  onChange={(e) => {
                    const next = Number(e.target.value || 0) || 0;
                    setAvatarFxGlow(next);
                    saveAvatarFxDebounced({ color: avatarFxColor, thickness: avatarFxThickness, glow: next, offlineColor: avatarFxOfflineColor });
                  }}
                  className="flex-1"
                  aria-label="Интенсивность свечения онлайн"
                />
                <div className="w-10 text-right text-xs text-site-muted">{avatarFxGlow}%</div>
              </div>
            </div>

            {/* Комментарий на русском: настройка цвета офлайн-обрамления. */}
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Цвет офлайн</div>
                <div className="text-xs text-site-muted truncate">{avatarFxOfflineColor}</div>
              </div>
              <input
                type="color"
                value={avatarFxOfflineColor}
                onChange={(e) => {
                  const next = String(e.target.value || '#64748b');
                  setAvatarFxOfflineColor(next);
                  saveAvatarFxDebounced({ color: avatarFxColor, thickness: avatarFxThickness, glow: avatarFxGlow, offlineColor: next });
                }}
                className="h-9 w-12 bg-transparent border border-site-border rounded-lg"
                aria-label="Цвет обрамления офлайн"
              />
            </div>

            {/* Комментарий на русском: информация про офлайн-режим. */}
            <div className="px-4 py-2 bg-site-bg/30">
              <div className="text-xs text-site-muted"><UiIcon name="Lightbulb" /> Офлайн: фикс. толщина 2px, без свечения</div>
            </div>

            <div className="px-4 py-3">
              <button
                type="button"
                onClick={() => {
                  const color = '#22c55e';
                  const thickness = 2;
                  const glow = 0;
                  const offlineColor = '#64748b';
                  setAvatarFxColor(color);
                  setAvatarFxThickness(thickness);
                  setAvatarFxGlow(glow);
                  setAvatarFxOfflineColor(offlineColor);
                  void updateChatSettings({
                    avatar_fx_color: color,
                    avatar_fx_thickness: thickness,
                    avatar_fx_glow: glow,
                    avatar_fx_offline_color: offlineColor,
                  } as any);
                }}
                className="w-full px-3 py-2 rounded-lg text-xs font-semibold border border-site-border text-site-text hover:text-site-text hover:bg-site-raised/25"
                title="Вернуть настройки по умолчанию"
              >
                Сбросить по умолчанию
              </button>
            </div>
          </div>
        </>
      )}

      {/* ===== УВЕДОМЛЕНИЯ ===== */}
      {settingsSection === 'notifications' && (
        <>
          <div className="text-xs text-site-muted px-1 sm:hidden">Уведомления</div>
          <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Push-уведомления</div>
                <div className="text-xs text-site-muted truncate">Уведомления о новых сообщениях</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={pushEnabled}
                onClick={() => void requestBrowserNotifications()}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${pushEnabled ? 'bg-site-success' : 'bg-site-raised/60'}`}
                title={pushEnabled ? 'Отключить уведомления' : 'Включить уведомления'}
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${pushEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Превью сообщений</div>
                <div className="text-xs text-site-muted">Показывать текст в списке чатов</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={showMessagePreview}
                onClick={() => setShowMessagePreview((v) => !v)}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${showMessagePreview ? 'bg-site-success' : 'bg-site-raised/60'}`}
                title={showMessagePreview ? 'Скрыть содержимое' : 'Показывать содержимое'}
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${showMessagePreview ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Содержимое в уведомлениях</div>
                <div className="text-xs text-site-muted">Показывать текст/тип вложения в уведомлениях</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={showNotificationContent}
                onClick={() => setShowNotificationContent((v) => !v)}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${showNotificationContent ? 'bg-site-success' : 'bg-site-raised/60'}`}
                title={showNotificationContent ? 'Скрыть содержимое' : 'Показывать содержимое'}
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${showNotificationContent ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* ===== ПРИВАТНОСТЬ ===== */}
      {settingsSection === 'privacy' && (
        <>
          <div className="text-xs text-site-muted px-1 sm:hidden">Приватность</div>
          <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Подтверждения прочтения</div>
                <div className="text-xs text-site-muted truncate">Показывать собеседнику статус «прочитано»</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={readReceiptsEnabled}
                onClick={() => {
                  const next = !readReceiptsEnabled;
                  setReadReceiptsEnabled(next);
                  void updateChatSettings({ read_receipts_enabled: next }).then((ok) => {
                    if (!ok) setReadReceiptsEnabled(!next);
                  });
                }}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${readReceiptsEnabled ? 'bg-site-success' : 'bg-site-raised/60'}`}
                title={readReceiptsEnabled ? 'Отключить' : 'Включить'}
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${readReceiptsEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* ===== ИНТЕРФЕЙС ===== */}
      {settingsSection === 'interface' && (
        <>
          {/* Комментарий на русском: липкая шапка-превью — пользователь всегда видит эффект настроек. */}
          <div className="sticky top-0 z-30 -mx-3 sm:-mx-4 px-3 sm:px-4 pt-2 pb-3 bg-site-bg/70 backdrop-blur-md border-b border-site-border">
            <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm p-3 font-[var(--dm-font-family)]">
              <div className="sr-only">Пример сообщений</div>
              <div className="space-y-[var(--dm-gap,0.75rem)]">
                <div className="flex justify-start">
                  <div
                    className="lisad-msg-bubble lisad-msg-bubble--peer max-w-[min(78%,360px)] px-[var(--dm-pad-x,1rem)] py-[var(--dm-pad-y,0.5rem)] rounded-2xl rounded-bl-md"
                    style={{ fontSize: 'var(--dm-font,0.875rem)', lineHeight: 'var(--dm-leading,1.5)' }}
                  >
                    Наука начинается там, где сомнение становится методом, а наблюдение — дисциплиной.
                  </div>
                </div>
                <div className="flex justify-end">
                  <div
                    className="lisad-msg-bubble lisad-msg-bubble--me max-w-[min(78%,360px)] px-[var(--dm-pad-x,1rem)] py-[var(--dm-pad-y,0.5rem)] rounded-2xl rounded-br-md"
                    style={{ fontSize: 'var(--dm-font,0.875rem)', lineHeight: 'var(--dm-leading,1.5)' }}
                  >
                    Истина — не трофей, а процесс: измерение уточняет теорию, а теория учит, что именно измерять.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-xs text-site-muted px-1 sm:hidden">Интерфейс</div>
          <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
            <div className="px-4 py-2 bg-site-bg/25 text-[11px] text-site-muted uppercase tracking-wider">Основное</div>
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Превью ссылок</div>
                <div className="text-xs text-site-muted truncate">Преобразовывать URL в кликабельные ссылки</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={linkPreviewsEnabled}
                onClick={() => {
                  const next = !linkPreviewsEnabled;
                  setLinkPreviewsEnabled(next);
                  void updateChatSettings({ link_previews_enabled: next }).then((ok) => {
                    if (!ok) setLinkPreviewsEnabled(!next);
                  });
                }}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${linkPreviewsEnabled ? 'bg-site-success' : 'bg-site-raised/60'}`}
                title={linkPreviewsEnabled ? 'Отключить' : 'Включить'}
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${linkPreviewsEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Стопки сообщений</div>
                <div className="text-xs text-site-muted truncate">Группировать подряд идущие сообщения</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={messageStackingEnabled}
                onClick={() => {
                  const next = !messageStackingEnabled;
                  setMessageStackingEnabled(next);
                  void updateChatSettings({ message_stacking_enabled: next }).then((ok) => {
                    if (!ok) setMessageStackingEnabled(!next);
                  });
                }}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${messageStackingEnabled ? 'bg-site-success' : 'bg-site-raised/60'}`}
                title={messageStackingEnabled ? 'Отключить' : 'Включить'}
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${messageStackingEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Компактный режим</div>
                <div className="text-xs text-site-muted truncate">Плотнее список диалогов</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={compactMode}
                onClick={() => {
                  const next = !compactMode;
                  setCompactMode(next);
                  void updateChatSettings({ compact_mode: next }).then((ok) => {
                    if (!ok) setCompactMode(!next);
                  });
                }}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${compactMode ? 'bg-site-success' : 'bg-site-raised/60'}`}
                title={compactMode ? 'Отключить' : 'Включить'}
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${compactMode ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Размер текста</div>
                <div className="text-xs text-site-muted truncate">Масштаб сообщений</div>
              </div>
              <div className="w-44 sm:w-48">
                <input
                  type="range"
                  min={8}
                  max={24}
                  step={1}
                  value={Math.max(8, Math.min(24, Math.round(14 * (Number(fontScale) || 100) / 100)))}
                  onChange={(e) => {
                    const prev = fontScale;
                    const px = Number(e.target.value || 14) || 14;
                    const next = Math.max(50, Math.min(200, Math.round((px / 14) * 100)));
                    setFontScale(next);
                    void updateChatSettings({ font_scale: next }).then((ok) => {
                      if (!ok) setFontScale(prev);
                    });
                  }}
                  className="touch-slider"
                  aria-label="Размер текста сообщений"
                />
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-[10px] text-site-secondary select-none">Аа</span>
                  <span className="text-[11px] text-site-muted tabular-nums select-none">
                    {Math.max(8, Math.min(24, Math.round(14 * (Number(fontScale) || 100) / 100)))}px
                  </span>
                  <span className="text-base text-site-text select-none">Аа</span>
                </div>
              </div>
            </div>
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Плотность сообщений</div>
                <div className="text-xs text-site-muted truncate">Отступы и межстрочный интервал</div>
              </div>
              <div className="w-44 sm:w-48">
                <div className="relative">
                  <input
                    type="range"
                    min={0}
                    max={2}
                    step={1}
                    value={messageDensity === 'compact' ? 0 : messageDensity === 'comfortable' ? 2 : 1}
                    onChange={(e) => {
                      const prev = messageDensity;
                      const idx = Number(e.target.value || 1) || 0;
                      const next: MessageDensity = idx <= 0 ? 'compact' : idx >= 2 ? 'comfortable' : 'normal';
                      setMessageDensity(next);
                      void updateChatSettings({ message_density: next }).then((ok) => {
                        if (!ok) setMessageDensity(prev);
                      });
                    }}
                    className="touch-slider"
                    aria-label="Плотность сообщений"
                  />
                  <div aria-hidden="true" className="pointer-events-none absolute left-0 right-0 top-[7px] flex justify-between">
                    <span className="w-2 h-2 rounded-full bg-site-raised/60" />
                    <span className="w-2 h-2 rounded-full bg-site-raised/60" />
                    <span className="w-2 h-2 rounded-full bg-site-raised/60" />
                  </div>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      const prev = messageDensity;
                      const next: MessageDensity = 'compact';
                      setMessageDensity(next);
                      void updateChatSettings({ message_density: next }).then((ok) => {
                        if (!ok) setMessageDensity(prev);
                      });
                    }}
                    className="text-[10px] text-site-secondary hover:text-site-text select-none"
                    title="Компактно"
                    aria-label="Компактно"
                  >
                    Аа
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const prev = messageDensity;
                      const next: MessageDensity = 'normal';
                      setMessageDensity(next);
                      void updateChatSettings({ message_density: next }).then((ok) => {
                        if (!ok) setMessageDensity(prev);
                      });
                    }}
                    className="text-xs text-site-secondary hover:text-site-text select-none"
                    title="Нормально"
                    aria-label="Нормально"
                  >
                    Аа
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const prev = messageDensity;
                      const next: MessageDensity = 'comfortable';
                      setMessageDensity(next);
                      void updateChatSettings({ message_density: next }).then((ok) => {
                        if (!ok) setMessageDensity(prev);
                      });
                    }}
                    className="text-sm text-site-text hover:text-site-text select-none"
                    title="Комфортно"
                    aria-label="Комфортно"
                  >
                    Аа
                  </button>
                </div>
              </div>
            </div>

            {/* Комментарий на русском: настройки фона чата перенесены в профиль комнаты (вкладка "Дизайн"). */}

            {/* Комментарий на русском: ручное "Сохранить" убрано — настройки должны применяться сразу. */}
          </div>

          <div className="mt-3 rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden">
            <div className="px-4 py-2 bg-site-bg/25 text-[11px] text-site-muted uppercase tracking-wider">Темы</div>
            <div className="px-4 py-3">
              <div className="text-xs text-site-muted">Палитра · прозрачность · свечение</div>
              <div className="mt-2 flex gap-3 overflow-x-auto pb-1 scrollbar-hide" data-scroll-lock-allow>
                {MESSAGE_THEMES.map((t) => {
                  const isActive = messageTheme === t.id;
                  const bg = `conic-gradient(from 210deg, ${t.me} 0deg, ${t.base} 110deg, ${t.peer} 220deg, ${t.me} 360deg)`;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => void applyMessageThemePreset(t.id)}
                      className={`w-11 h-11 rounded-full shrink-0 border ${isActive ? 'border-site-border ring-2 ring-site-accent/60' : 'border-site-border hover:border-site-border'} shadow-[0_10px_30px_rgba(0,0,0,0.35)]`}
                      style={{ background: bg }}
                      title={t.label}
                      aria-label={`Тема: ${t.label}`}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
            <div className="px-4 py-2 bg-site-bg/25 text-[11px] text-site-muted uppercase tracking-wider">Вид сообщений</div>

            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Прозрачность</div>
                <div className="text-xs text-site-muted truncate">0 — плотнее, 100 — прозрачнее</div>
              </div>
              <div className="flex items-center gap-3 w-48">
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={bubbleTransparency}
                  onChange={(e) => {
                    const next = Number(e.target.value || 0) || 0;
                    setBubbleTransparency(next);
                    markThemeCustom();
                    saveBubbleGlassDebounced({
                      transparency: next, color: bubbleColor, shadow: bubbleShadow, shadowColor: bubbleShadowColor,
                      shadowEnabled: bubbleShadowEnabled, glowEnabled: bubbleGlowEnabled, glowColor: bubbleGlowColor, glowX: bubbleGlowX, glowY: bubbleGlowY,
                    });
                  }}
                  className="flex-1"
                  aria-label="Прозрачность пузырьков"
                />
                <div className="w-10 text-right text-xs text-site-muted">{bubbleTransparency}%</div>
              </div>
            </div>

            {/* Комментарий на русском: ползунок "Размытие" убран для оптимизации производительности (backdrop-filter слишком дорогой). */}

            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Цвет пузырьков</div>
                <div className="text-xs text-site-muted truncate">{bubbleColor}</div>
              </div>
              <input
                type="color"
                value={bubbleColor}
                onChange={(e) => {
                  const next = String(e.target.value || '#7dd3fc');
                  setBubbleColor(next);
                  markThemeCustom();
                  saveBubbleGlassDebounced({
                    transparency: bubbleTransparency,
                    color: next,
                    colorMe: bubbleColorMe,
                    colorPeer: bubbleColorPeer,
                    shadow: bubbleShadow,
                    shadowColor: bubbleShadowColor,
                    shadowEnabled: bubbleShadowEnabled,
                    glowEnabled: bubbleGlowEnabled,
                    glowColor: bubbleGlowColor,
                    glowX: bubbleGlowX,
                    glowY: bubbleGlowY,
                  });
                }}
                className="h-9 w-12 bg-transparent border border-site-border rounded-lg"
                aria-label="Цвет пузырьков"
              />
            </div>

            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Отдельные цвета</div>
                <div className="text-xs text-site-muted truncate">
                  {bubbleColorMe ? 'Мои — свои' : 'Мои — по базовому'}
                  {' · '}
                  {bubbleColorPeer ? 'Собеседник — свой' : 'Собеседник — по базовому'}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-site-secondary">Мои</span>
                  <input
                    type="color"
                    value={bubbleColorMe || bubbleColor}
                    onChange={(e) => {
                      const next = String(e.target.value || '').toLowerCase();
                      markThemeCustom();
                      // Комментарий на русском: bubble_color = цвет "моих" (его видят другие в комнатах).
                      setBubbleColor(next);
                      setBubbleColorMe(next);
                      saveBubbleGlassDebounced({
                        transparency: bubbleTransparency,
                        color: next,
                        colorMe: next,
                        colorPeer: bubbleColorPeer,
                        shadow: bubbleShadow,
                        shadowColor: bubbleShadowColor,
                        shadowEnabled: bubbleShadowEnabled,
                        glowEnabled: bubbleGlowEnabled,
                        glowColor: bubbleGlowColor,
                        glowX: bubbleGlowX,
                        glowY: bubbleGlowY,
                      });
                    }}
                    className="h-9 w-12 bg-transparent border border-site-border rounded-lg"
                    aria-label="Цвет моих сообщений"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      markThemeCustom();
                      setBubbleColorMe('');
                      saveBubbleGlassDebounced({
                        transparency: bubbleTransparency,
                        color: bubbleColor,
                        colorMe: '',
                        colorPeer: bubbleColorPeer,
                        shadow: bubbleShadow,
                        shadowColor: bubbleShadowColor,
                        shadowEnabled: bubbleShadowEnabled,
                        glowEnabled: bubbleGlowEnabled,
                        glowColor: bubbleGlowColor,
                        glowX: bubbleGlowX,
                        glowY: bubbleGlowY,
                      });
                    }}
                    className="px-2 py-1.5 rounded-lg border border-site-border text-xs text-site-text hover:text-site-text hover:bg-site-raised/25"
                    title="Сбросить к базовому"
                  >
                    Сброс
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-site-secondary">Собеседник</span>
                  <input
                    type="color"
                    value={bubbleColorPeer || bubbleColor}
                    onChange={(e) => {
                      const next = String(e.target.value || '').toLowerCase();
                      setBubbleColorPeer(next);
                      saveBubbleGlassDebounced({
                        transparency: bubbleTransparency,
                        color: bubbleColor,
                        colorMe: bubbleColorMe,
                        colorPeer: next,
                        shadow: bubbleShadow,
                        shadowColor: bubbleShadowColor,
                        shadowEnabled: bubbleShadowEnabled,
                        glowEnabled: bubbleGlowEnabled,
                        glowColor: bubbleGlowColor,
                        glowX: bubbleGlowX,
                        glowY: bubbleGlowY,
                      });
                    }}
                    className="h-9 w-12 bg-transparent border border-site-border rounded-lg"
                    aria-label="Цвет сообщений собеседника"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setBubbleColorPeer('');
                      saveBubbleGlassDebounced({
                        transparency: bubbleTransparency,
                        color: bubbleColor,
                        colorMe: bubbleColorMe,
                        colorPeer: '',
                        shadow: bubbleShadow,
                        shadowColor: bubbleShadowColor,
                        shadowEnabled: bubbleShadowEnabled,
                        glowEnabled: bubbleGlowEnabled,
                        glowColor: bubbleGlowColor,
                        glowX: bubbleGlowX,
                        glowY: bubbleGlowY,
                      });
                    }}
                    className="px-2 py-1.5 rounded-lg border border-site-border text-xs text-site-text hover:text-site-text hover:bg-site-raised/25"
                    title="Сбросить к базовому"
                  >
                    Сброс
                  </button>
                </div>
              </div>
            </div>

            {/* ===== ТЕНИ (collapsible) ===== */}
            <div className="border-t border-site-border">
              <div className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-site-text">Тени</div>
                  <div className="text-xs text-site-muted truncate">{bubbleShadowEnabled ? `Вкл, ${bubbleShadow}%` : 'Выкл'}</div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={bubbleShadowEnabled}
                  onClick={() => {
                    const next = !bubbleShadowEnabled;
                    setBubbleShadowEnabled(next);
                    markThemeCustom();
                    saveBubbleGlassDebounced({
                      transparency: bubbleTransparency,
                      color: bubbleColor,
                      shadow: bubbleShadow,
                      shadowColor: bubbleShadowColor,
                      shadowEnabled: next,
                      glowEnabled: bubbleGlowEnabled,
                      glowColor: bubbleGlowColor,
                      glowX: bubbleGlowX,
                      glowY: bubbleGlowY,
                    });
                  }}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${bubbleShadowEnabled ? 'bg-site-success' : 'bg-site-raised/60'}`}
                >
                  <span className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${bubbleShadowEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
              </div>
              {bubbleShadowEnabled && (
                <div className="px-4 pb-3 space-y-3 sm:pl-8">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-site-secondary">Интенсивность</div>
                    <div className="flex items-center gap-2 w-32 sm:w-40">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={5}
                        value={bubbleShadow}
                        onChange={(e) => {
                          const next = Number(e.target.value || 0) || 0;
                          setBubbleShadow(next);
                          saveBubbleGlassDebounced({
                            transparency: bubbleTransparency, color: bubbleColor, shadow: next, shadowColor: bubbleShadowColor,
                            shadowEnabled: bubbleShadowEnabled, glowEnabled: bubbleGlowEnabled, glowColor: bubbleGlowColor, glowX: bubbleGlowX, glowY: bubbleGlowY,
                          });
                        }}
                        className="flex-1"
                      />
                      <div className="w-8 text-right text-xs text-site-muted">{bubbleShadow}%</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-site-secondary">Цвет</div>
                    <input
                      type="color"
                      value={bubbleShadowColor}
                      onChange={(e) => {
                        const next = String(e.target.value || '#0b1220');
                        setBubbleShadowColor(next);
                        saveBubbleGlassDebounced({
                          transparency: bubbleTransparency, color: bubbleColor, shadow: bubbleShadow, shadowColor: next,
                          shadowEnabled: bubbleShadowEnabled, glowEnabled: bubbleGlowEnabled, glowColor: bubbleGlowColor, glowX: bubbleGlowX, glowY: bubbleGlowY,
                        });
                      }}
                      className="h-8 w-10 bg-transparent border border-site-border rounded-lg"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ===== СВЕЧЕНИЕ (collapsible) ===== */}
            <div className="border-t border-site-border">
              <div className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-site-text">Свечение</div>
                  <div className="text-xs text-site-muted truncate">{bubbleGlowEnabled ? 'Вкл' : 'Выкл'}</div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={bubbleGlowEnabled}
                  onClick={() => {
                    const next = !bubbleGlowEnabled;
                    setBubbleGlowEnabled(next);
                    markThemeCustom();
                    saveBubbleGlassDebounced({
                      transparency: bubbleTransparency,
                      color: bubbleColor,
                      shadow: bubbleShadow,
                      shadowColor: bubbleShadowColor,
                      shadowEnabled: bubbleShadowEnabled,
                      glowEnabled: next,
                      glowColor: bubbleGlowColor,
                      glowX: bubbleGlowX,
                      glowY: bubbleGlowY,
                    });
                  }}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${bubbleGlowEnabled ? 'bg-site-success' : 'bg-site-raised/60'}`}
                >
                  <span className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${bubbleGlowEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
              </div>
              {bubbleGlowEnabled && (
                <div className="px-4 pb-3 space-y-3 sm:pl-8">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-site-secondary">Цвет</div>
                    <input
                      type="color"
                      value={bubbleGlowColor}
                      onChange={(e) => {
                        const next = String(e.target.value || '#ffffff');
                        setBubbleGlowColor(next);
                        markThemeCustom();
                        saveBubbleGlassDebounced({
                          transparency: bubbleTransparency, color: bubbleColor, shadow: bubbleShadow, shadowColor: bubbleShadowColor,
                          shadowEnabled: bubbleShadowEnabled, glowEnabled: bubbleGlowEnabled, glowColor: next, glowX: bubbleGlowX, glowY: bubbleGlowY,
                        });
                      }}
                      className="h-8 w-10 bg-transparent border border-site-border rounded-lg"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-site-secondary">Смещение X</div>
                    <div className="flex items-center gap-2 w-32 sm:w-40">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={bubbleGlowX}
                        onChange={(e) => {
                          const next = Number(e.target.value || 0) || 0;
                          setBubbleGlowX(next);
                          saveBubbleGlassDebounced({
                            transparency: bubbleTransparency, color: bubbleColor, shadow: bubbleShadow, shadowColor: bubbleShadowColor,
                            shadowEnabled: bubbleShadowEnabled, glowEnabled: bubbleGlowEnabled, glowColor: bubbleGlowColor, glowX: next, glowY: bubbleGlowY,
                          });
                        }}
                        className="flex-1"
                      />
                      <div className="w-8 text-right text-xs text-site-muted">{bubbleGlowX}%</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-site-secondary">Смещение Y</div>
                    <div className="flex items-center gap-2 w-32 sm:w-40">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={bubbleGlowY}
                        onChange={(e) => {
                          const next = Number(e.target.value || 0) || 0;
                          setBubbleGlowY(next);
                          saveBubbleGlassDebounced({
                            transparency: bubbleTransparency, color: bubbleColor, shadow: bubbleShadow, shadowColor: bubbleShadowColor,
                            shadowEnabled: bubbleShadowEnabled, glowEnabled: bubbleGlowEnabled, glowColor: bubbleGlowColor, glowX: bubbleGlowX, glowY: next,
                          });
                        }}
                        className="flex-1"
                      />
                      <div className="w-8 text-right text-xs text-site-muted">{bubbleGlowY}%</div>
                    </div>
                  </div>
                  {/* Комментарий на русском: параллакс по гироскопу телефона */}
                  <div className="flex items-center justify-between gap-3 pt-2 border-t border-site-border">
                    <div className="min-w-0">
                      <div className="text-xs text-site-secondary">Параллакс</div>
                      <div className="text-[10px] text-site-muted">По наклону телефона</div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={bubbleGlowGyro}
                      onClick={() => {
                        const next = !bubbleGlowGyro;
                        setBubbleGlowGyro(next);
                        void updateChatSettings({ bubble_glow_gyro: next } as any);
                      }}
                      className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${bubbleGlowGyro ? 'bg-site-success' : 'bg-site-raised/60'}`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-site-surface transition ${bubbleGlowGyro ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  {bubbleGlowGyro && (
                    <div className="flex items-center justify-between gap-3 pt-2">
                      <div className="text-xs text-site-secondary">Интенсивность</div>
                      <div className="flex items-center gap-2 w-32 sm:w-40">
                        <input
                          type="range"
                          min={1}
                          max={5}
                          value={bubbleGlowGyroIntensity}
                          onChange={(e) => {
                            const next = Number(e.target.value || 1) || 1;
                            setBubbleGlowGyroIntensity(next);
                            void updateChatSettings({ bubble_glow_gyro_intensity: next } as any);
                          }}
                          className="flex-1"
                        />
                        <div className="w-6 text-right text-xs text-site-muted">{bubbleGlowGyroIntensity}x</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Цвет текста</div>
                <div className="text-xs text-site-muted truncate">{bubbleTextColor}</div>
              </div>
              <input
                type="color"
                value={bubbleTextColor}
                onChange={(e) => {
                  const next = String(e.target.value || '#ffffff');
                  setBubbleTextColor(next);
                  saveMessageTextStyleDebounced({ textColor: next, font: messageFont });
                }}
                className="h-9 w-12 bg-transparent border border-site-border rounded-lg"
                aria-label="Цвет текста в пузырьках"
              />
            </div>

            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Шрифт текста</div>
                <div className="text-xs text-site-muted truncate">Для сообщений</div>
              </div>
              <select
                value={messageFont}
                onChange={(e) => {
                  const raw = String(e.target.value || 'system').toLowerCase();
                  const next = (raw === 'inter' || raw === 'serif' || raw === 'mono' ? raw : 'system') as any;
                  setMessageFont(next);
                  saveMessageTextStyleDebounced({ textColor: bubbleTextColor, font: next });
                }}
                className="bg-site-surface/60 border border-site-border rounded-lg px-2 py-1 text-sm text-site-text focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                aria-label="Шрифт текста сообщений"
              >
                <option value="system">Системный</option>
                <option value="inter">Inter (современный)</option>
                <option value="serif">Serif (книга)</option>
                <option value="mono">Mono (код)</option>
              </select>
            </div>

            <div className="px-4 py-3">
              <button
                type="button"
                onClick={() => {
                  const defs = { transparency: 82, color: '#7dd3fc', shadow: 35, shadowColor: '#0b1220', shadowEnabled: true, glowEnabled: true, glowColor: '#ffffff', glowX: 22, glowY: 18, glowGyro: false, glowGyroInt: 1 };
                  setBubbleTransparency(defs.transparency);
                  setBubbleBlur(90);
                  setBubbleColor(defs.color);
                  setBubbleShadow(defs.shadow);
                  setBubbleShadowColor(defs.shadowColor);
                  setBubbleShadowEnabled(defs.shadowEnabled);
                  setBubbleGlowEnabled(defs.glowEnabled);
                  setBubbleGlowColor(defs.glowColor);
                  setBubbleGlowX(defs.glowX);
                  setBubbleGlowY(defs.glowY);
                  setBubbleGlowGyro(defs.glowGyro);
                  setBubbleGlowGyroIntensity(defs.glowGyroInt);
                  void updateChatSettings({
                    bubble_transparency: defs.transparency, bubble_blur: 90, bubble_color: defs.color,
                    bubble_shadow: defs.shadow, bubble_shadow_color: defs.shadowColor, bubble_shadow_enabled: defs.shadowEnabled,
                    bubble_glow_enabled: defs.glowEnabled, bubble_glow_color: defs.glowColor, bubble_glow_x: defs.glowX, bubble_glow_y: defs.glowY,
                    bubble_glow_gyro: defs.glowGyro, bubble_glow_gyro_intensity: defs.glowGyroInt,
                  } as any);
                }}
                className="w-full px-3 py-2 rounded-lg text-xs font-semibold border border-site-border text-site-text hover:text-site-text hover:bg-site-raised/25"
                title="Вернуть стиль пузырьков по умолчанию"
              >
                Стандартный стиль сообщений
              </button>
            </div>
          </div>
        </>
      )}

      {/* ===== КЭШ ===== */}
      {settingsSection === 'storage' && (
        <>
          <div className="text-xs text-site-muted px-1 sm:hidden">Кэш</div>
          <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
            <div className="px-4 py-3 flex flex-wrap gap-3 items-center">
              <div className="min-w-[140px]">
                <div className="text-sm text-site-text">Занято</div>
                <div className="text-lg font-semibold text-site-text/90">{formatBytesShort(storageStats?.used || 0)}</div>
              </div>
              <div className="min-w-[140px]">
                <div className="text-sm text-site-text">Доступно</div>
                <div className="text-lg font-semibold text-site-success">{formatBytesShort(storageStats?.available || 0)}</div>
              </div>
              <div className="min-w-[140px]">
                <div className="text-sm text-site-text">Квота</div>
                <div className="text-lg font-semibold text-site-text">{formatBytesShort(storageStats?.quota || 0)}</div>
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="text-xs text-site-muted mb-1">Использование</div>
                <div className="w-full h-2 rounded-full bg-site-surface/60 overflow-hidden">
                  <div
                    className="h-full bg-site-success"
                    style={{
                      width: `${Math.min(100, Math.max(0, storageStats?.quota ? ((storageStats?.used ?? 0) / storageStats.quota) * 100 : 0))}%`,
                      transition: 'width 180ms ease',
                    }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void calcStorageStats()}
                  disabled={storageLoading || cachePurging}
                  className="px-3 py-2 text-xs rounded-lg border border-site-border text-site-text hover:text-site-text hover:bg-site-raised/25 disabled:opacity-50"
                  title="Пересчитать"
                >
                  ↻ Обновить
                </button>
                <button
                  type="button"
                  onClick={() => void handlePurgeStorage()}
                  disabled={cachePurging}
                  className="px-3 py-2 text-xs rounded-lg border border-site-danger/35 text-site-danger hover:bg-site-danger/15 disabled:opacity-50"
                  title="Очистить кэш"
                >
                  {cachePurging ? 'Очистка...' : cachePurgeArmed ? 'Нажмите ещё раз' : <><UiIcon name="Trash" />{" Очистить кэш"}</>}
                </button>
              </div>
            </div>

            {storageError && <div className="px-4 py-2 text-xs text-site-danger">{storageError}</div>}
            {storageLoading && <div className="px-4 py-2 text-xs text-site-muted">Считаем кэш...</div>}

            {storageStats && (
              <div className="divide-y divide-site-border">
                {storageStats.categories.map((c) => (
                  <div key={c.id} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm text-site-text truncate">{c.label}</div>
                      <div className="text-sm text-site-text">{formatBytesShort(c.size)}</div>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-site-surface/60 overflow-hidden">
                      <div
                        className="h-full bg-site-accent/80"
                        style={{
                          width: `${(storageStats?.used ?? 0) ? Math.min(100, Math.max(0, (c.size / (storageStats?.used ?? 0)) * 100)) : 0}%`,
                          transition: 'width 180ms ease',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ===== БЕЗОПАСНОСТЬ ===== */}
      {settingsSection === 'security' && (
        <>
          <div className="text-xs text-site-muted px-1 sm:hidden">Безопасность</div>
          <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Активные сессии</div>
                <div className="text-xs text-site-muted truncate">{sessions.length ? `Устройств: ${sessions.length}` : 'Нет данных'}</div>
              </div>
              <button
                type="button"
                onClick={() => void loadSessions()}
                className="px-2 py-1 text-xs text-site-secondary hover:text-site-text hover:bg-site-raised/30 rounded-md"
                title="Обновить"
              >
                ↻
              </button>
            </div>
            {sessions.length === 0 ? (
              <div className="px-4 py-3 text-xs text-site-muted">Список сессий пуст</div>
            ) : (
              <>
                {currentSession && (
                  <div className="px-4 py-3 bg-site-bg/20">
                    <div className="text-[11px] text-site-success mb-1">Это устройство</div>
                    <div className="text-xs text-site-text truncate">{String(currentSession.user_agent || 'Устройство')}</div>
                    <div className="text-[11px] text-site-muted truncate">
                      {[
                        currentSession.ip ? `IP: ${currentSession.ip}` : '',
                        currentSession.last_used_at ? formatDialogTime(String(currentSession.last_used_at)) : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                )}

                <div className="px-4 py-2 text-[11px] text-site-muted">
                  Другие устройства: {otherSessions.length}
                </div>

                {otherSessions.length === 0 ? (
                  <div className="px-4 py-3 text-xs text-site-muted">Других устройств нет</div>
                ) : (
                  otherSessions.slice(0, 8).map((s) => (
                    <div key={s.id} className="px-4 py-2">
                      <div className="text-xs text-site-text truncate">{String(s.user_agent || 'Устройство')}</div>
                      <div className="text-[11px] text-site-muted truncate">
                        {[s.ip ? `IP: ${s.ip}` : '', s.last_used_at ? formatDialogTime(String(s.last_used_at)) : '']
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
            <div className="px-4 py-3">
              <button
                type="button"
                onClick={() => {
                  if (confirm('Выйти на всех устройствах, кроме этого?')) void logoutOtherDevices();
                }}
                className="w-full px-3 py-2 rounded-lg text-xs font-semibold border border-site-danger/30 text-site-danger hover:bg-site-danger/10"
                title="Отозвать все сессии, кроме текущей"
              >
                Выйти везде (кроме этого устройства)
              </button>
            </div>
          </div>

          <div className="text-xs text-site-muted px-1 mt-4">Чёрный список</div>
          <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-site-text">Заблокированные</div>
                <div className="text-xs text-site-muted">{blockedUsers.length ? `${blockedUsers.length} пользователей` : 'Список пуст'}</div>
              </div>
              <button
                type="button"
                onClick={() => void loadBlockedUsers()}
                className="px-2 py-1 text-xs text-site-secondary hover:text-site-text hover:bg-site-raised/30 rounded-md"
                title="Обновить"
              >
                ↻
              </button>
            </div>
            {blockedUsers.length === 0 ? (
              <div className="px-4 py-3 text-xs text-site-muted">Нет заблокированных</div>
            ) : (
              blockedUsers.slice(0, 50).map((u) => (
                <div key={u.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-site-text truncate">{u.display_name || u.username}</div>
                    <div className="text-xs text-site-muted truncate">@{u.nickname || u.username}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void unblockUser(u.id)}
                    className="px-3 py-2 text-xs text-site-text hover:text-site-text hover:bg-site-raised/30 rounded-lg border border-site-border"
                    title="Разблокировать"
                  >
                    Разблокировать
                  </button>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </>
  );

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center text-site-text">
        <div className="lisad-top-loader" />
        <div className="animate-pulse">Загрузка...</div>
      </div>
    );
  }

  if (!isAuthed) {
    return (
      <div className="min-h-screen text-site-text relative overflow-hidden">
        <div className="hidden sm:block">

        </div>
        <SiteHeader />
        <div className="relative z-10 max-w-3xl mx-auto px-6 pb-10">
          <Card className="p-6">
            <div className="text-site-text">Нужна авторизация, чтобы открыть мессенджер.</div>
            <div className="mt-4">
              <Link href="/profile">
                <Button variant="outline">Перейти в профиль</Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    // На мобилке мессенджер должен ощущаться как отдельное приложение (во весь экран).
    // запрещаем выделение UI (текст/иконки), чтобы выделялся только текст сообщений в пузырьках.
    <div
      className="fixed left-0 right-0 top-[var(--app-top,0px)] h-[var(--app-height,100vh)] sm:static sm:min-h-screen text-site-text select-none relative overflow-hidden overscroll-none lisad-messenger"
      style={dmStyle}
    >
      <style jsx global>{`
        /* Комментарий на русском: кастомные стили для ползунков (touch-friendly) */
        input[type="range"].touch-slider {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
          width: 100%;
          height: 16px;
          padding: 0;
          margin: 0;
          outline: none;
          -webkit-tap-highlight-color: transparent;
          touch-action: pan-y;
        }

        /* Track */
        input[type="range"].touch-slider::-webkit-slider-runnable-track {
          background: linear-gradient(90deg, rgba(59, 130, 246, 0.3) 0%, rgba(59, 130, 246, 0.6) 100%);
          height: 4px;
          border-radius: 2px;
        }

        input[type="range"].touch-slider::-moz-range-track {
          background: linear-gradient(90deg, rgba(59, 130, 246, 0.3) 0%, rgba(59, 130, 246, 0.6) 100%);
          height: 4px;
          border-radius: 2px;
        }

        /* Thumb */
        input[type="range"].touch-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          margin-top: -7px;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          height: 18px;
          width: 18px;
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(59, 130, 246, 0.5), 0 0 0 3px rgba(59, 130, 246, 0.15);
          border: 2px solid rgba(255, 255, 255, 0.95);
        }

        input[type="range"].touch-slider::-moz-range-thumb {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          height: 18px;
          width: 18px;
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(59, 130, 246, 0.5), 0 0 0 3px rgba(59, 130, 246, 0.15);
          border: 2px solid rgba(255, 255, 255, 0.95);
        }

        /* Active/Focus states */
        input[type="range"].touch-slider:active::-webkit-slider-thumb {
          box-shadow: 0 2px 10px rgba(59, 130, 246, 0.65), 0 0 0 5px rgba(59, 130, 246, 0.25);
          transform: scale(1.15);
        }

        input[type="range"].touch-slider:active::-moz-range-thumb {
          box-shadow: 0 2px 10px rgba(59, 130, 246, 0.65), 0 0 0 5px rgba(59, 130, 246, 0.25);
          transform: scale(1.15);
        }

        @media (max-width: 640px) {
          input[type="range"].touch-slider {
            height: 18px;
          }
          input[type="range"].touch-slider::-webkit-slider-thumb {
            height: 22px;
            width: 22px;
            margin-top: -9px;
          }
          input[type="range"].touch-slider::-moz-range-thumb {
            height: 22px;
            width: 22px;
          }
        }
      `}</style>

      <div className="hidden sm:block">

      </div>

      {myAvatarCropFile && (
        <ImageCropper
          file={myAvatarCropFile}
          aspectRatio={1}
          mode="avatar"
          allowSkip={false}
          title="Фото профиля"
          confirmText="Установить фотографию"
          onCancel={() => setMyAvatarCropFile(null)}
          onCrop={(cropped) => {
            setMyAvatarCropFile(null);
            void uploadMyAvatar(cropped);
          }}
        />
      )}

      {/* Комментарий на русском: редактор фото перед отправкой (обрезка + простые правки как в Telegram). */}
      {pendingPhotoEditOpen && pendingPhotos[pendingPhotoEditIdx]?.file && (
        <ImageCropper
          file={pendingPhotos[pendingPhotoEditIdx]!.file}
          title="Редактирование фото"
          confirmText="Готово"
          allowSkip={true}
          skipText="Без обрезки"
          enableAdjustments={true}
          onCancel={() => setPendingPhotoEditOpen(false)}
          onCrop={(edited) => {
            const idx = pendingPhotoEditIdx;
            setPendingPhotoEditOpen(false);
            // Комментарий на русском: обновляем pending фото после закрытия модалки, чтобы не “перезагружать” editor по ходу.
            setTimeout(() => replacePendingPhotoAt(idx, edited), 0);
          }}
        />
      )}

      {/* Комментарий на русском: обрезка фона чата (вертикальный прямоугольник). */}
      {wallpaperCropFile && (
        <ImageCropper
          file={wallpaperCropFile}
          aspectRatio={3 / 4}
          allowSkip={true}
          skipText="Без обрезки"
          title="Обрезка фона"
          confirmText="Использовать"
          cropPresets={[
            { name: 'Вертикальный 3:4', ratio: 3 / 4 },
            { name: 'Вертикальный 9:16', ratio: 9 / 16 },
          ]}
          enableAdjustments={false}
          onCancel={() => setWallpaperCropFile(null)}
          onCrop={(cropped) => {
            setWallpaperCropFile(null);
            setDmWallpaperFile(cropped);
          }}
        />
      )}

      {dmWallpaperOpen &&
        (typeof document !== 'undefined' && document.body
          ? createPortal(
              <div className="fixed inset-0 z-[10080] flex items-center justify-center p-2 sm:p-4">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={applyWallpaperAndClose} aria-hidden="true" />
                <div className="relative w-full max-w-[560px] max-h-[calc(100vh-16px)] sm:max-h-[calc(100vh-32px)] rounded-2xl border border-site-border bg-site-bg/90 shadow-[0_20px_70px_rgba(0,0,0,0.55)] overflow-hidden flex flex-col">
                  <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-site-border flex items-center justify-between gap-3 shrink-0">
                    <div className="w-9 h-9" aria-hidden="true" />
                    <div className="flex-1 min-w-0 text-center">
                      <div className="text-sm font-semibold text-site-text truncate">{selectedRoom ? 'Фон комнаты' : 'Фон чата'}</div>
                      <div className="text-[11px] text-site-muted truncate">
                        {selectedRoom
                          ? `${selectedRoom.title || 'Комната'}${roomCanManage ? '' : ' · только просмотр'}`
                          : selectedUser?.username
                            ? `@${selectedUser.username}`
                            : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={applyWallpaperAndClose}
                      className="w-9 h-9 rounded-full glass-btn text-site-text/80 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
                      title="Закрыть"
                      aria-label="Закрыть"
                    >
                      ✕
                    </button>
                  </div>

                  <input
                    ref={dmWallpaperFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = (e.currentTarget as HTMLInputElement).files?.[0] || null;
                      setDmWallpaperFile(f);
                    }}
                  />

                  <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 overflow-y-auto flex-1 min-h-0"
                    style={{ 
                      WebkitOverflowScrolling: 'touch',
                      scrollbarWidth: 'thin',
                    } as any}
                  >
                    {dmWallpaperToast && (
                      <div className="px-3 py-2 rounded-xl border border-site-border bg-site-bg/25 text-sm text-site-text/90">
                        {dmWallpaperToast}
                      </div>
                    )}

                    <div className="rounded-xl sm:rounded-2xl border border-site-border bg-site-bg/25 overflow-hidden">
                      <div className="p-2 sm:p-3">
                        {dmWallpaperFile &&
                          (String(dmWallpaperFile.type || '').toLowerCase() === 'image/gif' ||
                            String(dmWallpaperFile.name || '').toLowerCase().endsWith('.gif')) && (
                            <div className="mb-2 text-[10px] sm:text-[11px] text-site-secondary/90">
                              GIF выбран: обрезка недоступна (иначе потеряется анимация)
                            </div>
                          )}

                        <div className="relative w-full aspect-[4/5] sm:aspect-[3/4] rounded-xl sm:rounded-2xl overflow-hidden border border-site-border bg-site-bg/30">
                          {(dmWallpaperFilePreview || (selectedRoom ? roomWallpaperSrc : dmWallpaperSrc)) && (
                            <div
                              className="absolute inset-0 bg-center bg-cover"
                              style={{
                                backgroundImage: `url(${dmWallpaperFilePreview || (selectedRoom ? roomWallpaperSrc : dmWallpaperSrc)})`,
                                filter: `blur(${Number(dmWallpaperBlurDraft || 0) || 0}px) brightness(${Number(dmWallpaperBrightnessDraft || 100) || 100}%)`,
                                transform: Number(dmWallpaperBlurDraft || 0) ? 'scale(1.06)' : 'none',
                              }}
                            />
                          )}
                          {!(dmWallpaperFilePreview || (selectedRoom ? roomWallpaperSrc : dmWallpaperSrc)) && (
                            <div className="absolute inset-0 flex items-center justify-center text-xs sm:text-sm text-site-secondary">Фон не выбран</div>
                          )}

                          {/* Комментарий на русском: кнопки управления внутри картинки (как просили — без нижней панели). */}
                          <div className="absolute left-2 top-2 flex items-center gap-1.5">
                            <button
                              type="button"
                              disabled={dmWallpaperBusy || (!!selectedRoom && !roomCanManage)}
                              onClick={() => dmWallpaperFileInputRef.current?.click()}
                              className="px-2 py-1 rounded-lg bg-site-bg/60 border border-site-border text-[11px] text-site-text/90 hover:text-site-text hover:bg-site-bg/75 transition disabled:opacity-60"
                              title="Выбрать картинку"
                            >
                              Выбрать
                            </button>

                            {dmWallpaperFile &&
                              !(
                                String(dmWallpaperFile.type || '').toLowerCase() === 'image/gif' ||
                                String(dmWallpaperFile.name || '').toLowerCase().endsWith('.gif')
                              ) && (
                                <button
                                  type="button"
                                  disabled={dmWallpaperBusy || (!!selectedRoom && !roomCanManage)}
                                  onClick={() => setWallpaperCropFile(dmWallpaperFile)}
                                  className="px-2 py-1 rounded-lg bg-site-bg/60 border border-site-border text-[11px] text-site-text/90 hover:text-site-text hover:bg-site-bg/75 transition disabled:opacity-60"
                                  title="Обрезать (вертикальный прямоугольник)"
                                >
                                  Обрезать
                                </button>
                              )}

                            {dmWallpaperFile && (
                              <button
                                type="button"
                                disabled={dmWallpaperBusy}
                                onClick={() => setDmWallpaperFile(null)}
                                className="px-2 py-1 rounded-lg bg-site-bg/60 border border-site-border text-[11px] text-site-text/80 hover:text-site-text hover:bg-site-bg/75 transition disabled:opacity-60"
                                title="Убрать выбранный файл"
                              >
                                Сброс
                              </button>
                            )}
                          </div>

                          <div className="absolute right-2 top-2 flex items-center gap-1.5">
                            <button
                              type="button"
                              disabled={
                                dmWallpaperBusy ||
                                (!!selectedRoom && !roomCanManage) ||
                                (!selectedRoom ? !dmWallpaper?.id : !roomWallpaper?.id)
                              }
                              onClick={() => void (selectedRoom ? clearRoomWallpaper() : clearDmWallpaper())}
                              className="w-9 h-9 rounded-full border border-site-danger/35 bg-site-danger/15 text-site-danger hover:bg-site-danger/25 hover:text-site-danger transition flex items-center justify-center disabled:opacity-60"
                              title="Удалить фон"
                              aria-label="Удалить фон"
                            >
                              <MonoTrashIcon className="w-4.5 h-4.5" />
                            </button>
                          </div>

                          <div className="absolute right-2 bottom-2">
                            <button
                              type="button"
                              disabled={dmWallpaperBusy}
                              onClick={applyWallpaperAndClose}
                              className="px-2.5 py-1.5 rounded-lg bg-site-accent/85 hover:bg-site-accent text-site-on-accent text-[11px] font-semibold transition disabled:opacity-60"
                              title="Готово (автоприменение)"
                            >
                              Готово
                            </button>
                          </div>

                          {!selectedRoom && (
                            <div className="absolute left-1/2 bottom-2 -translate-x-1/2">
                              <button
                                type="button"
                                disabled={dmWallpaperBusy}
                                onClick={() => void sendDmWallpaperRequestToPeer()}
                                className="px-3 py-1.5 rounded-lg bg-site-bg/60 border border-site-border text-[11px] text-site-text/90 hover:text-site-text hover:bg-site-bg/75 disabled:opacity-60 transition"
                                title="Предложить фон собеседнику"
                              >
                                Предложить{' '}
                                <span className="text-site-success font-semibold">
                                  {String((selectedUser as any)?.display_name || (selectedUser as any)?.name || (selectedUser as any)?.username || 'собеседнику')}
                                </span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl sm:rounded-2xl border border-site-border bg-site-bg/25 p-2 sm:p-3">
                      <div className="text-[11px] sm:text-xs text-site-secondary text-center font-semibold">
                        Размытие · {Number(dmWallpaperBlurDraft || 0) || 0}px
                      </div>
                      <input
                        type="range"
                        step={1}
                        min={0}
                        max={30}
                        value={Number(dmWallpaperBlurDraft || 0) || 0}
                        onChange={(e) => setDmWallpaperBlurDraft(Number(e.target.value || 0) || 0)}
                        onPointerDown={(e) => {
                          touchRangeDragRef.current = true;
                          try {
                            (e.currentTarget as any).setPointerCapture?.((e as any).pointerId);
                          } catch {}
                          setRangeValueFromClientX(e.currentTarget, (e as any).clientX ?? 0, 0, 30, setDmWallpaperBlurDraft);
                        }}
                        onPointerMove={(e) => {
                          if (!touchRangeDragRef.current) return;
                          setRangeValueFromClientX(e.currentTarget, (e as any).clientX ?? 0, 0, 30, setDmWallpaperBlurDraft);
                        }}
                        onPointerUp={() => {
                          touchRangeDragRef.current = false;
                        }}
                        onPointerCancel={() => {
                          touchRangeDragRef.current = false;
                        }}
                        onTouchStart={(e) => {
                          touchRangeDragRef.current = true;
                          const t = e.touches?.[0];
                          if (!t) return;
                          setRangeValueFromClientX(e.currentTarget, t.clientX, 0, 30, setDmWallpaperBlurDraft);
                        }}
                        onTouchMove={(e) => {
                          if (!touchRangeDragRef.current) return;
                          const t = e.touches?.[0];
                          if (!t) return;
                          setRangeValueFromClientX(e.currentTarget, t.clientX, 0, 30, setDmWallpaperBlurDraft);
                        }}
                        onTouchEnd={() => {
                          touchRangeDragRef.current = false;
                        }}
                        className="w-full touch-slider"
                        aria-label="Размытие фона"
                      />

                      <div className="mt-2 pt-2 border-t border-site-border">
                        <div className="text-[11px] sm:text-xs text-site-secondary text-center font-semibold">
                          Яркость · {Number(dmWallpaperBrightnessDraft || 100) || 100}%
                        </div>
                        <input
                          type="range"
                          step={1}
                          min={50}
                          max={150}
                          value={Number(dmWallpaperBrightnessDraft || 100) || 100}
                          onChange={(e) => setDmWallpaperBrightnessDraft(Number(e.target.value || 100) || 100)}
                          onPointerDown={(e) => {
                            touchRangeDragRef.current = true;
                            try {
                              (e.currentTarget as any).setPointerCapture?.((e as any).pointerId);
                            } catch {}
                            setRangeValueFromClientX(e.currentTarget, (e as any).clientX ?? 0, 50, 150, setDmWallpaperBrightnessDraft);
                          }}
                          onPointerMove={(e) => {
                            if (!touchRangeDragRef.current) return;
                            setRangeValueFromClientX(e.currentTarget, (e as any).clientX ?? 0, 50, 150, setDmWallpaperBrightnessDraft);
                          }}
                          onPointerUp={() => {
                            touchRangeDragRef.current = false;
                          }}
                          onPointerCancel={() => {
                            touchRangeDragRef.current = false;
                          }}
                          onTouchStart={(e) => {
                            touchRangeDragRef.current = true;
                            const t = e.touches?.[0];
                            if (!t) return;
                            setRangeValueFromClientX(e.currentTarget, t.clientX, 50, 150, setDmWallpaperBrightnessDraft);
                          }}
                          onTouchMove={(e) => {
                            if (!touchRangeDragRef.current) return;
                            const t = e.touches?.[0];
                            if (!t) return;
                            setRangeValueFromClientX(e.currentTarget, t.clientX, 50, 150, setDmWallpaperBrightnessDraft);
                          }}
                          onTouchEnd={() => {
                            touchRangeDragRef.current = false;
                          }}
                          className="w-full touch-slider"
                          aria-label="Яркость фона"
                        />
                      </div>

                    </div>
                  </div>
                </div>
              </div>,
              document.body
            )
          : null)}

      {/*верхнюю навигацию убрали — будет нижняя панель (как в приложении). */}

      <div className="relative z-10 h-full sm:h-auto max-w-7xl mx-auto px-0 sm:px-6 pb-0 sm:pb-10">
        <Card
          variant="glass"
          // Комментарий на русском: приподнимаем блок мессенджера на 5px (десктоп), чтобы шапка выглядела плотнее.
          className="h-full sm:mt-[15px] sm:h-[calc(100vh-44px)] rounded-none sm:rounded-2xl flex flex-col overflow-hidden glass-strong"
        >
          {/* Комментарий на русском: шапку STEM показываем в списках/настройках, но скрываем в режиме переписки (в чате). */}
          {!mobileChatOpen && (
            <div
              className="flex items-center justify-center py-1 sm:py-1.5 transition-all duration-300 glass-header relative overflow-hidden"
              style={
                netBanner === 'offline' || !!loadError
                  ? {
                      background: 'linear-gradient(180deg, rgba(127, 29, 29, 0.4) 0%, rgba(127, 29, 29, 0.2) 100%)',
                      boxShadow: '0 0 30px rgba(239, 68, 68, 0.25), inset 0 0 40px rgba(239, 68, 68, 0.08)',
                    }
                  : undefined
              }
            >
              <AnimatedSTEM
                color={netBanner === 'offline' || !!loadError ? '#934110' : '#4E8E88'}
                textShadow={
                  netBanner === 'offline' || !!loadError
                    ? '0 2px 3px rgba(0,0,0,0.55), 0 4px 12px rgba(147,65,16,0.45)'
                    : '0 2px 3px rgba(0,0,0,0.55), 0 4px 12px rgba(78,142,136,0.45)'
                }
              />

              {/* Комментарий на русском: статус сети/загрузки показываем только в шапке. */}
              {(!!loadError || netBanner !== 'hidden') && (
                <div className="absolute right-3 sm:right-4 flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
                  {loadError ? (
                    <>
                      <span className="w-2 h-2 bg-site-danger rounded-full animate-pulse" />
                      <span className="text-site-danger">Ошибка</span>
                    </>
                  ) : netBanner === 'offline' ? (
                    <>
                      <span className="w-2 h-2 bg-site-danger rounded-full animate-pulse" />
                      <span className="text-site-danger">Нет сети</span>
                    </>
                  ) : (
                    <>
                      <span className="w-3.5 h-3.5 rounded-full border-2 border-site-border border-t-slate-200/80 animate-spin" />
                      <span className="text-site-text">Загрузка</span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex-1 min-h-0 flex overflow-hidden">
          {/* Левая панель (чаты/друзья). Glass-стиль. На мобилке скрываем, когда открыт чат */}
          <div className={`${mobileChatOpen ? 'hidden sm:flex' : 'flex'} w-full sm:w-80 border-r border-site-border-control/20 flex-col min-h-0 glass-subtle`}>
            {/* Комментарий на русском: верхнюю кнопку "ЛИСАД" и большую "Поддержку" убрали — поддержка будет первым элементом в списке чатов. */}

            {/* Inbox поддержки для суперадмина */}
            {supportOpen && isSuperAdmin ? (
              <div className="flex-1 flex flex-col min-h-0">
                <div className="p-3 border-b border-site-border-control/30">
                  <div className="text-sm font-semibold text-site-text">Обращения</div>
                  <div className="text-xs text-site-muted">Входящие сообщения</div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-scroll overscroll-contain pb-20" data-scroll-lock-allow>
                  {/* Комментарий на русском: "Сайт" — первый контакт, показывает ошибки фронтенда */}
                  <button
                    onClick={() => {
                      setShowErrorsChat(true);
                      setSupportTargetUser(null);
                      setSupportMessages([]);
                      void loadFrontendErrors();
                    }}
                    className={`w-full text-left ${chatRowPad} border-b border-site-border hover:bg-site-raised/25 transition-colors ${
                      showErrorsChat ? 'bg-site-raised/45 border-l-4 border-l-orange-500' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative shrink-0">
                        <div className={`${chatAvatarSize} rounded-full bg-gradient-to-br from-site-warning to-site-danger flex items-center justify-center text-site-text font-bold text-lg`}>
                          <UiIcon name="Bug" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-site-warning">Сайт</div>
                          <div className="text-xs text-site-muted shrink-0">Ошибки</div>
                        </div>
                        <div className={`${chatSnippetText} text-site-muted truncate`}>Логи фронтенда</div>
                      </div>
                    </div>
                  </button>

                  {/* Комментарий на русском: список обращений пользователей */}
                  {supportInbox.length === 0 ? (
                    <div className="p-4 text-site-muted">Нет обращений от пользователей</div>
                  ) : (
                    supportInbox.map((conv) => {
                      const lastMsg = conv.last_message || null;
                      const lastAt = lastMsg?.created_at ? formatDialogTime(lastMsg.created_at) : '';
                      const lastPreview = lastMsg?.message
                        ? showMessagePreview
                          ? lastMsg.message
                          : 'Новое сообщение'
                        : 'Нет сообщений';
                      const gamePresenceText = formatGamePresenceText(conv.user.game_presence);
                      return (
                        <button
                          key={conv.user.id}
                          onClick={() => {
                            setShowErrorsChat(false);
                            setSupportTargetUser(conv.user);
                            setSupportMessages([]);
                            supportLastIdRef.current = 0;
                            void loadSupportMessages(conv.user.id);
                            setTimeout(() => void loadSupportUnread(), 300);
                          }}
                          className={`w-full text-left ${chatRowPad} border-b border-site-border hover:bg-site-raised/25 transition-colors ${
                            !showErrorsChat && supportTargetUser?.id === conv.user.id ? 'bg-site-raised/45 border-l-4 border-l-blue-500' : ''
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="relative shrink-0">
                              <AvatarFx
                                sizeClass={chatAvatarSize}
                                avatarUrl={conv.user.avatar_url || null}
                                alt={conv.user.display_name || conv.user.username || ''}
                                fallback={(conv.user.display_name?.[0] || conv.user.username?.[0] || '?').toUpperCase()}
                                isOnline={!!conv.user.is_online}
                                fxColor={conv.user.avatar_fx_color}
                                fxThickness={conv.user.avatar_fx_thickness}
                                fxGlow={conv.user.avatar_fx_glow}
                                fxOfflineColor={conv.user.avatar_fx_offline_color}
                              />
                              {conv.unread_count > 0 && (
                                <div className="absolute -top-1 -right-1 bg-site-danger text-site-on-accent text-xs font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center">
                                  {conv.unread_count > 99 ? '99+' : conv.unread_count}
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <div className="font-semibold text-site-text truncate">{conv.user.display_name || conv.user.username}</div>
                                <div className="text-xs text-site-muted shrink-0">{lastAt}</div>
                              </div>
                              {gamePresenceText ? (
                                <div className="text-[11px] font-semibold text-site-warning truncate">{gamePresenceText}</div>
                              ) : null}
                              <div className={`${chatSnippetText} text-site-muted truncate`}>{lastPreview}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            ) : (
              <>
              </>
            )}

            {(!supportOpen || !isSuperAdmin) && tab === 'chats' && (
              <>
                <div className="p-3 border-b border-site-border-control/20 flex items-center gap-2">
                  <input
                    value={chatSearch}
                    onChange={(e) => setChatSearch(e.target.value)}
                    placeholder="Поиск..."
                    className="flex-1 min-w-0 px-4 py-2 glass-input rounded-full text-base sm:text-sm text-site-text placeholder-site-muted focus:outline-none select-text"
                  />
                  <button
                    ref={createBtnRef}
                    type="button"
                    onClick={() => {
                      setCreateMenuOpen((prev) => {
                        const next = !prev;
                        if (next) setCreateMenuPos(null);
                        return next;
                      });
                    }}
                    className="w-10 h-10 rounded-full glass-btn glass-btn-green text-site-text flex items-center justify-center shrink-0 transition active:scale-[0.9] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-success/40"
                    title="Создать"
                    aria-label="Создать (группа/канал/помощник)"
                  >
                    {/* Комментарий на русском: SVG вместо текста — так “плюс” всегда идеально по центру. */}
                    <svg
                      viewBox="0 0 24 24"
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 5v14" />
                      <path d="M5 12h14" />
                    </svg>
                  </button>
                </div>

                {/* Комментарий на русском: контекстное меню создания — в стиле меню сообщения (glass-strong, fixed, portal). */}
                {createMenuOpen &&
                  (typeof document !== 'undefined' && document.body
                    ? createPortal(
                        <div
                          ref={createMenuRef}
                          className="fixed z-[9999] glass-strong glass-warm rounded-2xl shadow-[0_20px_70px_rgba(0,0,0,0.55)] overflow-hidden min-w-[240px]"
                          style={
                            createMenuPos
                              ? { left: createMenuPos.left, top: createMenuPos.top }
                              : { left: 0, top: 0, opacity: 0, pointerEvents: 'none' }
                          }
                        >
                          <button
                            type="button"
                            onClick={() => void createRoom('group')}
                            className="w-full px-3.5 py-2.5 text-left text-sm text-site-text hover:bg-site-text/6 transition-colors flex items-center gap-3"
                            title="Новая группа"
                            aria-label="Новая группа"
                          >
                            <span className="w-9 h-9 rounded-full glass-btn flex items-center justify-center text-lg shrink-0"><UiIcon name="Users" /></span>
                            <span className="font-medium">Новая группа</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => void createRoom('channel')}
                            className="w-full px-3.5 py-2.5 text-left text-sm text-site-text hover:bg-site-text/6 transition-colors flex items-center gap-3"
                            title="Новый канал"
                            aria-label="Новый канал"
                          >
                            <span className="w-9 h-9 rounded-full glass-btn flex items-center justify-center text-lg shrink-0"><UiIcon name="Megaphone" /></span>
                            <span className="font-medium">Новый канал</span>
                          </button>
                          <div className="glass-divider" aria-hidden="true" />
                          <button
                            type="button"
                            onClick={() => void createRoom('assistant')}
                            className="w-full px-3.5 py-2.5 text-left text-sm text-site-text hover:bg-site-text/6 transition-colors flex items-center gap-3"
                            title="Новый помощник"
                            aria-label="Новый помощник"
                          >
                            <span className="w-9 h-9 rounded-full glass-btn flex items-center justify-center text-lg shrink-0"><UiIcon name="Robot" /></span>
                            <span className="font-medium">Новый помощник</span>
                          </button>
                        </div>,
                        document.body
                      )
                    : null)}

                <div
                  ref={chatsListScrollRef}
                  onScroll={(e) => writeChatsListScrollThrottled((e.target as HTMLDivElement).scrollTop)}
                  className="flex-1 min-h-0 overflow-y-scroll overscroll-contain pb-20"
                  data-scroll-lock-allow
                >
                  {/* Комментарий на русском: Telegram-стиль — добавляем невидимый "spacer" снизу, чтобы скролл работал даже при коротком списке. */}
                  <div className="flex flex-col">
                  {/* Комментарий на русском: "Поддержка" — glassmorphism стиль в списке чатов */}
                  <button
                    onClick={() => {
                      // Toggle поддержка
                      if (supportOpen) {
                        setSupportOpen(false);
                        setSupportTargetUser(null);
                        setSupportMessages([]);
                        supportLastIdRef.current = 0;
                        return;
                      }

                      setSupportOpen(true);
                      // Комментарий на русском: при входе в поддержку сразу убираем бейдж (optimistic).
                      setSupportUnread(0);
                      setTab('chats');
                      setSelectedUser(null);
                      setSelectedRoom(null);
                    chatMessagesStore.setState({ chats: {} });
                      lastMessageIdRef.current = 0;
                      setError(null);
                      setSupportTargetUser(null);
                      setSupportMessages([]);
                      supportLastIdRef.current = 0;

                      void loadSupportUnread();
                      if (myIsSuperRef.current) {
                        void loadSupportInbox();
                      } else {
                        void loadSupportMessages();
                      }
                    }}
                    className={`w-full text-left ${chatRowPad} border-b border-site-border glass-chat-item transition-all ${
                      supportOpen ? 'active glass-tinted' : ''
                    }`}
                    title="Поддержка"
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative shrink-0">
                          <div
                            className={`${chatAvatarSize} rounded-full overflow-hidden bg-site-success flex items-center justify-center text-site-on-accent font-bold`}
                          >
                          <UiIcon name="Lifebuoy" />
                        </div>
                        {supportUnread > 0 && (
                          <div className="absolute -top-1 -right-1 bg-site-danger text-site-on-accent text-xs font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center">
                            {supportUnread > 99 ? '99+' : supportUnread}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                            <div className="font-semibold text-site-text truncate">
                              {myIsSuperRef.current ? 'Поддержка (Inbox)' : 'Поддержка'}
                            </div>
                          <div className="text-xs text-site-muted shrink-0"><UiIcon name="Lifebuoy" /></div>
                        </div>
                        <div className={`${chatSnippetText} text-site-muted truncate`}>Чат с суперадмином сайта</div>
                      </div>
                    </div>
                  </button>
                  {!loadingRooms &&
                    filteredRooms.map((c, idx) => {
                      const r = c.room;
                      const icon = r.type === 'channel' ? '📣' : r.type === 'assistant' ? '🤖' : '👥';
                      const roomAvatarUrl = (r as any)?.avatar_url ? String((r as any).avatar_url) : '';
                      const vc = (r as any)?.voice_call || null;
                      const lastAt = c.last_message?.created_at ? formatDialogTime(c.last_message.created_at) : '';
                      const preview =
                        c.last_message?.message
                          ? showMessagePreview
                            ? c.last_message.message
                            : 'Новое сообщение'
                          : r.type === 'assistant'
                            ? 'Личный помощник'
                            : r.type === 'channel'
                              ? 'Канал'
                              : 'Группа';
                      // Комментарий на русском: соседи для prefetch (±2 элемента).
                      const neighborIds = filteredRooms
                        .slice(Math.max(0, idx - 2), idx + 3)
                        .map((n) => Number((n as any)?.room?.id || 0))
                        .filter((nid) => nid && nid !== r.id);
                      return (
                        <button
                          key={`room_${r.id}`}
                          ref={(node) => observePrefetch(node, 'room', r.id, neighborIds)}
                          onClick={() => selectRoom(r, (c as any)?.chat_hash)}
                          className={`w-full text-left ${chatRowPad} border-b border-site-border glass-chat-item transition-all ${
                            selectedRoom?.id === r.id ? 'active' : ''
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="relative shrink-0">
                                <div
                                  className={`${chatAvatarSize} rounded-full overflow-hidden glass-btn border border-site-border flex items-center justify-center text-site-text font-bold`}
                                >
                                {roomAvatarUrl ? (
                                  <img src={roomAvatarUrl} alt={r.title || 'Чат'} className="w-full h-full object-cover" />
                                ) : (
                                  <UiIcon name={icon} />
                                )}
                              </div>
                              {c.unread_count > 0 && (
                                <div className="absolute -top-1 -right-1 bg-site-danger text-site-on-accent text-xs font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center">
                                  {c.unread_count > 99 ? '99+' : c.unread_count}
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0 flex items-center gap-2">
                                  <div className="font-semibold text-site-text truncate">{r.title || 'Чат'}</div>
                                  {vc?.active && (
                                    <div
                                      className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-site-success/15 border border-site-success/25 text-[11px] text-site-success"
                                      title="Идёт голосовой чат"
                                    >
                                      <span><UiIcon name="Microphone" /></span>
                                      <span>{Math.min(50, Math.max(0, Number(vc.participants_count || 0) || 0))}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="text-xs text-site-muted shrink-0">{lastAt}</div>
                              </div>
                              <div className={`${chatSnippetText} text-site-muted truncate`}>{preview}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  {loadingChats ? (
                    <div className="p-4 text-site-muted">Загрузка...</div>
                  ) : filteredConversations.length === 0 ? (
                    <div className="p-4 text-site-muted">Нет диалогов</div>
                  ) : (
                    filteredConversations.map((conv, idx) => {
                      const lastMsg = conv.last_message || null;
                      const lastAt = lastMsg?.created_at ? formatDialogTime(lastMsg.created_at) : '';
                      const lastPreview = lastMsg?.message
                        ? showMessagePreview
                          ? lastMsg.message
                          : 'Новое сообщение'
                        : 'Нет сообщений';
                      // Комментарий на русском: соседи для prefetch (±2 элемента).
                      const neighborIds = filteredConversations
                        .slice(Math.max(0, idx - 2), idx + 3)
                        .map((n) => Number((n as any)?.user?.id || 0))
                        .filter((nid) => nid && nid !== conv.user.id);
                      return (
                        <button
                          key={conv.user.id}
                          ref={(node) => observePrefetch(node, 'dm', conv.user.id, neighborIds)}
                          onClick={() => selectUser(conv.user, (conv as any)?.chat_hash)}
                          className={`w-full text-left ${chatRowPad} border-b border-site-border glass-chat-item transition-all ${
                            selectedUser?.id === conv.user.id ? 'active' : ''
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="relative shrink-0">
                              <AvatarFx
                                sizeClass={chatAvatarSize}
                                avatarUrl={conv.user.avatar_url || null}
                                alt={conv.user.display_name || conv.user.username || ''}
                                fallback={(conv.user.display_name?.[0] || conv.user.username?.[0] || '?').toUpperCase()}
                                isOnline={!!conv.user.is_online}
                                fxColor={conv.user.avatar_fx_color}
                                fxThickness={conv.user.avatar_fx_thickness}
                                fxGlow={conv.user.avatar_fx_glow}
                                fxOfflineColor={conv.user.avatar_fx_offline_color}
                              />
                              {conv.unread_count > 0 && (
                                <div className="absolute -top-1 -right-1 bg-site-danger text-site-on-accent text-xs font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center">
                                  {conv.unread_count > 99 ? '99+' : conv.unread_count}
                                </div>
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <div className="font-semibold text-site-text truncate">{conv.user.display_name || conv.user.username}</div>
                                <div className="text-xs text-site-muted shrink-0">{lastAt}</div>
                              </div>
                              <div className={`${chatSnippetText} text-site-muted truncate`}>{lastPreview}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                    {/* Комментарий на русском: невидимая зона для "живого" скролла (устойчиво работает на iOS/Android). */}
                    <div aria-hidden="true" className="h-[clamp(80px,18vh,180px)] pointer-events-none" />
                  </div>
                </div>
              </>
            )}

            {/* Комментарий на русском: iOS-стиль настроек — на десктопе слева разделы, справа контент. */}
            {(!supportOpen || !isSuperAdmin) && tab === 'settings' && (
              <div className="flex-1 flex flex-col min-h-0">
                {/* Комментарий на русском: боковая панель разделов (десктоп ≥1024px). */}
                <div className="hidden sm:flex flex-col min-h-0 bg-site-bg/30">
                  <div className="p-4 border-b border-site-border">
                    <div className="text-base font-semibold text-site-text">Настройки</div>
                    <div className="text-xs text-site-muted mt-0.5">{chatSettingsLoaded ? 'Мессенджер' : 'Загрузка...'}</div>
                  </div>
                  <div className="flex-1 overflow-y-auto py-1" data-scroll-lock-allow>
                    {([
                      { id: 'profile', icon: '👤', label: 'Профиль' },
                      { id: 'avatar', icon: '✨', label: 'Аватар' },
                      { id: 'notifications', icon: '🔔', label: 'Уведомления' },
                      { id: 'privacy', icon: '🔒', label: 'Приватность' },
                      { id: 'interface', icon: '🎨', label: 'Интерфейс' },
                      { id: 'security', icon: '🛡️', label: 'Безопасность' },
                      { id: 'storage', icon: '💾', label: 'Кэш' },
                    ] as const).map((sec) => (
                      <button
                        key={sec.id}
                        type="button"
                        onClick={() => setSettingsSection(sec.id)}
                        className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${
                          settingsSection === sec.id
                            ? 'bg-site-accent/20 text-site-text border-l-2 border-site-accent'
                            : 'text-site-secondary hover:bg-site-surface/40 hover:text-site-text border-l-2 border-transparent'
                        }`}
                      >
                        <span className="text-sm">{<UiIcon name={sec.icon} />}</span>
                        <span className="text-sm">{sec.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Комментарий на русском: основной контент настроек. */}
                <div className="sm:hidden flex-1 flex flex-col min-h-0">
                  {/* Мобильный заголовок */}
                  <div className="lg:hidden p-3 border-b border-site-border-control/30">
                    <div className="text-sm font-semibold text-site-text">Настройки</div>
                    <div className="text-xs text-site-muted">{chatSettingsLoaded ? 'Мессенджер' : 'Загрузка...'}</div>
                  </div>
                  {/* Десктоп заголовок раздела */}
                  <div className="hidden lg:block p-4 border-b border-site-border">
                    <div className="text-sm font-semibold text-site-text">{getSettingsSectionTitle(settingsSection)}</div>
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 lg:p-4 pb-[calc(6rem+env(safe-area-inset-bottom))] space-y-4" data-scroll-lock-allow>
                    {settingsToast && (
                      <div className="px-4 py-2 rounded-xl border border-site-border bg-site-bg/40 text-xs text-site-text">
                        {settingsToast}
                      </div>
                    )}

                    {/* ===== ПРОФИЛЬ ===== */}
                    {settingsSection === 'profile' && (
                      <>
                        <div className="text-xs text-site-muted px-1 lg:hidden">Профиль</div>
                  <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden">
                    <div className="px-4 py-4 flex items-center gap-4">
                      <AvatarFx
                        sizeClass="w-16 h-16"
                        avatarUrl={(myProfile?.avatar_url ?? meRef.current?.avatar_url ?? null) as any}
                        alt={String(myProfile?.display_name || myProfile?.username || 'Профиль')}
                        fallback={String((myProfile?.display_name || myProfile?.username || 'Вы')[0] || '?').toUpperCase()}
                        isOnline={true}
                        fxColor={avatarFxColor}
                        fxThickness={avatarFxThickness}
                        fxGlow={avatarFxGlow}
                        fxOfflineColor={avatarFxOfflineColor}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-base font-semibold text-site-text truncate">
                          {String(myProfile?.display_name || myProfile?.username || meRef.current?.display_name || 'Вы')}
                        </div>
                        <div className="text-xs text-site-muted truncate">
                          @{String(myProfile?.nickname || myProfile?.username || (meRef.current as any)?.nickname || meRef.current?.username || '')}
                        </div>
                        <Link
                          href="/profile"
                          className="mt-2 inline-flex w-full sm:w-fit items-center justify-center gap-2 px-3 py-2 rounded-full glass-btn border border-site-border text-xs font-semibold text-site-text hover:text-site-text hover:bg-site-text/5 transition-colors"
                          title="Открыть профиль ЛИСАД"
                        >
                          <span className="text-sm"><UiIcon name="FileText" /></span>
                          <span>Открыть профиль ЛИСАД</span>
                        </Link>
                        <div className="text-[11px] text-site-success truncate">● онлайн</div>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        <label className="px-3 py-2 text-xs rounded-lg border border-site-border text-site-text hover:text-site-text hover:bg-site-raised/25 transition-colors">
                          {avatarBusy ? 'Загрузка...' : 'Изменить фото'}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={avatarBusy}
                            onChange={handleMyAvatarSelect}
                          />
                        </label>
                        {(myProfile?.avatar_url || meRef.current?.avatar_url) && (
                          <button
                            type="button"
                            onClick={() => void deleteMyAvatar()}
                            disabled={avatarBusy}
                            className="px-3 py-2 text-xs rounded-lg border border-site-danger/25 text-site-danger hover:bg-site-danger/10 disabled:opacity-60"
                          >
                            Удалить
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="px-4 py-3 border-t border-site-border">
                      <div className="text-sm font-semibold text-site-text">Публичный @username</div>
                      <div className="text-[11px] text-site-muted mt-0.5">Ссылка на профиль: https://lisad.kbsu.ru/@username (или /username)</div>
                      <div className="mt-2 flex flex-col sm:flex-row gap-2">
                        <input
                          value={myNicknameDraft}
                          onChange={(e) => {
                            myNicknameDirtyRef.current = true;
                            setMyNicknameDraft(e.target.value);
                          }}
                          maxLength={64}
                          className="flex-1 px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                          placeholder="@username"
                          disabled={profileBusy}
                          inputMode="text"
                        />
                        <button
                          type="button"
                          onClick={() => void saveMyNickname()}
                          disabled={profileBusy || !normalizeNicknameDraft(myNicknameDraft)}
                          className="px-3 py-2 text-xs font-semibold rounded-lg bg-site-accent hover:bg-site-accent disabled:bg-site-raised disabled:text-site-muted text-site-on-accent transition-colors"
                        >
                          Сохранить @username
                        </button>
                      </div>
                      <div className="text-[11px] text-site-muted mt-1">Правила: 3–32 символа, латиница/цифры/_"-". Менять можно 1 раз в сутки.</div>
                    </div>

                    <div className="px-4 py-4 border-t border-site-border">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <div className="text-xs text-site-secondary mb-1">Имя</div>
                          <input
                            value={myProfileForm.first_name}
                            onChange={(e) => {
                              myProfileDirtyRef.current = true;
                              setMyProfileForm({ ...myProfileForm, first_name: e.target.value });
                            }}
                            maxLength={100}
                            className="w-full px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                            placeholder="Имя"
                            disabled={profileBusy}
                          />
                        </div>
                        <div>
                          <div className="text-xs text-site-secondary mb-1">Фамилия</div>
                          <input
                            value={myProfileForm.last_name}
                            onChange={(e) => {
                              myProfileDirtyRef.current = true;
                              setMyProfileForm({ ...myProfileForm, last_name: e.target.value });
                            }}
                            maxLength={100}
                            className="w-full px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                            placeholder="Фамилия"
                            disabled={profileBusy}
                          />
                        </div>
                        <div>
                          <div className="text-xs text-site-secondary mb-1">Отчество</div>
                          <input
                            value={myProfileForm.middle_name}
                            onChange={(e) => {
                              myProfileDirtyRef.current = true;
                              setMyProfileForm({ ...myProfileForm, middle_name: e.target.value });
                            }}
                            maxLength={100}
                            className="w-full px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                            placeholder="Отчество"
                            disabled={profileBusy}
                          />
                        </div>
                        <div>
                          <div className="text-xs text-site-secondary mb-1">Телефон</div>
                          <input
                            value={myProfileForm.phone}
                            onChange={(e) => {
                              myProfileDirtyRef.current = true;
                              setMyProfileForm({ ...myProfileForm, phone: e.target.value });
                            }}
                            maxLength={100}
                            className="w-full px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                            placeholder="+7..."
                            disabled={profileBusy}
                            inputMode="tel"
                          />
                        </div>
                        <div>
                          <div className="text-xs text-site-secondary mb-1">Должность</div>
                          <input
                            value={myProfileForm.position}
                            onChange={(e) => {
                              myProfileDirtyRef.current = true;
                              setMyProfileForm({ ...myProfileForm, position: e.target.value });
                            }}
                            maxLength={100}
                            className="w-full px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                            placeholder="Должность"
                            disabled={profileBusy}
                          />
                        </div>
                        <div>
                          <div className="text-xs text-site-secondary mb-1">Организация</div>
                          <input
                            value={myProfileForm.organization}
                            onChange={(e) => {
                              myProfileDirtyRef.current = true;
                              setMyProfileForm({ ...myProfileForm, organization: e.target.value });
                            }}
                            maxLength={100}
                            className="w-full px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                            placeholder="Организация"
                            disabled={profileBusy}
                          />
                        </div>
                        <div>
                          <div className="text-xs text-site-secondary mb-1">Сайт</div>
                          <input
                            value={myProfileForm.website}
                            onChange={(e) => {
                              myProfileDirtyRef.current = true;
                              setMyProfileForm({ ...myProfileForm, website: e.target.value });
                            }}
                            maxLength={200}
                            className="w-full px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                            placeholder="https://..."
                            disabled={profileBusy}
                            inputMode="url"
                          />
                        </div>
                        <div>
                          <div className="text-xs text-site-secondary mb-1">Время работы</div>
                          <input
                            value={myProfileForm.work_time}
                            onChange={(e) => {
                              myProfileDirtyRef.current = true;
                              setMyProfileForm({ ...myProfileForm, work_time: e.target.value });
                            }}
                            maxLength={255}
                            className="w-full px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                            placeholder="Пн–Пт 09:00–18:00"
                            disabled={profileBusy}
                          />
                        </div>
                        <div>
                          <div className="text-xs text-site-secondary mb-1">GitHub</div>
                          <input
                            value={myProfileForm.github_username}
                            onChange={(e) => {
                              myProfileDirtyRef.current = true;
                              setMyProfileForm({ ...myProfileForm, github_username: e.target.value });
                            }}
                            maxLength={100}
                            className="w-full px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                            placeholder="username"
                            disabled={profileBusy}
                            inputMode="text"
                          />
                        </div>
                        <div>
                          <div className="text-xs text-site-secondary mb-1">Telegram</div>
                          <input
                            value={myProfileForm.telegram_username}
                            onChange={(e) => {
                              myProfileDirtyRef.current = true;
                              setMyProfileForm({ ...myProfileForm, telegram_username: e.target.value });
                            }}
                            maxLength={100}
                            className="w-full px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                            placeholder="username"
                            disabled={profileBusy}
                            inputMode="text"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <div className="text-xs text-site-secondary mb-1">О себе</div>
                          <textarea
                            value={myProfileForm.bio}
                            onChange={(e) => {
                              myProfileDirtyRef.current = true;
                              setMyProfileForm({ ...myProfileForm, bio: e.target.value });
                            }}
                            rows={3}
                            maxLength={1000}
                            className="w-full px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50 resize-none"
                            placeholder="Коротко о вас"
                            disabled={profileBusy}
                          />
                          <div className="text-[11px] text-site-muted mt-1">{myProfileForm.bio.length}/1000</div>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-col sm:flex-row gap-2">
                        <button
                          type="button"
                          onClick={() => void saveMyProfileForm()}
                          disabled={profileBusy}
                          className="px-3 py-2 text-xs font-semibold rounded-lg bg-site-accent hover:bg-site-accent disabled:bg-site-raised disabled:text-site-muted text-site-on-accent transition-colors"
                        >
                          Сохранить профиль
                        </button>
                        <button
                          type="button"
                          onClick={() => resetMyProfileForm()}
                          disabled={profileBusy}
                          className="px-3 py-2 text-xs font-semibold rounded-lg border border-site-border text-site-text hover:text-site-text hover:bg-site-text/5 disabled:opacity-60 transition-colors"
                        >
                          Сбросить
                        </button>
                      </div>
                    </div>
                        </div>
                      </>
                    )}

                    {/* ===== ЭФФЕКТЫ АВАТАРА ===== */}
                    {settingsSection === 'avatar' && (
                      <>
                        <div className="text-xs text-site-muted px-1 lg:hidden">Эффекты аватара</div>
                        <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
                          <div className="px-4 py-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm text-site-text">Предпросмотр</div>
                              <div className="text-xs text-site-muted truncate">Онлайн + свечение, офлайн без свечения</div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <AvatarFx
                                  sizeClass="w-11 h-11"
                                  avatarUrl={(myProfile?.avatar_url ?? meRef.current?.avatar_url ?? null) as any}
                                  alt="Онлайн"
                                  fallback=""
                                  isOnline={true}
                                  fxColor={avatarFxColor}
                                  fxThickness={avatarFxThickness}
                                  fxGlow={avatarFxGlow}
                                  fxOfflineColor={avatarFxOfflineColor}
                                />
                                <div className="text-[11px] text-site-success">● онлайн</div>
                              </div>
                              <div className="flex items-center gap-2">
                                <AvatarFx
                                  sizeClass="w-11 h-11"
                                  avatarUrl={(myProfile?.avatar_url ?? meRef.current?.avatar_url ?? null) as any}
                                  alt="Офлайн"
                                  fallback=""
                                  isOnline={false}
                                  fxColor={avatarFxColor}
                                  fxThickness={avatarFxThickness}
                                  fxGlow={avatarFxGlow}
                                  fxOfflineColor={avatarFxOfflineColor}
                                />
                                <div className="text-[11px] text-site-muted">○ офлайн</div>
                              </div>
                            </div>
                          </div>

                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-site-text">Цвет</div>
                        <div className="text-xs text-site-muted truncate">{avatarFxColor}</div>
                      </div>
                      <input
                        type="color"
                        value={avatarFxColor}
                        onChange={(e) => {
                          const next = String(e.target.value || '#22c55e');
                          setAvatarFxColor(next);
                          saveAvatarFxDebounced({ color: next, thickness: avatarFxThickness, glow: avatarFxGlow, offlineColor: avatarFxOfflineColor });
                        }}
                        className="h-9 w-12 bg-transparent border border-site-border rounded-lg"
                        aria-label="Цвет обрамления"
                      />
                    </div>

                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-site-text">Толщина</div>
                        <div className="text-xs text-site-muted truncate">{avatarFxThickness}px</div>
                      </div>
                      <div className="flex items-center gap-3 w-48">
                        <input
                          type="range"
                          min={0}
                          max={10}
                          value={avatarFxThickness}
                          onChange={(e) => {
                            const next = Number(e.target.value || 0) || 0;
                            setAvatarFxThickness(next);
                            saveAvatarFxDebounced({ color: avatarFxColor, thickness: next, glow: avatarFxGlow, offlineColor: avatarFxOfflineColor });
                          }}
                          className="flex-1"
                          aria-label="Толщина обрамления"
                        />
                        <div className="w-10 text-right text-xs text-site-muted">{avatarFxThickness}px</div>
                      </div>
                    </div>

                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-site-text">Свечение</div>
                        <div className="text-xs text-site-muted truncate">{avatarFxGlow}%</div>
                      </div>
                      <div className="flex items-center gap-3 w-48">
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={5}
                          value={avatarFxGlow}
                          onChange={(e) => {
                            const next = Number(e.target.value || 0) || 0;
                            setAvatarFxGlow(next);
                            saveAvatarFxDebounced({ color: avatarFxColor, thickness: avatarFxThickness, glow: next, offlineColor: avatarFxOfflineColor });
                          }}
                          className="flex-1"
                          aria-label="Интенсивность свечения"
                        />
                        <div className="w-10 text-right text-xs text-site-muted">{avatarFxGlow}%</div>
                      </div>
                    </div>

                    {/* Комментарий на русском: настройка цвета офлайн-обрамления. */}
                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-site-text">Цвет офлайн</div>
                        <div className="text-xs text-site-muted truncate">{avatarFxOfflineColor}</div>
                      </div>
                      <input
                        type="color"
                        value={avatarFxOfflineColor}
                        onChange={(e) => {
                          const next = String(e.target.value || '#64748b');
                          setAvatarFxOfflineColor(next);
                          saveAvatarFxDebounced({ color: avatarFxColor, thickness: avatarFxThickness, glow: avatarFxGlow, offlineColor: next });
                        }}
                        className="h-9 w-12 bg-transparent border border-site-border rounded-lg"
                        aria-label="Цвет обрамления офлайн"
                      />
                    </div>

                    {/* Комментарий на русском: информация про офлайн-режим. */}
                    <div className="px-4 py-2 bg-site-bg/30">
                      <div className="text-xs text-site-muted"><UiIcon name="Lightbulb" /> Офлайн: фикс. толщина 2px, без свечения</div>
                    </div>

                    <div className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => {
                          const color = '#22c55e';
                          const thickness = 2;
                          const glow = 0;
                          const offlineColor = '#64748b';
                          setAvatarFxColor(color);
                          setAvatarFxThickness(thickness);
                          setAvatarFxGlow(glow);
                          setAvatarFxOfflineColor(offlineColor);
                          void updateChatSettings({ avatar_fx_color: color, avatar_fx_thickness: thickness, avatar_fx_glow: glow, avatar_fx_offline_color: offlineColor } as any);
                        }}
                        className="w-full px-3 py-2 rounded-lg text-xs font-semibold border border-site-border text-site-text hover:text-site-text hover:bg-site-raised/25"
                        title="Вернуть настройки по умолчанию"
                      >
                        Сбросить по умолчанию
                      </button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* ===== УВЕДОМЛЕНИЯ ===== */}
                    {settingsSection === 'notifications' && (
                      <>
                        <div className="text-xs text-site-muted px-1 lg:hidden">Уведомления</div>
                        <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
                          <div className="px-4 py-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm text-site-text">Push-уведомления</div>
                              <div className="text-xs text-site-muted truncate">Уведомления о новых сообщениях</div>
                            </div>
                            <button
                              type="button"
                        role="switch"
                        aria-checked={pushEnabled}
                        onClick={() => void requestBrowserNotifications()}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                          pushEnabled ? 'bg-site-success' : 'bg-site-raised/60'
                        }`}
                        title={pushEnabled ? 'Отключить уведомления' : 'Включить уведомления'}
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${
                            pushEnabled ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                            </button>
                          </div>
                          <div className="px-4 py-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm text-site-text">Превью сообщений</div>
                              <div className="text-xs text-site-muted">Показывать текст в списке чатов</div>
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={showMessagePreview}
                              onClick={() => setShowMessagePreview((v) => !v)}
                              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${showMessagePreview ? 'bg-site-success' : 'bg-site-raised/60'}`}
                            >
                              <span className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${showMessagePreview ? 'translate-x-5' : 'translate-x-1'}`} />
                            </button>
                          </div>
                          <div className="px-4 py-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm text-site-text">Содержимое в уведомлениях</div>
                              <div className="text-xs text-site-muted">Показывать текст/тип вложения в уведомлениях</div>
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={showNotificationContent}
                              onClick={() => setShowNotificationContent((v) => !v)}
                              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${showNotificationContent ? 'bg-site-success' : 'bg-site-raised/60'}`}
                              title={showNotificationContent ? 'Скрыть содержимое' : 'Показывать содержимое'}
                            >
                              <span className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${showNotificationContent ? 'translate-x-5' : 'translate-x-1'}`} />
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* ===== ПРИВАТНОСТЬ ===== */}
                    {settingsSection === 'privacy' && (
                      <>
                        <div className="text-xs text-site-muted px-1 lg:hidden">Приватность</div>
                  <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-site-text">Подтверждения прочтения</div>
                        <div className="text-xs text-site-muted truncate">Показывать собеседнику статус «прочитано»</div>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={readReceiptsEnabled}
                        onClick={() => {
                          const next = !readReceiptsEnabled;
                          setReadReceiptsEnabled(next);
                          void updateChatSettings({ read_receipts_enabled: next }).then((ok) => {
                            if (!ok) setReadReceiptsEnabled(!next);
                          });
                        }}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                          readReceiptsEnabled ? 'bg-site-success' : 'bg-site-raised/60'
                        }`}
                        title={readReceiptsEnabled ? 'Отключить' : 'Включить'}
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${
                            readReceiptsEnabled ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* ===== ИНТЕРФЕЙС ===== */}
                    {settingsSection === 'interface' && (
                      <>
                        <div className="text-xs text-site-muted px-1 lg:hidden">Интерфейс</div>
                        {/* Комментарий на русском: липкая шапка-превью — эффект настроек виден всегда. */}
                        <div className="sticky top-0 z-30 -mx-3 lg:-mx-4 px-3 lg:px-4 pt-2 pb-3 bg-site-bg/70 backdrop-blur-md border-b border-site-border">
                          <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm p-3 font-[var(--dm-font-family)]">
                            <div className="sr-only">Пример сообщений</div>
                            <div className="space-y-[var(--dm-gap,0.75rem)]">
                              <div className="flex justify-start">
                                <div
                                  className="lisad-msg-bubble lisad-msg-bubble--peer max-w-[min(78%,280px)] px-[var(--dm-pad-x,1rem)] py-[var(--dm-pad-y,0.5rem)] rounded-2xl rounded-bl-md"
                                  style={{ fontSize: 'var(--dm-font,0.875rem)', lineHeight: 'var(--dm-leading,1.5)' }}
                                >
                                  Наука — метод сомнения и проверки гипотез.
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <div
                                  className="lisad-msg-bubble lisad-msg-bubble--me max-w-[min(78%,280px)] px-[var(--dm-pad-x,1rem)] py-[var(--dm-pad-y,0.5rem)] rounded-2xl rounded-br-md"
                                  style={{ fontSize: 'var(--dm-font,0.875rem)', lineHeight: 'var(--dm-leading,1.5)' }}
                                >
                                  Истина — не трофей, а бесконечный процесс приближения.
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                  <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
                    <div className="px-4 py-2 bg-site-bg/25 text-[11px] text-site-muted uppercase tracking-wider">Основное</div>
                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-site-text">Превью ссылок</div>
                        <div className="text-xs text-site-muted truncate">Преобразовывать URL в кликабельные ссылки</div>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={linkPreviewsEnabled}
                        onClick={() => {
                          const next = !linkPreviewsEnabled;
                          setLinkPreviewsEnabled(next);
                          void updateChatSettings({ link_previews_enabled: next }).then((ok) => {
                            if (!ok) setLinkPreviewsEnabled(!next);
                          });
                        }}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                          linkPreviewsEnabled ? 'bg-site-success' : 'bg-site-raised/60'
                        }`}
                        title={linkPreviewsEnabled ? 'Отключить' : 'Включить'}
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${
                            linkPreviewsEnabled ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-site-text">Стопки сообщений</div>
                        <div className="text-xs text-site-muted truncate">Группировать подряд идущие сообщения</div>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={messageStackingEnabled}
                        onClick={() => {
                          const next = !messageStackingEnabled;
                          setMessageStackingEnabled(next);
                          void updateChatSettings({ message_stacking_enabled: next }).then((ok) => {
                            if (!ok) setMessageStackingEnabled(!next);
                          });
                        }}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                          messageStackingEnabled ? 'bg-site-success' : 'bg-site-raised/60'
                        }`}
                        title={messageStackingEnabled ? 'Отключить' : 'Включить'}
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${
                            messageStackingEnabled ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-site-text">Компактный режим</div>
                        <div className="text-xs text-site-muted truncate">Плотнее список диалогов</div>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={compactMode}
                        onClick={() => {
                          const next = !compactMode;
                          setCompactMode(next);
                          void updateChatSettings({ compact_mode: next }).then((ok) => {
                            if (!ok) setCompactMode(!next);
                          });
                        }}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                          compactMode ? 'bg-site-success' : 'bg-site-raised/60'
                        }`}
                        title={compactMode ? 'Отключить' : 'Включить'}
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-site-surface transition ${
                            compactMode ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-site-text">Размер текста</div>
                        <div className="text-xs text-site-muted truncate">Масштаб сообщений</div>
                      </div>
                            <div className="w-44 sm:w-48">
                              <input
                                type="range"
                                min={8}
                                max={24}
                                step={1}
                                value={Math.max(8, Math.min(24, Math.round(14 * (Number(fontScale) || 100) / 100)))}
                                onChange={(e) => {
                                  const prev = fontScale;
                                  const px = Number(e.target.value || 14) || 14;
                                  const next = Math.max(50, Math.min(200, Math.round((px / 14) * 100)));
                                  setFontScale(next);
                                  void updateChatSettings({ font_scale: next }).then((ok) => {
                                    if (!ok) setFontScale(prev);
                                  });
                                }}
                                className="touch-slider"
                                aria-label="Размер текста сообщений"
                              />
                              <div className="mt-1 flex items-center justify-between">
                                <span className="text-[10px] text-site-secondary select-none">Аа</span>
                                <span className="text-[11px] text-site-muted tabular-nums select-none">
                                  {Math.max(8, Math.min(24, Math.round(14 * (Number(fontScale) || 100) / 100)))}px
                                </span>
                                <span className="text-base text-site-text select-none">Аа</span>
                              </div>
                            </div>
                    </div>
                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-site-text">Плотность сообщений</div>
                        <div className="text-xs text-site-muted truncate">Отступы и межстрочный интервал</div>
                      </div>
                            <div className="w-44 sm:w-48">
                              <div className="relative">
                                <input
                                  type="range"
                                  min={0}
                                  max={2}
                                  step={1}
                                  value={messageDensity === 'compact' ? 0 : messageDensity === 'comfortable' ? 2 : 1}
                                  onChange={(e) => {
                                    const prev = messageDensity;
                                    const idx = Number(e.target.value || 1) || 0;
                                    const next: MessageDensity = idx <= 0 ? 'compact' : idx >= 2 ? 'comfortable' : 'normal';
                                    setMessageDensity(next);
                                    void updateChatSettings({ message_density: next }).then((ok) => {
                                      if (!ok) setMessageDensity(prev);
                                    });
                                  }}
                                  className="touch-slider"
                                  aria-label="Плотность сообщений"
                                />
                                <div aria-hidden="true" className="pointer-events-none absolute left-0 right-0 top-[7px] flex justify-between">
                                  <span className="w-2 h-2 rounded-full bg-site-raised/60" />
                                  <span className="w-2 h-2 rounded-full bg-site-raised/60" />
                                  <span className="w-2 h-2 rounded-full bg-site-raised/60" />
                                </div>
                              </div>
                              <div className="mt-1 flex items-center justify-between">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const prev = messageDensity;
                                    const next: MessageDensity = 'compact';
                                    setMessageDensity(next);
                                    void updateChatSettings({ message_density: next }).then((ok) => {
                                      if (!ok) setMessageDensity(prev);
                                    });
                                  }}
                                  className="text-[10px] text-site-secondary hover:text-site-text select-none"
                                  title="Компактно"
                                  aria-label="Компактно"
                                >
                                  Аа
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const prev = messageDensity;
                                    const next: MessageDensity = 'normal';
                                    setMessageDensity(next);
                                    void updateChatSettings({ message_density: next }).then((ok) => {
                                      if (!ok) setMessageDensity(prev);
                                    });
                                  }}
                                  className="text-xs text-site-secondary hover:text-site-text select-none"
                                  title="Нормально"
                                  aria-label="Нормально"
                                >
                                  Аа
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const prev = messageDensity;
                                    const next: MessageDensity = 'comfortable';
                                    setMessageDensity(next);
                                    void updateChatSettings({ message_density: next }).then((ok) => {
                                      if (!ok) setMessageDensity(prev);
                                    });
                                  }}
                                  className="text-sm text-site-text hover:text-site-text select-none"
                                  title="Комфортно"
                                  aria-label="Комфортно"
                                >
                                  Аа
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="px-4 py-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm text-site-text">Цвет текста</div>
                              <div className="text-xs text-site-muted truncate">{bubbleTextColor}</div>
                            </div>
                            <input
                              type="color"
                              value={bubbleTextColor}
                              onChange={(e) => {
                                const next = String(e.target.value || '#ffffff');
                                setBubbleTextColor(next);
                                saveMessageTextStyleDebounced({ textColor: next, font: messageFont });
                              }}
                              className="h-9 w-12 bg-transparent border border-site-border rounded-lg"
                              aria-label="Цвет текста в пузырьках"
                            />
                          </div>

                          <div className="px-4 py-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm text-site-text">Шрифт текста</div>
                              <div className="text-xs text-site-muted truncate">Для сообщений</div>
                            </div>
                            <select
                              value={messageFont}
                              onChange={(e) => {
                                const raw = String(e.target.value || 'system').toLowerCase();
                                const next = (raw === 'inter' || raw === 'serif' || raw === 'mono' ? raw : 'system') as any;
                                setMessageFont(next);
                                saveMessageTextStyleDebounced({ textColor: bubbleTextColor, font: next });
                              }}
                              className="bg-site-surface/60 border border-site-border rounded-lg px-2 py-1 text-sm text-site-text focus:outline-none focus:ring-2 focus:ring-site-accent/50"
                              aria-label="Шрифт текста сообщений"
                            >
                              <option value="system">Системный</option>
                              <option value="inter">Inter (современный)</option>
                              <option value="serif">Serif (книга)</option>
                              <option value="mono">Mono (код)</option>
                            </select>
                          </div>

                          {/* Комментарий на русском: настройки фона чата перенесены в профиль комнаты (вкладка "Дизайн"). */}

                          {/* Комментарий на русском: ручное "Сохранить" убрано — настройки должны применяться сразу. */}
                        </div>

                        <div className="mt-3 rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden">
                          <div className="px-4 py-2 bg-site-bg/25 text-[11px] text-site-muted uppercase tracking-wider">Темы</div>
                          <div className="px-4 py-3">
                            <div className="text-xs text-site-muted">Палитра · прозрачность · свечение</div>
                            <div className="mt-2 flex gap-3 overflow-x-auto pb-1 scrollbar-hide" data-scroll-lock-allow>
                              {MESSAGE_THEMES.map((t) => {
                                const isActive = messageTheme === t.id;
                                const bg = `conic-gradient(from 210deg, ${t.me} 0deg, ${t.base} 110deg, ${t.peer} 220deg, ${t.me} 360deg)`;
                                return (
                                  <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => void applyMessageThemePreset(t.id)}
                                    className={`w-11 h-11 rounded-full shrink-0 border ${
                                      isActive ? 'border-site-border ring-2 ring-site-accent/60' : 'border-site-border hover:border-site-border'
                                    } shadow-[0_10px_30px_rgba(0,0,0,0.35)]`}
                                    style={{ background: bg }}
                                    title={t.label}
                                    aria-label={`Тема: ${t.label}`}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* ===== ВИД СООБЩЕНИЙ (мобильная версия) ===== */}
                        <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border mt-3">
                          <div className="px-4 py-3">
                            <div className="text-sm text-site-text font-medium">Вид сообщений</div>
                            <div className="text-xs text-site-muted">Стиль пузырьков: цвет, прозрачность, тени</div>
                          </div>

                          {/* Прозрачность + Цвет — компактно, но с широким ползунком (iOS-стиль) */}
                          <div className="px-4 py-3 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm text-site-text min-w-0">Прозрачность</div>
                              <input
                                type="color"
                                value={bubbleColor}
                                onChange={(e) => {
                                  const next = String(e.target.value || '#7dd3fc');
                                  setBubbleColor(next);
                                  markThemeCustom();
                                  saveBubbleGlassDebounced({
                                    transparency: bubbleTransparency, color: next, colorMe: bubbleColorMe, colorPeer: bubbleColorPeer, shadow: bubbleShadow, shadowColor: bubbleShadowColor,
                                    shadowEnabled: bubbleShadowEnabled, glowEnabled: bubbleGlowEnabled, glowColor: bubbleGlowColor, glowX: bubbleGlowX, glowY: bubbleGlowY,
                                  });
                                }}
                                className="h-8 w-10 bg-transparent border border-site-border rounded"
                              />
                            </div>
                            <div className="flex items-center gap-2 w-full">
                              <input
                                type="range"
                                min={0}
                                max={100}
                                value={bubbleTransparency}
                                onChange={(e) => {
                                  const next = Number(e.target.value || 0) || 0;
                                  setBubbleTransparency(next);
                                  saveBubbleGlassDebounced({
                                    transparency: next, color: bubbleColor, shadow: bubbleShadow, shadowColor: bubbleShadowColor,
                                    shadowEnabled: bubbleShadowEnabled, glowEnabled: bubbleGlowEnabled, glowColor: bubbleGlowColor, glowX: bubbleGlowX, glowY: bubbleGlowY,
                                  });
                                }}
                                className="flex-1 h-8"
                              />
                              <div className="w-10 text-right text-xs text-site-muted">{bubbleTransparency}%</div>
                            </div>
                          </div>

                          <div className="px-4 py-3 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm text-site-text min-w-0">Отдельные цвета</div>
                              <div className="text-[11px] text-site-muted">
                                {bubbleColorMe ? 'Мои: свои' : 'Мои: базовый'} · {bubbleColorPeer ? 'Собес: свои' : 'Собес: базовый'}
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] text-site-secondary">Мои</span>
                                <input
                                  type="color"
                                  value={bubbleColorMe || bubbleColor}
                                  onChange={(e) => {
                                    const next = String(e.target.value || '').toLowerCase();
                                    markThemeCustom();
                                    setBubbleColor(next);
                                    setBubbleColorMe(next);
                                    saveBubbleGlassDebounced({
                                      transparency: bubbleTransparency,
                                      color: next,
                                      colorMe: next,
                                      colorPeer: bubbleColorPeer,
                                      shadow: bubbleShadow,
                                      shadowColor: bubbleShadowColor,
                                      shadowEnabled: bubbleShadowEnabled,
                                      glowEnabled: bubbleGlowEnabled,
                                      glowColor: bubbleGlowColor,
                                      glowX: bubbleGlowX,
                                      glowY: bubbleGlowY,
                                    });
                                  }}
                                  className="h-8 w-10 bg-transparent border border-site-border rounded"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    markThemeCustom();
                                    setBubbleColorMe('');
                                    saveBubbleGlassDebounced({
                                      transparency: bubbleTransparency,
                                      color: bubbleColor,
                                      colorMe: '',
                                      colorPeer: bubbleColorPeer,
                                      shadow: bubbleShadow,
                                      shadowColor: bubbleShadowColor,
                                      shadowEnabled: bubbleShadowEnabled,
                                      glowEnabled: bubbleGlowEnabled,
                                      glowColor: bubbleGlowColor,
                                      glowX: bubbleGlowX,
                                      glowY: bubbleGlowY,
                                    });
                                  }}
                                  className="px-2 py-1 rounded-lg border border-site-border text-[11px] text-site-text hover:text-site-text hover:bg-site-raised/25"
                                  title="Сбросить к базовому"
                                >
                                  Сброс
                                </button>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] text-site-secondary">Собеседник</span>
                                <input
                                  type="color"
                                  value={bubbleColorPeer || bubbleColor}
                                  onChange={(e) => {
                                    const next = String(e.target.value || '').toLowerCase();
                                    setBubbleColorPeer(next);
                                    saveBubbleGlassDebounced({
                                      transparency: bubbleTransparency,
                                      color: bubbleColor,
                                      colorMe: bubbleColorMe,
                                      colorPeer: next,
                                      shadow: bubbleShadow,
                                      shadowColor: bubbleShadowColor,
                                      shadowEnabled: bubbleShadowEnabled,
                                      glowEnabled: bubbleGlowEnabled,
                                      glowColor: bubbleGlowColor,
                                      glowX: bubbleGlowX,
                                      glowY: bubbleGlowY,
                                    });
                                  }}
                                  className="h-8 w-10 bg-transparent border border-site-border rounded"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setBubbleColorPeer('');
                                    saveBubbleGlassDebounced({
                                      transparency: bubbleTransparency,
                                      color: bubbleColor,
                                      colorMe: bubbleColorMe,
                                      colorPeer: '',
                                      shadow: bubbleShadow,
                                      shadowColor: bubbleShadowColor,
                                      shadowEnabled: bubbleShadowEnabled,
                                      glowEnabled: bubbleGlowEnabled,
                                      glowColor: bubbleGlowColor,
                                      glowX: bubbleGlowX,
                                      glowY: bubbleGlowY,
                                    });
                                  }}
                                  className="px-2 py-1 rounded-lg border border-site-border text-[11px] text-site-text hover:text-site-text hover:bg-site-raised/25"
                                  title="Сбросить к базовому"
                                >
                                  Сброс
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* ===== ТЕНИ (мобильная) ===== */}
                          <div className="px-4 py-3 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm text-site-text">Тени</div>
                              <button type="button" role="switch" aria-checked={bubbleShadowEnabled}
                                onClick={() => {
                                  const next = !bubbleShadowEnabled;
                                  setBubbleShadowEnabled(next);
                                  saveBubbleGlassDebounced({
                                    transparency: bubbleTransparency, color: bubbleColor, shadow: bubbleShadow, shadowColor: bubbleShadowColor,
                                    shadowEnabled: next, glowEnabled: bubbleGlowEnabled, glowColor: bubbleGlowColor, glowX: bubbleGlowX, glowY: bubbleGlowY,
                                  });
                                }}
                                className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${bubbleShadowEnabled ? 'bg-site-success' : 'bg-site-raised/60'}`}
                              >
                                <span className={`inline-block h-5 w-5 transform rounded-full bg-site-surface transition ${bubbleShadowEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                              </button>
                            </div>
                            {bubbleShadowEnabled && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 w-full">
                                  <div className="text-xs text-site-secondary w-14 shrink-0">Интенс.</div>
                                  <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    step={5}
                                    value={bubbleShadow}
                                    onChange={(e) => {
                                      const next = Number(e.target.value || 0) || 0;
                                      setBubbleShadow(next);
                                      saveBubbleGlassDebounced({
                                        transparency: bubbleTransparency, color: bubbleColor, shadow: next, shadowColor: bubbleShadowColor,
                                        shadowEnabled: bubbleShadowEnabled, glowEnabled: bubbleGlowEnabled, glowColor: bubbleGlowColor, glowX: bubbleGlowX, glowY: bubbleGlowY,
                                      });
                                    }}
                                    className="flex-1 h-8"
                                  />
                                  <div className="w-10 text-right text-xs text-site-muted">{bubbleShadow}%</div>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-xs text-site-secondary">Цвет теней</div>
                                  <input
                                    type="color"
                                    value={bubbleShadowColor}
                                    onChange={(e) => {
                                      const next = String(e.target.value || '#0b1220');
                                      setBubbleShadowColor(next);
                                      saveBubbleGlassDebounced({
                                        transparency: bubbleTransparency, color: bubbleColor, shadow: bubbleShadow, shadowColor: next,
                                        shadowEnabled: bubbleShadowEnabled, glowEnabled: bubbleGlowEnabled, glowColor: bubbleGlowColor, glowX: bubbleGlowX, glowY: bubbleGlowY,
                                      });
                                    }}
                                    className="h-8 w-10 bg-transparent border border-site-border rounded"
                                  />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* ===== СВЕЧЕНИЕ (мобильная) ===== */}
                          <div className="px-4 py-3 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm text-site-text">Свечение</div>
                              <button type="button" role="switch" aria-checked={bubbleGlowEnabled}
                                onClick={() => {
                                  const next = !bubbleGlowEnabled;
                                  setBubbleGlowEnabled(next);
                                  saveBubbleGlassDebounced({
                                    transparency: bubbleTransparency, color: bubbleColor, shadow: bubbleShadow, shadowColor: bubbleShadowColor,
                                    shadowEnabled: bubbleShadowEnabled, glowEnabled: next, glowColor: bubbleGlowColor, glowX: bubbleGlowX, glowY: bubbleGlowY,
                                  });
                                }}
                                className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${bubbleGlowEnabled ? 'bg-site-success' : 'bg-site-raised/60'}`}
                              >
                                <span className={`inline-block h-5 w-5 transform rounded-full bg-site-surface transition ${bubbleGlowEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                              </button>
                            </div>
                            {bubbleGlowEnabled && (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-xs text-site-secondary">Цвет</div>
                                  <input
                                    type="color"
                                    value={bubbleGlowColor}
                                    onChange={(e) => {
                                      const next = String(e.target.value || '#ffffff');
                                      setBubbleGlowColor(next);
                                      saveBubbleGlassDebounced({
                                        transparency: bubbleTransparency, color: bubbleColor, shadow: bubbleShadow, shadowColor: bubbleShadowColor,
                                        shadowEnabled: bubbleShadowEnabled, glowEnabled: bubbleGlowEnabled, glowColor: next, glowX: bubbleGlowX, glowY: bubbleGlowY,
                                      });
                                    }}
                                    className="h-8 w-10 bg-transparent border border-site-border rounded"
                                  />
                                </div>
                                <div className="flex items-center gap-2 w-full">
                                  <div className="text-xs text-site-secondary w-8 shrink-0">X</div>
                                  <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    value={bubbleGlowX}
                                    onChange={(e) => {
                                      const next = Number(e.target.value || 0) || 0;
                                      setBubbleGlowX(next);
                                      saveBubbleGlassDebounced({
                                        transparency: bubbleTransparency, color: bubbleColor, shadow: bubbleShadow, shadowColor: bubbleShadowColor,
                                        shadowEnabled: bubbleShadowEnabled, glowEnabled: bubbleGlowEnabled, glowColor: bubbleGlowColor, glowX: next, glowY: bubbleGlowY,
                                      });
                                    }}
                                    className="flex-1 h-8"
                                  />
                                  <div className="w-10 text-right text-xs text-site-muted">{bubbleGlowX}</div>
                                </div>
                                <div className="flex items-center gap-2 w-full">
                                  <div className="text-xs text-site-secondary w-8 shrink-0">Y</div>
                                  <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    value={bubbleGlowY}
                                    onChange={(e) => {
                                      const next = Number(e.target.value || 0) || 0;
                                      setBubbleGlowY(next);
                                      saveBubbleGlassDebounced({
                                        transparency: bubbleTransparency, color: bubbleColor, shadow: bubbleShadow, shadowColor: bubbleShadowColor,
                                        shadowEnabled: bubbleShadowEnabled, glowEnabled: bubbleGlowEnabled, glowColor: bubbleGlowColor, glowX: bubbleGlowX, glowY: next,
                                      });
                                    }}
                                    className="flex-1 h-8"
                                  />
                                  <div className="w-10 text-right text-xs text-site-muted">{bubbleGlowY}</div>
                                </div>
                                {/* Параллакс по гироскопу */}
                                <div className="flex items-center justify-between gap-2 pt-2 mt-1 border-t border-site-border">
                                  <div className="text-xs text-site-secondary">Параллакс</div>
                                  <button type="button" role="switch" aria-checked={bubbleGlowGyro}
                                    onClick={() => {
                                      const next = !bubbleGlowGyro;
                                      setBubbleGlowGyro(next);
                                      void updateChatSettings({ bubble_glow_gyro: next } as any);
                                    }}
                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${bubbleGlowGyro ? 'bg-site-success' : 'bg-site-raised/60'}`}
                                  >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-site-surface transition ${bubbleGlowGyro ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                  </button>
                                </div>
                                {bubbleGlowGyro && (
                                  <div className="space-y-1 pt-1">
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="text-[11px] text-site-muted">Сила</div>
                                      <div className="flex items-center gap-2 flex-1">
                                        <input
                                          type="range"
                                          min={1}
                                          max={5}
                                          value={bubbleGlowGyroIntensity}
                                          onChange={(e) => {
                                            const next = Number(e.target.value || 1) || 1;
                                            setBubbleGlowGyroIntensity(next);
                                            void updateChatSettings({ bubble_glow_gyro_intensity: next } as any);
                                          }}
                                          className="flex-1 h-8"
                                        />
                                        <div className="w-10 text-right text-[11px] text-site-muted">{bubbleGlowGyroIntensity}x</div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => {
                                const defs = { transparency: 82, color: '#7dd3fc', shadow: 35, shadowColor: '#0b1220', shadowEnabled: true, glowEnabled: true, glowColor: '#ffffff', glowX: 22, glowY: 18, glowGyro: false, glowGyroInt: 1 };
                                setBubbleTransparency(defs.transparency);
                                setBubbleBlur(90);
                                setBubbleColor(defs.color);
                                setBubbleShadow(defs.shadow);
                                setBubbleShadowColor(defs.shadowColor);
                                setBubbleShadowEnabled(defs.shadowEnabled);
                                setBubbleGlowEnabled(defs.glowEnabled);
                                setBubbleGlowColor(defs.glowColor);
                                setBubbleGlowX(defs.glowX);
                                setBubbleGlowY(defs.glowY);
                                setBubbleGlowGyro(defs.glowGyro);
                                setBubbleGlowGyroIntensity(defs.glowGyroInt);
                                void updateChatSettings({
                                  bubble_transparency: defs.transparency, bubble_blur: 90, bubble_color: defs.color,
                                  bubble_shadow: defs.shadow, bubble_shadow_color: defs.shadowColor, bubble_shadow_enabled: defs.shadowEnabled,
                                  bubble_glow_enabled: defs.glowEnabled, bubble_glow_color: defs.glowColor, bubble_glow_x: defs.glowX, bubble_glow_y: defs.glowY,
                                  bubble_glow_gyro: defs.glowGyro, bubble_glow_gyro_intensity: defs.glowGyroInt,
                                } as any);
                              }}
                              className="w-full px-3 py-2 rounded-lg text-xs font-semibold border border-site-border text-site-text hover:text-site-text hover:bg-site-raised/25"
                            >
                              Стандартный стиль сообщений
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* ===== КЭШ ===== */}
                    {settingsSection === 'storage' && (
                      <>
                        <div className="text-xs text-site-muted px-1 lg:hidden">Кэш</div>
                        <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
                          <div className="px-4 py-3 flex flex-wrap gap-3 items-center">
                            <div className="min-w-[140px]">
                              <div className="text-sm text-site-text">Занято</div>
                              <div className="text-lg font-semibold text-site-text/90">{formatBytesShort(storageStats?.used || 0)}</div>
                            </div>
                            <div className="min-w-[140px]">
                              <div className="text-sm text-site-text">Доступно</div>
                              <div className="text-lg font-semibold text-site-success">{formatBytesShort(storageStats?.available || 0)}</div>
                            </div>
                            <div className="min-w-[140px]">
                              <div className="text-sm text-site-text">Квота</div>
                              <div className="text-lg font-semibold text-site-text">{formatBytesShort(storageStats?.quota || 0)}</div>
                            </div>
                            <div className="flex-1 min-w-[200px]">
                              <div className="text-xs text-site-muted mb-1">Использование</div>
                              <div className="w-full h-2 rounded-full bg-site-surface/60 overflow-hidden">
                                <div
                                  className="h-full bg-site-success"
                                  style={{
                                    width: `${Math.min(100, Math.max(0, storageStats?.quota ? ((storageStats?.used ?? 0) / storageStats.quota) * 100 : 0))}%`,
                                    transition: 'width 180ms ease',
                                  }}
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => void calcStorageStats()}
                                disabled={storageLoading || cachePurging}
                                className="px-3 py-2 text-xs rounded-lg border border-site-border text-site-text hover:text-site-text hover:bg-site-raised/25 disabled:opacity-50"
                                title="Пересчитать"
                              >
                                ↻ Обновить
                              </button>
                              <button
                                type="button"
                                onClick={() => void handlePurgeStorage()}
                                disabled={cachePurging}
                                className="px-3 py-2 text-xs rounded-lg border border-site-danger/35 text-site-danger hover:bg-site-danger/15 disabled:opacity-50"
                                title="Очистить кэш"
                              >
                                {cachePurging ? 'Очистка...' : cachePurgeArmed ? 'Нажмите ещё раз' : <><UiIcon name="Trash" />{" Очистить кэш"}</>}
                              </button>
                            </div>
                          </div>

                          {storageError && <div className="px-4 py-2 text-xs text-site-danger">{storageError}</div>}
                          {storageLoading && <div className="px-4 py-2 text-xs text-site-muted">Считаем кэш...</div>}

                          {storageStats && (
                            <div className="divide-y divide-site-border">
                              {storageStats.categories.map((c) => (
                                <div key={c.id} className="px-4 py-3">
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm text-site-text truncate">{c.label}</div>
                                    <div className="text-sm text-site-text">{formatBytesShort(c.size)}</div>
                                  </div>
                                  <div className="mt-2 h-2 rounded-full bg-site-surface/60 overflow-hidden">
                                    <div
                                      className="h-full bg-site-accent/80"
                                      style={{
                                        width: `${(storageStats?.used ?? 0) ? Math.min(100, Math.max(0, (c.size / (storageStats?.used ?? 0)) * 100)) : 0}%`,
                                        transition: 'width 180ms ease',
                                      }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* ===== БЕЗОПАСНОСТЬ ===== */}
                    {settingsSection === 'security' && (
                      <>
                        <div className="text-xs text-site-muted px-1 lg:hidden">Безопасность</div>
                  <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-site-text">Активные сессии</div>
                        <div className="text-xs text-site-muted truncate">{sessions.length ? `Устройств: ${sessions.length}` : 'Нет данных'}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => void loadSessions()}
                        className="px-2 py-1 text-xs text-site-secondary hover:text-site-text hover:bg-site-raised/30 rounded-md"
                        title="Обновить"
                      >
                        ↻
                            </button>
                          </div>
                          {sessions.length === 0 ? (
                            <div className="px-4 py-3 text-xs text-site-muted">Список сессий пуст</div>
                          ) : (
                            <>
                              {currentSession && (
                                <div className="px-4 py-3 bg-site-bg/20">
                                  <div className="text-[11px] text-site-success mb-1">Это устройство</div>
                                  <div className="text-xs text-site-text truncate">{String(currentSession.user_agent || 'Устройство')}</div>
                                  <div className="text-[11px] text-site-muted truncate">
                                    {[
                                      currentSession.ip ? `IP: ${currentSession.ip}` : '',
                                      currentSession.last_used_at ? formatDialogTime(String(currentSession.last_used_at)) : '',
                                    ]
                                      .filter(Boolean)
                                      .join(' · ')}
                                  </div>
                                </div>
                              )}

                              <div className="px-4 py-2 text-[11px] text-site-muted">Другие устройства: {otherSessions.length}</div>

                              {otherSessions.length === 0 ? (
                                <div className="px-4 py-3 text-xs text-site-muted">Других устройств нет</div>
                              ) : (
                                otherSessions.slice(0, 8).map((s) => (
                                  <div key={s.id} className="px-4 py-2">
                                    <div className="text-xs text-site-text truncate">{String(s.user_agent || 'Устройство')}</div>
                                    <div className="text-[11px] text-site-muted truncate">
                                      {[s.ip ? `IP: ${s.ip}` : '', s.last_used_at ? formatDialogTime(String(s.last_used_at)) : '']
                                        .filter(Boolean)
                                        .join(' · ')}
                                    </div>
                                  </div>
                                ))
                              )}
                            </>
                          )}
                          <div className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => {
                                if (confirm('Выйти на всех устройствах, кроме этого?')) void logoutOtherDevices();
                              }}
                              className="w-full px-3 py-2 rounded-lg text-xs font-semibold border border-site-danger/30 text-site-danger hover:bg-site-danger/10"
                            >
                              Выйти везде (кроме этого устройства)
                            </button>
                          </div>
                        </div>

                        <div className="text-xs text-site-muted px-1 mt-4">Чёрный список</div>
                        <div className="rounded-2xl border border-site-border bg-site-bg/40 backdrop-blur-sm overflow-hidden divide-y divide-site-border">
                          <div className="px-4 py-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm text-site-text">Заблокированные</div>
                              <div className="text-xs text-site-muted">{blockedUsers.length ? `${blockedUsers.length} пользователей` : 'Список пуст'}</div>
                            </div>
                            <button type="button" onClick={() => void loadBlockedUsers()} className="px-2 py-1 text-xs text-site-secondary hover:text-site-text hover:bg-site-raised/30 rounded-md">↻</button>
                          </div>
                          {blockedUsers.length === 0 ? (
                            <div className="px-4 py-3 text-xs text-site-muted">Нет заблокированных</div>
                          ) : (
                            blockedUsers.slice(0, 50).map((u) => (
                              <div key={u.id} className="px-4 py-3 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="text-sm text-site-text truncate">{u.display_name || u.username}</div>
                                  <div className="text-xs text-site-muted truncate">@{u.nickname || u.username}</div>
                                </div>
                                <button type="button" onClick={() => void unblockUser(u.id)} className="px-3 py-2 text-xs text-site-text hover:text-site-text hover:bg-site-raised/30 rounded-lg border border-site-border">Разблокировать</button>
                              </div>
                            ))
                          )}
                        </div>
                      </>
                    )}

                  </div>
                </div>
              </div>
            )}

            {(!supportOpen || !isSuperAdmin) && tab === 'employees' && isEmployee && (
              <>
                <div className="p-3 border-b border-site-border-control/30 flex items-center justify-between">
                  <div className="text-sm font-semibold text-site-text">Команда</div>
                  <button
                    onClick={() => void loadEmployees()}
                    className="px-2 py-1 text-xs text-site-secondary hover:text-site-text hover:bg-site-raised/30 rounded-md"
                    title="Обновить список"
                  >
                    ↻
                  </button>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pb-20" data-scroll-lock-allow>
                  {employeesLoading ? (
                    <div className="p-4 text-site-muted">Загрузка...</div>
                  ) : employees.length === 0 ? (
                    <div className="p-4 text-site-muted">Список пуст</div>
                  ) : (
                    employees.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => selectUser(u)}
                        className="w-full text-left p-3 border-b border-site-border hover:bg-site-raised/25 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <AvatarFx
                            sizeClass="w-11 h-11"
                            className="shrink-0"
                            avatarUrl={u.avatar_url || null}
                            alt={u.display_name || u.username || ''}
                            fallback={(u.display_name?.[0] || u.username?.[0] || '?').toUpperCase()}
                            isOnline={false}
                            fxColor={u.avatar_fx_color}
                            fxThickness={u.avatar_fx_thickness}
                            fxGlow={u.avatar_fx_glow}
                            fxOfflineColor={u.avatar_fx_offline_color}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-site-text truncate">{u.display_name || u.username}</div>
                            <div className="text-xs text-site-muted truncate">@{(u as any).nickname || u.username}</div>
                            {u.role && <div className="text-[11px] text-site-muted">{u.role}</div>}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}

            {(!supportOpen || !isSuperAdmin) && tab === 'friends' && (
              <div className="flex-1 flex flex-col min-h-0">
                <div className="p-3 border-b border-site-border-control/30">
                  <input
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    placeholder="Поиск..."
                    className="w-full px-4 py-1.5 bg-site-surface/50 border border-site-border rounded-full text-base sm:text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50 select-text"
                  />
                  <div className="mt-2 flex items-center justify-between text-[11px] text-site-muted px-1">
                    <span>{friendRequests.length > 0 ? `Заявки: ${friendRequests.length}` : 'Заявок нет'}</span>
                    <span>Друзья: {friends.length}</span>
                  </div>
                </div>

                <div
                  ref={contactsScrollRef}
                  className="flex-1 min-h-0 overflow-y-auto overscroll-contain pb-20"
                  data-scroll-lock-allow
                  onScroll={() => {
                    // Комментарий на русском: при скролле сворачиваем расширенную карточку.
                    if (expandedContactId !== null) {
                      setExpandedContactId(null);
                      setExpandedContactProfile(null);
                    }
                  }}
                >
                  {(() => {
                    const q = userQuery.trim().toLowerCase();
                    const match = (u: User) => {
                      if (!q) return true;
                      const dn = String(u.display_name || '').toLowerCase();
                      const un = String(u.username || '').toLowerCase();
                      return dn.includes(q) || un.includes(q);
                    };

                    // Комментарий на русском: список = заявки + друзья + (при запросе >= 2 символов) результаты поиска.
                    const reqs = friendRequests.filter((r) => match(r.from_user));
                    const frs = friends.filter((f) => match(f));
                    const known = new Set<number>();
                    reqs.forEach((r) => known.add(r.from_user.id));
                    frs.forEach((f) => known.add(f.id));
                    const extra = q.length >= 2 ? userResults.filter((u) => match(u) && !known.has(u.id)) : [];

                    // Комментарий на русском: обработчик тапа по контакту — на мобилке раскрываем, на десктопе открываем справа.
                    const handleContactTap = async (u: User) => {
                      const isMobile = window.matchMedia?.('(max-width: 639px)').matches;
                      if (isMobile) {
                        // Комментарий на русском: тоггл раскрытия карточки.
                        if (expandedContactId === u.id) {
                          setExpandedContactId(null);
                          setExpandedContactProfile(null);
                          return;
                        }
                        setExpandedContactId(u.id);
                        setExpandedContactLoading(true);
                        setExpandedContactProfile(null);
                        try {
                          const res = await apiCall(`/api/users/preview/${u.id}/`);
                          if (res.ok) {
                            const data = await res.json();
                            setExpandedContactProfile(data);
                          }
                        } catch { /* ignore */ }
                        setExpandedContactLoading(false);
                      } else {
                        setSelectedContact(u);
                      }
                    };

                    // Комментарий на русском: рендер строки контакта с поддержкой расширения (мобилка).
                    // Комментарий на русском: вспомогательная функция для форматирования "был в сети".
                    const formatLastSeen = (ls: string | null | undefined) => {
                      if (!ls) return null;
                      const ts = new Date(ls).getTime();
                      if (Number.isNaN(ts)) return null;
                      const diff = Date.now() - ts;
                      const mins = Math.floor(diff / 60000);
                      if (mins < 1) return 'только что';
                      if (mins < 60) return `${mins} мин назад`;
                      const hours = Math.floor(mins / 60);
                      if (hours < 24) return `${hours} ч назад`;
                      const days = Math.floor(hours / 24);
                      if (days === 1) return 'вчера';
                      if (days < 7) return `${days} д назад`;
                      return new Date(ts).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
                    };

                    // Комментарий на русском: маппинг ролей на читаемые названия.
                    const roleLabel = (r: string) => {
                      const map: Record<string, string> = {
                        superadmin: 'Админ', admin: 'Админ', staff: 'Сотрудник', moderator: 'Модератор', student: 'Студент', user: 'Пользователь',
                      };
                      return map[r.toLowerCase()] || r;
                    };

                    const renderRow = (u: User, right?: React.ReactNode, key?: string, incomingReq?: FriendRequest | null) => {
                      const isExpanded = expandedContactId === u.id;
                      const isFriend = friends.some((f) => f.id === u.id);
                      const status = String((u as any).friendship_status || '');
                      const isOutgoingPending = status === 'outgoing_pending';
                      const isMe = u.id === (meRef.current?.id || 0);
                      const p = isExpanded ? expandedContactProfile : null;

                      // Комментарий на русском: данные для отображения в обычном состоянии.
                      const isOnline = isExpanded ? !!p?.is_online : !!u.is_online;
                      const lastSeen = isExpanded ? p?.last_seen : u.last_seen;
                      const gamePresenceText = formatGamePresenceText(((p as any)?.game_presence ?? u.game_presence) as User['game_presence']);
                      // Комментарий на русском: roles может быть string[] (старый формат) или объектами ({name}) из fetchUserRolesMap.
                      const roleNames = Array.isArray((u as any).roles)
                        ? ((u as any).roles as any[])
                            .map((r) => (typeof r === 'string' ? r : typeof r?.name === 'string' ? r.name : ''))
                            .map((s) => String(s || '').trim())
                            .filter(Boolean)
                        : [];
                      const typeName = roleNames[0] ? roleLabel(roleNames[0]) : 'Пользователь';
                      const projectsCnt = u.projects_count ?? 0;
                      const newsCnt = u.news_count ?? 0;
                      const showMeta = !isExpanded && (isFriend || !!incomingReq);

                      return (
                        <div
                          key={key || String(u.id)}
                          className={`relative transition-all duration-300 ease-out ${
                            isExpanded
                              ? 'glass-contact-expanded my-2 mx-2 z-10'
                              : 'border-b border-site-border hover:bg-site-raised/25'
                          } ${!isExpanded && selectedContact?.id === u.id ? 'bg-site-raised/45 border-l-4 border-l-blue-500' : ''}`}
                        >
                          {/* Комментарий на русском: основная строка контакта */}
                          <button
                            type="button"
                            onClick={() => void handleContactTap(u)}
                            className={`w-full text-left ${isExpanded ? 'p-4' : chatRowPad} transition-all`}
                          >
                            <div className="flex items-center gap-3">
                              <AvatarFx
                                sizeClass={isExpanded ? 'w-14 h-14' : chatAvatarSize}
                                className="shrink-0 transition-all duration-300"
                                avatarUrl={((p as any)?.avatar_display_url ?? p?.avatar_url ?? u.avatar_url) || null}
                                alt={u.display_name || u.username || ''}
                                fallback={(u.display_name?.[0] || u.username?.[0] || '?').toUpperCase()}
                                isOnline={isOnline}
                                fxColor={u.avatar_fx_color}
                                fxThickness={u.avatar_fx_thickness}
                                fxGlow={u.avatar_fx_glow}
                                fxOfflineColor={u.avatar_fx_offline_color}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`font-semibold text-site-text truncate ${isExpanded ? 'text-base' : ''}`}>
                                    {u.display_name || u.username}
                                  </span>
                                  {/* Комментарий на русском: онлайн/офлайн статус текстом */}
                                  {isOnline ? (
                                    <span className="text-[10px] text-site-success font-medium whitespace-nowrap">●&nbsp;онлайн</span>
                                  ) : lastSeen ? (
                                    <span className="text-[10px] text-site-muted whitespace-nowrap">{formatLastSeen(lastSeen)}</span>
                                  ) : null}
                                </div>
                                <div className="text-xs text-site-muted truncate">@{(u as any).nickname || u.username}</div>
                                {gamePresenceText ? (
                                  <div className="mt-0.5 truncate text-[11px] font-semibold text-site-warning">{gamePresenceText}</div>
                                ) : null}
                                {/* Комментарий на русском: дополнительная строка с информацией (роли, счётчики) */}
                                {showMeta && (
                                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-site-muted">
                                    <span className="px-1.5 py-0.5 bg-site-raised/40 rounded text-site-secondary">{typeName}</span>
                                    <span title="Проектов"><UiIcon name="Folder" /> {projectsCnt}</span>
                                    <span title="Новостей"><UiIcon name="Article" /> {newsCnt}</span>
                                  </div>
                                )}
                              </div>
                              {!isExpanded && right ? <div className="shrink-0">{right}</div> : null}
                              {/* Комментарий на русском: стрелочка для индикации расширения */}
                              <svg
                                viewBox="0 0 24 24"
                                className={`w-5 h-5 text-site-muted transition-transform duration-300 sm:hidden ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M6 9l6 6 6-6" />
                              </svg>
                            </div>
                          </button>

                          {/* Комментарий на русском: раскрытый контент карточки (только мобилка) */}
                          <div
                            className={`overflow-hidden transition-all duration-300 ease-out ${
                              isExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
                            }`}
                          >
                            <div className="px-4 pb-4 pt-2 space-y-3">
                              {/* Комментарий на русском: кнопки действий */}
                              <div className="flex flex-wrap gap-2">
                                {incomingReq ? (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void handleFriendRequest(incomingReq.id, 'accept');
                                        setFriends((prev) => (prev.some((f) => f.id === u.id) ? prev : [u, ...prev]));
                                        setExpandedContactId(null);
                                      }}
                                      className="flex-1 px-4 py-2.5 bg-site-success hover:bg-site-success text-site-on-accent rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                                    >
                                      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M5 12l5 5L20 7" />
                                      </svg>
                                      Принять
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void handleFriendRequest(incomingReq.id, 'decline');
                                        setExpandedContactId(null);
                                      }}
                                      className="flex-1 px-4 py-2.5 bg-site-raised/60 hover:bg-site-raised/60 text-site-text rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                                    >
                                      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                      Отклонить
                                    </button>
                                  </>
                                ) : isFriend ? (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setTab('chats');
                                        setExpandedContactId(null);
                                        selectUser(u);
                                      }}
                                      className="flex-1 px-4 py-2.5 bg-site-accent hover:bg-site-accent text-site-on-accent rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                                    >
                                      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15a4 4 0 0 1-4 4H9l-4 3v-3a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
                                      </svg>
                                      Написать
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void removeFriend(u.id);
                                        setExpandedContactId(null);
                                      }}
                                      className="px-4 py-2.5 text-site-danger hover:text-site-danger hover:bg-site-danger/15 rounded-xl border border-site-danger/25 text-sm font-semibold"
                                    >
                                      Удалить
                                    </button>
                                  </>
                                ) : isMe ? (
                                  <span className="px-4 py-2.5 bg-site-raised/40 text-site-text rounded-xl text-sm font-semibold">
                                    Это вы
                                  </span>
                                ) : isOutgoingPending ? (
                                  <span className="px-4 py-2.5 bg-site-warning/20 text-site-warning rounded-xl text-sm font-semibold">
                                    Заявка отправлена
                                  </span>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void sendFriendRequest(u.id);
                                    }}
                                    className="flex-1 px-4 py-2.5 bg-site-success hover:bg-site-success text-site-on-accent rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                                  >
                                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M12 5v14M5 12h14" />
                                    </svg>
                                    Добавить в друзья
                                  </button>
                                )}
                              </div>

                              {/* Комментарий на русском: профильная информация */}
                              {expandedContactLoading && isExpanded ? (
                                <div className="text-sm text-site-muted text-center py-2">Загрузка...</div>
                              ) : p ? (
                                <div className="space-y-2 text-sm">
                                  {p.position && (
                                    <div className="flex items-center gap-2 text-site-secondary">
                                      <span className="text-site-muted">Должность:</span>
                                      <span className="truncate">{p.position}</span>
                                    </div>
                                  )}
                                  {p.organization && (
                                    <div className="flex items-center gap-2 text-site-secondary">
                                      <span className="text-site-muted">Организация:</span>
                                      <span className="truncate">{p.organization}</span>
                                    </div>
                                  )}
                                  {p.email && (
                                    <div className="flex items-center gap-2 text-site-secondary">
                                      <span className="text-site-muted">Email:</span>
                                      <a href={`mailto:${p.email}`} className="text-site-accent hover:underline truncate">{p.email}</a>
                                    </div>
                                  )}
                                </div>
                              ) : null}

                              {/* Комментарий на русском: кнопка полного профиля */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.location.href = `/users/${u.id}`;
                                }}
                                className="w-full px-4 py-2.5 bg-site-surface/60 hover:bg-site-raised/60 text-site-text rounded-xl text-sm font-medium flex items-center justify-center gap-2 border border-site-border"
                              >
                                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                  <circle cx="9" cy="7" r="4" />
                                  <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                Открыть полный профиль
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    };

                    if (!q && reqs.length === 0 && frs.length === 0) {
                      return <div className="p-4 text-site-muted">Контактов пока нет</div>;
                    }

                    return (
                      <>
                        {userSearching && q.length >= 2 && <div className="p-4 text-site-muted">Поиск...</div>}

                        {reqs.map((r) =>
                          renderRow(
                            r.from_user,
                            <span className="px-2 py-1 bg-site-danger/15 border border-site-danger/25 text-site-danger rounded-full text-[10px] font-semibold">
                              Заявка
                            </span>,
                            `req_${r.id}`,
                            r
                          )
                        )}

                        {frs.map((f) => renderRow(f, undefined, `fr_${f.id}`, null))}

                        {q.length >= 2 &&
                          extra.map((u) => {
                            const status = String(u.friendship_status || '');
                            const isFriend =
                              status === 'outgoing_accepted' || status === 'incoming_accepted' || friends.some((f) => f.id === u.id);
                            const isPending = status === 'outgoing_pending';
                            const isIncoming = status === 'incoming_pending';

                            return renderRow(
                              u,
                              isFriend ? (
                                <span className="px-2 py-1 bg-site-accent/15 border border-site-accent/25 text-site-accent rounded-full text-[10px] font-semibold">Друг</span>
                              ) : isPending ? (
                                <span className="px-2 py-1 bg-site-warning/15 border border-site-warning/25 text-site-warning rounded-full text-[10px] font-semibold">Отправлено</span>
                              ) : isIncoming ? (
                                <span className="px-2 py-1 bg-site-accent/15 border border-site-accent/25 text-site-accent rounded-full text-[10px] font-semibold">Входящая</span>
                              ) : (
                                <span className="px-2 py-1 bg-site-raised/40 border border-site-border-control/30 text-site-text rounded-full text-[10px] font-semibold">Пользователь</span>
                              ),
                              `sr_${u.id}`,
                              null
                            );
                          })}

                        {q && !userSearching && q.length >= 2 && reqs.length === 0 && frs.length === 0 && extra.length === 0 && (
                          <div className="p-4 text-site-muted">Ничего не найдено</div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Комментарий на русском: нижняя нав-панель — высота 1px, кнопки выходят за границы панели */}
            {(() => {
              const active = supportOpen ? 'chats' : tab === 'employees' ? 'friends' : tab;
              const btn =
                'flex-1 h-full flex items-center justify-center transition active:scale-[0.85] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/30 rounded-xl';
              const cls = (isActive: boolean) =>
                `${btn} ${isActive ? 'text-site-text glass-tinted' : 'text-site-secondary hover:text-site-text hover:bg-site-text/8'}`;
              const icon = (name: 'lab' | 'contacts' | 'chats' | 'settings') => (
                <svg viewBox="0 0 24 24" className="w-[1.625rem] h-[1.625rem]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {name === 'lab' && (
                    <>
                      {/* Колба/flask — символ лаборатории */}
                      <path d="M9 3h6v5l4 9a2 2 0 0 1-1.8 2.9H6.8A2 2 0 0 1 5 17l4-9V3z" />
                      <path d="M9 3h6" strokeWidth="2.5" />
                      <path d="M7 14.5h10" strokeDasharray="2 2" />
                    </>
                  )}
                  {name === 'contacts' && (
                    <>
                      <path d="M16 11a4 4 0 1 0-8 0" />
                      <path d="M4 21a8 8 0 0 1 16 0" />
                    </>
                  )}
                  {name === 'chats' && (
                    <>
                      <path d="M21 15a4 4 0 0 1-4 4H9l-4 3v-3H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
                    </>
                  )}
                  {name === 'settings' && (
                    <>
                      <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
                      <path d="M19.4 15a7.8 7.8 0 0 0 .1-1 7.8 7.8 0 0 0-.1-1l2-1.6-2-3.4-2.4 1a7.6 7.6 0 0 0-1.7-1L15 3h-6l-.3 2.4a7.6 7.6 0 0 0-1.7 1l-2.4-1-2 3.4 2 1.6a7.8 7.8 0 0 0-.1 1 7.8 7.8 0 0 0 .1 1l-2 1.6 2 3.4 2.4-1a7.6 7.6 0 0 0 1.7 1L9 21h6l.3-2.4a7.6 7.6 0 0 0 1.7-1l2.4 1 2-3.4z" />
                    </>
                  )}
                </svg>
              );

              // Комментарий на русском: вычисляем цвет подложки на основе цвета свечения профиля пользователя (прозрачность 90% = 10% opacity).
              const navBarBgColor = (() => {
                const userColor = selectedUser?.avatar_fx_color || '#22c55e';
                const hex = /^#[0-9a-fA-F]{6}$/.test(userColor) ? userColor : '#22c55e';
                const r = parseInt(hex.slice(1, 3), 16);
                const g = parseInt(hex.slice(3, 5), 16);
                const b = parseInt(hex.slice(5, 7), 16);
                return `rgba(${r}, ${g}, ${b}, 0.1)`;
              })();

              return (
                <div className="relative z-20 h-px border-t border-transparent bg-transparent overflow-visible">
                  {/* Комментарий на русском: сама панель = 1px */}
                  <div className="mx-auto w-[78%] max-w-[calc(100%-4rem)] glass-divider" aria-hidden="true" />
                  {/* Комментарий на русском: кнопки выходят за границы панели, не увеличивая её высоту */}
                  {/* Комментарий на русском: поднимаем панель с 4 кнопками на 30px на всех платформах. Добавлена подложка в стиле жидкого стекла с цветом профиля пользователя. */}
                  <div 
                    className="absolute left-1/2 -translate-x-1/2 bottom-[20px] z-20 w-[78%] max-w-[calc(100%-4rem)] h-[3.43rem] sm:h-[3.74rem] flex items-center justify-between gap-2 px-2 overflow-visible rounded-2xl backdrop-blur-xl border border-site-border"
                    style={{
                      background: `linear-gradient(145deg, ${navBarBgColor}, ${navBarBgColor.replace('0.1', '0.05')})`,
                      boxShadow: `0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 1px 0 0 rgba(255, 255, 255, 0.1)`,
                    }}
                  >
                    {/* Комментарий на русском: кнопка возврата в лабораторию — на 20% шире, иконка колбы + на десктопе при hover расширяется влево с текстом LABORATORY (зелёный стиль). */}
                    <Link
                      href="/"
                      className={`group/lab flex-[2.4] h-full flex items-center justify-center gap-1 sm:gap-0 overflow-hidden transition-all duration-300 ease-out active:scale-[0.92] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-success/60 rounded-xl text-site-secondary hover:text-site-text backdrop-blur-xl border border-transparent`}
                      title="Вернуться в лабораторию ЛИСАД"
                      aria-label="Вернуться в лабораторию ЛИСАД"
                      style={{
                        background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.5) 0%, rgba(15, 23, 42, 0.4) 100%)',
                      }}
                      onMouseEnter={(e) => {
                        if (window.matchMedia('(min-width: 640px)').matches) {
                          e.currentTarget.style.background = 'linear-gradient(145deg, rgba(16, 185, 129, 0.55) 0%, rgba(5, 150, 105, 0.45) 50%, rgba(4, 120, 87, 0.4) 100%)';
                          e.currentTarget.style.borderColor = 'rgba(52, 211, 153, 0.5)';
                          e.currentTarget.style.boxShadow = '0 8px 28px rgba(16, 185, 129, 0.35), 0 0 18px rgba(52, 211, 153, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(145deg, rgba(30, 41, 59, 0.5) 0%, rgba(15, 23, 42, 0.4) 100%)';
                        e.currentTarget.style.borderColor = 'transparent';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {/* Комментарий на русском: текст LABORATORY появляется слева от иконки при hover на десктопе. */}
                      <span
                        className="hidden sm:flex flex-col items-end leading-[0.9] max-w-0 opacity-0 overflow-hidden whitespace-nowrap sm:group-hover/lab:max-w-[4.5rem] sm:group-hover/lab:opacity-100 sm:group-hover/lab:mr-1.5 transition-all duration-300 ease-out"
                        style={{
                          fontFamily: 'GlitchGoblin, system-ui, sans-serif',
                          fontSize: '11px',
                          color: '#fff',
                          textShadow: '0 2px 8px rgba(52, 211, 153, 0.7), 0 0 14px rgba(16, 185, 129, 0.4)',
                          letterSpacing: '0.08em',
                        }}
                      >
                        <span>LABO</span>
                        <span>RATORY</span>
                      </span>
                      {/* Комментарий на русском: на мобилке текст виден всегда. */}
                      <span
                        className="flex sm:hidden flex-col items-start leading-[0.95] text-[0.52rem] font-bold tracking-wide uppercase"
                        style={{ fontFamily: 'GlitchGoblin, sans-serif' }}
                      >
                        <span>LABO</span>
                        <span>RATORY</span>
                      </span>
                      <svg
                        viewBox="0 0 24 24"
                        className="w-[1.625rem] h-[1.625rem] flex-shrink-0 transition-all duration-300 sm:group-hover/lab:drop-shadow-[0_0_6px_rgba(52,211,153,0.6)]"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        {/* Колба/flask — символ лаборатории */}
                        <path d="M9 3h6v5l4 9a2 2 0 0 1-1.8 2.9H6.8A2 2 0 0 1 5 17l4-9V3z" />
                        <path d="M9 3h6" strokeWidth="2.5" />
                        <path d="M7 14.5h10" strokeDasharray="2 2" />
                      </svg>
                    </Link>
                    <button
                      type="button"
                      className={cls(active === 'friends')}
                      title="Контакты"
                      aria-label="Контакты"
                      onClick={() => {
                        setSupportOpen(false);
                        setSupportTargetUser(null);
                        setSupportMessages([]);
                        supportLastIdRef.current = 0;
                        setSelectedUser(null);
                        setSelectedRoom(null);
                        setSelectedContact(null);
                        chatMessagesStore.setState({ chats: {} });
                        lastMessageIdRef.current = 0;
                        if (isEmployee) {
                          setTab('employees');
                          void loadEmployees();
                        } else {
                          setTab('friends');
                          void loadFriends();
                          void loadFriendRequests();
                        }
                      }}
                    >
                      {icon('contacts')}
                    </button>
                    <button
                      type="button"
                      className={cls(active === 'chats')}
                      title="Чаты"
                      aria-label="Чаты"
                      onClick={() => {
                        setTab('chats');
                        setSupportOpen(false);
                        setSupportTargetUser(null);
                        setSupportMessages([]);
                        supportLastIdRef.current = 0;
                        setSelectedUser(null);
                        setSelectedRoom(null);
                        setSelectedContact(null);
                        chatMessagesStore.setState({ chats: {} });
                        lastMessageIdRef.current = 0;
                        loadConversationsThrottled();
                        loadRoomsThrottled();
                      }}
                    >
                      {icon('chats')}
                    </button>
                    <button
                      type="button"
                      className={cls(active === 'settings')}
                      title="Настройки"
                      aria-label="Настройки"
                      onClick={() => {
                        setSupportOpen(false);
                        setSupportTargetUser(null);
                        setSupportMessages([]);
                        supportLastIdRef.current = 0;
                        setSelectedUser(null);
                        setSelectedRoom(null);
                        setSelectedContact(null);
                        chatMessagesStore.setState({ chats: {} });
                        lastMessageIdRef.current = 0;
                        setTab('settings');
                      }}
                    >
                      {icon('settings')}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Чат. На мобилке открывается как отдельный экран (Telegram-режим) */}
          {/* Комментарий на русском: поднимаем правую панель чата на 10px везде (через нижний внутренний отступ). */}
          <div
            // Комментарий на русском: isolate+z-0 создаёт новый stacking context,
            // чтобы фон (wallpaper) с отрицательным z-index не "проваливался" под стеклянные слои родителя.
            className={`${mobileChatOpen ? 'flex' : 'hidden sm:flex'} relative z-0 isolate flex-1 flex-col min-h-0 overflow-hidden pb-[10px]`}
            onTouchStart={handleEdgeSwipeStart}
            onTouchEnd={handleEdgeSwipeEnd}
          >
            {/* Комментарий на русском: правая панель ошибок ("чат с Сайтом") */}
            {errorsChatOpen ? (
              <>
                {/* Комментарий на русском: на мобилках шапка невидима, оставляем только десктоп. */}
                <div className="hidden sm:flex items-center justify-between gap-3 glass-header">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-site-warning to-site-danger flex items-center justify-center text-site-on-accent font-bold shrink-0">
                      <UiIcon name="Bug" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-site-warning">Сайт</div>
                      <div className="text-xs text-site-muted">Логи ошибок фронтенда</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setShowErrorsChat(false);
                        setSupportOpen(false);
                      }}
                      className="px-3 py-2 text-xs text-site-text hover:text-site-text hover:bg-site-raised/30 rounded-lg border border-site-border-control/30"
                      title="Закрыть"
                    >
                      Закрыть
                    </button>
                  </div>
                </div>

                {/* Фильтры и поиск */}
                <div className="px-3 py-2 border-b border-site-border-control/30 flex flex-wrap gap-2 items-center bg-site-bg/30">
                  <select
                    value={errorsCategory}
                    onChange={(e) => {
                      setErrorsCategory(e.target.value);
                      void loadFrontendErrors(e.target.value);
                    }}
                    className="px-2 py-1.5 glass-input rounded-lg text-xs text-site-text min-w-[120px]"
                  >
                    <option value="all">Все</option>
                    <option value="voice">Голосовые</option>
                    <option value="upload">Вложения</option>
                    <option value="network">Сеть</option>
                    <option value="api">API</option>
                    <option value="auth">Авторизация</option>
                    <option value="ui">UI</option>
                    <option value="unknown">Прочее</option>
                  </select>
                  <input
                    type="text"
                    value={errorsSearch}
                    onChange={(e) => setErrorsSearch(e.target.value)}
                    placeholder="Поиск..."
                    className="flex-1 min-w-[100px] px-2 py-1.5 glass-input rounded-lg text-xs text-site-text placeholder-site-muted"
                  />
                  <button
                    onClick={() => void loadFrontendErrors()}
                    className="px-2 py-1.5 text-xs text-site-secondary hover:text-site-text hover:bg-site-raised/40 rounded-lg border border-site-border-control/30"
                    title="Обновить"
                  >
                    <UiIcon name="ArrowsClockwise" />
                  </button>
                </div>

                {/* Список ошибок как сообщения */}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-4 space-y-2" data-scroll-lock-allow>
                  {errorsLoading ? (
                    <div className="text-site-muted text-center py-8">Загрузка...</div>
                  ) : frontendErrors.length === 0 ? (
                    <div className="text-site-muted text-center py-8">Нет ошибок <UiIcon name="Confetti" /></div>
                  ) : (
                    (() => {
                      const searchLower = errorsSearch.toLowerCase().trim();
                      const filtered = searchLower
                        ? frontendErrors.filter(
                            (e) =>
                              e.message.toLowerCase().includes(searchLower) ||
                              (e.details || '').toLowerCase().includes(searchLower) ||
                              (e.username || '').toLowerCase().includes(searchLower) ||
                              (e.url || '').toLowerCase().includes(searchLower)
                          )
                        : frontendErrors;

                      if (filtered.length === 0) {
                        return <div className="text-site-muted text-center py-8">Ничего не найдено</div>;
                      }

                      return filtered.map((err) => (
                        <div
                          key={err.id}
                          className="bg-site-surface/60 rounded-xl p-3 border border-site-border hover:border-site-border-control/50 transition-colors group select-text"
                        >
                          <div className="flex items-start gap-2">
                            <span className="shrink-0 text-lg mt-0.5">
                              {err.category === 'voice' && <><UiIcon name="Microphone" /></>}
                              {err.category === 'upload' && <><UiIcon name="Paperclip" /></>}
                              {err.category === 'network' && <><UiIcon name="Globe" /></>}
                              {err.category === 'api' && <><UiIcon name="Broadcast" /></>}
                              {err.category === 'auth' && <><UiIcon name="LockKey" /></>}
                              {err.category === 'ui' && <><UiIcon name="Desktop" /></>}
                              {err.category === 'unknown' && <><UiIcon name="Question" /></>}
                            </span>
                            <div className="flex-1 min-w-0">
                              {/* Сообщение об ошибке — можно выделить и скопировать */}
                              <div
                                className="text-sm text-site-text font-medium whitespace-pre-wrap break-words select-text cursor-text"
                                onDoubleClick={() => {
                                  const text = err.message;
                                  try {
                                    const p = navigator.clipboard?.writeText?.(text);
                                    if (p && typeof (p as any).catch === 'function') (p as any).catch(() => {});
                                  } catch {}
                                }}
                                title="Двойной клик — скопировать"
                              >
                                {err.message}
                              </div>

                              {/* Детали */}
                              {err.details && (
                                <div
                                  className="mt-2 text-xs text-site-secondary whitespace-pre-wrap break-words font-mono bg-site-bg/70 rounded-lg p-2 max-h-32 overflow-auto select-text cursor-text border border-site-border"
                                  onDoubleClick={() => {
                                    try {
                                      const p = navigator.clipboard?.writeText?.(err.details || '');
                                      if (p && typeof (p as any).catch === 'function') (p as any).catch(() => {});
                                    } catch {}
                                  }}
                                  title="Двойной клик — скопировать"
                                >
                                  {err.details}
                                </div>
                              )}

                              {/* Мета-информация */}
                              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-site-muted">
                                <span className="text-site-muted">{formatDialogTime(err.created_at)}</span>
                                {err.username && (
                                  <span className="text-site-accent/80">@{err.username}</span>
                                )}
                                {err.ip && <span>IP: {err.ip}</span>}
                              </div>

                              {/* URL страницы */}
                              {err.url && (
                                <div
                                  className="mt-1 text-[10px] text-site-muted truncate select-text cursor-text"
                                  title={err.url}
                                  onDoubleClick={() => {
                                    try {
                                      const p = navigator.clipboard?.writeText?.(err.url || '');
                                      if (p && typeof (p as any).catch === 'function') (p as any).catch(() => {});
                                    } catch {}
                                  }}
                                >
                                  <UiIcon name="MapPin" /> {err.url}
                                </div>
                              )}

                              {/* Кнопка копирования всего */}
                              <button
                                onClick={() => {
                                  const full = [
                                    `[${err.category}] ${err.message}`,
                                    err.details ? `Details: ${err.details}` : '',
                                    `Time: ${err.created_at}`,
                                    err.username ? `User: @${err.username}` : '',
                                    err.ip ? `IP: ${err.ip}` : '',
                                    err.url ? `URL: ${err.url}` : '',
                                    err.stack ? `Stack: ${err.stack}` : '',
                                  ]
                                    .filter(Boolean)
                                    .join('\n');
                                  try {
                                    const p = navigator.clipboard?.writeText?.(full);
                                    if (p && typeof (p as any).catch === 'function') (p as any).catch(() => {});
                                  } catch {}
                                }}
                                className="mt-2 px-2 py-1 text-[10px] text-site-muted hover:text-site-text bg-site-raised/30 hover:bg-site-raised/60 rounded border border-site-border-control/30"
                              >
                                <UiIcon name="ClipboardText" /> Копировать всё
                              </button>
                            </div>
                          </div>
                        </div>
                      ));
                    })()
                  )}
                </div>

                {/* Внизу: пустой блок вместо ввода (писать нельзя) */}
                <div className="p-3 sm:p-4 border-t border-site-border-control/40 bg-site-bg/35">
                  <div className="text-center text-xs text-site-muted">
                    Это автоматические логи ошибок. Писать сюда нельзя.
                  </div>
                </div>
              </>
            ) : supportChatOpen ? (
              <>
                {/* Комментарий на русском: на мобилках шапка невидима, оставляем только десктоп. */}
                <div className="hidden sm:flex items-center justify-between gap-3 glass-header">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-site-success flex items-center justify-center text-site-on-accent font-bold shrink-0">
                      <UiIcon name="Lifebuoy" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-site-text truncate">
                        {isSuperAdmin ? `Поддержка: ${supportTargetUser?.display_name || supportTargetUser?.username || ''}` : 'Поддержка'}
                      </div>
                      <div className="text-xs text-site-muted truncate">
                        {isSuperAdmin ? `@${supportTargetUser?.username || ''}` : 'Чат с суперадмином сайта'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSupportOpen(false);
                        setSupportTargetUser(null);
                        setSupportMessages([]);
                        supportLastIdRef.current = 0;
                      }}
                      className="px-3 py-2 text-xs text-site-text hover:text-site-text hover:bg-site-raised/30 rounded-lg border border-site-border-control/30"
                      title="Закрыть поддержку"
                    >
                      Закрыть
                    </button>
                  </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-scroll overscroll-contain p-3 sm:p-4 space-y-1" data-scroll-lock-allow>
                  {supportMessages.length === 0 ? (
                    <div className="text-site-muted">Напишите в поддержку — ответит суперадмин.</div>
                  ) : (
                    supportMessages.map((msg, idx) => {
                      const meId = meRef.current?.id || 0;
                      const isOwn = isSuperAdmin ? msg.from_user?.id === 0 : msg.from_user?.id === meId;
                      const prev = supportMessages[idx - 1];
                      const isGrouped =
                        !!prev &&
                        prev.from_user?.id === msg.from_user?.id &&
                        new Date(msg.created_at).getTime() - new Date(prev.created_at).getTime() < 60000;

                      return (
                        <MessageBubble
                          key={msg.id}
                          message={msg as any}
                          isOwn={isOwn}
                          showAvatar={!isOwn}
                          isGrouped={isGrouped}
                          onOpenPhotoViewer={openChatPhotoViewer}
                          linkify={true}
                          enableLinkPreview={linkPreviewsEnabled}
                        />
                      );
                    })
                  )}
                  <div ref={supportMessagesEndRef} />
                </div>

                <div className="p-3 sm:p-4 border-t border-site-border-control/40 bg-site-bg/35">
                  {error && <div className="mb-3 px-3 py-2 bg-site-danger/15 border border-site-danger/25 rounded-lg text-site-danger text-sm">{error}</div>}

                  <PendingPhotosBar disabled={sending} />
                  {supportDraftFirstUrl &&
                    supportDraftFirstUrl !== supportDraftPreviewSuppressedUrl &&
                    (supportDraftLinkPreviewLoading || supportDraftLinkPreview) && (
                      <DraftLinkPreviewCard
                        url={supportDraftFirstUrl}
                        loading={supportDraftLinkPreviewLoading}
                        preview={supportDraftLinkPreview}
                        onClose={() => setSupportDraftPreviewSuppressedUrl(supportDraftFirstUrl)}
                      />
                    )}

                  <div className="flex gap-2 items-end">
                    <button
                      type="button"
                      onClick={openPhotoPicker}
                      disabled={sending}
                      className={chatAttachBtnClass}
                      title="Прикрепить фото"
                      aria-label="Прикрепить фото"
                    >
                      {paperclipIcon}
                    </button>
                    <textarea
                      ref={supportDraftRef}
                      value={supportDraft}
                      onChange={(e) => setSupportDraft(e.target.value)}
                      onFocus={(e) => {
                        try {
                          iosSingleInputCleanupRef.current?.();
                        } catch {}
                        iosSingleInputCleanupRef.current = enterIosSingleInputMode(e.currentTarget as any);
                      }}
                      onBlur={() => {
                        try {
                          iosSingleInputCleanupRef.current?.();
                        } catch {}
                        iosSingleInputCleanupRef.current = null;
                      }}
                      title="Enter — отправить · Shift+Enter — новая строка"
                      onKeyDown={(e) => {
                        if (e.key === 'Control') {
                          supportCtrlUsedRef.current = false;
                          return;
                        }
                        if (supportCtrlUsedRef.current === false && e.ctrlKey && e.key !== 'Control') supportCtrlUsedRef.current = true;
                        if (e.key !== 'Enter') return;
                        // Комментарий на русском: Shift+Enter (десктоп) и Ctrl/Alt/Meta+Enter — перенос строки (не надеемся на дефолт браузера).
                        const allowNewLine = e.shiftKey || e.ctrlKey || e.altKey || e.metaKey;
                        if (allowNewLine) {
                          e.preventDefault();
                          const el = e.currentTarget;
                          insertNewline(el, supportDraft, setSupportDraft);
                          return;
                        }
                        if (!supportDraft.trim() && !hasPendingPhoto) {
                          e.preventDefault();
                          return;
                        }
                        e.preventDefault();
                        if (hasPendingPhoto) return void sendSupportPhotoMessage();
                        void sendSupportMessage();
                      }}
                      onKeyUp={(e) => {
                        if (e.key !== 'Control') return;
                        const el = e.currentTarget;
                        const used = supportCtrlUsedRef.current;
                        supportCtrlUsedRef.current = false;
                        if (used) return;
                        insertNewline(el, supportDraft, setSupportDraft);
                      }}
                      placeholder="Сообщение в поддержку..."
                      className="flex-1 px-3 py-2 bg-site-surface/60 border border-site-border rounded-lg text-base sm:text-sm text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/50 resize-none overflow-y-auto scrollbar-hide select-text"
                      rows={1}
                      maxLength={MAX_MESSAGE_LENGTH}
                      disabled={sending}
                    />
                    <button
                      onClick={() => {
                        if (hasPendingPhoto) return void sendSupportPhotoMessage();
                        void sendSupportMessage();
                      }}
                      disabled={(!supportDraft.trim() && !hasPendingPhoto) || sending}
                      className="w-11 h-11 rounded-full bg-site-success hover:bg-site-success disabled:bg-site-raised disabled:text-site-muted text-site-on-accent flex items-center justify-center transition-colors"
                      title="Отправить"
                    >
                      ➤
                    </button>
                  </div>
                </div>
              </>
            ) : tab === 'friends' ? (
              selectedContact ? (
                <>
                  {(() => {
                    const p = selectedContactProfile;
                    const incomingReq = friendRequests.find((r) => r.from_user.id === selectedContact.id) || null;
                    const isFriend = friends.some((f) => f.id === selectedContact.id);
                    const status = String(p?.friendship_status || (selectedContact as any)?.friendship_status || '');
                    const isOutgoingPending = status === 'outgoing_pending';
                    const isMe = selectedContact.id === (meRef.current?.id || 0);
                    const pv =
                      typeof p?.profile_visibility === 'number' ? p.profile_visibility : p?.is_profile_public === false ? 0 : 1;
                    const selectedContactGamePresenceText = formatGamePresenceText(
                      ((p as any)?.game_presence ?? selectedContact.game_presence) as User['game_presence']
                    );

                    const infoCard = (label: string, value: any, href?: string) => {
                      const v = String(value ?? '').trim();
                      if (!v) return null;
                      return (
                        <div className="p-4 rounded-xl border border-site-border bg-site-bg/30">
                          <div className="text-xs text-site-muted">{label}</div>
                          {href ? (
                            <a
                              href={href}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 block text-site-text break-all hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {v}
                            </a>
                          ) : (
                            <div className="mt-1 text-site-text break-all">{v}</div>
                          )}
                        </div>
                      );
                    };

                    return (
                      <>
                        <div className="p-3 sm:p-4 border-b border-site-border-control/40 flex items-center justify-between gap-3 bg-site-bg/35">
                          <button
                            type="button"
                            className="flex items-center gap-3 min-w-0 text-left"
                            onClick={() => (window.location.href = `/users/${selectedContact.id}`)}
                            title="Открыть профиль"
                          >
                            <AvatarFx
                              sizeClass="w-9 h-9"
                              className="shrink-0"
                              avatarUrl={((p as any)?.avatar_display_url ?? p?.avatar_url ?? selectedContact.avatar_url) || null}
                              alt={selectedContact.display_name || selectedContact.username || ''}
                              fallback={(selectedContact.display_name?.[0] || selectedContact.username?.[0] || '?').toUpperCase()}
                              isOnline={!!p?.is_online}
                              fxColor={selectedContact.avatar_fx_color}
                              fxThickness={selectedContact.avatar_fx_thickness}
                              fxGlow={selectedContact.avatar_fx_glow}
                              fxOfflineColor={selectedContact.avatar_fx_offline_color}
                            />
                            <div className="min-w-0">
                              <div className="font-semibold text-site-text truncate">{selectedContact.display_name || selectedContact.username}</div>
                              <div className="text-xs text-site-muted truncate">@{(selectedContact as any).nickname || selectedContact.username}</div>
                              {selectedContactGamePresenceText ? (
                                <div className="truncate text-[11px] font-semibold text-site-warning">{selectedContactGamePresenceText}</div>
                              ) : null}
                            </div>
                          </button>

                          <div className="flex items-center gap-2">
                            {incomingReq ? (
                              <>
                                <button
                                  onClick={() => {
                                    void handleFriendRequest(incomingReq.id, 'accept');
                                    // Комментарий на русском: делаем оптимистичное добавление, чтобы сразу показать профиль как "друга".
                                    setFriends((prev) => (prev.some((f) => f.id === selectedContact.id) ? prev : [selectedContact, ...prev]));
                                  }}
                                  className="px-3 py-2 bg-site-success hover:bg-site-success text-site-on-accent rounded-lg text-xs font-semibold"
                                  title="Принять заявку"
                                >
                                  ✓ Принять
                                </button>
                                <button
                                  onClick={() => {
                                    void handleFriendRequest(incomingReq.id, 'decline');
                                    setSelectedContact(null);
                                  }}
                                  className="px-3 py-2 bg-site-raised hover:bg-site-raised text-site-text rounded-lg text-xs font-semibold"
                                  title="Отклонить заявку"
                                >
                                  ✕ Отклонить
                                </button>
                              </>
                            ) : isFriend ? (
                              <>
                                <button
                                  onClick={() => {
                                    setTab('chats');
                                    setSelectedContact(null);
                                    selectUser(selectedContact);
                                  }}
                                  className="px-3 py-2 bg-site-accent hover:bg-site-accent text-site-on-accent rounded-lg text-xs font-semibold"
                                  title="Открыть чат"
                                >
                                  <UiIcon name="ChatCircleDots" /> Написать
                                </button>
                                <button
                                  onClick={() => {
                                    void removeFriend(selectedContact.id);
                                    setSelectedContact(null);
                                  }}
                                  className="px-3 py-2 text-site-danger hover:text-site-danger hover:bg-site-danger/10 rounded-lg border border-site-danger/20 text-xs font-semibold"
                                  title="Удалить из друзей"
                                >
                                  ✕ Удалить
                                </button>
                              </>
                            ) : isMe ? (
                              <span className="px-3 py-2 bg-site-raised/40 border border-site-border-control/30 text-site-text rounded-lg text-xs font-semibold">
                                Это вы
                              </span>
                            ) : isOutgoingPending ? (
                              <span className="px-3 py-2 bg-site-warning/20 text-site-warning rounded-lg text-xs font-semibold">Заявка отправлена</span>
                            ) : (
                              <button
                                onClick={() => {
                                  void sendFriendRequest(selectedContact.id);
                                  setSelectedContact((prev) =>
                                    prev && prev.id === selectedContact.id ? ({ ...prev, friendship_status: 'outgoing_pending' } as any) : prev
                                  );
                                }}
                                className="px-3 py-2 bg-site-success hover:bg-site-success text-site-on-accent rounded-lg text-xs font-semibold"
                                title="Отправить заявку в друзья"
                              >
                                + Добавить
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-4" data-scroll-lock-allow>
                          {/* Комментарий на русском: справа показываем профиль (вся правая колонка), без переписки. */}
                          {selectedContactProfileLoading ? (
                            <div className="text-site-muted">Загрузка профиля...</div>
                          ) : p ? (
                            <div className="space-y-3">
                              <div className="rounded-2xl border border-site-border bg-site-bg/25 p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <div className="text-xs text-site-muted">Профиль</div>
                                  <div className="text-[11px] text-site-muted">
                                    {pv === 0 ? 'Скрыт' : pv === 2 ? 'Расширенный' : 'Открытый'}
                                  </div>
                                </div>
                                {pv === 0 ? (
                                  <div className="mt-3 text-site-secondary text-sm">
                                    Пользователь скрыл профиль. Доступна только базовая информация.
                                  </div>
                                ) : (
                                  <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                                    {infoCard('Должность', p.position)}
                                    {infoCard('Организация', p.organization)}
                                    {infoCard('Время работы', p.work_time)}
                                    {infoCard('Email', p.email, p.email ? `mailto:${p.email}` : undefined)}
                                    {infoCard('Сайт', p.website, p.website ? (String(p.website).startsWith('http') ? String(p.website) : `https://${String(p.website)}`) : undefined)}
                                    {infoCard(
                                      'Telegram',
                                      p.telegram_username ? `@${String(p.telegram_username).replace(/^@/, '')}` : '',
                                      p.telegram_username ? `https://t.me/${String(p.telegram_username).replace(/^@/, '')}` : undefined
                                    )}
                                    {infoCard(
                                      'GitHub',
                                      p.github_username ? `@${String(p.github_username).replace(/^@/, '')}` : '',
                                      p.github_username ? `https://github.com/${String(p.github_username).replace(/^@/, '')}` : undefined
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-site-muted">Профиль недоступен</div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </>
              ) : (
                <div className="hidden sm:flex flex-1 items-center justify-center text-site-muted">Выберите контакт слева</div>
              )
            ) : tab === 'settings' ? (
              <MessengerSettingsPane
                activeSectionId={settingsSection}
                sections={MESSENGER_SETTINGS_SECTIONS}
                chatSettingsLoaded={chatSettingsLoaded}
                settingsToast={settingsToast}
                onSectionChange={setSettingsSection}
              >
                {settingsSectionContent}
              </MessengerSettingsPane>
            ) : selectedUser ? (
              (() => {
                // Комментарий на русском: открываем профиль внутри чата (как в Telegram), а не переходим на страницу.
                const openPeerProfile = () => setPeerProfileOpen(true);
                // Комментарий на русском: единый текст статуса для шапки (одинаково на мобилке и на десктопе).
                const peerStatusText = (() => {
                  if (!isOnline) return pendingCount > 0 ? `Нет сети (${pendingCount} в очереди)` : 'Нет сети';
                  if (peerTyping) return 'печатает...';
                  if (peerOnline) return 'онлайн';
                  if (peerLastSeen) return `был(а) ${formatDialogTime(peerLastSeen)}`;
                  return 'офлайн';
                })();
                // Комментарий на русском: иконка устройства собеседника (последнее активное устройство в мессенджере).
                const peerDeviceIcon = peerDeviceType === 'mobile' ? '📱' : peerDeviceType === 'tablet' ? '📟' : peerDeviceType === 'desktop' ? '🖥️' : '';
                const peerDeviceTitle =
                  peerDeviceType === 'mobile' ? 'Мобильный' : peerDeviceType === 'tablet' ? 'Планшет' : peerDeviceType === 'desktop' ? 'Десктоп' : '';
                const peerGamePresenceText = formatGamePresenceText(selectedUser.game_presence);
                // Комментарий на русском: содержимое шапки переписки (без дублирования разметки).
                const peerHeaderInner = (
                  <div className="flex items-center gap-2">
                    {/* Комментарий на русском: на десктопе аватар чуть крупнее (+≈35%) и немного сдвинут влево. */}
                    {/* Комментарий на русском: размер "таблетки" не зависит от фото — фото может выходить за границы (overflow) только на sm+. */}
                    <div className="w-10 h-10 min-w-[2.5rem] min-h-[2.5rem] shrink-0 flex items-center justify-center overflow-visible">
                      <AvatarFx
                        sizeClass="w-10 h-10 sm:w-[3.375rem] sm:h-[3.375rem]"
                        className="shrink-0 sm:-translate-x-2"
                        avatarUrl={selectedUser.avatar_url || null}
                        alt={selectedUser.display_name || selectedUser.username || ''}
                        fallback={(selectedUser.display_name?.[0] || selectedUser.username?.[0] || '?').toUpperCase()}
                        isOnline={peerOnline}
                        fxColor={selectedUser.avatar_fx_color}
                        fxThickness={selectedUser.avatar_fx_thickness}
                        fxGlow={selectedUser.avatar_fx_glow}
                        fxOfflineColor={selectedUser.avatar_fx_offline_color}
                      />
                    </div>
                    <div className="min-w-0 flex flex-col text-left">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-semibold text-site-text truncate">
                          {selectedUser.display_name || selectedUser.username || 'Пользователь'}
                        </span>
                        {peerDeviceIcon ? (
                          <span className="shrink-0 text-xs text-site-text/80" title={peerDeviceTitle} aria-label={peerDeviceTitle}>
                            <UiIcon name={peerDeviceIcon} />
                          </span>
                        ) : null}
                      </div>
                      <span className="text-[11px] text-site-text truncate">
                        @{(selectedUser as any).nickname || selectedUser.username}
                        {' · '}
                        {peerStatusText}
                      </span>
                      {peerGamePresenceText ? (
                        <span className="text-[11px] font-semibold text-site-warning truncate">{peerGamePresenceText}</span>
                      ) : null}
                    </div>
                  </div>
                );
                // Комментарий на русском: иконки-кнопки в стиле Liquid Glass (без текста).
                const peerHeaderIconBtnClass =
                  'w-10 h-10 rounded-full glass-btn border border-site-border shadow-lg flex items-center justify-center text-lg transition active:scale-[0.9] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/40';

                // Комментарий на русском: цвет капельки зависит от состояния: расширена (обычный bg) или свёрнута (цвет свечения пользователя).
                const pillBgColor = (() => {
                  if (inChatSearchOpen) return 'rgb(15 23 42 / 0.9)'; // bg-site-bg/90
                  // Комментарий на русском: в обычном режиме используем цвет свечения собеседника с прозрачностью.
                  const userColor = selectedUser.avatar_fx_color || '#22c55e';
                  const hex = /^#[0-9a-fA-F]{6}$/.test(userColor) ? userColor : '#22c55e';
                  const r = parseInt(hex.slice(1, 3), 16);
                  const g = parseInt(hex.slice(3, 5), 16);
                  const b = parseInt(hex.slice(5, 7), 16);
                  return `rgba(${r}, ${g}, ${b}, 0.15)`;
                })();

                return (
                  <>
                    {dmWallpaperSrc && (
                      <div className="absolute inset-0 -z-10 pointer-events-none">
                        <div
                          className="absolute inset-0 bg-center bg-cover"
                          style={{
                            backgroundImage: `url(${dmWallpaperSrc})`,
                            filter: `blur(${Number(dmWallpaper?.blur || 0) || 0}px) brightness(${Number(dmWallpaper?.brightness || 100) || 100}%)`,
                            transform: Number(dmWallpaper?.blur || 0) ? 'scale(1.06)' : 'none',
                          }}
                        />
                      </div>
                    )}

                    {/* Комментарий на русском: капля пользователя по центру — на hover раскрывает вниз строку поиска (без отдельной кнопки "Поиск" и без кнопки очистки). */}
                    <div ref={inChatSearchRootRef} className="absolute left-1/2 -translate-x-1/2 top-2 z-30 group flex flex-col items-center">
                      {/* Комментарий на русском: фиксируем радиус, чтобы при раскрытии вниз скругления не менялись (растут только боковые линии). */}
                      <div 
                        className="inline-block max-w-[92vw] sm:max-w-none rounded-[28px] glass-btn messenger-peer-pill--desktop border border-site-border shadow-lg backdrop-blur transition-colors duration-200"
                        style={{ backgroundColor: pillBgColor }}
                      >
                    <button
                      type="button"
                          onClick={() => {
                            // Комментарий на русском: на тач-устройствах 1-й тап открывает поиск, 2-й тап открывает профиль.
                            if (isMobileLikeViewport()) {
                              if (!inChatSearchOpen) {
                                setInChatSearchOpen(true);
                                // Комментарий на русском: если вместо поиска показываем запрос на фон — фокус не нужен.
                                if (!dmWallpaperRequest) requestAnimationFrame(() => inChatSearchInputRef.current?.focus());
                                return;
                              }
                              setInChatSearchOpen(false);
                            }
                            openPeerProfile();
                          }}
                          className="w-full px-3.5 py-[0.45rem] text-left active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/50 touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' } as any}
                      title="Открыть профиль"
                      aria-label="Открыть профиль"
                    >
                      {peerHeaderInner}
                    </button>

                        <div
                          className={`transition-all duration-200 overflow-hidden ${
                            inChatSearchOpen ? (dmWallpaperRequest ? 'max-h-[132px] opacity-100 pointer-events-auto' : 'max-h-[96px] opacity-100 pointer-events-auto') : 'max-h-0 opacity-0 pointer-events-none'
                          } ${dmWallpaperRequest ? 'group-hover:max-h-[132px] group-focus-within:max-h-[132px]' : 'group-hover:max-h-[96px] group-focus-within:max-h-[96px]'} group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto`}
                        >
                          <div className="px-3.5 pb-2 pt-1 border-t border-site-border">
                            {dmWallpaperRequest ? (
                              <>
                                <div className="flex items-center justify-between gap-2">
                                  <div className="min-w-0">
                                    <div className="text-[11px] text-site-text truncate">
                                      Новый фон ·{' '}
                                      {String(
                                        (dmWallpaperRequest as any)?.from_user?.display_name ||
                                          (dmWallpaperRequest as any)?.from_user?.username ||
                                          'пользователь'
                                      )}
                                    </div>
                                    <div className="text-[10px] text-site-muted truncate">
                                      Принять или отклонить предложение фона
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <button
                                      type="button"
                                      disabled={dmWallpaperBusy}
                                      onClick={() => void respondDmWallpaperRequest(true)}
                                      className="px-2.5 py-1.5 rounded-lg bg-site-success/25 border border-site-success/25 text-site-success text-[11px] font-semibold hover:bg-site-success/35 transition disabled:opacity-60"
                                      title="Принять новый фон"
                                    >
                                      Принять
                                    </button>
                                    <button
                                      type="button"
                                      disabled={dmWallpaperBusy}
                                      onClick={() => void respondDmWallpaperRequest(false)}
                                      className="px-2.5 py-1.5 rounded-lg bg-site-danger/15 border border-site-danger/20 text-site-danger text-[11px] font-semibold hover:bg-site-danger/25 transition disabled:opacity-60"
                                      title="Отклонить"
                                    >
                                      Отклонить
                                    </button>
                                  </div>
                                </div>
                                <div className="mt-2 text-[10px] text-site-secondary/80">
                                  Запрос: поставить фон · размытие {Number((dmWallpaperRequest as any)?.wallpaper?.blur || 0) || 0}px · яркость{' '}
                                  {Number((dmWallpaperRequest as any)?.wallpaper?.brightness || 100) || 100}%
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-2">
                                  <input
                                    ref={inChatSearchInputRef}
                                    value={inChatSearchQuery}
                                    onChange={(e) => {
                                      setInChatSearchQuery(e.target.value);
                                      setInChatSearchActiveIdx(0);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        e.preventDefault();
                                        setInChatSearchQuery('');
                                        setInChatSearchActiveIdx(0);
                                        setInChatSearchOpen(false);
                                        (e.currentTarget as HTMLInputElement).blur();
                                        return;
                                      }
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        goInChatSearchNext();
                                      }
                                    }}
                                    placeholder="Поиск в чате…"
                                    className="flex-1 h-9 px-3 rounded-xl bg-site-bg/40 border border-site-border text-site-text text-sm placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/40 select-text"
                                    inputMode="search"
                                    autoComplete="off"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setInChatSearchOpen(false);
                                      openDmWallpaperModal();
                                    }}
                                    className="shrink-0 w-9 h-9 flex items-center justify-center rounded-xl glass-btn border border-site-border text-site-text/90 hover:text-site-text transition active:scale-[0.98]"
                                    title="Настроить фон чата"
                                    aria-label="Настроить фон чата"
                                  >
                                    <MonoWallpaperIcon className="w-[18px] h-[18px]" />
                                  </button>
                                </div>
                                <div className="mt-1 text-[10px] text-site-text/80">
                                  {inChatSearch.q
                                    ? inChatSearch.items.length
                                      ? `Найдено: ${inChatSearch.items.length} · совпадений: ${inChatSearch.total}`
                                      : 'Ничего не найдено'
                                    : 'Поиск по сообщениям этого чата'}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Комментарий на русском: на мобильных список тоже показываем (иначе приходится прятать панель ввода). */}
                        {!dmWallpaperRequest && inChatSearch.q && (
                        <div
                          className={`mt-2 transition-all duration-200 ${
                            inChatSearchOpen ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none -translate-y-1'
                          } group-hover:opacity-100 group-hover:pointer-events-auto group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0`}
                        >
                          <div className="mx-auto rounded-2xl glass-strong border border-site-border shadow-[0_20px_70px_rgba(0,0,0,0.55)] overflow-hidden w-[min(92vw,320px)] bg-site-bg/90 backdrop-blur">
                            <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-site-border">
                              <div className="text-xs text-site-text">
                                {inChatSearch.items.length ? `${Math.min(inChatSearchActiveIdx + 1, inChatSearch.items.length)}/${inChatSearch.items.length}` : '0/0'}
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  disabled={!inChatSearch.items.length}
                                  onClick={goInChatSearchPrev}
                                  className="w-8 h-8 rounded-xl glass-btn text-site-text/80 hover:text-site-text disabled:opacity-40 flex items-center justify-center transition active:scale-[0.92]"
                                  title="Предыдущее"
                                  aria-label="Предыдущее совпадение"
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  disabled={!inChatSearch.items.length}
                                  onClick={goInChatSearchNext}
                                  className="w-8 h-8 rounded-xl glass-btn text-site-text/80 hover:text-site-text disabled:opacity-40 flex items-center justify-center transition active:scale-[0.92]"
                                  title="Следующее"
                                  aria-label="Следующее совпадение"
                                >
                                  ↓
                                </button>
                              </div>
                            </div>
                            <div ref={inChatSearchListRef} className="max-h-[132px] overflow-y-auto">
                              {inChatSearch.items.length === 0 ? (
                                <div className="px-3 py-3 text-xs text-site-secondary/80">Нет совпадений</div>
                              ) : (
                                inChatSearch.items.map((it, idx) => (
                                  <button
                                    key={it.id}
                                    type="button"
                                    data-inchat-find-id={it.id}
                                    onClick={() => goToInChatSearchResult(idx)}
                                    className={`w-full px-3 py-2 text-left hover:bg-site-text/6 transition-colors flex items-start justify-between gap-3 ${
                                      idx === inChatSearchActiveIdx ? 'bg-site-text/6' : ''
                                    }`}
                                    title="Перейти к сообщению"
                                  >
                                    <div className="min-w-0">
                                      <div className="text-xs text-site-text/90 line-clamp-2">{it.snippet}</div>
                                    </div>
                                    <div className="shrink-0 text-[11px] text-site-text/80">×{it.count}</div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Комментарий на русском: панель профиля пользователя (раскрывается из капельки). */}
                    {peerProfileOpen && (
                      <div
                        className="absolute inset-0 z-50 flex flex-col overflow-hidden"
                        style={{ animation: peerProfileDragY ? 'none' : 'peerProfileOpen 0.3s ease-out forwards' }}
                      >
                        {/* Комментарий на русском: blur-overlay поверх чата (95% прозрачность). */}
                        <div
                          className="absolute inset-0 bg-site-bg/95 backdrop-blur-md transition-opacity"
                          style={{ opacity: peerProfileDragY ? Math.max(0, 1 - peerProfileDragY / 150) : 1 }}
                          onClick={() => { setPeerProfileOpen(false); setPeerProfileMenu(null); setPeerProfilePullY(0); }}
                        />
                        {/* Комментарий на русском: сама панель профиля с поддержкой свайпа вверх для закрытия. */}
                        <div
                          className="relative z-10 flex flex-col h-full max-h-full overflow-hidden touch-pan-y"
                          onTouchStart={(e) => {
                            // Комментарий на русском: pull-down/up только на мобильных.
                            if (window.matchMedia && window.matchMedia('(min-width: 640px)').matches) return;
                            const t = e.touches?.[0];
                            if (!t) return;
                            const target = e.target as HTMLElement | null;
                            // Комментарий на русском: НЕ обрабатываем жесты внутри вкладок (медиа/голосовые/файлы) и внутри зоны свайпа закрытия.
                            if (target?.closest?.('[data-peer-profile-scroll-area]')) return;
                            if (target?.closest?.('[data-peer-profile-swipe-handle]')) return;
                            peerProfilePullRef.current = { startY: t.clientY, startPullY: peerProfilePullY };
                          }}
                          onTouchMove={(e) => {
                            if (window.matchMedia && window.matchMedia('(min-width: 640px)').matches) return;
                            const st = peerProfilePullRef.current;
                            if (!st) return;
                            const t = e.touches?.[0];
                            if (!t) return;
                            const delta = t.clientY - st.startY; // + вниз, - вверх
                            const next = Math.max(0, Math.min(260, st.startPullY + delta));
                            setPeerProfilePullY(next);
                            if (Math.abs(delta) > 6) e.preventDefault();
                          }}
                          onTouchEnd={() => {
                            if (window.matchMedia && window.matchMedia('(min-width: 640px)').matches) return;
                            if (!peerProfilePullRef.current) return;
                            peerProfilePullRef.current = null;
                            const y = peerProfilePullY;
                            // Комментарий на русском: pull-up тоже поддерживаем (если почти схлопнули — закрываем).
                            if (y < 70) setPeerProfilePullY(0);
                            else setPeerProfilePullY(240);
                          }}
                          style={{
                            animation: peerProfileDragY ? 'none' : 'peerProfileSlide 0.3s ease-out forwards',
                            transform: peerProfileDragY ? `translateY(${-peerProfileDragY}px)` : undefined,
                            opacity: peerProfileDragY ? Math.max(0.3, 1 - peerProfileDragY / 200) : 1,
                            transition: peerProfileDragY ? 'none' : 'transform 0.3s ease-out, opacity 0.3s ease-out',
                          }}
                        >
                          {/* Шапка профиля (аватар + имя на месте капельки). */}
                          <div
                            className="px-4 pt-3 pb-2 relative"
                            style={{
                              // Комментарий на русском: в увеличенном режиме “прижимаем” аватар к верху панели (≈3px).
                              paddingTop: peerProfilePullY > 50 ? 3 : undefined,
                            }}
                          >
                            {/* Комментарий на русском: десктоп — аватар сразу большой по центру + клик открывает viewer. */}
                            <div className="hidden sm:block">
                              <div className="flex items-center justify-between gap-3">
                                <button
                                  type="button"
                                  onClick={() => { setPeerProfileOpen(false); setPeerProfilePullY(0); }}
                                  className="w-10 h-10 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9] focus:outline-none"
                                  title="Закрыть"
                                  aria-label="Закрыть профиль"
                                >
                                  ←
                                </button>
                                <a
                                  href={`/users/${selectedUser.id}`}
                                  className="w-10 h-10 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9]"
                                  title="Открыть полный профиль"
                                  aria-label="Перейти на страницу профиля"
                                >
                                  <UiIcon name="ArrowUpRight" />
                                </a>
                              </div>
                              <div className="pt-4 pb-2 flex flex-col items-center justify-center">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const url = selectedUser.avatar_url;
                                    if (url) openPhotoViewer(url);
                                  }}
                                  className="focus:outline-none active:scale-[0.97] transition-transform"
                                  title="Открыть фото"
                                >
                                  <AvatarFx
                                    sizeClass="w-32 h-32"
                                    avatarUrl={selectedUser.avatar_url || null}
                                    alt={selectedUser.display_name || selectedUser.username || ''}
                                    fallback={(selectedUser.display_name?.[0] || selectedUser.username?.[0] || '?').toUpperCase()}
                                    isOnline={peerOnline}
                                    fxColor={selectedUser.avatar_fx_color}
                                    fxThickness={selectedUser.avatar_fx_thickness}
                                    fxGlow={selectedUser.avatar_fx_glow}
                                    fxOfflineColor={selectedUser.avatar_fx_offline_color}
                                  />
                                </button>
                                <div className="mt-3 text-center">
                                  <div className="text-base font-semibold text-site-text">
                                    {selectedUser.display_name || selectedUser.username || 'Пользователь'}
                                  </div>
                                  <div className="text-xs text-site-secondary">
                                    @{(selectedUser as any).nickname || selectedUser.username} · {peerStatusText}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Комментарий на русском: обычная компактная шапка (скрываем при pullY > 100). */}
                            <div
                              className="sm:hidden flex items-center gap-3 transition-opacity duration-200"
                              style={{ opacity: peerProfilePullY > 100 ? 0 : 1, pointerEvents: peerProfilePullY > 100 ? 'none' : 'auto' }}
                            >
                              <button
                                type="button"
                                onClick={() => { setPeerProfileOpen(false); setPeerProfilePullY(0); }}
                                className="w-10 h-10 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9] focus:outline-none"
                                title="Закрыть"
                              >
                                ←
                              </button>
                              <div className="flex-1 min-w-0 flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const url = selectedUser.avatar_url;
                                    if (url) openPhotoViewer(url);
                                  }}
                                  className="shrink-0 focus:outline-none"
                                >
                                  <AvatarFx
                                    sizeClass="w-12 h-12"
                                    avatarUrl={selectedUser.avatar_url || null}
                                    alt={selectedUser.display_name || selectedUser.username || ''}
                                    fallback={(selectedUser.display_name?.[0] || selectedUser.username?.[0] || '?').toUpperCase()}
                                    isOnline={peerOnline}
                                    fxColor={selectedUser.avatar_fx_color}
                                    fxThickness={selectedUser.avatar_fx_thickness}
                                    fxGlow={selectedUser.avatar_fx_glow}
                                    fxOfflineColor={selectedUser.avatar_fx_offline_color}
                                  />
                                </button>
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-site-text truncate">
                                    {selectedUser.display_name || selectedUser.username || 'Пользователь'}
                                  </div>
                                  <div className="text-xs text-site-secondary truncate">
                                    @{(selectedUser as any).nickname || selectedUser.username} · {peerStatusText}
                                  </div>
                                </div>
                              </div>
                              <a
                                href={`/users/${selectedUser.id}`}
                                className="w-10 h-10 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9]"
                                title="Открыть полный профиль"
                              >
                                <UiIcon name="ArrowUpRight" />
                              </a>
                            </div>

                            {/* Комментарий на русском: увеличенный аватар (центрированный, показываем при pullY > 50). */}
                            {peerProfilePullY > 50 && (
                              <div
                                className="sm:hidden flex flex-col items-center transition-opacity duration-200"
                                style={{
                                  // Комментарий на русском: без пустого пространства сверху, плавно проявляем.
                                  opacity: Math.min((peerProfilePullY - 50) / 90, 1),
                                  marginTop: -10, // Комментарий на русском: поднимаем фото ещё на 10px.
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    const url = selectedUser.avatar_url;
                                    if (url) openPhotoViewer(url);
                                  }}
                                  className="focus:outline-none active:scale-[0.96] transition-transform"
                                >
                                  <div
                                    style={{
                                      // Комментарий на русском: делаем масштаб заметно больше + плавнее + дольше.
                                      transform: `scale(${1 + Math.min(1, Math.max(0, (peerProfilePullY - 50) / 210)) * 0.9})`,
                                      transition: 'transform 520ms cubic-bezier(0.2, 0.9, 0.2, 1)',
                                    }}
                                  >
                                    <AvatarFx
                                      sizeClass="w-24 h-24"
                                      avatarUrl={selectedUser.avatar_url || null}
                                      alt={selectedUser.display_name || selectedUser.username || ''}
                                      fallback={(selectedUser.display_name?.[0] || selectedUser.username?.[0] || '?').toUpperCase()}
                                      isOnline={peerOnline}
                                      fxColor={selectedUser.avatar_fx_color}
                                      fxThickness={selectedUser.avatar_fx_thickness}
                                      fxGlow={selectedUser.avatar_fx_glow}
                                      fxOfflineColor={selectedUser.avatar_fx_offline_color}
                                    />
                                  </div>
                                </button>
                                {/* Комментарий на русском: небольшой отступ после надписи, дальше сразу панель кнопок. */}
                                <div className="mt-2 text-center">
                                  <div className="text-base font-semibold text-site-text">
                                    {selectedUser.display_name || selectedUser.username || 'Пользователь'}
                                  </div>
                                  <div className="text-xs text-site-secondary">
                                    @{(selectedUser as any).nickname || selectedUser.username} · {peerStatusText}
                                  </div>
                                </div>
                                {/* Комментарий на русском: кнопки закрыть и перейти в профиль (справа вверху). */}
                                <div className="absolute top-0 right-4 flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setPeerProfilePullY(0)}
                                    className="w-10 h-10 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9]"
                                    title="Свернуть"
                                  >
                                    ↑
                                  </button>
                                  <a
                                    href={`/users/${selectedUser.id}`}
                                    className="w-10 h-10 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9]"
                                    title="Открыть полный профиль"
                                  >
                                    <UiIcon name="ArrowUpRight" />
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Комментарий на русском: иконки действий над вкладками (монохромные, увеличенные). */}
                          <div
                            className={`mx-4 mb-5 flex items-center justify-center gap-4 relative ${peerProfilePullY > 50 ? 'mt-2' : 'mt-5'}`}
                          >
                            {/* Уведомления */}
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => setPeerProfileMenu(peerProfileMenu === 'notifications' ? null : 'notifications')}
                                className={`w-14 h-14 rounded-full glass-btn flex items-center justify-center transition active:scale-[0.9] ${peerMuted ? 'text-site-danger' : 'text-site-text/90 hover:text-site-text'}`}
                                title="Уведомления"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
                                  {peerMuted ? (
                                    <><path d="M13.73 21a2 2 0 01-3.46 0"/><path d="M18.63 13A17.89 17.89 0 0118 8"/><path d="M6.26 6.26A5.86 5.86 0 006 8c0 7-3 9-3 9h14"/><path d="M18 8a6 6 0 00-9.33-5"/><line x1="1" y1="1" x2="23" y2="23"/></>
                                  ) : (
                                    <><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></>
                                  )}
                                </svg>
                              </button>
                              {peerProfileMenu === 'notifications' && (
                                <div className="absolute top-full mt-2 left-0 sm:left-1/2 sm:-translate-x-1/2 z-20 w-56 max-w-[calc(100vw-2rem)] rounded-xl glass-strong border border-site-border shadow-xl overflow-hidden">
                                  <div className="px-3 py-2 text-xs text-site-muted border-b border-site-border">Уведомления</div>
                                  <div className="py-1">
                                    <button
                                      type="button"
                                      onClick={() => { setPeerMuted(!peerMuted); setPeerProfileMenu(null); }}
                                      className="w-full px-3 py-2 text-sm text-left hover:bg-site-text/6 flex items-center justify-between"
                                    >
                                      <span className={peerMuted ? 'text-site-success' : 'text-site-danger'}>{peerMuted ? 'Включить уведомления' : 'Отключить уведомления'}</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setPeerMuteContent(!peerMuteContent)}
                                      className="w-full px-3 py-2 text-sm text-left hover:bg-site-text/6 flex items-center justify-between"
                                    >
                                      <span className="text-site-text/90">Скрыть содержимое</span>
                                      <span className={`w-4 h-4 rounded border ${peerMuteContent ? 'bg-site-accent border-site-accent' : 'border-site-border-control'} flex items-center justify-center text-[10px]`}>{peerMuteContent ? '✓' : ''}</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setPeerMuteSound(!peerMuteSound)}
                                      className="w-full px-3 py-2 text-sm text-left hover:bg-site-text/6 flex items-center justify-between"
                                    >
                                      <span className="text-site-text/90">Отключить звук</span>
                                      <span className={`w-4 h-4 rounded border ${peerMuteSound ? 'bg-site-accent border-site-accent' : 'border-site-border-control'} flex items-center justify-center text-[10px]`}>{peerMuteSound ? '✓' : ''}</span>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            {/* Корзина */}
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => setPeerProfileMenu(peerProfileMenu === 'trash' ? null : 'trash')}
                                className="w-14 h-14 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9]"
                                title="Очистить чат"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
                                  <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
                                </svg>
                              </button>
                              {peerProfileMenu === 'trash' && (
                                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 z-20 w-48 max-w-[calc(100vw-2rem)] rounded-xl glass-strong border border-site-border shadow-xl overflow-hidden">
                                  <div className="px-3 py-2 text-xs text-site-muted border-b border-site-border">Очистка</div>
                                  <div className="py-1">
                                    <button
                                      type="button"
                                      onClick={() => { setPeerProfileMenu(null); setPeerProfileOpen(false); clearChat(); }}
                                      className="w-full px-3 py-2 text-sm text-left text-site-danger hover:bg-site-text/6"
                                    >
                                      Очистить чат
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            {/* Секретный чат */}
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => setPeerProfileMenu(peerProfileMenu === 'secret' ? null : 'secret')}
                                className="w-14 h-14 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9]"
                                title="Секретный чат"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
                                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                                </svg>
                              </button>
                              {peerProfileMenu === 'secret' && (
                                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 z-20 w-48 max-w-[calc(100vw-2rem)] rounded-xl glass-strong border border-site-border shadow-xl overflow-hidden">
                                  <div className="px-3 py-2 text-xs text-site-muted border-b border-site-border">Секретный чат</div>
                                  <div className="py-1">
                                    <button
                                      type="button"
                                      onClick={() => setPeerProfileMenu(null)}
                                      className="w-full px-3 py-2 text-sm text-left text-site-text/90 hover:bg-site-text/6"
                                    >
                                      Начать секретный чат
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            {/* Заблокировать */}
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => setPeerProfileMenu(peerProfileMenu === 'block' ? null : 'block')}
                                className={`w-14 h-14 rounded-full glass-btn flex items-center justify-center transition active:scale-[0.9] ${selectedIsBlocked ? 'text-site-success' : 'text-site-text/90 hover:text-site-text'}`}
                                title={selectedIsBlocked ? 'Разблокировать' : 'Заблокировать'}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
                                  {selectedIsBlocked ? (
                                    <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 019.9-1"/></>
                                  ) : (
                                    <><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></>
                                  )}
                                </svg>
                              </button>
                              {peerProfileMenu === 'block' && (
                                <div className="absolute top-full mt-2 right-0 sm:right-auto sm:left-1/2 sm:-translate-x-1/2 z-20 w-48 max-w-[calc(100vw-2rem)] rounded-xl glass-strong border border-site-border shadow-xl overflow-hidden">
                                  <div className="px-3 py-2 text-xs text-site-muted border-b border-site-border">Блокировка</div>
                                  <div className="py-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPeerProfileMenu(null);
                                        setPeerProfileOpen(false);
                                        if (selectedIsBlocked) {
                                          if (confirm('Разблокировать пользователя?')) void unblockUser(selectedUser.id);
                                        } else {
                                          if (confirm('Заблокировать пользователя?')) void blockUser(selectedUser.id);
                                        }
                                      }}
                                      className={`w-full px-3 py-2 text-sm text-left hover:bg-site-text/6 ${selectedIsBlocked ? 'text-site-success' : 'text-site-danger'}`}
                                    >
                                      {selectedIsBlocked ? 'Разблокировать' : 'Заблокировать'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            {/* Отправить контакт */}
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => setPeerProfileMenu(peerProfileMenu === 'share' ? null : 'share')}
                                className="w-14 h-14 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9]"
                                title="Отправить контакт"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
                                  <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>
                                </svg>
                              </button>
                              {peerProfileMenu === 'share' && (
                                <div className="absolute top-full mt-2 right-0 z-20 w-48 max-w-[calc(100vw-2rem)] rounded-xl glass-strong border border-site-border shadow-xl overflow-hidden">
                                  <div className="px-3 py-2 text-xs text-site-muted border-b border-site-border">Поделиться</div>
                                  <div className="py-1">
                                    <button
                                      type="button"
                                      onClick={() => setPeerProfileMenu(null)}
                                      className="w-full px-3 py-2 text-sm text-left text-site-text/90 hover:bg-site-text/6"
                                    >
                                      Отправить контакт
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Вкладки (как в группах, но Участники → Голосовые) */}
                          <div className="mx-4 mb-3">
                            <div className="-mx-1 px-1 overflow-x-auto pb-1 scrollbar-hide" data-scroll-lock-allow>
                              <div className="flex w-max mx-auto rounded-xl overflow-hidden border border-site-border bg-site-bg/25 backdrop-blur-sm divide-x divide-site-border">
                                {([
                                  { id: 'voice', label: 'Голосовые' },
                                  { id: 'media', label: 'Медиа' },
                                  { id: 'files', label: 'Файлы' },
                                  { id: 'design', label: 'Дизайн' },
                                ] as const).map((t) => {
                                  const active = peerProfileTab === t.id;
                                  return (
                                    <button
                                      key={t.id}
                                      type="button"
                                      onClick={() => setPeerProfileTab(t.id)}
                                      className={`px-3 py-2 text-xs leading-none flex items-center gap-2 whitespace-nowrap transition-colors ${
                                        active ? 'bg-site-accent/18 text-site-text' : 'text-site-text/80 hover:bg-site-text/6 hover:text-site-text'
                                      }`}
                                    >
                                      <span className="font-semibold">{t.label}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>

                          {/* Контент вкладок */}
                          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pb-3" data-scroll-lock-allow data-peer-profile-scroll-area>
                            {peerProfileTab === 'voice' && (() => {
                              // Комментарий на русском: фильтруем голосовые сообщения из текущего чата.
                              const myId = meRef.current?.id || 0;
                              const peerNameFull = selectedUser.display_name || selectedUser.username || 'Собеседник';
                              const peerName = String(peerNameFull).trim().split(/\s+/)[0] || 'Собеседник';
                              const chatKey = selectedRoom?.id ? `room:${selectedRoom.id}` : selectedUser?.id ? `dm:${selectedUser.id}` : '';
                              const snap = chatKey ? getChatMessagesSnapshot(chatKey) : [];
                              const voiceMessages = snap.filter((m) => {
                                const vu = typeof (m as any)?.voice_url === 'string' ? String((m as any).voice_url) : '';
                                const msgText = String(m?.message || '');
                                return (
                                  String((m as any)?.type || '') === 'voice_message' ||
                                  !!vu ||
                                  msgText.startsWith('__voice__:') ||
                                  vu.includes('/chat_voice/') ||
                                  msgText.includes('/chat_voice/')
                                );
                              });
                              // Комментарий на русском: группировка как в чате (скрываем повторные аватары/отступы).
                              const isGrouped = (idx: number) => {
                                const prev = voiceMessages[idx - 1];
                                const cur = voiceMessages[idx];
                                if (!prev || !cur) return false;
                                return (prev.from_user?.id || 0) === (cur.from_user?.id || 0);
                              };
                              // Комментарий на русском: компактный плеер как в чате (с волнами).
                              return (
                                <div className="rounded-2xl border border-site-border bg-site-bg/25 overflow-hidden">
                                  <div className="px-4 py-3 border-b border-site-border">
                                    <div className="text-sm font-semibold text-site-text">Голосовые сообщения</div>
                                    <div className="text-xs text-site-muted">{voiceMessages.length ? `Найдено: ${voiceMessages.length}` : 'Нет голосовых сообщений'}</div>
                                  </div>
                                  {voiceMessages.length === 0 ? (
                                    <div className="px-4 py-6 text-center text-sm text-site-muted">
                                      Голосовые сообщения из этого чата появятся здесь
                                    </div>
                                  ) : (
                                    <div className="divide-y divide-site-border max-h-[52vh] overflow-y-auto" data-scroll-lock-allow>
                                      {voiceMessages.map((vm, idx) => {
                                        const isMe = vm.from_user?.id === myId;
                                        const senderName = isMe ? 'Вы' : peerName;
                                        const grouped = isGrouped(idx);
                                        return (
                                          <div key={vm.id} className="px-2 py-1.5">
                                            {/* Комментарий на русском: подпись отправителя — строго по ТЗ. */}
                                            {!grouped && <div className="px-1 pb-0.5 text-[10px] text-site-muted">{senderName}</div>}
                                            <MessageBubble
                                              message={vm as any}
                                              isOwn={isMe}
                                              showAvatar={false}
                                              peerOnline={peerOnline}
                                              isGrouped={grouped}
                                              linkify={false}
                                              enableLinkPreview={false}
                                              prefetchVoice={idx < 8}
                                              prefetchRank={idx}
                                            />
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {peerProfileTab === 'media' && (() => {
                              // Комментарий на русском: используем кэш индекса медиа (считается 1 раз при входе во вкладку).
                              const peerNameFull = selectedRoom ? (selectedRoom.title || 'Группа') : (selectedUser?.display_name || selectedUser?.username || 'Собеседник');
                              const peerName = String(peerNameFull).trim().split(/\s+/)[0] || 'Собеседник';
                              const allMedia = peerMediaIndex;
                              // Комментарий на русском: фильтрация по владельцу и типу.
                              const filtered = allMedia.filter((item) => {
                                if (mediaOwnerFilter === 'me' && !item.isMe) return false;
                                if (mediaOwnerFilter === 'peer' && item.isMe) return false;
                                if (mediaTypeFilter === 'photo' && item.kind !== 'photo') return false;
                                if (mediaTypeFilter === 'video' && item.kind !== 'video') return false;
                                return true;
                              });
                              const visible = filtered.slice(0, Math.max(0, mediaVisibleCount || 0));
                              const photoUrls = filtered.filter((x) => x.kind === 'photo').map((x) => x.url);
                              const ownerTabs = [
                                { id: 'all', label: 'Оба' },
                                { id: 'me', label: 'Вы' },
                                { id: 'peer', label: peerName },
                              ] as const;
                              const typeTabs = [
                                { id: 'all', label: 'Все' },
                                { id: 'photo', label: 'Фото' },
                                { id: 'video', label: 'Видео' },
                              ] as const;
                              return (
                                <div className="rounded-2xl border border-site-border bg-site-bg/25 overflow-hidden">
                                  {/* Подвкладки владельца */}
                                  <div className="px-3 py-2 border-b border-site-border flex items-center gap-1 overflow-x-auto scrollbar-hide">
                                    {ownerTabs.map((t) => (
                                      <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => setMediaOwnerFilter(t.id)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${mediaOwnerFilter === t.id ? 'bg-site-accent/20 text-site-accent' : 'text-site-text/70 hover:bg-site-text/6 hover:text-site-text'}`}
                                      >
                                        {t.label}
                                      </button>
                                    ))}
                                    <div className="w-px h-5 bg-site-raised/40 mx-1" />
                                    {typeTabs.map((t) => (
                                      <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => setMediaTypeFilter(t.id)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${mediaTypeFilter === t.id ? 'bg-site-success/20 text-site-success' : 'text-site-text/70 hover:bg-site-text/6 hover:text-site-text'}`}
                                      >
                                        {t.label}
                                      </button>
                                    ))}
                                  </div>
                                  {/* Плитки медиа */}
                                  {filtered.length === 0 ? (
                                    <div className="px-4 py-8 text-center text-sm text-site-muted">
                                      {allMedia.length === 0 ? 'Нет медиа в этом чате' : 'Нет медиа по выбранным фильтрам'}
                                    </div>
                                  ) : (
                                    <div
                                      className="p-2 grid grid-cols-3 sm:grid-cols-4 gap-1 max-h-[55vh] overflow-y-auto"
                                      data-scroll-lock-allow
                                      onScroll={(e) => {
                                        const el = e.currentTarget;
                                        if (el.scrollHeight - el.scrollTop - el.clientHeight < 320) {
                                          setMediaVisibleCount((c) => Math.min(filtered.length, (Number(c || 0) || 0) + 100));
                                        }
                                      }}
                                    >
                                      {visible.map((item) => {
                                        const src = item.kind === 'photo' ? String(mediaSrcByUrl[item.url] || '') : '';
                                        const loading = item.kind === 'photo' && !src;
                                        return (
                                          <button
                                            key={`${item.id}-${item.url}`}
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              if (item.kind === 'photo') openPhotoViewerList(photoUrls.length ? photoUrls : [item.url], item.url);
                                              else window.open(item.url, '_blank', 'noopener,noreferrer');
                                            }}
                                            className="relative aspect-square bg-site-surface/50 rounded-lg overflow-hidden group cursor-pointer"
                                            title={item.kind === 'video' ? 'Видео' : 'Фото'}
                                          >
                                            {item.kind === 'photo' ? (
                                              src ? (
                                                <img src={src} alt="Фото" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                                              ) : (
                                                <div className="w-full h-full bg-site-surface/25 animate-pulse flex items-center justify-center">
                                                  <button
                                                    type="button"
                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); void downloadPeerMediaPhoto(item.url); }}
                                                    className="w-10 h-10 rounded-full bg-black/35 border border-site-border text-site-text/90 flex items-center justify-center hover:bg-black/45 transition active:scale-[0.92]"
                                                    title="Загрузить"
                                                    aria-label="Загрузить фото"
                                                  >
                                                    <UiIcon name="ArrowDown" />
                                                  </button>
                                                </div>
                                              )
                                            ) : (
                                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-site-bg/70 to-site-surface/40">
                                                <span className="w-10 h-10 rounded-full bg-black/35 border border-site-border flex items-center justify-center">
                                                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-site-text/85">
                                                    <path d="M8 5v14l11-7z" />
                                                  </svg>
                                                </span>
                                              </div>
                                            )}
                                            <div className={`absolute top-1 right-1 w-2 h-2 rounded-full ${item.isMe ? 'bg-site-accent' : 'bg-site-success'}`} title={item.isMe ? 'Вы' : peerName} />
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                  <div className="px-3 py-2 border-t border-site-border text-[11px] text-site-muted">
                                    Показано: {visible.length} / {filtered.length} · всего в чате: {allMedia.length}
                                  </div>
                                </div>
                              );
                            })()}
                            {peerProfileTab === 'files' && (() => {
                              const myId = meRef.current?.id || 0;
                              const peerNameFull = selectedUser.display_name || selectedUser.username || 'Собеседник';
                              const peerName = String(peerNameFull).trim().split(/\s+/)[0] || 'Собеседник';
                              type FileItem = { id: number; url: string; name: string; mime: string; size: number; isMe: boolean; createdAt: string };
                              const allFiles: FileItem[] = [];
                              const chatKey = selectedRoom?.id ? `room:${selectedRoom.id}` : selectedUser?.id ? `dm:${selectedUser.id}` : '';
                              const snap = chatKey ? getChatMessagesSnapshot(chatKey) : [];
                              snap.forEach((m) => {
                                const isMe = m.from_user?.id === myId;
                                const atts = (m as any).attachments as Array<{ id: number; url: string; kind: string; mime?: string; size?: number; name?: string }> | undefined;
                                if (!atts?.length) return;
                                atts.forEach((a) => {
                                  const mime = String(a.mime || '').toLowerCase();
                                  const kind = String(a.kind || '').toLowerCase();
                                  const url = String(a.url || '');
                                  const isPhoto = kind === 'photo' || kind === 'image' || mime.startsWith('image/');
                                  const isVideo = kind === 'video' || mime.startsWith('video/');
                                  if (isPhoto || isVideo) return;
                                  const name = String(a.name || '').trim() || url.split('/').pop() || `Файл #${a.id}`;
                                  const size = Number(a.size || 0) || 0;
                                  allFiles.push({ id: a.id, url, name, mime, size, isMe, createdAt: m.created_at });
                                });
                              });
                              const filtered = allFiles
                                .filter((f) => {
                                  if (filesOwnerFilter === 'me' && !f.isMe) return false;
                                  if (filesOwnerFilter === 'peer' && f.isMe) return false;
                                  return true;
                                })
                                .sort((a, b) => (String(b.createdAt || '')).localeCompare(String(a.createdAt || '')));
                              const ownerTabs = [
                                { id: 'all', label: 'Все' },
                                { id: 'me', label: 'Вы' },
                                { id: 'peer', label: peerName },
                              ] as const;
                              const iconFor = (name: string, mime: string) => {
                                const n = name.toLowerCase();
                                if (mime.startsWith('application/pdf') || n.endsWith('.pdf')) return '📄';
                                if (n.endsWith('.doc') || n.endsWith('.docx')) return '📝';
                                if (n.endsWith('.xls') || n.endsWith('.xlsx') || n.endsWith('.csv')) return '📊';
                                if (n.endsWith('.zip') || n.endsWith('.rar') || n.endsWith('.7z')) return '🗜️';
                                if (mime.startsWith('audio/')) return '🎵';
                                return '📎';
                              };
                              return (
                                <div className="rounded-2xl border border-site-border bg-site-bg/25 overflow-hidden">
                                  <div className="px-3 py-2 border-b border-site-border flex items-center gap-1 overflow-x-auto scrollbar-hide">
                                    {ownerTabs.map((t) => (
                                      <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => setFilesOwnerFilter(t.id)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${
                                          filesOwnerFilter === t.id ? 'bg-site-accent/20 text-site-accent' : 'text-site-text/70 hover:bg-site-text/6 hover:text-site-text'
                                        }`}
                                      >
                                        {t.label}
                                      </button>
                                    ))}
                                  </div>
                                  {filtered.length === 0 ? (
                                    <div className="px-4 py-8 text-center text-sm text-site-muted">
                                      {allFiles.length === 0 ? 'Нет файлов в этом чате' : 'Нет файлов по выбранному фильтру'}
                                    </div>
                                  ) : (
                                    <div className="divide-y divide-site-border max-h-[55vh] overflow-y-auto" data-scroll-lock-allow>
                                      {filtered.map((f) => {
                                        const d = new Date(f.createdAt);
                                        const dt = d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
                                        const tm = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
                                        return (
                                          <a
                                            key={`${f.id}-${f.url}`}
                                            href={f.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="px-4 py-3 flex items-center gap-3 hover:bg-site-text/5 transition-colors"
                                            title="Открыть файл"
                                          >
                                            <span className="w-10 h-10 rounded-xl glass-btn flex items-center justify-center shrink-0"><UiIcon name={iconFor(f.name, f.mime)} /></span>
                                            <div className="min-w-0 flex-1">
                                              <div className="text-sm font-semibold text-site-text truncate">{f.name}</div>
                                              <div className="text-xs text-site-muted truncate">
                                                {(f.isMe ? 'Вы' : peerName) + ' · ' + (f.size ? formatBytesShort(f.size) : '—') + ' · ' + `${dt} ${tm}`}
                                              </div>
                                            </div>
                                            <div className="text-site-muted shrink-0">→</div>
                                          </a>
                                        );
                                      })}
                                    </div>
                                  )}
                                  <div className="px-3 py-2 border-t border-site-border text-[11px] text-site-muted">
                                    Всего: {filtered.length} из {allFiles.length}
                                  </div>
                                </div>
                              );
                            })()}
                            {peerProfileTab === 'design' && (
                              <div className="rounded-2xl border border-site-border bg-site-bg/25 p-4">
                                <div className="text-sm font-semibold text-site-text mb-3">Фон чата</div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPeerProfileOpen(false);
                                    setPeerProfileMenu(null);
                                    setPeerProfilePullY(0);
                                    openDmWallpaperModal();
                                  }}
                                  className="w-full px-4 py-3 rounded-xl glass-btn text-site-text/90 hover:text-site-text transition flex items-center justify-between"
                                >
                                  <span className="text-sm">Настроить фон</span>
                                  <MonoWallpaperIcon className="w-5 h-5" />
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Комментарий на русском: свайп закрытия делаем ТОЛЬКО на нижнем хэндле, чтобы списки нормально скроллились. */}
                          <div
                            className="px-4 pb-3 pt-2 mb-2"
                            data-peer-profile-swipe-handle
                            onTouchStart={(e) => {
                              const t = e.touches?.[0];
                              if (t) peerProfileSwipeRef.current = { startY: t.clientY, startTime: Date.now() };
                            }}
                            onTouchMove={(e) => {
                              if (!peerProfileSwipeRef.current) return;
                              const t = e.touches?.[0];
                              if (!t) return;
                              const deltaY = peerProfileSwipeRef.current.startY - t.clientY;
                              if (deltaY > 0) setPeerProfileDragY(Math.min(deltaY, 200));
                            }}
                            onTouchEnd={() => {
                              if (!peerProfileSwipeRef.current) return;
                              const dragY = peerProfileDragY;
                              peerProfileSwipeRef.current = null;
                              if (dragY > 60) {
                                setPeerProfileOpen(false);
                                setPeerProfileMenu(null);
                                setPeerProfileDragY(0);
                                setPeerProfilePullY(0);
                              } else {
                                setPeerProfileDragY(0);
                              }
                            }}
                          >
                            <div className="mx-auto w-16 h-1.5 rounded-full bg-site-text/15" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Комментарий на русском: photo viewer для вкладки "Медиа" (как в чате). */}
                    {false && photoViewerOpen &&
                      (typeof document !== 'undefined' && document.body
                        ? createPortal(
                            (() => {
                              const dy = Math.max(0, Math.round(photoViewerDragY || 0));
                              const dragK = Math.min(1, dy / 240);
                              const bgOpacity = Math.max(0.18, 0.75 - dragK * 0.45);
                              return (
                                <div className="fixed inset-0 z-[12000]">
                                  <div
                                    className="absolute inset-0 backdrop-blur-md"
                                    style={{ backgroundColor: `rgba(0,0,0,${bgOpacity})` }}
                                    onClick={closePhotoViewer}
                                    aria-hidden="true"
                                  />
                                  <div className="absolute z-[12002] flex items-center gap-2 bottom-4 left-1/2 -translate-x-1/2 sm:top-3 sm:right-3 sm:left-auto sm:bottom-auto sm:translate-x-0">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!photoViewerSrc) return; // Комментарий на русском: пока не загрузили blob — скачивание отключено.
                                        const a = document.createElement('a');
                                        a.href = photoViewerSrc;
                                        a.download = `photo_${Date.now()}.jpg`;
                                        a.click();
                                      }}
                                      disabled={!photoViewerSrc}
                                      className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92] disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Скачать"
                                    >
                                      <UiIcon name="ArrowDown" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); closePhotoViewer(); }}
                                      className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
                                      title="Закрыть"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                  <div
                                    className="absolute inset-0 z-[12001] flex items-center justify-center px-0 py-0 sm:p-10"
                                    onClick={(e) => e.stopPropagation()}
                                    onTouchStart={(e) => {
                                      if (e.touches.length === 2) {
                                        const t1 = e.touches[0], t2 = e.touches[1];
                                        const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                                        const centerX = (t1.clientX + t2.clientX) / 2, centerY = (t1.clientY + t2.clientY) / 2;
                                        const img = photoViewerImgRef.current;
                                        if (img) {
                                          const rect = img.getBoundingClientRect();
                                          photoViewerPinchRef.current = {
                                            distance: dist,
                                            center: { x: centerX - (rect.left + rect.width / 2), y: centerY - (rect.top + rect.height / 2) },
                                            initialScale: photoViewerScale,
                                            initialTranslate: { ...photoViewerTranslate },
                                          };
                                        }
                                      } else if (e.touches.length === 1 && photoViewerScale <= 1) {
                                        const t = e.touches[0];
                                        photoViewerPointerIdRef.current = t.identifier;
                                        photoViewerStartRef.current = { x: t.clientX, y: t.clientY };
                                        setPhotoViewerDragging(true);
                                        photoViewerSwipeRef.current = { startX: t.clientX, startY: t.clientY, dx: 0, dy: 0 };
                                      }
                                    }}
                                    onTouchMove={(e) => {
                                      e.preventDefault();
                                      if (e.touches.length === 2 && photoViewerPinchRef.current) {
                                        const t1 = e.touches[0], t2 = e.touches[1];
                                        const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                                        const pinch = photoViewerPinchRef.current;
                                        const scale = Math.max(1, Math.min(4, (pinch.initialScale * dist) / pinch.distance));
                                        setPhotoViewerScale(scale);
                                        if (scale > 1) {
                                          const centerX = (t1.clientX + t2.clientX) / 2, centerY = (t1.clientY + t2.clientY) / 2;
                                          const img = photoViewerImgRef.current;
                                          if (img) {
                                            const rect = img.getBoundingClientRect();
                                            const dx = centerX - (rect.left + rect.width / 2) - pinch.center.x;
                                            const dy = centerY - (rect.top + rect.height / 2) - pinch.center.y;
                                            setPhotoViewerTranslate({ x: pinch.initialTranslate.x + dx / scale, y: pinch.initialTranslate.y + dy / scale });
                                          }
                                        } else {
                                          setPhotoViewerTranslate({ x: 0, y: 0 });
                                        }
                                        return;
                                      }
                                      if (e.touches.length === 1 && photoViewerScale <= 1 && photoViewerDragging) {
                                        const t = e.touches[0];
                                        if (photoViewerPointerIdRef.current !== t.identifier) return;
                                        const st = photoViewerStartRef.current;
                                        if (!st) return;
                                        const dx = t.clientX - st.x, dyRaw = t.clientY - st.y;
                                        if (photoViewerSwipeRef.current) {
                                          photoViewerSwipeRef.current.dx = dx;
                                          photoViewerSwipeRef.current.dy = dyRaw;
                                        }
                                        // Комментарий на русском: горизонтальный свайп — листаем фото.
                                        if (Math.abs(dx) > Math.abs(dyRaw) * 1.1) return;
                                        photoViewerDragYRef.current = Math.max(0, dyRaw);
                                        setPhotoViewerDragY(photoViewerDragYRef.current);
                                      }
                                    }}
                                    onTouchEnd={(e) => {
                                      if (e.touches.length < 2) photoViewerPinchRef.current = null;
                                      if (e.touches.length === 0 && photoViewerDragging && photoViewerScale <= 1) {
                                        photoViewerPointerIdRef.current = null;
                                        photoViewerStartRef.current = null;
                                        setPhotoViewerDragging(false);
                                        const dyFinal = photoViewerDragYRef.current || 0;
                                        const sw = photoViewerSwipeRef.current;
                                        photoViewerSwipeRef.current = null;
                                        const dxFinal = sw?.dx || 0;
                                        const dy0 = sw?.dy || 0;
                                        if (Math.abs(dxFinal) > 80 && Math.abs(dxFinal) > Math.abs(dy0) * 1.1) {
                                          if (dxFinal < 0) goPhotoNext();
                                          else goPhotoPrev();
                                          photoViewerDragYRef.current = 0;
                                          setPhotoViewerDragY(0);
                                          return;
                                        }
                                        if (dyFinal > 140) closePhotoViewer();
                                        else { photoViewerDragYRef.current = 0; setPhotoViewerDragY(0); }
                                      }
                                    }}
                                    onTouchCancel={() => {
                                      photoViewerPinchRef.current = null;
                                      photoViewerPointerIdRef.current = null;
                                      photoViewerStartRef.current = null;
                                      setPhotoViewerDragging(false);
                                      photoViewerDragYRef.current = 0;
                                      setPhotoViewerDragY(0);
                                      photoViewerSwipeRef.current = null;
                                    }}
                                  >
                                    <div
                                      className="max-w-full max-h-full"
                                      onPointerDown={(e) => {
                                        if (e.pointerType !== 'touch' || photoViewerScale > 1) return;
                                        photoViewerPointerIdRef.current = e.pointerId;
                                        photoViewerStartRef.current = { x: e.clientX, y: e.clientY };
                                        setPhotoViewerDragging(true);
                                        try { (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId); } catch {}
                                      }}
                                      onPointerMove={(e) => {
                                        if (!photoViewerDragging || photoViewerScale > 1 || photoViewerPointerIdRef.current !== e.pointerId) return;
                                        const st = photoViewerStartRef.current;
                                        if (!st) return;
                                        const dx = e.clientX - st.x, dyRaw = e.clientY - st.y;
                                        if (Math.abs(dx) > Math.abs(dyRaw) * 0.9) return;
                                        photoViewerDragYRef.current = Math.max(0, dyRaw);
                                        setPhotoViewerDragY(photoViewerDragYRef.current);
                                      }}
                                      onPointerUp={(e) => {
                                        if (photoViewerPointerIdRef.current !== e.pointerId) return;
                                        try { (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId); } catch {}
                                        photoViewerPointerIdRef.current = null;
                                        photoViewerStartRef.current = null;
                                        setPhotoViewerDragging(false);
                                        const dyFinal = photoViewerDragYRef.current || 0;
                                        if (dyFinal > 140 && photoViewerScale <= 1) closePhotoViewer();
                                        else { photoViewerDragYRef.current = 0; setPhotoViewerDragY(0); }
                                      }}
                                      onPointerCancel={(e) => {
                                        if (photoViewerPointerIdRef.current !== e.pointerId) return;
                                        photoViewerPointerIdRef.current = null;
                                        photoViewerStartRef.current = null;
                                        setPhotoViewerDragging(false);
                                        photoViewerDragYRef.current = 0;
                                        setPhotoViewerDragY(0);
                                      }}
                                      style={{
                                        touchAction: 'none',
                                        transform: `translate3d(${photoViewerTranslate.x}px, ${photoViewerDragY + photoViewerTranslate.y}px, 0) scale(${photoViewerScale})`,
                                        transition: photoViewerDragging || photoViewerPinchRef.current ? 'none' : 'transform 180ms ease-out',
                                      }}
                                    >
                                      {photoViewerSrc ? (
                                        <img
                                          ref={photoViewerImgRef}
                                          src={photoViewerSrc}
                                          alt="Фото"
                                          className="max-w-[100vw] max-h-[100vh] sm:max-w-[min(92vw,980px)] sm:max-h-[86vh] object-contain rounded-none sm:rounded-2xl border-0 sm:border sm:border-site-border shadow-[0_25px_90px_rgba(0,0,0,0.65)]"
                                          draggable={false}
                                          style={{ touchAction: 'none' }}
                                        />
                                      ) : (
                                        <div className="w-[min(92vw,680px)] aspect-square rounded-2xl border border-site-border bg-black/20 flex items-center justify-center">
                                          <span className="w-7 h-7 border-2 border-site-border border-t-white/80 rounded-full animate-spin" />
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Комментарий на русском: десктоп — кнопки пролистывания (влево/вправо). */}
                                  {photoViewerList.length > 1 && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); goPhotoPrev(); }}
                                        disabled={photoViewerIndex <= 0}
                                        className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 z-[12003] w-12 h-12 rounded-full glass-btn items-center justify-center text-site-text/90 hover:text-site-text transition disabled:opacity-40 disabled:cursor-not-allowed"
                                        title="Предыдущее"
                                        aria-label="Предыдущее фото"
                                      >
                                        ‹
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); goPhotoNext(); }}
                                        disabled={photoViewerIndex >= photoViewerList.length - 1}
                                        className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 z-[12003] w-12 h-12 rounded-full glass-btn items-center justify-center text-site-text/90 hover:text-site-text transition disabled:opacity-40 disabled:cursor-not-allowed"
                                        title="Следующее"
                                        aria-label="Следующее фото"
                                      >
                                        ›
                                      </button>
                                    </>
                                  )}
                                </div>
                              );
                            })(),
                            document.body
                          )
                        : null)}

                    {renderPhotoViewerPortal()}

                    {/* Комментарий на русском: десктоп — плавающие иконки (без видимой панели/шапки под ними). */}
                    <div className="hidden sm:flex absolute right-4 top-2 z-30 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (!selectedUser) return;
                          if (selectedIsBlocked) {
                            if (confirm('Разблокировать пользователя?')) void unblockUser(selectedUser.id);
                            return;
                          }
                          if (confirm('Заблокировать пользователя? Он не сможет писать вам, и вы не сможете писать ему.')) {
                            void blockUser(selectedUser.id);
                          }
                        }}
                        className={`${peerHeaderIconBtnClass} ${selectedIsBlocked ? 'text-site-success' : 'text-site-danger'}`}
                        title={selectedIsBlocked ? 'Разблокировать' : 'Заблокировать'}
                        aria-label={selectedIsBlocked ? 'Разблокировать' : 'Заблокировать'}
                      >
                        {selectedIsBlocked ? <><UiIcon name="LockOpen" /></> : <><UiIcon name="Prohibit" /></>}
                      </button>
                      <button
                        type="button"
                        onClick={clearChat}
                        className={`${peerHeaderIconBtnClass} text-site-danger`}
                        title="Очистить чат"
                        aria-label="Очистить чат"
                      >
                        <UiIcon name="Trash" />
                      </button>
                    </div>

                {/* Комментарий на русском: виртуализированный список сообщений (react-virtuoso) — лучшие практики для чатов. */}
                <div
                  className="flex-1 min-h-0"
                  onPointerDown={(e) => {
                    // Комментарий на русском: тап по чату скрывает клавиатуру (blur), но черновик остаётся.
                    if (!composeInChat) return;
                    const t = e.target as HTMLElement | null;
                    if (t && t.closest('[data-compose-bubble]')) return;
                    draftChatRef.current?.blur();
                  }}
                  data-scroll-lock-allow
                >
                  <MessageList
                    key={selectedUser?.id ? `dm:${selectedUser.id}` : selectedRoom?.id ? `room:${selectedRoom.id}` : 'chat:none'}
                    chatKey={selectedUser?.id ? `dm:${selectedUser.id}` : selectedRoom?.id ? `room:${selectedRoom.id}` : ''}
                    chatId={selectedUser?.id ? `dm:${selectedUser.id}` : selectedRoom?.id ? `room:${selectedRoom.id}` : ''}
                    ref={virtuosoRef}
                    meId={meRef.current?.id || 0}
                    showAvatar={false}
                    bottomInsetPx={chatBottomInsetPx}
                    scrollerRef={(el) => {
                      // Комментарий на русском: привязываем старый messagesScrollRef к реальному CSS-скроллеру.
                      messagesScrollRef.current = el;
                    }}
                    pinnedMessageId={pinnedMessageId}
                    loadingOlder={loadingOlder}
                    loadingMessages={loadingMessages}
                    hasMoreMessages={hasMoreMessages}
                    linkPreviewsEnabled={linkPreviewsEnabled}
                    selectionMode={selectionMode}
                    selectedMsgKeySet={selectedMsgKeySet}
                    emptyText="Нет сообщений. Напишите первым."
                    enableStacking={messageStackingEnabled}
                    onLoadOlder={loadOlderMessages}
                    onScrollPositionChange={(atBottom, offset) => {
                      isNearBottomRef.current = atBottom;
                      dmScrollBottomOffsetRef.current = offset;
                      // Комментарий на русском: не вызываем setState если значение не изменилось — бережём iOS momentum.
                      const next = !atBottom && offset >= 60;
                      if (showScrollToBottomRef.current !== next) {
                        showScrollToBottomRef.current = next;
                        setShowScrollToBottom(next);
                      }
                    }}
                    onOpenPhotoViewer={openChatPhotoViewer}
                    onDelete={deleteMessage as any}
                    onReply={startReply as any}
                    onReact={reactToMessage as any}
                    onToggleSelect={toggleSelectMessage as any}
                    onForward={openForwardOne as any}
                    onEdit={startEditMessage as any}
                    onPin={togglePinMessage as any}
                    msgKeyOf={msgKeyOf as any}
                    togglePinMessage={togglePinMessage as any}
                  />
                </div>
                    
                {/* нижняя панель ввода = высота 0px, содержимое плавающее (absolute) */}
                <div className="relative shrink-0 h-0 overflow-visible">
                  <div
                    ref={composePanelRef}
                    className="absolute left-0 right-0 bottom-0 px-3 pb-3 sm:px-4 sm:pb-4 pt-2 overflow-visible"
                  >
                  {error && <div className="mb-2 px-3 py-2 bg-site-danger/12 border border-site-danger/20 rounded-xl text-site-danger text-sm">{error}</div>}
                    {/* кнопки "Назад/Отправить" и ввод — не занимают высоту layout, но остаются внизу. */}
                    <div className="relative w-full overflow-visible">
                    {/* Комментарий на русском: на мобилках делаем панель ввода уже на 20% (85% → 68%). */}
                    <div className="w-[calc(68%_-_4px)] sm:w-[calc(85%_-_5px)] mx-auto flex flex-col gap-2">
                        {selectionMode && (
                          <div className="w-full rounded-2xl glass-subtle border border-site-border px-3 py-2 flex items-center justify-between">
                            <div className="text-xs text-site-text">
                              Выбрано: <span className="font-semibold text-site-text">{selectedMsgKeys.length}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={openForwardSelected}
                                className="w-9 h-9 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/40"
                                title="Переслать выбранные"
                                aria-label="Переслать выбранные сообщения"
                              >
                                <UiIcon name="ArrowBendUpRight" />
                              </button>
                              <button
                                type="button"
                                onClick={clearSelectedMessages}
                                className="w-9 h-9 rounded-full glass-btn text-site-text/80 hover:text-site-text flex items-center justify-center transition active:scale-[0.9] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/40"
                                title="Отменить выбор"
                                aria-label="Отменить выбор сообщений"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="relative w-full rounded-2xl glass-input text-site-text" data-compose-bubble>
                        {editTarget && (
                          <div className="flex items-center gap-2 px-4 py-2 border-b border-site-border bg-site-surface/20 rounded-t-2xl">
                            <div className="w-1 self-stretch rounded-full bg-site-success/60" aria-hidden="true" />
                            <div className="min-w-0 flex-1">
                              <div className="text-[11px] text-site-text truncate">Изменение сообщения</div>
                              <div className="text-[11px] text-site-muted truncate">{String((editTarget as any)?.message || '')}</div>
                            </div>
                            <button
                              type="button"
                              onClick={cancelEditMessage}
                              className="w-8 h-8 rounded-full glass-btn text-site-text/80 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
                              title="Отменить редактирование"
                              aria-label="Отменить редактирование"
                            >
                              ✕
                            </button>
                          </div>
                        )}
                        {replyTo && (
                          <div className="flex items-center gap-2 px-4 py-2 border-b border-site-border bg-site-surface/20 rounded-t-2xl">
                            <div className="w-1 self-stretch rounded-full bg-site-accent/60" aria-hidden="true" />
                            <div className="min-w-0 flex-1">
                              <div className="text-[11px] text-site-text truncate">
                                Ответ: {replyTo.from_user?.id === (meRef.current?.id || 0) ? 'Вы' : (replyTo.from_user?.display_name || replyTo.from_user?.username || 'Сообщение')}
                              </div>
                              <div className="text-[11px] text-site-muted truncate">
                                {(() => {
                                  const t = String(replyTo.message || '');
                                  const isV =
                                    (replyTo as any)?.type === 'voice_message' ||
                                    t.includes('/chat_voice/') ||
                                    t.includes('/uploads/chat_voice/') ||
                                    t.includes('/media/chat_voice/');
                                  return isV ? 'Голосовое сообщение' : (t || '...');
                                })()}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setReplyTo(null)}
                              className="text-site-secondary/80 hover:text-site-text text-lg leading-none px-1"
                              title="Отменить ответ"
                              aria-label="Отменить ответ"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        <PendingPhotosBar disabled={sending || voiceSending} />
                        <PendingFilesBar disabled={sending || voiceSending} />
                        {recording ? (
                          <div className="px-4 py-3">
                            <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-site-danger whitespace-nowrap">
                              <span className="w-2 h-2 rounded-full bg-site-danger animate-pulse" aria-hidden="true" />
                              REC {formatRecTime(voiceElapsedMs)}
                            </div>
                            <div className="mt-2 rounded-xl bg-black/20 border border-site-border px-3 py-2">
                              <canvas ref={voiceCanvasRef} className="w-full h-14" />
                            </div>
                          </div>
                        ) : (
                          <>
                            {(offeredProject || dmDraft.projectId || dmDraft.legacyAvailable || dmDraft.storageNotice || contextNotice?.key === dmDraft.key) && <div className="mx-3 mt-2 space-y-2 rounded-xl border border-site-accent/20 bg-site-bg/70 p-3 text-xs text-site-text" data-testid="dm-project-context">
                              {dmDraft.projectId && <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0"><span className="block text-site-accent">Проект: {activeDraftProject?.name || 'проверка доступности…'}</span><span className="text-site-muted">Ссылка добавится только при обычной отправке.</span></div>
                                <button type="button" className="min-h-11 min-w-11 shrink-0 text-site-accent" aria-label="Убрать проект из сообщения" disabled={sending} onClick={() => { dmDraft.setProjectId(null); setVerifiedDraftProject(null); setContextNotice(null); }}>×</button>
                              </div>}
                              {offeredProject && <div>
                                <p className="break-words">Обсудить проект «{offeredProject.name}»? Текст черновика останется без изменений.</p>
                                {offeredProject.id !== dmDraft.projectId && <button type="button" className="mr-4 min-h-11 text-site-accent disabled:opacity-40" disabled={!canUseProjectContext} onClick={() => {
                                  if (!canUseProjectContext || !dmDraft.isCurrent()) return;
                                  dmDraft.setProjectId(offeredProject.id);
                                  setVerifiedDraftProject({ key: dmDraft.key, revision: dmDraft.revision, project: offeredProject });
                                  setPendingProjectContext(null);
                                  setContextNotice(null);
                                }}>{dmDraft.projectId ? 'Заменить проект' : 'Добавить проект'}</button>}
                                <button type="button" className="min-h-11 text-site-secondary" onClick={() => setPendingProjectContext(null)}>Не добавлять</button>
                              </div>}
                              {(offeredProject || dmDraft.projectId) && !canUseProjectContext && <p className="text-site-warning" role="status">Контекст отложен: сначала завершите редактирование, ответ или работу с вложениями. Эти сообщения отправляются без тега проекта.</p>}
                              {dmDraft.legacyAvailable && <div>
                                <p className="text-site-warning">На устройстве есть старый черновик без привязки к аккаунту. Он может принадлежать другому пользователю. Восстанавливайте только свой текст.</p>
                                <button type="button" className="min-h-11 text-site-accent disabled:opacity-40" disabled={Boolean(draft.length) || !canUseProjectContext} onClick={dmDraft.restoreLegacy}>Восстановить старый черновик</button>
                                {draft.length > 0 && <p className="text-site-muted">Сначала сохраните или отправьте текущий текст: он не будет заменён.</p>}
                              </div>}
                              {contextNotice?.key === dmDraft.key && <p role="status" className="text-site-warning">{contextNotice.text}</p>}
                              {dmDraft.storageNotice && <p role="status" className="text-site-warning">{dmDraft.storageNotice}</p>}
                            </div>}
                            {composeInChat &&
                              draftFirstUrl &&
                              draftFirstUrl !== draftPreviewSuppressedUrl &&
                              (draftLinkPreviewLoading || draftLinkPreview) && (
                                <div className="px-3 pt-2">
                                  <DraftLinkPreviewCard
                                    url={draftFirstUrl}
                                    loading={draftLinkPreviewLoading}
                                    preview={draftLinkPreview}
                                    onClose={() => setDraftPreviewSuppressedUrl(draftFirstUrl)}
                                  />
                                </div>
                              )}
                            <div className="flex items-end gap-2 px-3 py-2">
                            <button
                              type="button"
                              onClick={openPhotoPicker}
                              disabled={sending || voiceSending}
                              className={chatAttachBtnClass}
                              title="Прикрепить фото"
                              aria-label="Прикрепить фото"
                            >
                              {paperclipIcon}
                            </button>
                            <button
                              type="button"
                              onClick={openFilePicker}
                              disabled={sending || voiceSending}
                              className={chatAttachBtnClass}
                              title="Прикрепить файл"
                              aria-label="Прикрепить файл"
                            >
                              <UiIcon name="FileText" />
                            </button>
                            {composeInChat ? (
                              <textarea
                                ref={draftChatRef}
                                value={draft}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setDraft(v);
                                  if (v.trim()) pingTyping(selectedUser.id);
                                }}
                                onFocus={(e) => {
                                  try {
                                    iosSingleInputCleanupRef.current?.();
                                  } catch {}
                                  iosSingleInputCleanupRef.current = enterIosSingleInputMode(e.currentTarget as any);
                                }}
                                title="Enter — отправить · Shift+Enter — новая строка"
                                onKeyDown={(e) => {
                                  if (e.key === 'Control') {
                                    draftCtrlUsedRef.current = false;
                                    return;
                                  }
                                  if (draftCtrlUsedRef.current === false && e.ctrlKey && e.key !== 'Control') draftCtrlUsedRef.current = true;
                                  if (e.key !== 'Enter') return;
                                  // Комментарий на русском: Shift+Enter (десктоп) и Ctrl/Alt/Meta+Enter — перенос строки (не надеемся на браузер).
                                  if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) {
                                    e.preventDefault();
                                    const el = e.currentTarget;
                                    insertNewline(el, draft, setDraft);
                                    return;
                                  }
                                  // Комментарий на русском: десктоп — Enter отправка; мобилка оставляем как есть.
                                  if (isMobileViewport()) return;
                                  if (!draft.trim() && !hasPendingPhoto && !hasPendingFile) return;
                                  e.preventDefault();
                                  if (hasPendingPhoto) return void sendPhotoMessage();
                                  if (hasPendingFile) return void sendFileMessage();
                                  void sendMessage();
                                }}
                                onKeyUp={(e) => {
                                  if (e.key !== 'Control') return;
                                  const el = e.currentTarget;
                                  const used = draftCtrlUsedRef.current;
                                  draftCtrlUsedRef.current = false;
                                  if (used) return;
                                  insertNewline(el, draft, setDraft);
                                }}
                                onBlur={() => {
                                  try {
                                    iosSingleInputCleanupRef.current?.();
                                  } catch {}
                                  iosSingleInputCleanupRef.current = null;
                                  // Комментарий на русском: если пользователь свернул клавиатуру и текста нет — возвращаем "Сообщение".
                                  if (!draft.trim() && !hasPendingPhoto && !hasPendingFile) setComposeInChat(false);
                                }}
                                placeholder=""
                                className="flex-1 bg-transparent text-site-text text-base sm:text-sm leading-relaxed resize-none overflow-y-auto scrollbar-hide focus:outline-none px-2 py-2 select-text"
                                rows={1}
                                maxLength={MAX_MESSAGE_LENGTH}
                                disabled={!dmDraft.ready || (sending && isMobileViewport())}
                              />
                            ) : (
                              <button
                                type="button"
                                onClick={openChatComposer}
                                className="flex-1 text-center text-site-secondary/80 hover:text-site-text transition px-2 py-2"
                                title="Сообщение"
                                aria-label="Сообщение"
                              >
                                Сообщение
                              </button>
                            )}
                            </div>
                          </>
                        )}
                      </div>
                      {/* Комментарий на русском: эмодзи убрали — тут будет голосовая запись (микрофон). */}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        // Комментарий на русском: во время записи “Назад” = отмена записи (без отправки).
                        if (recording) return void stopVoiceRecording(false);
                        // Комментарий на русском: "Назад" всегда возвращает в список чатов.
                        try {
                          // Комментарий на русском: сохраняем позицию ДО сброса selectedUser (иначе иногда cleanup не успевает/не имеет DOM).
                          const uid = selectedUser?.id || 0;
                          if (uid) {
                            const el = messagesScrollRef.current;
                            const offRaw = el ? Math.max(0, Math.round(el.scrollHeight - el.clientHeight - el.scrollTop)) : dmScrollBottomOffsetRef.current;
                            // Комментарий на русском: если почти внизу — сохраняем 0, чтобы при возврате чат открывался внизу (и не “подпрыгивал” на iOS).
                            const off = offRaw < 120 ? 0 : offRaw;
                            dmScrollBottomOffsetRef.current = off;
                            dmScrollPersistedOffsetRef.current = off;
                            isNearBottomRef.current = off < 120;
                            writeDmScroll(uid, off);
                          }
                        } catch {}
                        setTab('chats');
                        setSupportOpen(false);
                        setSupportTargetUser(null);
                        setSupportMessages([]);
                        supportLastIdRef.current = 0;
                        setSelectedUser(null);
                        setSelectedRoom(null);
                        const chatKey = selectedRoom?.id ? `room:${selectedRoom.id}` : selectedUser?.id ? `dm:${selectedUser.id}` : '';
                        if (chatKey) chatMessagesStore.getState().clearChat(chatKey);
                        lastMessageIdRef.current = 0;
                      }}
                      // Комментарий на русском: выравниваем кнопку по высоте с FAB (bottom-6) на мобилках.
                      className={`${chatIconBtnClass} absolute left-[12px] sm:left-0 bottom-6 sm:bottom-0${recording ? ' text-site-danger' : ''}`}
                      title={recording ? 'Отменить запись' : 'Назад'}
                      aria-label={recording ? 'Отменить запись' : 'Назад'}
                    >
                      {recording ? '✕' : '←'}
                    </button>

                    <button
                      type="button"
                      onClick={onRightBtnClick}
                      onContextMenu={(e) => {
                        if (hasDraftText) return;
                        e.preventDefault();
                      }}
                      disabled={sending || voiceSending}
                      // Комментарий на русском: выравниваем кнопку по высоте с FAB (bottom-6) на мобилках.
                      className={`${chatIconBtnClass} absolute right-[12px] sm:right-0 bottom-6 sm:bottom-0${
                        recording ? ' ring-2 ring-site-danger/35 animate-pulse' : ''
                      }`}
                      title={rightBtnTitle}
                      aria-label={rightBtnTitle}
                    >
                      <span className={recording ? 'animate-pulse' : ''}>{rightBtnContent}</span>
                    </button>

                    {showScrollToBottom && (
                      <button
                        type="button"
                        onClick={() => {
                          // Комментарий на русском: быстрый переход в самый низ (и обновление локально сохранённой позиции).
                          const uid = selectedUser?.id || 0;
                          virtuosoRef.current?.scrollToBottom('smooth');
                          dmScrollBottomOffsetRef.current = 0;
                          dmScrollPersistedOffsetRef.current = 0;
                          isNearBottomRef.current = true;
                          setShowScrollToBottom(false);
                          if (uid) writeDmScroll(uid, 0);
                        }}
                        className={`absolute right-0 z-20 w-10 h-10 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/40${
                          inChatSearchOpen ? ' hidden sm:flex' : ''
                        }`}
                        // Комментарий на русском: позиционирование с учётом подъёма кнопок на мобилках (+3px).
                        style={{ bottom: 'calc(25px + 3.5rem + 3px)' }}
                        title="Вниз"
                        aria-label="Вниз (к последнему сообщению)"
                      >
                        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 5v12" />
                          <path d="M7 13l5 5 5-5" />
                        </svg>
                      </button>
                    )}

                    {/* Комментарий на русском: во время поиска прячем кнопку "Вниз" (чтобы не мешала) — навигация поиска в дропдауне сверху. */}
                              {/* Комментарий на русском: стрелки поиска на мобилке показываем в дропдауне (сверху), чтобы не ломать панель ввода. */}
                    </div>
                  </div>
                </div>
                  </>
                );
              })()
            ) : selectedRoom ? (
              (() => {
                const icon = selectedRoom.type === 'channel' ? '📣' : selectedRoom.type === 'assistant' ? '🤖' : '👥';
                const title = selectedRoom.title || 'Чат';
                const canSend = !!roomDraft.trim() && !sending;
                const typeLabel = selectedRoom.type === 'assistant' ? 'помощника' : selectedRoom.type === 'channel' ? 'канала' : 'группы';
                const meta = roomMeta && roomMeta.id === selectedRoom.id ? roomMeta : null;
                const membersCount = meta?.members_count || roomMembers.length || 0;
                const onlineCount = meta?.online_count || 0;
                const headerSubtitle =
                  meta && membersCount > 0
                    ? `${membersCount} участников · ${onlineCount} онлайн`
                    : selectedRoom.type === 'assistant'
                      ? 'Помощник'
                      : selectedRoom.type === 'channel'
                        ? 'Канал'
                        : 'Группа';
                const headerAvatarUrl = meta?.avatar_url || null;
                const canManage = roomCanManage;
                const tabs = [
                  { id: 'members', label: 'Участники' },
                  { id: 'media', label: 'Медиа' },
                  { id: 'projects', label: 'Проекты' },
                  { id: 'files', label: 'Файлы' },
                  { id: 'design', label: 'Дизайн' },
                ] as const;
                return (
                  <>
                    {roomSettingsOpen && (
                      <div className="absolute inset-0 z-40 flex flex-col glass-subtle">
                        <div className="px-3 py-2 border-b border-site-border-control/25 flex items-center justify-between bg-site-bg/30">
                          <button
                            type="button"
                            onClick={() => setRoomSettingsOpen(false)}
                            className="w-10 h-10 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-success/30"
                            title="Назад"
                            aria-label="Назад"
                          >
                            ←
                          </button>
                          <div className="min-w-0 text-sm font-semibold text-site-text truncate">Настройки {typeLabel}</div>
                          <div className="w-10 h-10" aria-hidden="true" />
                        </div>

                        <input ref={roomAvatarInputRef} type="file" accept="image/*" onChange={handleRoomAvatarSelect} className="hidden" />

                        {roomSettingsToast && (
                          <div className="px-3 pt-2">
                            <div className="px-3 py-2 rounded-xl border border-site-border bg-site-bg/25 text-sm text-site-text/90">
                              {roomSettingsToast}
                            </div>
                          </div>
                        )}

                        {/* Комментарий на русском: выпадающие меню выбора (участники/проекты) рендерим в портал, чтобы не резались overflow. */}
                        {memberAddOpen &&
                          (typeof document !== 'undefined' && document.body
                            ? createPortal(
                                <div
                                  ref={memberAddMenuRef}
                                  className="fixed z-[10060] glass-strong rounded-2xl border border-site-border shadow-[0_20px_70px_rgba(0,0,0,0.55)] overflow-hidden w-[340px] max-w-[calc(100vw_-_16px)]"
                                  style={
                                    memberAddPos
                                      ? { left: memberAddPos.left, top: memberAddPos.top }
                                      : { left: 0, top: 0, opacity: 0, pointerEvents: 'none' }
                                  }
                                >
                                  <div className="p-3">
                                    <div className="text-xs text-site-secondary/80 mb-2">Добавить участника</div>
                                    <input
                                      ref={memberAddInputRef}
                                      value={memberAddQuery}
                                      onChange={(e) => setMemberAddQuery(e.target.value)}
                                      placeholder="Поиск пользователя..."
                                      className="w-full px-3 py-2 glass-input rounded-xl text-sm text-site-text placeholder-site-muted focus:outline-none"
                                    />
                                    <div className="mt-2 text-[11px] text-site-muted">
                                      Введите минимум 2 символа. Можно искать по username или имени.
                                    </div>
                                  </div>
                                  <div className="max-h-[320px] overflow-y-auto border-t border-site-border divide-y divide-site-border" data-scroll-lock-allow>
                                    {memberAddQuery.trim().length < 2 ? (
                                      <div className="px-4 py-4 text-sm text-site-muted">Начните ввод для поиска</div>
                                    ) : memberAddLoading ? (
                                      <div className="px-4 py-4 text-sm text-site-muted">Поиск...</div>
                                    ) : memberAddResults.length === 0 ? (
                                      <div className="px-4 py-4 text-sm text-site-muted">Ничего не найдено</div>
                                    ) : (
                                      memberAddResults.map((u) => {
                                        const id = u?.id || 0;
                                        const name = u?.display_name || u?.username || 'Пользователь';
                                        const initial = (name[0] || '?').toUpperCase();
                                        const already = id > 0 && roomMemberIdSet.has(id);
                                        return (
                                          <button
                                            key={id || `${u.username}-${name}`}
                                            type="button"
                                            disabled={already}
                                            onClick={() => void pickRoomMember(u)}
                                            className="w-full px-4 py-2.5 text-left hover:bg-site-text/6 transition-colors flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                          >
                                            <AvatarFx
                                              sizeClass="w-9 h-9"
                                              avatarUrl={u.avatar_url || null}
                                              alt={name}
                                              fallback={initial}
                                              isOnline={!!u.is_online}
                                              fxColor={u.avatar_fx_color}
                                              fxThickness={u.avatar_fx_thickness}
                                              fxGlow={u.avatar_fx_glow}
                                              fxOfflineColor={u.avatar_fx_offline_color}
                                            />
                                            <div className="min-w-0 flex-1">
                                              <div className="text-sm font-semibold text-site-text truncate">{name}</div>
                                              <div className="text-xs text-site-muted truncate">@{(u as any).nickname || u.username}</div>
                                            </div>
                                            <div className="text-[11px] text-site-text/80 shrink-0">{already ? 'Уже в группе' : 'Добавить'}</div>
                                          </button>
                                        );
                                      })
                                    )}
                                  </div>
                                </div>,
                                document.body
                              )
                            : null)}

                        {projectAddOpen &&
                          (typeof document !== 'undefined' && document.body
                            ? createPortal(
                                <div
                                  ref={projectAddMenuRef}
                                  className="fixed z-[10060] glass-strong rounded-2xl border border-site-border shadow-[0_20px_70px_rgba(0,0,0,0.55)] overflow-hidden w-[360px] max-w-[calc(100vw_-_16px)]"
                                  style={
                                    projectAddPos
                                      ? { left: projectAddPos.left, top: projectAddPos.top }
                                      : { left: 0, top: 0, opacity: 0, pointerEvents: 'none' }
                                  }
                                >
                                  <div className="p-3">
                                    <div className="text-xs text-site-secondary/80 mb-2">Добавить проект (только где вы участник)</div>
                                    <input
                                      ref={projectAddInputRef}
                                      value={projectAddQuery}
                                      onChange={(e) => setProjectAddQuery(e.target.value)}
                                      placeholder="Поиск проекта..."
                                      className="w-full px-3 py-2 glass-input rounded-xl text-sm text-site-text placeholder-site-muted focus:outline-none"
                                    />
                                  </div>
                                  <div className="max-h-[320px] overflow-y-auto border-t border-site-border divide-y divide-site-border" data-scroll-lock-allow>
                                    {projectAddLoading && myProjects.length === 0 ? (
                                      <div className="px-4 py-4 text-sm text-site-muted">Загрузка проектов...</div>
                                    ) : projectAddCandidates.length === 0 ? (
                                      <div className="px-4 py-4 text-sm text-site-muted">
                                        {myProjects.length === 0 ? 'У вас пока нет проектов' : 'Нет подходящих проектов'}
                                      </div>
                                    ) : (
                                      projectAddCandidates.slice(0, 80).map((p) => (
                                        <button
                                          key={p.id}
                                          type="button"
                                          onClick={() => void pickRoomProject(p.id)}
                                          className="w-full px-4 py-2.5 text-left hover:bg-site-text/6 transition-colors flex items-center gap-3"
                                        >
                                          <span className="w-9 h-9 rounded-full glass-btn flex items-center justify-center shrink-0"><UiIcon name="PuzzlePiece" /></span>
                                          <div className="min-w-0 flex-1">
                                            <div className="text-sm font-semibold text-site-text truncate">{p.name || `Проект #${p.id}`}</div>
                                            <div className="text-xs text-site-muted truncate">
                                              #{p.id}
                                              {p.status ? ` · ${p.status}` : ''}
                                              {typeof p.progress === 'number' ? ` · ${p.progress}%` : ''}
                                            </div>
                                          </div>
                                          <div className="text-[11px] text-site-text/80 shrink-0">Добавить</div>
                                        </button>
                                      ))
                                    )}
                                  </div>
                                </div>,
                                document.body
                              )
                            : null)}

                        {roomAvatarCropFile && (
                          <ImageCropper
                            file={roomAvatarCropFile}
                            aspectRatio={1}
                            mode="avatar"
                            allowSkip={false}
                            title={selectedRoom?.type === 'channel' ? 'Фото канала' : 'Фото группы'}
                            confirmText="Установить фотографию"
                            onCancel={() => setRoomAvatarCropFile(null)}
                            onCrop={(cropped) => {
                              setRoomAvatarCropFile(null);
                              void uploadRoomAvatar(cropped);
                            }}
                          />
                        )}

                        <div className="flex-1 min-h-0 overflow-y-scroll overscroll-contain p-3 sm:p-4 space-y-3" data-scroll-lock-allow>
                          {/* Комментарий на русском: “профиль” комнаты (фото/имя/описание/участники). */}
                          <div className="rounded-2xl border border-site-border bg-site-bg/35 backdrop-blur-sm overflow-hidden">
                            <div className="px-4 py-4 flex items-start gap-4">
                              <div className="relative w-16 h-16 rounded-full overflow-hidden glass-btn flex items-center justify-center text-site-text text-2xl shrink-0">
                                {roomMeta?.avatar_url ? (
                                  <img src={roomMeta.avatar_url} alt={title} className="w-full h-full object-cover" />
                                ) : (
                                  <UiIcon name={icon} />
                                )}
                                {selectedRoom.type !== 'assistant' && canManage && (
                                  <button
                                    type="button"
                                    onClick={openRoomAvatarPicker}
                                    disabled={roomAvatarUploading}
                                    className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9] disabled:opacity-60 disabled:cursor-not-allowed"
                                    title="Загрузить фото"
                                    aria-label="Загрузить фото"
                                  >
                                    {roomAvatarUploading ? <UiIcon name="Hourglass" /> : <UiIcon name="Image" />}
                                  </button>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <input
                                  value={roomTitleDraft}
                                  onChange={(e) => setRoomTitleDraft(e.target.value)}
                                  disabled={!canManage}
                                  className="w-full px-3 py-2 glass-input rounded-xl text-sm text-site-text placeholder-site-muted focus:outline-none disabled:opacity-70"
                                  placeholder={selectedRoom.type === 'channel' ? 'Название канала' : 'Название группы'}
                                />
                                <div className="mt-1 text-xs text-site-muted">{membersCount ? `${membersCount} участников` : '—'}</div>
                              </div>
                              {selectedRoom.type !== 'assistant' && canManage && (
                                <button
                                  type="button"
                                  onClick={() => void saveRoomMeta()}
                                  disabled={roomMetaBusy || !roomTitleDraft.trim()}
                                  className="px-3 py-2 rounded-xl glass-btn text-site-text/90 hover:text-site-text transition disabled:opacity-60 disabled:cursor-not-allowed"
                                  title="Сохранить"
                                  aria-label="Сохранить настройки"
                                >
                                  <UiIcon name="FloppyDisk" />
                                </button>
                              )}
                            </div>
                            <div className="px-4 pb-4">
                              <textarea
                                value={roomDescDraft}
                                onChange={(e) => setRoomDescDraft(e.target.value)}
                                disabled={!canManage}
                                className="w-full px-3 py-2 glass-input rounded-xl text-sm text-site-text placeholder-site-muted focus:outline-none min-h-[72px] resize-none disabled:opacity-70"
                                placeholder="Короткое описание"
                              />

                              {/* Комментарий на русском: вкладки настроек — горизонтально и сразу под описанием (desktop+mobile). */}
                              {selectedRoom.type !== 'assistant' && (
                                <div className="mt-3">
                                  {/* Комментарий на русском: общий контейнер + разделители, при переполнении — горизонтальный скролл. */}
                                  <div className="-mx-1 px-1 overflow-x-auto pb-1 scrollbar-hide" data-scroll-lock-allow>
                                    <div className="flex w-max mx-auto rounded-xl overflow-hidden border border-site-border bg-site-bg/25 backdrop-blur-sm divide-x divide-site-border">
                                      {tabs.map((t) => {
                                        const active = roomSettingsTab === t.id;
                                        return (
                                          <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => setRoomSettingsTab(t.id)}
                                            className={`px-3 py-2 text-[12px] sm:text-xs leading-none flex items-center gap-2 whitespace-nowrap transition-colors ${
                                              active ? 'bg-site-success/18 text-site-text' : 'text-site-text/80 hover:bg-site-text/6 hover:text-site-text'
                                            }`}
                                            title={t.label}
                                            aria-label={t.label}
                                          >
                                            <span className="font-semibold">{t.label}</span>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {selectedRoom.type === 'assistant' ? (
                            <div className="rounded-2xl border border-site-border bg-site-bg/25 p-4 text-site-secondary text-sm">
                              Настройки помощника сделаем позже.
                            </div>
                          ) : (
                            <>
                              {/* ===== ВКЛАДКА: Участники ===== */}
                              {roomSettingsTab === 'members' && (
                                <div className="rounded-2xl border border-site-border bg-site-bg/25 overflow-hidden">
                                  <div className="px-4 py-3 flex items-center justify-between gap-3">
                                    <div className="text-sm font-semibold text-site-text">Участники</div>
                                    {canManage && (
                                      <button
                                        ref={memberAddBtnRef}
                                        type="button"
                                        onClick={toggleMemberAddMenu}
                                        className="w-9 h-9 rounded-full glass-btn glass-btn-green text-site-text flex items-center justify-center transition active:scale-[0.9]"
                                        title="Добавить участника"
                                        aria-label="Добавить участника"
                                        aria-haspopup="dialog"
                                        aria-expanded={memberAddOpen}
                                      >
                                        {memberAddOpen ? '✕' : '+'}
                                      </button>
                                    )}
                                  </div>
                                  <div className="divide-y divide-site-border">
                                    {roomMembers.length === 0 ? (
                                      <div className="px-4 py-4 text-sm text-site-muted">Пока нет участников</div>
                                    ) : (
                                      roomMembers.map((m) => {
                                        const u = m.user;
                                        const name = u?.display_name || u?.username || 'Пользователь';
                                        const initial = (name[0] || '?').toUpperCase();
                                        const role = String((m as any)?.role || '');
                                        const roleLabel = role === 'owner' ? 'Создатель' : role === 'admin' ? 'Админ' : 'Участник';
                                        return (
                                          <div key={u.id} className="px-4 py-2.5 flex items-center gap-3">
                                            <AvatarFx
                                              sizeClass="w-9 h-9"
                                              avatarUrl={u.avatar_url || null}
                                              alt={name}
                                              fallback={initial}
                                              isOnline={!!u.is_online}
                                              fxColor={u.avatar_fx_color}
                                              fxThickness={u.avatar_fx_thickness}
                                              fxGlow={u.avatar_fx_glow}
                                              fxOfflineColor={u.avatar_fx_offline_color}
                                            />
                                            <div className="min-w-0 flex-1">
                                              <div className="text-sm text-site-text font-semibold truncate">{name}</div>
                                              <div className="text-xs text-site-muted truncate">@{(u as any).nickname || u.username}</div>
                                            </div>
                                            <div className="text-[11px] text-site-secondary/80 shrink-0">{roleLabel}</div>
                                          </div>
                                        );
                                      })
                                    )}
                                  </div>
                                </div>
                              )}

                                {/* ===== ВКЛАДКА: Медиа ===== */}
                                {roomSettingsTab === 'media' && (
                                  <div className="rounded-2xl border border-site-border bg-site-bg/25 overflow-hidden">
                                    <div className="px-4 py-3 text-sm font-semibold text-site-text">Медиа</div>
                                    <div className="px-4 pb-4">
                                      {roomPhotos.length === 0 ? (
                                        <div className="text-sm text-site-muted">Нет фотографий</div>
                                      ) : (
                                        <div className="grid grid-cols-3 gap-2">
                                          {roomPhotos.map((p) => {
                                            const url = resolveChatMediaUrl(String(p.url || ''));
                                            const src = String(mediaSrcByUrl[url] || '');
                                            return (
                                              <button
                                                key={p.id}
                                                type="button"
                                                onClick={() => openPhotoViewerList(roomPhotos.map((x) => resolveChatMediaUrl(String(x.url || ''))), url)}
                                                className="block text-left"
                                                title="Открыть фото"
                                              >
                                                <div className="w-full aspect-square rounded-xl border border-site-border overflow-hidden bg-site-bg/30 flex items-center justify-center">
                                                  {src ? (
                                                    <img src={src} alt="Фото" className="w-full h-full object-cover" loading="lazy" draggable={false} />
                                                  ) : (
                                                    <button
                                                      type="button"
                                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); void downloadPeerMediaPhoto(url); }}
                                                      className="w-10 h-10 rounded-full bg-black/35 border border-site-border text-site-text/90 flex items-center justify-center hover:bg-black/45 transition active:scale-[0.92]"
                                                      title="Загрузить"
                                                      aria-label="Загрузить фото"
                                                    >
                                                      <UiIcon name="ArrowDown" />
                                                    </button>
                                                  )}
                                                </div>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* ===== ВКЛАДКА: Проекты ===== */}
                                {roomSettingsTab === 'projects' && (
                                  <div className="rounded-2xl border border-site-border bg-site-bg/25 overflow-hidden">
                                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                                      <div className="text-sm font-semibold text-site-text">Проекты</div>
                                      {canManage && (
                                        <button
                                          ref={projectAddBtnRef}
                                          type="button"
                                          onClick={toggleProjectAddMenu}
                                          className="w-9 h-9 rounded-full glass-btn glass-btn-green text-site-text flex items-center justify-center transition active:scale-[0.9]"
                                          title="Добавить проект"
                                          aria-label="Добавить проект"
                                          aria-haspopup="dialog"
                                          aria-expanded={projectAddOpen}
                                        >
                                          {projectAddOpen ? '✕' : '+'}
                                        </button>
                                      )}
                                    </div>
                                    <div className="divide-y divide-site-border">
                                      {roomProjects.length === 0 ? (
                                        <div className="px-4 py-4 text-sm text-site-muted">Пока нет проектов</div>
                                      ) : (
                                        roomProjects.map((p) => (
                                          <Link
                                            key={p.id}
                                            href={`/projects/${p.id}`}
                                            className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-site-text/5 transition-colors"
                                          >
                                            <div className="min-w-0">
                                              <div className="text-sm font-semibold text-site-text truncate">{p.name || `Проект #${p.id}`}</div>
                                              <div className="text-xs text-site-muted truncate">
                                                {p.status ? `Статус: ${p.status}` : 'Проект'}
                                                {typeof p.progress === 'number' ? ` · ${p.progress}%` : ''}
                                              </div>
                                            </div>
                                            <div className="text-site-muted shrink-0">→</div>
                                          </Link>
                                        ))
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* ===== ВКЛАДКА: Файлы ===== */}
                                {roomSettingsTab === 'files' && (
                                  <div className="rounded-2xl border border-site-border bg-site-bg/25 overflow-hidden">
                                    <div className="px-4 py-3 text-sm font-semibold text-site-text">Файлы</div>
                                    <div className="divide-y divide-site-border">
                                      {roomFiles.length === 0 ? (
                                        <div className="px-4 py-4 text-sm text-site-muted">Нет файлов</div>
                                      ) : (
                                        roomFiles.map((f) => (
                                          <a
                                            key={f.id}
                                            href={f.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="px-4 py-3 flex items-center gap-3 hover:bg-site-text/5 transition-colors"
                                          >
                                            <span className="w-9 h-9 rounded-full glass-btn flex items-center justify-center shrink-0"><UiIcon name="Paperclip" /></span>
                                            <div className="min-w-0 flex-1">
                                              <div className="text-sm font-semibold text-site-text truncate">{f.name || 'Файл'}</div>
                                              <div className="text-xs text-site-muted truncate">{f.url}</div>
                                            </div>
                                            <div className="text-site-muted shrink-0">→</div>
                                          </a>
                                        ))
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* ===== ВКЛАДКА: Дизайн (фон комнаты) ===== */}
                                {roomSettingsTab === 'design' && (
                                  <div className="space-y-4">
                                    {!canManage && (
                                      <div className="rounded-xl bg-site-warning/10 border border-site-warning/25 px-4 py-3 text-sm text-site-warning">
                                        Только администратор или владелец комнаты может менять фон
                                      </div>
                                    )}

                                    <div className="rounded-2xl border border-site-border bg-site-bg/25 overflow-hidden divide-y divide-site-border">
                                      <div className="px-4 py-3">
                                        <div className="text-sm font-semibold text-site-text">Фон комнаты</div>
                                        <div className="text-xs text-site-muted mt-1">Общий фон виден всем участникам</div>
                                      </div>

                                      <div className="px-4 py-3 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                          <div className="text-sm text-site-text">Картинка фона</div>
                                          <div className="text-xs text-site-muted truncate">
                                            {roomWallpaper?.id ? 'Фон установлен' : 'Фон не установлен'}
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={openDmWallpaperModal}
                                          disabled={!canManage}
                                          className="px-3 py-1.5 rounded-lg glass-btn border border-site-border text-xs text-site-text/90 hover:text-site-text disabled:opacity-60 transition active:scale-[0.98] inline-flex items-center gap-2"
                                          title="Выбрать картинку фона"
                                        >
                                          <MonoWallpaperIcon className="w-4 h-4" />
                                          Выбрать
                                        </button>
                                      </div>

                                      <div className="px-4 py-3 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                          <div className="text-sm text-site-text">Размытие</div>
                                          <div className="text-xs text-site-muted truncate">0–30px</div>
                                        </div>
                                        <div className="flex items-center gap-3 w-56">
                                          <input
                                            type="range"
                                            min={0}
                                            max={30}
                                            value={Number(dmWallpaperBlurDraft || 0) || 0}
                                            onChange={(e) => setDmWallpaperBlurDraft(Number(e.target.value || 0) || 0)}
                                            disabled={!roomWallpaper?.id || !canManage}
                                            className="w-full disabled:opacity-50"
                                            aria-label="Размытие фона"
                                          />
                                          <div className="text-xs text-site-secondary w-10 text-right">{Number(dmWallpaperBlurDraft || 0) || 0}px</div>
                                        </div>
                                      </div>

                                      <div className="px-4 py-3 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                          <div className="text-sm text-site-text">Яркость</div>
                                          <div className="text-xs text-site-muted truncate">50–150%</div>
                                        </div>
                                        <div className="flex items-center gap-3 w-56">
                                          <input
                                            type="range"
                                            min={50}
                                            max={150}
                                            value={Number(dmWallpaperBrightnessDraft || 100) || 100}
                                            onChange={(e) => setDmWallpaperBrightnessDraft(Number(e.target.value || 100) || 100)}
                                            disabled={!roomWallpaper?.id || !canManage}
                                            className="w-full disabled:opacity-50"
                                            aria-label="Яркость фона"
                                          />
                                          <div className="text-xs text-site-secondary w-10 text-right">{Number(dmWallpaperBrightnessDraft || 100) || 100}%</div>
                                        </div>
                                      </div>

                                      {/* Комментарий на русском: кнопка "Сохранить" убрана — авто-сохранение по ползункам. */}
                                    </div>

                                    {roomWallpaperSrc && (
                                      <div className="rounded-2xl border border-site-border bg-site-bg/25 p-4">
                                        <div className="text-xs text-site-muted mb-2">Предпросмотр</div>
                                        <div className="relative w-full aspect-[3/4] rounded-xl overflow-hidden border border-site-border">
                                          <div
                                            className="absolute inset-0 bg-center bg-cover"
                                            style={{
                                              backgroundImage: `url(${roomWallpaperSrc})`,
                                              filter: `blur(${Number(dmWallpaperBlurDraft || 0) || 0}px) brightness(${Number(dmWallpaperBrightnessDraft || 100) || 100}%)`,
                                              transform: Number(dmWallpaperBlurDraft || 0) ? 'scale(1.06)' : 'none',
                                            }}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {!roomSettingsOpen && (
                      <>
                        {roomWallpaperSrc && (
                          <div className="absolute inset-0 -z-10 pointer-events-none">
                            <div
                              className="absolute inset-0 bg-center bg-cover"
                              style={{
                                backgroundImage: `url(${roomWallpaperSrc})`,
                                filter: `blur(${Number(roomWallpaper?.blur || 0) || 0}px) brightness(${Number(roomWallpaper?.brightness || 100) || 100}%)`,
                                transform: Number(roomWallpaper?.blur || 0) ? 'scale(1.06)' : 'none',
                              }}
                            />
                          </div>
                        )}

                        {/* Комментарий на русском: капля комнаты по центру — на hover раскрывает вниз строку поиска (без отдельной кнопки "Поиск" и без кнопки очистки). */}
                        <div ref={inChatSearchRootRef} className="absolute left-1/2 -translate-x-1/2 top-2 z-30 group flex flex-col items-center">
                          {/* Комментарий на русском: фиксируем радиус, чтобы при раскрытии вниз скругления не менялись (растут только боковые линии). */}
                          <div className="inline-block max-w-[92vw] sm:max-w-none rounded-[28px] glass-btn messenger-peer-pill--desktop border border-site-border shadow-lg bg-site-bg/90 backdrop-blur relative">
                            {!!(selectedRoom as any)?.voice_call?.active && (
                              <>
                                {/* Комментарий на русском: визуальный индикатор активного голосового чата — “волны” вокруг островка. */}
                                <span className="pointer-events-none absolute -inset-2 rounded-[34px] border-2 border-site-warning/25 animate-ping" />
                                <span className="pointer-events-none absolute -inset-1 rounded-[32px] border border-site-warning/35 animate-pulse" />
                              </>
                            )}
                        <button
                          type="button"
                          onClick={() => {
                                // Комментарий на русском: на тач-устройствах 1-й тап открывает поиск, 2-й тап открывает настройки.
                                if (isMobileLikeViewport()) {
                                  if (!inChatSearchOpen) {
                                    setInChatSearchOpen(true);
                                    requestAnimationFrame(() => inChatSearchInputRef.current?.focus());
                                    return;
                                  }
                                  setInChatSearchOpen(false);
                                }
                            setRoomSettingsTab('members');
                            setRoomSettingsOpen(true);
                          }}
                              className="w-full px-3.5 py-[0.45rem] text-left active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-success/30 touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' } as any}
                          title="Настройки"
                          aria-label="Открыть настройки комнаты"
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 min-w-[2.5rem] min-h-[2.5rem] shrink-0 flex items-center justify-center overflow-visible">
                              <div className="w-10 h-10 sm:w-[3.375rem] sm:h-[3.375rem] rounded-full overflow-hidden glass-btn shrink-0 sm:-translate-x-2 flex items-center justify-center text-lg">
                                    {headerAvatarUrl ? <img src={headerAvatarUrl} alt={title} className="w-full h-full object-cover" /> : icon}
                              </div>
                            </div>
                            <div className="min-w-0 flex flex-col text-left">
                              <span className="text-sm font-semibold text-site-text truncate text-center">{title}</span>
                              <span className="text-[11px] text-site-text truncate">{headerSubtitle}</span>
                            </div>
                          </div>
                        </button>

                            <div
                              className={`transition-all duration-200 overflow-hidden ${
                                inChatSearchOpen ? 'max-h-[96px] opacity-100 pointer-events-auto' : 'max-h-0 opacity-0 pointer-events-none'
                              } group-hover:max-h-[96px] group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:max-h-[96px] group-focus-within:opacity-100 group-focus-within:pointer-events-auto`}
                            >
                              <div className="px-3.5 pb-2 pt-1 border-t border-site-border">
                                <div className="flex items-center gap-2">
                                  <input
                                    ref={inChatSearchInputRef}
                                    value={inChatSearchQuery}
                                    onChange={(e) => {
                                      setInChatSearchQuery(e.target.value);
                                      setInChatSearchActiveIdx(0);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        e.preventDefault();
                                        setInChatSearchQuery('');
                                        setInChatSearchActiveIdx(0);
                                        setInChatSearchOpen(false);
                                        (e.currentTarget as HTMLInputElement).blur();
                                        return;
                                      }
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        goInChatSearchNext();
                                      }
                                    }}
                                    placeholder="Поиск в чате…"
                                    className="flex-1 h-9 px-3 rounded-xl bg-site-bg/40 border border-site-border text-site-text text-sm placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-success/30 select-text"
                                    inputMode="search"
                                    autoComplete="off"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setInChatSearchOpen(false);
                                      setVoicePanelOpen(true);
                                    }}
                                    className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-xl glass-btn border transition active:scale-[0.98] ${
                                      !!(selectedRoom as any)?.voice_call?.active
                                        ? 'border-site-warning/30 text-site-warning hover:text-site-text bg-site-warning/10'
                                        : 'border-site-border text-site-text/90 hover:text-site-text'
                                    }`}
                                    title="Голосовой чат"
                                    aria-label="Голосовой чат"
                                  >
                                    <span className="text-[18px] leading-none"><UiIcon name="Microphone" /></span>
                                  </button>
                                </div>
                                <div className="mt-1 text-[10px] text-site-text/80">
                                  {inChatSearch.q
                                    ? inChatSearch.items.length
                                      ? `Найдено: ${inChatSearch.items.length} · совпадений: ${inChatSearch.total}`
                                      : 'Ничего не найдено'
                                    : `Поиск по сообщениям ${typeLabel}`}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Комментарий на русском: на мобильных список тоже показываем (иначе приходится прятать панель ввода). */}
                          {inChatSearch.q && (
                            <div
                              className={`mt-2 transition-all duration-200 ${
                                inChatSearchOpen ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none -translate-y-1'
                              } group-hover:opacity-100 group-hover:pointer-events-auto group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0`}
                            >
                              <div className="mx-auto rounded-2xl glass-strong border border-site-border shadow-[0_20px_70px_rgba(0,0,0,0.55)] overflow-hidden w-[min(92vw,320px)] bg-site-bg/90 backdrop-blur">
                                <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-site-border">
                                  <div className="text-xs text-site-text">
                                    {inChatSearch.items.length
                                      ? `${Math.min(inChatSearchActiveIdx + 1, inChatSearch.items.length)}/${inChatSearch.items.length}`
                                      : '0/0'}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      disabled={!inChatSearch.items.length}
                                      onClick={goInChatSearchPrev}
                                      className="w-8 h-8 rounded-xl glass-btn text-site-text/80 hover:text-site-text disabled:opacity-40 flex items-center justify-center transition active:scale-[0.92]"
                                      title="Предыдущее"
                                      aria-label="Предыдущее совпадение"
                                    >
                                      ↑
                                    </button>
                                    <button
                                      type="button"
                                      disabled={!inChatSearch.items.length}
                                      onClick={goInChatSearchNext}
                                      className="w-8 h-8 rounded-xl glass-btn text-site-text/80 hover:text-site-text disabled:opacity-40 flex items-center justify-center transition active:scale-[0.92]"
                                      title="Следующее"
                                      aria-label="Следующее совпадение"
                                    >
                                      ↓
                                    </button>
                                  </div>
                                </div>
                                <div ref={inChatSearchListRef} className="max-h-[132px] overflow-y-auto">
                                  {inChatSearch.items.length === 0 ? (
                                    <div className="px-3 py-3 text-xs text-site-secondary/80">Нет совпадений</div>
                                  ) : (
                                    inChatSearch.items.map((it, idx) => (
                                      <button
                                        key={it.id}
                                        type="button"
                                        data-inchat-find-id={it.id}
                                        onClick={() => goToInChatSearchResult(idx)}
                                        className={`w-full px-3 py-2 text-left hover:bg-site-text/6 transition-colors flex items-start justify-between gap-3 ${
                                          idx === inChatSearchActiveIdx ? 'bg-site-text/6' : ''
                                        }`}
                                        title="Перейти к сообщению"
                                      >
                                        <div className="min-w-0">
                                          <div className="text-xs text-site-text/90 line-clamp-2">{it.snippet}</div>
                                        </div>
                                        <div className="shrink-0 text-[11px] text-site-text/80">×{it.count}</div>
                                      </button>
                                    ))
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Комментарий на русском: список сообщений для комнат (CSS-скролл, без virtuoso). */}
                        <div className="flex-1 min-h-0" data-scroll-lock-allow>
                          <MessageList
                            key={selectedRoom?.id ? `room:${selectedRoom.id}` : selectedUser?.id ? `dm:${selectedUser.id}` : 'chat:none'}
                            chatKey={selectedRoom?.id ? `room:${selectedRoom.id}` : selectedUser?.id ? `dm:${selectedUser.id}` : ''}
                            chatId={selectedRoom?.id ? `room:${selectedRoom.id}` : selectedUser?.id ? `dm:${selectedUser.id}` : ''}
                            ref={virtuosoRef}
                            meId={meRef.current?.id || 0}
                            showAvatar={true}
                            bottomInsetPx={chatBottomInsetPx}
                            scrollerRef={(el) => {
                              // Комментарий на русском: привязываем старый messagesScrollRef к реальному CSS-скроллеру.
                              messagesScrollRef.current = el;
                            }}
                            pinnedMessageId={pinnedMessageId}
                            loadingOlder={loadingOlder}
                            loadingMessages={loadingMessages}
                            hasMoreMessages={hasMoreMessages}
                            linkPreviewsEnabled={linkPreviewsEnabled}
                            selectionMode={selectionMode}
                            selectedMsgKeySet={selectedMsgKeySet}
                            emptyText="Пока пусто. Напишите первым."
                            enableStacking={messageStackingEnabled}
                            onLoadOlder={loadOlderRoomMessages}
                            onScrollPositionChange={(atBottom, offset) => {
                              isNearBottomRef.current = atBottom;
                              dmScrollBottomOffsetRef.current = offset;
                              // Комментарий на русском: не вызываем setState если значение не изменилось — бережём iOS momentum.
                              const next = !atBottom && offset >= 60;
                              if (showScrollToBottomRef.current !== next) {
                                showScrollToBottomRef.current = next;
                                setShowScrollToBottom(next);
                              }
                            }}
                            onOpenPhotoViewer={openChatPhotoViewer}
                            onDelete={deleteMessage as any}
                            onReply={startReply as any}
                            onReact={reactToMessage as any}
                            onToggleSelect={toggleSelectMessage as any}
                            onForward={openForwardOne as any}
                            onEdit={startEditMessage as any}
                            onPin={togglePinMessage as any}
                            msgKeyOf={msgKeyOf as any}
                            togglePinMessage={togglePinMessage as any}
                          />
                          {renderPhotoViewerPortal()}
                        </div>

                        <div className="relative shrink-0 h-0 overflow-visible">
                          <div
                            ref={composePanelRef}
                            className="absolute left-0 right-0 bottom-0 px-3 pb-3 sm:px-4 sm:pb-4 pt-2 overflow-visible"
                          >
                            {error && (
                              <div className="mb-2 px-3 py-2 bg-site-danger/12 border border-site-danger/20 rounded-xl text-site-danger text-sm">
                                {error}
                              </div>
                            )}
                            <div className="relative w-full overflow-visible">
                              <div className="w-[calc(68%_-_4px)] sm:w-[calc(85%_-_5px)] mx-auto flex flex-col gap-2">
                                {selectionMode && (
                                  <div className="w-full rounded-2xl glass-subtle border border-site-border px-3 py-2 flex items-center justify-between">
                                    <div className="text-xs text-site-text">
                                      Выбрано: <span className="font-semibold text-site-text">{selectedMsgKeys.length}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={openForwardSelected}
                                        className="w-9 h-9 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/40"
                                        title="Переслать выбранные"
                                        aria-label="Переслать выбранные сообщения"
                                      >
                                        <UiIcon name="ArrowBendUpRight" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={clearSelectedMessages}
                                        className="w-9 h-9 rounded-full glass-btn text-site-text/80 hover:text-site-text flex items-center justify-center transition active:scale-[0.9] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/40"
                                        title="Отменить выбор"
                                        aria-label="Отменить выбор сообщений"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  </div>
                                )}

                                <div className="relative w-full rounded-2xl glass-input text-site-text" data-compose-bubble>
                                  {editTarget && (
                                    <div className="flex items-center gap-2 px-4 py-2 border-b border-site-border bg-site-surface/20 rounded-t-2xl">
                                      <div className="w-1 self-stretch rounded-full bg-site-success/60" aria-hidden="true" />
                                      <div className="min-w-0 flex-1">
                                        <div className="text-[11px] text-site-text truncate">Изменение сообщения</div>
                                        <div className="text-[11px] text-site-muted truncate">{String((editTarget as any)?.message || '')}</div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={cancelEditMessage}
                                        className="w-8 h-8 rounded-full glass-btn text-site-text/80 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
                                        title="Отменить редактирование"
                                        aria-label="Отменить редактирование"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  )}
                                  {replyTo && (
                                    <div className="flex items-center gap-2 px-4 py-2 border-b border-site-border bg-site-surface/20 rounded-t-2xl">
                                      <div className="w-1 self-stretch rounded-full bg-site-accent/60" aria-hidden="true" />
                                      <div className="min-w-0 flex-1">
                                        <div className="text-[11px] text-site-text truncate">
                                          Ответ: {replyTo.from_user?.id === (meRef.current?.id || 0) ? 'Вы' : (replyTo.from_user?.display_name || replyTo.from_user?.username || 'Сообщение')}
                                        </div>
                                        <div className="text-[11px] text-site-muted truncate">
                                          {(() => {
                                            const t = String(replyTo.message || '');
                                            const isV =
                                              (replyTo as any)?.type === 'voice_message' ||
                                              t.includes('/chat_voice/') ||
                                              t.includes('/uploads/chat_voice/') ||
                                              t.includes('/media/chat_voice/');
                                            return isV ? 'Голосовое сообщение' : (t || '...');
                                          })()}
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => setReplyTo(null)}
                                        className="text-site-secondary/80 hover:text-site-text text-lg leading-none px-1"
                                        title="Отменить ответ"
                                        aria-label="Отменить ответ"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  )}
                                  <PendingPhotosBar disabled={sending || voiceSending} />
                                  <PendingFilesBar disabled={sending || voiceSending} />

                                  {recording ? (
                                    <div className="px-4 py-3">
                                      <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-site-danger whitespace-nowrap">
                                        <span className="w-2 h-2 rounded-full bg-site-danger animate-pulse" aria-hidden="true" />
                                        REC {formatRecTime(voiceElapsedMs)}
                                      </div>
                                      <div className="mt-2 rounded-xl bg-black/20 border border-site-border px-3 py-2">
                                        <canvas ref={voiceCanvasRef} className="w-full h-14" />
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      {roomDraftFirstUrl &&
                                        roomDraftFirstUrl !== roomDraftPreviewSuppressedUrl &&
                                        (roomDraftLinkPreviewLoading || roomDraftLinkPreview) && (
                                          <div className="px-3 pt-2">
                                            <DraftLinkPreviewCard
                                              url={roomDraftFirstUrl}
                                              loading={roomDraftLinkPreviewLoading}
                                              preview={roomDraftLinkPreview}
                                              onClose={() => setRoomDraftPreviewSuppressedUrl(roomDraftFirstUrl)}
                                            />
                                          </div>
                                        )}
                                      <div className="flex items-end gap-2 px-3 py-2">
                                      <button
                                        type="button"
                                        onClick={openPhotoPicker}
                                        disabled={sending || voiceSending}
                                        className={chatAttachBtnClass}
                                        title="Прикрепить фото"
                                        aria-label="Прикрепить фото"
                                      >
                                        {paperclipIcon}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={openFilePicker}
                                        disabled={sending || voiceSending}
                                        className={chatAttachBtnClass}
                                        title="Прикрепить файл"
                                        aria-label="Прикрепить файл"
                                      >
                                        <UiIcon name="FileText" />
                                      </button>
                                      <textarea
                                        ref={roomDraftRef}
                                        value={roomDraft}
                                        onChange={(e) => setRoomDraft(e.target.value)}
                                        onFocus={(e) => {
                                          try {
                                            iosSingleInputCleanupRef.current?.();
                                          } catch {}
                                          iosSingleInputCleanupRef.current = enterIosSingleInputMode(e.currentTarget as any);
                                        }}
                                        title="Enter — отправить · Shift+Enter — новая строка"
                                        onKeyDown={(e) => {
                                          if (e.key === 'Control') {
                                            roomCtrlUsedRef.current = false;
                                            return;
                                          }
                                          if (roomCtrlUsedRef.current === false && e.ctrlKey && e.key !== 'Control') roomCtrlUsedRef.current = true;
                                          if (e.key !== 'Enter') return;
                                          // Комментарий на русском: Shift+Enter (десктоп) и Ctrl/Alt/Meta+Enter — перенос строки.
                                          const allowNewLine = e.shiftKey || e.ctrlKey || e.altKey || e.metaKey;
                                          if (allowNewLine) {
                                            e.preventDefault();
                                            const el = e.currentTarget;
                                            insertNewline(el, roomDraft, setRoomDraft);
                                            return;
                                          }
                                          if (!roomDraft.trim() && !hasPendingPhoto && !hasPendingFile) return;
                                          e.preventDefault();
                                          if (hasPendingPhoto) return void sendPhotoMessage();
                                          if (hasPendingFile) return void sendFileMessage();
                                          void sendRoomMessage();
                                        }}
                                        onKeyUp={(e) => {
                                          if (e.key !== 'Control') return;
                                          const el = e.currentTarget;
                                          const used = roomCtrlUsedRef.current;
                                          roomCtrlUsedRef.current = false;
                                          if (used) return;
                                          insertNewline(el, roomDraft, setRoomDraft);
                                        }}
                                        onBlur={() => {
                                          try {
                                            iosSingleInputCleanupRef.current?.();
                                          } catch {}
                                          iosSingleInputCleanupRef.current = null;
                                        }}
                                        placeholder="Сообщение"
                                        className="flex-1 bg-transparent text-site-text text-base sm:text-sm leading-relaxed resize-none overflow-y-auto scrollbar-hide focus:outline-none px-2 py-2 select-text"
                                        rows={1}
                                        maxLength={MAX_MESSAGE_LENGTH}
                                        disabled={(sending || voiceSending) && isMobileViewport()}
                                      />
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => {
                                  if (recording) return void stopVoiceRecording(false);
                                  setTab('chats');
                                  setSupportOpen(false);
                                  setSupportTargetUser(null);
                                  setSupportMessages([]);
                                  supportLastIdRef.current = 0;
                                  setSelectedRoom(null);
                                  const chatKey = selectedRoom?.id ? `room:${selectedRoom.id}` : '';
                                  if (chatKey) chatMessagesStore.getState().clearChat(chatKey);
                                  lastMessageIdRef.current = 0;
                                }}
                                className={`${chatIconBtnClass} absolute left-[12px] sm:left-0 bottom-6 sm:bottom-0${recording ? ' text-site-danger' : ''}`}
                                title={recording ? 'Отменить запись' : 'Назад'}
                                aria-label={recording ? 'Отменить запись' : 'Назад'}
                              >
                                {recording ? '✕' : '←'}
                              </button>

                              <button
                                type="button"
                                onClick={onRightBtnClick}
                                disabled={sending || voiceSending}
                                className={`${chatIconBtnClass} absolute right-[12px] sm:right-0 bottom-6 sm:bottom-0${
                                  recording ? ' ring-2 ring-site-danger/35 animate-pulse' : ''
                                }`}
                                title={rightBtnTitle}
                                aria-label={rightBtnTitle}
                              >
                                <span className={recording ? 'animate-pulse' : ''}>{rightBtnContent}</span>
                              </button>

                              {showScrollToBottom && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Комментарий на русском: быстрый переход в самый низ (к последнему сообщению) — для групп/каналов.
                                    virtuosoRef.current?.scrollToBottom('smooth');
                                    dmScrollBottomOffsetRef.current = 0;
                                    dmScrollPersistedOffsetRef.current = 0;
                                    isNearBottomRef.current = true;
                                    setShowScrollToBottom(false);
                                  }}
                                  className={`absolute right-0 z-20 w-10 h-10 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.9] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/40${
                                    inChatSearchOpen ? ' hidden sm:flex' : ''
                                  }`}
                                  // Комментарий на русском: позиционирование с учётом подъёма кнопок на мобилках (+3px).
                                  style={{ bottom: 'calc(25px + 3.5rem + 3px)' }}
                                  title="Вниз"
                                  aria-label="Вниз (к последнему сообщению)"
                                >
                                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 5v12" />
                                    <path d="M7 13l5 5 5-5" />
                                  </svg>
                                </button>
                              )}

                              {/* Комментарий на русском: во время поиска прячем кнопку "Вниз" (чтобы не мешала) — навигация поиска в дропдауне сверху. */}
                              {/* Комментарий на русском: стрелки поиска на мобилке показываем в дропдауне (сверху), чтобы не ломать панель ввода. */}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                );
              })()
            ) : (
              <div className="hidden sm:flex flex-1 items-center justify-center text-site-muted">
                {supportOpen && isSuperAdmin ? 'Выберите обращение слева' : 'Выберите чат слева'}
              </div>
            )}
          </div>
          </div>

          {/* Комментарий на русском: скрытый input для выбора фото (скрепка) — один на весь экран. */}
          <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={handlePhotoSelected} className="hidden" />
          {/* Комментарий на русском: скрытый input для выбора файлов (документы/архивы). */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.7z,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip,application/x-zip-compressed,application/vnd.rar,application/x-rar-compressed,application/x-7z-compressed"
            multiple
            onChange={handleFileSelected}
            className="hidden"
          />

          {/* Комментарий на русском: модалка пересылки — выбираем получателя из списка диалогов. */}
          {forwardOpen && (
            <div className="fixed inset-0 z-[10050] flex items-center justify-center px-4">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeForwardModal} />
              <div className="relative w-full max-w-md glass-strong rounded-2xl border border-site-border shadow-[0_25px_90px_rgba(0,0,0,0.65)] overflow-hidden">
                <div className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-site-text">
                    Переслать <span className="text-site-secondary font-normal">({forwardItems.length})</span>
                  </div>
                  <button
                    type="button"
                    onClick={closeForwardModal}
                    className="w-9 h-9 rounded-full glass-btn text-site-text/85 hover:text-site-text flex items-center justify-center transition active:scale-[0.9] focus:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/40"
                    title="Закрыть"
                    aria-label="Закрыть пересылку"
                  >
                    ✕
                  </button>
                </div>
                <div className="px-4 pb-4">
                  <input
                    value={forwardQuery}
                    onChange={(e) => setForwardQuery(e.target.value)}
                    placeholder="Кому переслать..."
                    className="w-full px-3 py-2 glass-input rounded-xl text-sm text-site-text placeholder-site-muted focus:outline-none"
                  />
                  <div className="mt-3 max-h-[50vh] overflow-auto rounded-xl border border-site-border divide-y divide-site-border">
                    {forwardCandidates.length === 0 ? (
                      <div className="p-3 text-sm text-site-muted">Нет подходящих диалогов</div>
                    ) : (
                      forwardCandidates.map((c) => {
                        const u = c.user;
                        const name = u?.display_name || u?.username || 'Пользователь';
                        const initial = (name[0] || '?').toUpperCase();
                        return (
                          <button
                            key={u.id}
                            type="button"
                            disabled={forwardSending}
                            onClick={() => void forwardToUser(u.id)}
                            className={`w-full px-3 py-2 text-left hover:bg-site-text/6 transition-colors flex items-center gap-3 ${
                              forwardSending ? 'opacity-60 cursor-not-allowed' : ''
                            }`}
                          >
                            <div className="w-9 h-9 rounded-full glass-btn flex items-center justify-center text-sm text-site-text/90 shrink-0">
                              {initial}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-site-text truncate">{name}</div>
                              <div className="text-xs text-site-muted truncate">@{(u as any)?.nickname || u?.username || 'user'}</div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                  {!isOnline && (
                    <div className="mt-2 text-xs text-site-muted">
                      Сейчас офлайн — пересылка попадёт в очередь и уйдёт при восстановлении связи.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Комментарий на русском: модальная панель голосового чата комнаты */}
          {voicePanelOpen && selectedRoom && typeof document !== 'undefined' && document.body && createPortal(
            <div className="fixed inset-0 z-[10100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
              <VoicePanel
                roomId={selectedRoom.id}
                currentUserId={meRef.current?.id || 0}
                onClose={() => setVoicePanelOpen(false)}
                onError={(err) => {
                  setError(err);
                  setTimeout(() => setError(null), 3000);
                }}
              />
            </div>,
            document.body
          )}
        </Card>
      </div>
    </div>
  );
}

export default function MessagesPage() {
  // Важно для next build: useSearchParams() должен быть внутри Suspense boundary
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center text-site-text">
          <div className="lisad-top-loader" />
          <div className="animate-pulse">Загрузка...</div>
        </div>
      }
    >
      <MessagesPageInner />
    </Suspense>
  );
}
